import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { supabase } from "./supabase";

const C = {
  // ── SQUADRA · colori dal logo: nero #16171A, arancio #E3701F, fondo #FAFAF8 ──
  bg: "#FAFAF8", sidebar: "#F3F3F0", card: "#ffffff", cardHover: "#F3F3F0",
  border: "#E7E6E1", borderLight: "#EFEEEA",
  accent: "#16171A", accentGlow: "rgba(227,112,31,0.12)",
  brand: "#E3701F", brandSoft: "#FCEBDD",
  text: "#16171A", textSub: "#6E6F73", textMuted: "#A3A4A8",
  green: "#2E8B57", red: "#C2412F", blue: "#2F6FDB", purple: "#7A5AA6", orange: "#E3701F",
  shadowSm: "0 1px 2px rgba(22,23,26,0.04)",
  shadowMd: "0 4px 16px rgba(22,23,26,0.06)",
  shadowLg: "0 16px 48px rgba(22,23,26,0.12)",
  radiusSm: 10, radiusMd: 14, radiusLg: 22, radiusXl: 28,
  font: "'Inter',-apple-system,BlinkMacSystemFont,'SF Pro Text','Segoe UI',Helvetica,Arial,sans-serif",
  fontBrand: "'Space Grotesk','Inter',-apple-system,system-ui,sans-serif",
};

// Font, titolo e icona del browser
if (typeof document !== "undefined" && !document.getElementById("sq-fonts")) {
  const l = document.createElement("link");
  l.id = "sq-fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
  document.title = "Squadra";
  document.querySelectorAll("link[rel~='icon'],link[rel='apple-touch-icon']").forEach(n => n.remove());
  const fav = Object.assign(document.createElement("link"), { rel: "icon" });
  fav.type = "image/svg+xml";
  fav.href = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><rect width="512" height="512" rx="112" fill="#16171A"/><g transform="translate(146 146) scale(2.2)" fill="none"><path d="M14 14 L14 86 L86 86 Z" stroke="#FAFAF8" stroke-width="9" stroke-linejoin="round"/><circle cx="14" cy="14" r="13" fill="#FAFAF8"/><circle cx="14" cy="86" r="13" fill="#FAFAF8"/><circle cx="86" cy="86" r="14" fill="#E3701F"/></g></svg>');
  document.head.appendChild(fav);
}

// ── LOGO SQUADRA ──────────────────────────────────────────────────────────────
function SquadraMark({size=26,inverted=false}){
  const ink = inverted ? "#FAFAF8" : C.text;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true" style={{flexShrink:0}}>
      <path d="M14 14 L14 86 L86 86 Z" stroke={ink} strokeWidth="9" strokeLinejoin="round"/>
      <circle cx="14" cy="14" r="13" fill={ink}/>
      <circle cx="14" cy="86" r="13" fill={ink}/>
      <circle cx="86" cy="86" r="14" fill={C.brand}/>
    </svg>
  );
}
function SquadraLogo({size=24}){
  return (
    <div style={{display:"flex",alignItems:"center",gap:Math.round(size*0.42)}}>
      <SquadraMark size={size}/>
      <span style={{fontFamily:C.fontBrand,fontWeight:600,fontSize:Math.round(size*0.92),letterSpacing:-0.6,color:C.text,lineHeight:1}}>squadra</span>
    </div>
  );
}

const STATI_COLOR = {
  "Preventivo": "#8A8B90", "Confermato": "#2F6FDB",
  "Programmato": "#E3701F", "Da organizzare": "#6B7A8F",
  "Completato": "#2E8B57", "Sospeso": "#B38B1E", "Annullato": "#C2412F",
  "Rifiutato": "#9A6B8F",
};
const MOTIVI_RIFIUTO = ["Prezzo troppo alto","Scelto un altro posatore","Tempi non compatibili","Lavoro rimandato","Cliente non risponde","Altro"];

const fmt = (d) => d ? new Date(d).toLocaleDateString("it-IT",{day:"2-digit",month:"short"}) : "—";
const fmtFull = (d) => d ? new Date(d).toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"}) : "—";
const today = new Date().toISOString().split("T")[0];
function getDaysInMonth(y,m){return new Date(y,m+1,0).getDate();}
function getFirstDayOfMonth(y,m){return new Date(y,m,1).getDay();}

function giorniLavoroRange(lavoro){
  if(!lavoro?.data_inizio||!lavoro?.data_fine) return [];
  const days=[]; let d=new Date(lavoro.data_inizio); const end=new Date(lavoro.data_fine);
  while(d<=end){ days.push(d.toISOString().split("T")[0]); d.setDate(d.getDate()+1); }
  return days;
}

function rangeDates(start,end){
  const days=[]; let d=new Date(start); const e=new Date(end);
  while(d<=e){ days.push(d.toISOString().split("T")[0]); d.setDate(d.getDate()+1); }
  return days;
}

// Ricostruisce i "periodi" (blocchi di giorni consecutivi con la stessa squadra)
// già assegnati su un lavoro, per mostrarli nel modale Assegna.
function blocchiAssegnazione(lavoro){
  const gl = (lavoro?.giorni_lavoro && lavoro.giorni_lavoro.length>0) ? [...lavoro.giorni_lavoro].sort() : giorniLavoroRange(lavoro);
  if(gl.length===0) return [];
  const gd = lavoro?.giorni_dettaglio||{};
  const opsOf = (ds)=>{
    const det = gd[ds];
    const sub = (det||[]).find(iv=>iv.subappalto)?.subappalto;
    if(sub && !(det||[]).some(iv=>(iv.operai_orari||[]).length>0)) return `sub:${sub.nome}`;
    if(det && det.length>0) return [...new Set(det.flatMap(iv=>(iv.operai_orari||[]).map(o=>o.operaio_id)))].sort((a,b)=>a-b).join(",");
    return (lavoro.operai_ids||[]).slice().sort((a,b)=>a-b).join(",");
  };
  const blocchi=[]; let cur=null;
  gl.forEach(ds=>{
    const key = opsOf(ds);
    const consecutivo = cur && (new Date(ds)-new Date(cur.fine))===86400000;
    if(cur && cur.key===key && consecutivo){ cur.fine = ds; }
    else { if(cur) blocchi.push(cur); cur = {inizio:ds, fine:ds, key}; }
  });
  if(cur) blocchi.push(cur);
  return blocchi;
}

// Giorni EFFETTIVI in cui un operaio lavora su un lavoro: usa i giorni_dettaglio
// (giorni specifici, anche non consecutivi) quando presenti; altrimenti l'intero
// intervallo data_inizio→data_fine come fallback per i lavori gestiti "in semplice".
// Piccolo hook per ricordare una preferenza dell'utente (es. filtri, toggle di vista)
// tra un accesso e l'altro, salvandola nel browser.
function usePersistedState(key, defaultValue){
  const [value, setValue] = useState(()=>{
    try{
      const salvato = window.localStorage.getItem(key);
      return salvato!==null ? JSON.parse(salvato) : defaultValue;
    }catch{ return defaultValue; }
  });
  useEffect(()=>{
    try{ window.localStorage.setItem(key, JSON.stringify(value)); }catch{}
  },[key, value]);
  return [value, setValue];
}

function giorniOperaioSuLavoro(lavoro, operaioId){
  const gd = lavoro?.giorni_dettaglio;
  const giorniConDettaglio = gd ? Object.keys(gd).filter(d=>(gd[d]||[]).some(iv=>(iv.operai_orari||[]).length>0)) : [];
  if(giorniConDettaglio.length>0){
    return giorniConDettaglio.filter(d=>(gd[d]||[]).some(iv=>(iv.operai_orari||[]).some(o=>o.operaio_id===operaioId)));
  }
  if(!(lavoro?.operai_ids||[]).includes(operaioId)) return [];
  return giorniLavoroRange(lavoro);
}

// Ruolo di un operaio su un lavoro in un giorno: "lavora" (default) o "supervisione"
function ruoloOperaioGiorno(lavoro, operaioId, ds){
  for(const iv of (lavoro?.giorni_dettaglio||{})[ds]||[]){
    const o = (iv.operai_orari||[]).find(x=>x.operaio_id===operaioId);
    if(o) return o.ruolo||"lavora";
  }
  return "lavora";
}
// Giorni in cui l'operaio LAVORA davvero sul lavoro (esclusa la sola supervisione).
// Serve per i controlli di doppia prenotazione: chi supervisiona può stare su più cantieri lo stesso giorno.
function giorniOperaioLavoraSuLavoro(lavoro, operaioId){
  return giorniOperaioSuLavoro(lavoro, operaioId).filter(ds=>{
    const mie = ((lavoro?.giorni_dettaglio||{})[ds]||[]).flatMap(iv=>(iv.operai_orari||[]).filter(o=>o.operaio_id===operaioId));
    return mie.length===0 || mie.some(o=>o.ruolo!=="supervisione");
  });
}

// ── Interventi: un operaio può avere più interventi nello stesso giorno (anche su cantieri diversi) ──
const ATTIVITA = {
  lavoro:      {l:"Lavoro",            i:"🔨"},
  scarico:     {l:"Scarico materiale", i:"📦"},
  ritiro:      {l:"Ritiro materiale",  i:"🚚"},
  sopralluogo: {l:"Sopralluogo",       i:"📐"},
  altro:       {l:"Altro",             i:"•"},
};
function interventiSuLavoro(l, opId, ds){
  const out = [];
  ((l?.giorni_dettaglio||{})[ds]||[]).forEach(iv=>(iv.operai_orari||[]).forEach(o=>{ if(o.operaio_id===opId) out.push({...o, lavoro:l}); }));
  // Lavori gestiti "in semplice" (solo elenco operai + date): un intervento di giornata
  if(out.length===0 && giorniOperaioSuLavoro(l,opId).includes(ds)) out.push({operaio_id:opId, ora_inizio:"08:00", ora_fine:"16:00", lavoro:l});
  return out.sort((a,b)=>(a.ora_inizio||"").localeCompare(b.ora_inizio||""));
}
function interventiOperaioGiorno(lavori, opId, ds){
  return (lavori||[]).flatMap(l=>giorniOperaioSuLavoro(l,opId).includes(ds)?interventiSuLavoro(l,opId,ds):[])
    .sort((a,b)=>(a.ora_inizio||"").localeCompare(b.ora_inizio||""));
}
const orariSiSovrappongono = (a1,a2,b1,b2)=>a1<b2 && b1<a2;
// Primo intervento (non di sola supervisione) che si sovrappone all'orario indicato
function conflittoOrario(lavori, opId, ds, ini, fine, escludiIds=[], escludiIntervento=null){
  for(const l of (lavori||[])){
    if(escludiIds.includes(l.id) || !giorniOperaioSuLavoro(l,opId).includes(ds)) continue;
    for(const x of interventiSuLavoro(l,opId,ds)){
      if(x.ruolo==="supervisione") continue;
      if(escludiIntervento && l.id===escludiIntervento.lavoroId && x.ora_inizio===escludiIntervento.ora_inizio && x.ora_fine===escludiIntervento.ora_fine) continue;
      if(orariSiSovrappongono(ini, fine, x.ora_inizio, x.ora_fine)) return {lavoro:l, intervento:x};
    }
  }
  return null;
}
const etichettaIntervento = (x)=>`${x.ora_inizio}–${x.ora_fine}${x.attivita&&x.attivita!=="lavoro"?` ${ATTIVITA[x.attivita]?.l||""}`:""}`;

// Ore tra due orari "HH:MM" (0 se mancanti o non validi)
function oreTraOrari(inizio, fine){
  if(!inizio||!fine) return 0;
  const [h1,m1] = inizio.split(":").map(Number);
  const [h2,m2] = fine.split(":").map(Number);
  if(Number.isNaN(h1)||Number.isNaN(h2)) return 0;
  const diff = (h2*60+(m2||0)) - (h1*60+(m1||0));
  return diff>0 ? diff/60 : 0;
}

// Costo manodopera interna di un lavoro: usa i giorni_dettaglio (ore reali per
// operaio, dal calendario) quando presenti; altrimenti stima 8h/giorno per
// operaio assegnato sull'intervallo data_inizio→data_fine.
function costoManodoperaInterna(lavoro, operai){
  const gd = lavoro?.giorni_dettaglio;
  const haDettaglio = gd && Object.values(gd).some(interventi=>(interventi||[]).some(iv=>(iv.operai_orari||[]).length>0));
  let costo = 0;
  if(haDettaglio){
    Object.values(gd).forEach(interventi=>{
      (interventi||[]).forEach(iv=>{
        (iv.operai_orari||[]).forEach(o=>{
          const op = (operai||[]).find(x=>x.id===o.operaio_id);
          if(!op?.costo_ora) return;
          costo += oreTraOrari(o.ora_inizio, o.ora_fine) * op.costo_ora;
        });
      });
    });
  } else {
    const giorniList = (lavoro?.giorni_lavoro && lavoro.giorni_lavoro.length>0)
      ? lavoro.giorni_lavoro
      : giorniLavoroRange(lavoro).filter(ds=>{ const dow=new Date(ds+"T12:00:00").getDay(); return dow!==0 && dow!==6; });
    const giorni = giorniList.length;
    (lavoro?.operai_ids||[]).forEach(id=>{
      const op = (operai||[]).find(x=>x.id===id);
      if(op?.costo_ora) costo += giorni * 8 * op.costo_ora;
    });
  }
  return costo;
}

// ── ASSEGNAZIONI GIORNO PER GIORNO (motore comune) ───────────────────────────
// Tutte le modifiche a operai/subappaltatori su un cantiere passano da qui, così
// Calendario (Cantieri e Operai), Assegna e Contabilità leggono sempre gli stessi giorni:
// giorni_dettaglio = { "YYYY-MM-DD": [ {descrizione, operai_orari:[{operaio_id,ora_inizio,ora_fine,ruolo?}], subappalto?:{nome,costo_giorno?}} ] }
function giornoFeriale(ds){ const d=new Date(ds+"T12:00:00").getDay(); return d!==0 && d!==6; }
// ── Orari di lavoro dell'azienda (pannello in Operai) ──
const ORARI_DEFAULT = {feriali:{inizio:"07:30",fine:"16:30"}, sabato:{attivo:false,inizio:"08:00",fine:"12:00"}};
const normOrari = (v)=>({feriali:{...ORARI_DEFAULT.feriali,...(v?.feriali||{})}, sabato:{...ORARI_DEFAULT.sabato,...(v?.sabato||{})}});
let ORARI_LAVORO = (()=>{ try{ const x=window.localStorage.getItem("orari_lavoro"); return x?normOrari(JSON.parse(x)):ORARI_DEFAULT; }catch{ return ORARI_DEFAULT; } })();
// Orario standard di un giorno: {ora_inizio, ora_fine, lavorativo}
function orarioDelGiorno(ds){
  const d = new Date(ds+"T12:00:00").getDay();
  if(d===6) return {ora_inizio:ORARI_LAVORO.sabato.inizio, ora_fine:ORARI_LAVORO.sabato.fine, lavorativo:!!ORARI_LAVORO.sabato.attivo};
  return {ora_inizio:ORARI_LAVORO.feriali.inizio, ora_fine:ORARI_LAVORO.feriali.fine, lavorativo:d!==0};
}
const giornoLavorativo = (ds)=>orarioDelGiorno(ds).lavorativo;
// Scala delle barrette del calendario (in minuti): almeno 7–18, allargata se gli orari escono
function scalaOraria(){
  const m = (t)=>{ const [h,mm]=t.split(":").map(Number); return h*60+mm; };
  const ini = [ORARI_LAVORO.feriali.inizio, ...(ORARI_LAVORO.sabato.attivo?[ORARI_LAVORO.sabato.inizio]:[])].map(m);
  const fin = [ORARI_LAVORO.feriali.fine, ...(ORARI_LAVORO.sabato.attivo?[ORARI_LAVORO.sabato.fine]:[])].map(m);
  return [Math.min(7*60, Math.floor(Math.min(...ini)/60)*60), Math.max(18*60, Math.ceil(Math.max(...fin)/60)*60)];
}
// Legge/salva gli orari su Supabase (tabella "impostazioni"); copia locale per l'avvio veloce
function useOrariLavoro(db){
  const [orari, setOrari] = useState(ORARI_LAVORO);
  useEffect(()=>{
    if(!db) return;
    db.from("impostazioni").select("valore").eq("chiave","orari_lavoro").maybeSingle().then(({data,error})=>{
      if(error || !data?.valore) return;
      const v = normOrari(data.valore); ORARI_LAVORO = v;
      try{ window.localStorage.setItem("orari_lavoro", JSON.stringify(v)); }catch{}
      setOrari(v);
    });
  },[]);
  async function salva(v){
    v = normOrari(v);
    const {error} = await db.from("impostazioni").upsert({chiave:"orari_lavoro", valore:v},{onConflict:"azienda_id,chiave"});
    if(error){ alert("Errore nel salvataggio degli orari: "+error.message); return false; }
    ORARI_LAVORO = v;
    try{ window.localStorage.setItem("orari_lavoro", JSON.stringify(v)); }catch{}
    setOrari(v);
    return true;
  }
  return [orari, salva];
}
// Una impostazione condivisa tra tutti i dispositivi (tabella "impostazioni"); copia locale per l'avvio veloce
function useImpostazione(db, chiave, predefinito){
  const [val, setVal] = useState(()=>{ try{ const x=window.localStorage.getItem("imp_"+chiave); return x!==null?JSON.parse(x):predefinito; }catch{ return predefinito; } });
  useEffect(()=>{
    if(!db) return;
    db.from("impostazioni").select("valore").eq("chiave",chiave).maybeSingle().then(({data,error})=>{
      if(error || data?.valore===undefined || data?.valore===null) return;
      setVal(data.valore);
      try{ window.localStorage.setItem("imp_"+chiave, JSON.stringify(data.valore)); }catch{}
    });
  },[]);
  async function salva(v){
    setVal(v);
    try{ window.localStorage.setItem("imp_"+chiave, JSON.stringify(v)); }catch{}
    if(!db) return true;
    const {error} = await db.from("impostazioni").upsert({chiave, valore:v},{onConflict:"azienda_id,chiave"});
    if(error){ alert("Errore nel salvataggio: "+error.message); return false; }
    return true;
  }
  return [val, salva];
}

function giorniPianificati(l){
  if(l?.giorni_lavoro && l.giorni_lavoro.length>0) return [...l.giorni_lavoro].sort();
  return giorniLavoroRange(l).filter(giornoFeriale);
}
// Se il lavoro è gestito "in semplice" (solo operai_ids + date), lo trasforma in giorni espliciti
// con 8 ore/giorno, così il costo in Contabilità resta identico.
function dettaglioNormalizzato(l){
  const gd = {};
  Object.entries(l?.giorni_dettaglio||{}).forEach(([ds,ivs])=>{ gd[ds] = (ivs||[]).map(iv=>({...iv, operai_orari:[...(iv.operai_orari||[])]})); });
  const haOperai = Object.values(gd).some(ivs=>ivs.some(iv=>iv.operai_orari.length>0));
  if(!haOperai && (l?.operai_ids||[]).length>0){
    giorniPianificati(l).forEach(ds=>{
      const sub = (gd[ds]||[]).filter(iv=>iv.subappalto);
      gd[ds] = [{descrizione:"", operai_orari:l.operai_ids.map(id=>({operaio_id:id, ora_inizio:"08:00", ora_fine:"16:00"}))}, ...sub];
    });
  }
  return gd;
}
function giornoOccupato(ivs){ return (ivs||[]).some(iv=>(iv.operai_orari||[]).length>0 || iv.subappalto); }
// Ricalcola i campi collegati (giorni_lavoro, operai_ids, date) dopo una modifica al dettaglio.
// giorniToccati: giorni modificati; se restano vuoti escono da giorni_lavoro.
function campiDaDettaglio(l, gd, giorniToccati=[]){
  const toccati = new Set(giorniToccati);
  Object.keys(gd).forEach(ds=>{ if(toccati.has(ds) && !giornoOccupato(gd[ds])) delete gd[ds]; });
  const set = new Set(giorniPianificati(l).filter(ds=>!toccati.has(ds) || giornoOccupato(gd[ds])));
  Object.keys(gd).forEach(ds=>{ if(giornoOccupato(gd[ds])) set.add(ds); });
  const giorni_lavoro = [...set].sort();
  const operai_ids = [...new Set(Object.values(gd).flatMap(ivs=>ivs.flatMap(iv=>(iv.operai_orari||[]).map(o=>o.operaio_id))))];
  const campi = {giorni_dettaglio:gd, giorni_lavoro, operai_ids};
  if(giorni_lavoro.length>0){ campi.data_inizio = giorni_lavoro[0]; campi.data_fine = giorni_lavoro[giorni_lavoro.length-1]; }
  return campi;
}
// Aggiunge (o aggiorna) un operaio su più giorni
function conOperaioGiorni(l, operaioId, giorni, orario=null, ruolo="lavora"){
  const gd = dettaglioNormalizzato(l);
  giorni.forEach(ds=>{
    const ivs = gd[ds] && gd[ds].length>0 ? gd[ds] : [];
    let i0 = ivs.findIndex(iv=>!iv.subappalto);
    if(i0<0){ ivs.unshift({descrizione:"", operai_orari:[]}); i0 = 0; }
    const orari = ivs[i0].operai_orari.filter(o=>o.operaio_id!==operaioId);
    const o = orario || orarioDelGiorno(ds);
    orari.push({operaio_id:operaioId, ora_inizio:o.ora_inizio, ora_fine:o.ora_fine, ...(ruolo==="supervisione"?{ruolo:"supervisione"}:{})});
    ivs[i0] = {...ivs[i0], operai_orari:orari};
    gd[ds] = ivs;
  });
  return campiDaDettaglio(l, gd, giorni);
}
function conIntervento(l, opId, ds, {ora_inizio, ora_fine, ruolo="lavora", attivita="lavoro", nota=""}){
  const gd = dettaglioNormalizzato(l);
  const ivs = gd[ds] ? [...gd[ds]] : [];
  let i0 = ivs.findIndex(iv=>!iv.subappalto);
  if(i0<0){ ivs.unshift({descrizione:"", operai_orari:[]}); i0 = 0; }
  const voce = {operaio_id:opId, ora_inizio, ora_fine, ...(ruolo==="supervisione"?{ruolo:"supervisione"}:{}), ...(attivita&&attivita!=="lavoro"?{attivita}:{}), ...(nota?{nota}:{})};
  ivs[i0] = {...ivs[i0], operai_orari:[...(ivs[i0].operai_orari||[]), voce]};
  gd[ds] = ivs;
  return campiDaDettaglio(l, gd, [ds]);
}
// Modifica (nuovo = campi) o toglie (nuovo = null) UN intervento, riconosciuto dal suo orario
function cambiaIntervento(l, opId, ds, rif, nuovo=null){
  const gd = dettaglioNormalizzato(l);
  let fatto = false;
  gd[ds] = (gd[ds]||[]).map(iv=>({...iv, operai_orari:(iv.operai_orari||[]).flatMap(o=>{
    if(fatto || o.operaio_id!==opId || o.ora_inizio!==rif.ora_inizio || o.ora_fine!==rif.ora_fine) return [o];
    fatto = true;
    if(!nuovo) return [];
    const r = {...o, ...nuovo};
    if(r.attivita==="lavoro") delete r.attivita;
    if(r.ruolo!=="supervisione") delete r.ruolo;
    if(!r.nota) delete r.nota;
    return [r];
  })})).filter(iv=>iv.subappalto || iv.operai_orari.length>0 || iv.descrizione);
  return campiDaDettaglio(l, gd, [ds]);
}
function senzaOperaioGiorni(l, operaioId, giorni){
  const gd = dettaglioNormalizzato(l);
  giorni.forEach(ds=>{ if(gd[ds]) gd[ds] = gd[ds].map(iv=>({...iv, operai_orari:(iv.operai_orari||[]).filter(o=>o.operaio_id!==operaioId)})).filter(iv=>iv.subappalto || iv.operai_orari.length>0 || iv.descrizione); });
  return campiDaDettaglio(l, gd, giorni);
}
// Subappaltatore a giornata, con costo che finisce in Contabilità
function conSubGiorno(l, nome, giorni, costoGiorno){
  const gd = dettaglioNormalizzato(l);
  giorni.forEach(ds=>{
    const ivs = (gd[ds]||[]).filter(iv=>iv.subappalto?.nome!==nome);
    ivs.push({descrizione:`Subappalto — ${nome}`, operai_orari:[], subappalto:{nome, costo_giorno:parseFloat(costoGiorno)||0}});
    gd[ds] = ivs;
  });
  return campiDaDettaglio(l, gd, giorni);
}
function senzaSubGiorni(l, nome, giorni){
  const gd = dettaglioNormalizzato(l);
  giorni.forEach(ds=>{ if(gd[ds]) gd[ds] = gd[ds].filter(iv=>iv.subappalto?.nome!==nome); });
  return campiDaDettaglio(l, gd, giorni);
}
// Sposta tutto il cantiere (giorni + squadre) di "delta" giorni
function spostaDateDettaglio(l, delta){
  const sposta = (ds)=>{ const d=new Date(ds+"T12:00:00"); d.setDate(d.getDate()+delta); return d.toISOString().slice(0,10); };
  const gdVecchio = dettaglioNormalizzato(l);
  const gd = {};
  Object.entries(gdVecchio).forEach(([ds,ivs])=>{ gd[sposta(ds)] = ivs; });
  const giorni_lavoro = giorniPianificati(l).map(sposta).sort();
  const campi = {giorni_dettaglio:gd, giorni_lavoro};
  if(l.data_inizio) campi.data_inizio = sposta(l.data_inizio);
  if(l.data_fine)   campi.data_fine   = sposta(l.data_fine);
  return campi;
}
function subDelGiorno(l, ds){ return ((l?.giorni_dettaglio||{})[ds]||[]).filter(iv=>iv.subappalto).map(iv=>iv.subappalto); }
function giorniSubSuLavoro(l, nome){ return Object.entries(l?.giorni_dettaglio||{}).filter(([,ivs])=>(ivs||[]).some(iv=>iv.subappalto?.nome===nome)).map(([ds])=>ds).sort(); }
// Subappaltatori noti: quelli già usati nei cantieri (con l'ultimo costo a giornata)
function subappaltatoriDaLavori(lavori){
  const m = {};
  (lavori||[]).forEach(l=>Object.entries(l.giorni_dettaglio||{}).sort(([a],[b])=>a.localeCompare(b)).forEach(([,ivs])=>(ivs||[]).forEach(iv=>{
    if(iv.subappalto?.nome){ const n=iv.subappalto.nome; m[n] = {nome:n, costo_giorno: iv.subappalto.costo_giorno ?? m[n]?.costo_giorno ?? null}; }
  })));
  return Object.values(m).sort((a,b)=>a.nome.localeCompare(b.nome));
}
// Costo dei subappalti segnati a giornata dal calendario (quelli da "Assegna" sono già spese in Contabilità)
function costoSubappaltiCalendario(l){
  return Object.values(l?.giorni_dettaglio||{}).reduce((s,ivs)=>s+(ivs||[]).reduce((t,iv)=>t+(parseFloat(iv.subappalto?.costo_giorno)||0),0),0);
}
function assenzaDelGiorno(assenze, operaioId, ds){
  return (assenze||[]).find(a=>a.operaio_id===operaioId && a.data_inizio<=ds && a.data_fine>=ds) || null;
}
const TIPI_ASSENZA = [{v:"ferie",l:"🌴 Ferie"},{v:"malattia",l:"🤒 Malattia"},{v:"permesso",l:"⏰ Permesso"},{v:"altro",l:"📋 Altro"}];
const labelAssenza = (t)=>(TIPI_ASSENZA.find(x=>x.v===t)?.l)||"📋 Assente";
async function salvaCampiLavoro(db, lavoro, campi, setLavori){
  const {error} = await db.from("lavori").update(campi).eq("id",lavoro.id);
  if(error){ alert("Errore nel salvataggio: "+error.message); return false; }
  setLavori(ls=>ls.map(l=>l.id===lavoro.id?{...l,...campi}:l));
  return true;
}
// Salvataggio del modale Assegna (usato da liste lavori e calendario)
async function salvaAssegnazione(db, lavoro, data, setLavori){
  const {spesaSubappalto, ...dati} = data;
  const {error} = await db.from("lavori").update({
    data_inizio:dati.data_inizio, data_fine:dati.data_fine, operai_ids:dati.operai_ids,
    fascia_oraria:dati.fascia_oraria, giorni_lavoro:dati.giorni_lavoro, giorni_dettaglio:dati.giorni_dettaglio,
    stato:dati.stato||lavoro.stato, responsabile_id:dati.responsabile_id??null
  }).eq("id",lavoro.id);
  if(error){ alert("Errore salvataggio assegnazione: "+error.message); return false; }
  if(spesaSubappalto){
    const {error:errSpesa} = await db.from("spese_cantiere").insert([{lavoro_id:lavoro.id, ...spesaSubappalto}]);
    if(errSpesa) alert("Periodo salvato, ma errore nel registrare il costo del subappalto: "+errSpesa.message);
  }
  setLavori(ls=>ls.map(l=>l.id===lavoro.id?{...l,...dati}:l));
  return true;
}

// ── UI BASE ───────────────────────────────────────────────────────────────────
function Badge({label,color}){
  return <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 10px 3px 8px",borderRadius:999,fontSize:11.5,fontWeight:600,background:C.card,color:C.textSub,boxShadow:`inset 0 0 0 1px ${C.border}`,whiteSpace:"nowrap"}}><span style={{width:7,height:7,borderRadius:"50%",background:color,flexShrink:0}}/>{label}</span>;
}

function Btn({children,onClick,variant="primary",small,style={},disabled}){
  const base={border:"none",borderRadius:999,cursor:disabled?"not-allowed":"pointer",fontFamily:"inherit",fontWeight:600,transition:"opacity 0.15s",opacity:disabled?0.5:1,padding:small?"6px 13px":"11px 21px",fontSize:small?12:14,...style};
  const v={
    primary:{background:C.accent,color:"#fff"},
    ghost:{background:C.card,color:C.text,boxShadow:`inset 0 0 0 1px ${C.border}`},
    danger:{background:C.red+"14",color:C.red},
    success:{background:C.green+"14",color:C.green},
  };
  return <button onClick={onClick} disabled={disabled} style={{...base,...v[variant]}}
    onMouseEnter={e=>{if(!disabled)e.currentTarget.style.opacity="0.85";}}
    onMouseLeave={e=>{e.currentTarget.style.opacity=disabled?0.5:1;}}
  >{children}</button>;
}

const fieldBase={background:C.bg,border:"1px solid transparent",borderRadius:C.radiusSm,color:C.text,padding:"10px 13px",fontSize:14,fontFamily:"inherit",outline:"none",transition:"all 0.15s"};
const fieldLabel={fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.8};

function Input({label,value,onChange,type="text",placeholder,style={}}){
  return(
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {label&&<label style={fieldLabel}>{label}</label>}
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{...fieldBase,...style}}
        onFocus={e=>{e.target.style.borderColor=C.accent;e.target.style.background=C.card;e.target.style.boxShadow=`0 0 0 3px ${C.accentGlow}`;}}
        onBlur={e=>{e.target.style.borderColor="transparent";e.target.style.background=C.bg;e.target.style.boxShadow="none";}}
      />
    </div>
  );
}

function Select({label,value,onChange,options}){
  return(
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {label&&<label style={fieldLabel}>{label}</label>}
      <select value={value} onChange={e=>onChange(e.target.value)} style={{...fieldBase,cursor:"pointer"}}
        onFocus={e=>{e.target.style.borderColor=C.accent;e.target.style.background=C.card;}}
        onBlur={e=>{e.target.style.borderColor="transparent";e.target.style.background=C.bg;}}
      >
        {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Textarea({label,value,onChange,rows=3,placeholder}){
  return(
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {label&&<label style={fieldLabel}>{label}</label>}
      <textarea rows={rows} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{...fieldBase,resize:"vertical"}}
        onFocus={e=>{e.target.style.borderColor=C.accent;e.target.style.background=C.card;e.target.style.boxShadow=`0 0 0 3px ${C.accentGlow}`;}}
        onBlur={e=>{e.target.style.borderColor="transparent";e.target.style.background=C.bg;e.target.style.boxShadow="none";}}
      />
    </div>
  );
}

function Modal({title,onClose,children,wide}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(20,22,28,0.45)",backdropFilter:"blur(3px)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.card,borderRadius:C.radiusLg,width:"100%",maxWidth:wide?760:500,maxHeight:"90vh",overflowY:"auto",boxShadow:C.shadowLg}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"22px 26px",borderBottom:`1px solid ${C.border}`}}>
          <span style={{fontSize:16,fontWeight:750,color:C.text}}>{title}</span>
          <button onClick={onClose} style={{background:C.bg,border:"none",borderRadius:"50%",width:30,height:30,color:C.textMuted,fontSize:18,cursor:"pointer",lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>
        <div style={{padding:"26px"}}>{children}</div>
      </div>
    </div>
  );
}

// ── QUICK ASSIGN ──────────────────────────────────────────────────────────────
function QuickAssign({lavoro, operai, lavori, onClose, onSave, db}){
  const initYear  = parseInt(today.split("-")[0]);
  const initMonth = parseInt(today.split("-")[1]) - 1;

  const [calYear,  setCalYear]  = useState(initYear);
  const [calMonth, setCalMonth] = useState(initMonth);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd,   setDateEnd]   = useState("");
  const [selOperai, setSelOperai] = useState([]);
  const [fase, setFase] = useState("start");
  const blocchiEsistenti = blocchiAssegnazione(lavoro);
  const [fascia, setFascia] = useState("mattina");
  const [oraDa, setOraDa] = useState("08:00");
  const [oraA,  setOraA]  = useState("18:00"); // "start" | "end"
  const [conflitti, setConflitti] = useState([]);
  const [mostraNonDispQA, setMostraNonDispQA] = useState(false);

  // ── Tipo assegnazione: dipendenti interni o subappalto ──
  const [ruoli, setRuoli] = useState({}); // {operaioId: "supervisione"} — assente = lavora
  const ruoloDi = (id)=>ruoli[id]||"lavora";
  const setRuolo = (id,r)=>setRuoli(x=>({...x,[id]:r}));
  const [responsabileId, setResponsabileId] = useState(lavoro.responsabile_id||null);
  const [tipoAss, setTipoAss] = useState("dipendenti"); // "dipendenti" | "subappalto"
  const [nomeSub, setNomeSub] = useState("");
  const [modoCostoSub, setModoCostoSub] = useState("forfait"); // "forfait" | "giornata"
  const [importoSub, setImportoSub] = useState("");
  const [giornateSub, setGiornateSub] = useState("");
  const [giornateTouched, setGiornateTouched] = useState(false);

  useEffect(()=>{
    if(!db || tipoAss!=="dipendenti" || selOperai.length===0 || !dateStart || !dateEnd) { setConflitti([]); return; }
    db.from("assenze_operai").select("*").in("operaio_id",selOperai)
      .lte("data_inizio",dateEnd).gte("data_fine",dateStart)
      .then(({data})=>setConflitti(data||[]));
  },[selOperai.join(","), dateStart, dateEnd, tipoAss]);

  // Doppia prenotazione su altri cantieri: confronto giorno per giorno, non l'intervallo intero
  const giorniTargetQA = (()=>{ const days=[]; if(!dateStart||!dateEnd) return days; let d=new Date(dateStart); const end=new Date(dateEnd); while(d<=end){ days.push(d.toISOString().split("T")[0]); d.setDate(d.getDate()+1); } return days; })();
  const [qaIni, qaFine] = (fascia==="mattina"?"08:00-13:00":fascia==="pomeriggio"?"13:00-18:00":fascia==="intera"?"08:00-18:00":`${oraDa}-${oraA}`).split("-");
  const lavoriApertiQA = (lavori||[]).filter(l=>l.id!==lavoro.id && !["Annullato","Rifiutato","Sospeso","Completato"].includes(l.stato));
  const conflittiCantiere = tipoAss!=="dipendenti" ? [] : selOperai.filter(opId=>ruoloDi(opId)!=="supervisione").flatMap(opId=>{
    const visti = new Set();
    return giorniTargetQA.map(ds=>conflittoOrario(lavoriApertiQA, opId, ds, qaIni, qaFine)).filter(c=>c && !visti.has(c.lavoro.id) && visti.add(c.lavoro.id)).map(c=>({operaioId:opId, lavoro:c.lavoro}));
  });

  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay    = (getFirstDayOfMonth(calYear, calMonth) + 6) % 7;
  const monthLabel  = new Date(calYear, calMonth, 1).toLocaleDateString("it-IT",{month:"long",year:"numeric"});

  function dayStr(d){ return `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }

  function clickDay(d){
    const ds = dayStr(d);
    if(fase === "start"){ setDateStart(ds); setDateEnd(""); setFase("end"); }
    else { if(ds < dateStart){ setDateStart(ds); setDateEnd(""); } else { setDateEnd(ds); setFase("start"); } }
  }

  function toggleOp(id){ setSelOperai(ids => ids.includes(id) ? ids.filter(x=>x!==id) : [...ids, id]); }

  function prevMonth(){ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }
  function nextMonth(){ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }

  // Giornate lavorative (lun-ven) nel periodo selezionato, usate come proposta per il subappalto a giornata
  const giornateFeriali = giorniTargetQA.filter(ds=>{ const dow=new Date(ds+"T12:00:00").getDay(); return dow!==0 && dow!==6; }).length;
  useEffect(()=>{ if(!giornateTouched) setGiornateSub(giornateFeriali>0?String(giornateFeriali):""); },[dateStart, dateEnd]);

  const costoSubTotale = modoCostoSub==="forfait"
    ? (parseFloat(importoSub)||0)
    : (parseFloat(importoSub)||0) * (parseFloat(giornateSub)||0);

  // Stima costo dipendenti per il periodo (ore della fascia × costo_ora × giorni). È già conteggiato
  // in automatico in Contabilità, qui è solo un'anteprima.
  const flCorrente = fascia==="mattina"?"08:00-13:00":fascia==="pomeriggio"?"13:00-18:00":fascia==="intera"?"08:00-18:00":`${oraDa}-${oraA}`;
  const oreFascia = oreTraOrari(...flCorrente.split("-"));
  const operaiSenzaCosto = selOperai.map(id=>operai.find(o=>o.id===id)).filter(o=>o && !o.costo_ora);
  const costoDipStimato = selOperai.reduce((t,id)=>{ const o=operai.find(x=>x.id===id); return t + (o?.costo_ora ? o.costo_ora*oreFascia*giorniTargetQA.length : 0); },0);
  const eur = (n)=>n.toLocaleString("it-IT",{style:"currency",currency:"EUR"});

  const canSave = dateStart && dateEnd && (
    tipoAss==="dipendenti"
      ? selOperai.length > 0
      : (!!nomeSub.trim() && costoSubTotale > 0)
  );

  return(
    <Modal title={`📅 Assegna — ${lavoro.titolo}`} onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:20}}>

        {/* Tipo di personale */}
        <div style={{display:"flex",gap:4,background:C.bg,borderRadius:12,padding:4}}>
          {[{v:"dipendenti",l:"👷 Dipendenti"},{v:"subappalto",l:"🤝 Subappalto"}].map(t=>(
            <button key={t.v} onClick={()=>setTipoAss(t.v)} style={{flex:1,border:"none",borderRadius:9,padding:"9px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:700,background:tipoAss===t.v?C.card:"transparent",color:tipoAss===t.v?C.text:C.textMuted,boxShadow:tipoAss===t.v?C.shadowMd:"none",transition:"all 0.15s"}}>{t.l}</button>
          ))}
        </div>

        <Select label="⭐ Responsabile di cantiere" value={responsabileId||""} onChange={v=>setResponsabileId(v?parseInt(v):null)}
          options={[{value:"",label:"— Nessuno —"}, ...operai.map(o=>({value:o.id,label:o.nome}))]}/>

        {blocchiEsistenti.length>0 && (
          <div style={{background:C.bg,borderRadius:14,padding:"12px 14px",border:`1px solid ${C.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Periodi già programmati</div>
            {blocchiEsistenti.map((b,i)=>{
              if(b.key.startsWith("sub:")) return <div key={i} style={{fontSize:12.5,color:C.text,marginBottom:4}}>📅 {fmt(b.inizio)} → {fmt(b.fine)} · 🤝 {b.key.slice(4)} (subappalto)</div>;
              const nomiOp = b.key.split(",").filter(Boolean).map(id=>operai.find(o=>o.id===parseInt(id))?.nome).filter(Boolean).join(", ")||"—";
              return <div key={i} style={{fontSize:12.5,color:C.text,marginBottom:4}}>📅 {fmt(b.inizio)} → {fmt(b.fine)} · 👷 {nomiOp}</div>;
            })}
            <div style={{fontSize:11,color:C.textMuted,marginTop:6,fontStyle:"italic"}}>Seleziona qui sotto un nuovo periodo: verrà aggiunto senza toccare questi.</div>
          </div>
        )}

        {/* Indicatore fase */}
        <div style={{display:"flex",gap:8}}>
          {["start","end"].map((f,i)=>(
            <div key={f} onClick={()=>setFase(f)} style={{flex:1,padding:"10px",borderRadius:11,textAlign:"center",cursor:"pointer",border:`2px solid ${fase===f?C.accent:C.border}`,background:fase===f?C.accentGlow:C.bg}}>
              <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{i===0?"Inizio":"Fine"}</div>
              <div style={{fontSize:14,fontWeight:700,color:i===0?(dateStart?C.text:C.textMuted):(dateEnd?C.text:C.textMuted)}}>
                {i===0?(dateStart?fmt(dateStart):"Clicca sul calendario"):(dateEnd?fmt(dateEnd):"Clicca sul calendario")}
              </div>
            </div>
          ))}
        </div>

        {/* Calendario */}
        <div style={{background:C.bg,borderRadius:16,padding:16,border:`1px solid ${C.border}`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <button onClick={prevMonth} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:9,color:C.text,padding:"4px 12px",cursor:"pointer",fontSize:16}}>‹</button>
            <span style={{fontSize:13,fontWeight:700,color:C.text,textTransform:"capitalize"}}>{monthLabel}</span>
            <button onClick={nextMonth} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:9,color:C.text,padding:"4px 12px",cursor:"pointer",fontSize:16}}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:6}}>
            {["L","M","M","G","V","S","D"].map((d,i)=>(
              <div key={i} style={{textAlign:"center",fontSize:10,fontWeight:700,color:C.textMuted,padding:"4px 0"}}>{d}</div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {Array.from({length:firstDay}).map((_,i)=><div key={`e${i}`}/>)}
            {Array.from({length:daysInMonth},(_,i)=>i+1).map(d=>{
              const ds = dayStr(d);
              const isStart = ds === dateStart;
              const isEnd   = ds === dateEnd;
              const inRange = dateStart && dateEnd && ds > dateStart && ds < dateEnd;
              const isToday = ds === today;
              return(
                <div key={d} onClick={()=>clickDay(d)} style={{
                  textAlign:"center", padding:"8px 0", borderRadius:9, cursor:"pointer",
                  fontSize:13, fontWeight: isStart||isEnd ? 800 : 400,
                  background: isStart||isEnd ? C.accent : inRange ? C.accentGlow : "transparent",
                  color: isStart||isEnd ? "#111" : inRange ? C.accent : isToday ? C.accent : C.text,
                  border: isToday&&!isStart&&!isEnd ? `1px solid ${C.accent}55` : "1px solid transparent",
                  transition:"background 0.1s",
                }}>{d}</div>
              );
            })}
          </div>
        </div>

        {tipoAss==="subappalto" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1}}>🤝 Subappalto</div>
            <Input label="Subappaltatore / squadra" value={nomeSub} onChange={setNomeSub} placeholder="Es. Squadra Rossi"/>
            <div style={{display:"flex",gap:4,background:C.bg,borderRadius:12,padding:4}}>
              {[{v:"forfait",l:"A forfait"},{v:"giornata",l:"A giornata"}].map(t=>(
                <button key={t.v} onClick={()=>setModoCostoSub(t.v)} style={{flex:1,border:"none",borderRadius:9,padding:"8px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:700,background:modoCostoSub===t.v?C.card:"transparent",color:modoCostoSub===t.v?C.text:C.textMuted,boxShadow:modoCostoSub===t.v?C.shadowMd:"none"}}>{t.l}</button>
              ))}
            </div>
            {modoCostoSub==="forfait" ? (
              <Input label="Costo concordato (€)" type="number" value={importoSub} onChange={setImportoSub} placeholder="Es. 1500"/>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <Input label="Costo a giornata (€)" type="number" value={importoSub} onChange={setImportoSub} placeholder="Es. 250"/>
                <Input label="N. giornate" type="number" value={giornateSub} onChange={v=>{setGiornateTouched(true);setGiornateSub(v);}} placeholder="Es. 5"/>
              </div>
            )}
            <div style={{background:C.accentGlow,borderRadius:11,padding:"10px 14px",fontSize:13,color:C.text,display:"flex",justifyContent:"space-between"}}>
              <span>Costo subappalto</span>
              <b>{eur(costoSubTotale)}</b>
            </div>
            <div style={{fontSize:11,color:C.textMuted,fontStyle:"italic"}}>Verrà registrato in Contabilità come spesa Manodopera di questo cantiere.</div>
          </div>
        )}

        {tipoAss==="dipendenti" && (<>
        {/* Operai */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Operai</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {operai.filter(o=>mostraNonDispQA || o.disponibile!==false || selOperai.includes(o.id)).map(o=>{
              const sel = selOperai.includes(o.id);
              const assente = conflitti.find(c=>c.operaio_id===o.id);
              return(
                <div key={o.id} style={{display:"flex",flexDirection:"column",gap:8}}>
                <div onClick={()=>toggleOp(o.id)} style={{
                  display:"flex",alignItems:"center",gap:12,padding:"10px 14px",
                  borderRadius:14,cursor:"pointer",transition:"all 0.15s",
                  background: C.card,
                  border:`1.5px solid ${sel ? C.text : C.border}`,
                  boxShadow: sel ? C.shadowMd : "none",
                }}>
                  <div style={{position:"relative",width:34,height:34,borderRadius:"50%",background:C.bg,boxShadow:`inset 0 0 0 1px ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:C.fontBrand,fontSize:14,fontWeight:600,color:C.text,flexShrink:0}}>{o.nome[0]}<span style={{position:"absolute",right:-1,bottom:-1,width:10,height:10,borderRadius:"50%",background:o.colore,border:`2px solid ${C.card}`}}/></div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,color:sel?C.text:C.textSub,fontSize:13}}>{o.nome}</div>
                    <div style={{fontSize:11.5,color:C.textMuted}}>{(o.specialita?.split(",")[0]||"").toLowerCase().replace(/^./,c=>c.toUpperCase())}
                      {o.disponibile===false && <span style={{color:C.orange,fontWeight:700}}> · non disponibile</span>}
                      {assente && <span style={{color:C.red,fontWeight:700}}> · {labelAssenza(assente.tipo)} {fmt(assente.data_inizio)}→{fmt(assente.data_fine)}</span>}
                    </div>
                  </div>
                  <div style={{width:20,height:20,borderRadius:"50%",background:sel?C.text:"transparent",border:`1.5px solid ${sel?C.text:C.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {sel&&<span style={{color:"#fff",fontSize:11,fontWeight:800,lineHeight:1}}>✓</span>}
                  </div>
                  {sel && o.telefono && (
                    <WhatsAppBtn tel={o.telefono} testo={`Ciao ${o.nome}, ti ho assegnato il cantiere "${lavoro.titolo}"${ruoloDi(o.id)==="supervisione"?" come supervisore":""}${lavoro.indirizzo?` (${lavoro.indirizzo})`:""} dal ${fmt(dateStart)} al ${fmt(dateEnd)}${fascia&&fascia!=="custom"?`, fascia ${fascia}`:""}.`}/>
                  )}
                </div>
                {sel && (
                  <div style={{display:"flex",gap:4,marginTop:-4,marginLeft:46}}>
                    {[{v:"lavora",l:"Lavora"},{v:"supervisione",l:"Supervisiona"}].map(r=>(
                      <button key={r.v} onClick={()=>setRuolo(o.id,r.v)} style={{border:"none",borderRadius:20,padding:"4px 11px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,background:ruoloDi(o.id)===r.v?C.text:C.card,color:ruoloDi(o.id)===r.v?"#fff":C.textSub,boxShadow:ruoloDi(o.id)===r.v?"none":`inset 0 0 0 1px ${C.border}`}}>{r.l}</button>
                    ))}
                  </div>
                )}
                </div>
              );
            })}
            {operai.some(o=>o.disponibile===false) && (
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11.5,color:C.textMuted,cursor:"pointer",marginTop:2}}>
                <input type="checkbox" checked={mostraNonDispQA} onChange={e=>setMostraNonDispQA(e.target.checked)}/>
                Mostra anche operai non disponibili
              </label>
            )}
          </div>
        </div>

        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Fascia Oraria</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[{label:"Mattina",sub:"08:00 - 13:00",val:"mattina"},
              {label:"Pomeriggio",sub:"13:00 - 18:00",val:"pomeriggio"},
              {label:"Giornata intera",sub:"08:00 - 18:00",val:"intera"},
              {label:"Personalizzata",sub:"Scegli tu",val:"custom"}
            ].map(f=>(
              <div key={f.val} onClick={()=>setFascia(f.val)} style={{padding:"10px 12px",borderRadius:11,cursor:"pointer",border:`2px solid ${fascia===f.val?"#E3701F":C.border}`,background:fascia===f.val?"#E3701F22":C.bg,transition:"all 0.15s"}}>
                <div style={{fontWeight:700,fontSize:12,color:fascia===f.val?"#E3701F":C.text}}>{f.label}</div>
                <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>{f.sub}</div>
              </div>
            ))}
          </div>
          {fascia==="custom"&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1}}>Dalle</label>
                <input type="time" value={oraDa} onChange={e=>setOraDa(e.target.value)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,color:C.text,padding:"9px 12px",fontSize:14,fontFamily:"inherit",outline:"none"}}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1}}>Alle</label>
                <input type="time" value={oraA} onChange={e=>setOraA(e.target.value)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,color:C.text,padding:"9px 12px",fontSize:14,fontFamily:"inherit",outline:"none"}}/>
              </div>
            </div>
          )}
        </div>

        {selOperai.length>0 && dateStart && dateEnd && (
          <div style={{background:C.bg,borderRadius:11,padding:"10px 14px",fontSize:12.5,color:C.textSub}}>
            <div style={{display:"flex",justifyContent:"space-between",color:C.text,fontSize:13}}>
              <span>Costo personale stimato</span><b>{eur(costoDipStimato)}</b>
            </div>
            <div style={{fontSize:11,color:C.textMuted,marginTop:4}}>{oreFascia}h × {giorniTargetQA.length} gg × costo orario (anche le ore di supervisione). Già calcolato in automatico in Contabilità.</div>
            {operaiSenzaCosto.length>0 && <div style={{fontSize:11,color:C.orange,marginTop:4}}>⚠️ Senza costo orario: {operaiSenzaCosto.map(o=>o.nome).join(", ")} — impostalo nella pagina Operai.</div>}
          </div>
        )}
        </>)}

        {conflitti.length>0 && (
          <div style={{background:C.red+"10",borderRadius:11,padding:"10px 14px",fontSize:12.5,color:C.red}}>
            ⚠️ Attenzione: {conflitti.map(c=>{
              const op = operai.find(o=>o.id===c.operaio_id);
              return `${op?.nome||"Operaio"} è in ${c.tipo} dal ${fmt(c.data_inizio)} al ${fmt(c.data_fine)}`;
            }).join(" · ")}
          </div>
        )}

        {conflittiCantiere.length>0 && (
          <div style={{background:C.red+"10",borderRadius:11,padding:"10px 14px",fontSize:12.5,color:C.red}}>
            🚫 Doppia prenotazione: {conflittiCantiere.map((c,i)=>{
              const op = operai.find(o=>o.id===c.operaioId);
              return `${op?.nome||"Operaio"} è già su "${c.lavoro.titolo}" (${fmt(c.lavoro.data_inizio)} → ${fmt(c.lavoro.data_fine)})`;
            }).join(" · ")} negli stessi orari. Cambia fascia oraria o giorni.
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",borderTop:`1px solid ${C.border}`,paddingTop:16}}>
          <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
          <Btn disabled={!canSave||conflittiCantiere.length>0} onClick={()=>{
            if(tipoAss==="dipendenti" && conflitti.length>0 && !window.confirm("Alcuni operai risultano assenti in questo periodo (vedi avviso in rosso). Assegnarli comunque?")) return;
            const fl = fascia==="mattina"?"08:00-13:00":fascia==="pomeriggio"?"13:00-18:00":fascia==="intera"?"08:00-18:00":`${oraDa}-${oraA}`;
            const [oD,oA] = fl.split("-");

            // Se il lavoro non ha ancora giorni_lavoro/giorni_dettaglio (assegnazione "semplice"
            // fatta prima di questa modifica), li ricostruiamo dal vecchio data_inizio/data_fine
            // così il periodo esistente non viene perso quando ne aggiungiamo uno nuovo.
            let giorniLavoro = (lavoro.giorni_lavoro && lavoro.giorni_lavoro.length>0) ? [...lavoro.giorni_lavoro] : [];
            let giorniDettaglio = {...(lavoro.giorni_dettaglio||{})};
            if(giorniLavoro.length===0 && lavoro.data_inizio && lavoro.data_fine && (lavoro.operai_ids||[]).length>0){
              const vecchiGiorni = giorniLavoroRange(lavoro);
              vecchiGiorni.forEach(ds=>{
                giorniDettaglio[ds] = [{descrizione:"", operai_orari:(lavoro.operai_ids||[]).map(id=>({operaio_id:id, ora_inizio:"08:00", ora_fine:"18:00"}))}];
              });
              giorniLavoro = vecchiGiorni;
            }

            // Aggiungiamo il nuovo periodo selezionato (senza toccare i giorni già presenti)
            const nuoviGiorni = rangeDates(dateStart, dateEnd);

            if(tipoAss==="subappalto"){
              const nome = nomeSub.trim();
              nuoviGiorni.forEach(ds=>{
                const esistenti = giorniDettaglio[ds]||[];
                giorniDettaglio[ds] = [...esistenti.filter(iv=>iv.subappalto?.nome!==nome), {descrizione:`Subappalto — ${nome}`, operai_orari:[], subappalto:{nome}}];
                if(!giorniLavoro.includes(ds)) giorniLavoro.push(ds);
              });
              giorniLavoro.sort();
              const dettaglioCosto = modoCostoSub==="forfait" ? "forfait" : `${giornateSub} gg × ${eur(parseFloat(importoSub)||0)}`;
              onSave({
                data_inizio: giorniLavoro[0],
                data_fine: giorniLavoro[giorniLavoro.length-1],
                operai_ids: lavoro.operai_ids||[],
                fascia_oraria: lavoro.fascia_oraria||null,
                giorni_lavoro: giorniLavoro,
                giorni_dettaglio: giorniDettaglio,
                stato: "Programmato",
                responsabile_id: responsabileId,
                spesaSubappalto: {
                  categoria: "manodopera",
                  descrizione: `Subappalto — ${nome} (${fmt(dateStart)} → ${fmt(dateEnd)}, ${dettaglioCosto})`,
                  importo: Math.round(costoSubTotale*100)/100,
                  data: dateStart,
                },
              });
              return;
            }

            nuoviGiorni.forEach(ds=>{
              // Mantiene gli operai già presenti quel giorno e gli eventuali subappaltatori
              const esistenti = giorniDettaglio[ds]||[];
              const restoOperai = (esistenti.find(iv=>!iv.subappalto)?.operai_orari||[]).filter(o=>!selOperai.includes(o.operaio_id));
              giorniDettaglio[ds] = [{descrizione:"", operai_orari:[...restoOperai, ...selOperai.map(id=>({operaio_id:id, ora_inizio:oD, ora_fine:oA, ...(ruoloDi(id)==="supervisione"?{ruolo:"supervisione"}:{})}))]}, ...esistenti.filter(iv=>iv.subappalto)];
              if(!giorniLavoro.includes(ds)) giorniLavoro.push(ds);
            });
            giorniLavoro.sort();

            const tuttiOperai = new Set(lavoro.operai_ids||[]);
            selOperai.forEach(id=>tuttiOperai.add(id));

            onSave({
              data_inizio: giorniLavoro[0],
              data_fine: giorniLavoro[giorniLavoro.length-1],
              operai_ids: [...tuttiOperai],
              fascia_oraria: fl,
              giorni_lavoro: giorniLavoro,
              giorni_dettaglio: giorniDettaglio,
              stato: "Programmato",
              responsabile_id: responsabileId,
            });
          }}>
            {blocchiEsistenti.length>0 ? "Aggiungi periodo" : "Salva & Programma"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── SIDEBAR ───────────────────────────────────────────────────────────────────
const NAV = [
  {id:"dashboard",label:"Dashboard",icon:"▦"},
  {id:"calendario",label:"Calendario",icon:"◫"},
  {divider:true,label:"CLIENTI & PREVENTIVI"},
  {id:"messaggi",label:"Messaggi",icon:"✉",countKey:"nonletti"},
  {id:"contatti",label:"Contatti",icon:"☎",countKey:"richiami"},
  {id:"clienti",label:"Clienti",icon:"👤"},
  {id:"preventivi-new",label:"Inserisci Preventivo",icon:"💰"},
  {id:"preventivi",label:"Preventivi inviati",icon:"◇",stato:"Preventivo",color:"#8A8B90"},
  {id:"preventivi-rifiutati",label:"Preventivi rifiutati",icon:"⌀",stato:"Rifiutato",color:"#9A6B8F"},
  {divider:true,label:"CANTIERI"},
  {id:"confermati",label:"Confermati (acconto)",icon:"◈",stato:"Confermato",color:"#2F6FDB"},
  {id:"in-corso",label:"Da Organizzare",icon:"◉",stato:"Da organizzare",color:"#6B7A8F"},
  {id:"programmati",label:"In Corso",icon:"◐",stato:"Programmato",color:"#E3701F"},
  {id:"materiali",label:"Magazzino",icon:"📦"},
  {id:"cantiere",label:"Lavoro in corso",icon:"📊"},
  {divider:true,label:"CHIUSURA LAVORI"},
  {id:"archivio",label:"Completati",icon:"▣",stato:"arch"},
  {id:"sospesi",label:"Sospesi",icon:"⊘",stato:"Sospeso",color:"#B38B1E"},
  {divider:true,label:"GESTIONE"},
  {id:"contabilita",label:"Contabilità",icon:"💶"},
  {id:"operai",label:"Operai",icon:"◎"},
  {id:"crm",label:"Rivenditori",icon:"◆"},
  {id:"assenze",label:"Ferie / Assenze",icon:"🗓"},
  {divider:true,label:"IMPOSTAZIONI"},
  {id:"account",label:"Account",icon:"◑"},
];
const NAV_GRUPPI_DEFAULT_APERTI = ["CANTIERI"]; // solo questo gruppo aperto di default

function Sidebar({tab,setTab,lavori,contatti=[],nonLetti=0,user,onLogout,mobileOpen,setMobileOpen,paginePermesse}){
  const isMobile = window.innerWidth < 768;
  const counts={
    "Da organizzare":lavori.filter(l=>l.stato==="Da organizzare").length,
    "Confermato":lavori.filter(l=>l.stato==="Confermato").length,
    "Preventivo":lavori.filter(l=>l.stato==="Preventivo").length,
    "Sospeso":lavori.filter(l=>l.stato==="Sospeso").length,
    "Rifiutato":lavori.filter(l=>l.stato==="Rifiutato").length,
    "arch":lavori.filter(l=>["Completato","Annullato"].includes(l.stato)).length,
    "richiami":richiamiDovuti(contatti).length,
    "nonletti":nonLetti,
  };
  const navItems = paginePermesse
    ? [...NAV.filter(n=>!n.divider && paginePermesse.includes(n.id)), {id:"account",label:"Account",icon:"◑"}]
    : NAV;

  // Raggruppa NAV per divider
  const gruppi = [];
  let corrente = {titolo:null, items:[]};
  navItems.forEach(item=>{
    if(item.divider){ if(corrente.items.length||corrente.titolo) gruppi.push(corrente); corrente = {titolo:item.label, items:[]}; }
    else corrente.items.push(item);
  });
  gruppi.push(corrente);

  const [aperti, setAperti] = useState(()=>{
    const salvati = JSON.parse(localStorage.getItem("posa_nav_aperti")||"null");
    return salvati || NAV_GRUPPI_DEFAULT_APERTI;
  });
  useEffect(()=>{
    // apri automaticamente il gruppo che contiene la voce attiva
    const g = gruppi.find(gr=>gr.items.some(it=>it.id===tab));
    if(g?.titolo && !aperti.includes(g.titolo)){
      const nuovi=[...aperti, g.titolo];
      setAperti(nuovi); localStorage.setItem("posa_nav_aperti", JSON.stringify(nuovi));
    }
  },[tab]);
  function toggleGruppo(titolo){
    setAperti(a=>{
      const n = a.includes(titolo) ? a.filter(x=>x!==titolo) : [...a,titolo];
      localStorage.setItem("posa_nav_aperti", JSON.stringify(n));
      return n;
    });
  }

  function renderItem(item){
    const active=tab===item.id;
    const count=item.stato?counts[item.stato]:(item.countKey?counts[item.countKey]:null);
    const color=item.color||C.accent;
    return(
      <div key={item.id} onClick={()=>setTab(item.id)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",margin:"1px 0",borderRadius:C.radiusSm,cursor:"pointer",background:active?C.card:"transparent",boxShadow:active?`0 0 0 1px ${C.border}`:"none",transition:"background 0.12s"}}
        onMouseEnter={e=>{if(!active)e.currentTarget.style.background=C.cardHover;}}
        onMouseLeave={e=>{if(!active)e.currentTarget.style.background="transparent";}}
      >
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{width:7,height:7,borderRadius:"50%",flexShrink:0,margin:"0 4px",background:active?C.brand:(item.color||C.border)}}/>
          <span style={{fontSize:13.5,fontWeight:active?600:450,color:active?C.text:C.textSub}}>{item.label}</span>
        </div>
        {count>0&&<span style={{fontSize:10,fontWeight:600,background:"transparent",color:C.textMuted,minWidth:16,textAlign:"center"}}>{count}</span>}
      </div>
    );
  }

  return(
    <div style={{width:240,minWidth:240,background:C.sidebar,display:"flex",flexDirection:"column",height:"100vh",position:isMobile?"fixed":"sticky",top:0,zIndex:200,transition:"transform 0.25s",transform:isMobile&&!mobileOpen?"translateX(-100%)":"translateX(0)",borderRight:`1px solid ${C.border}`,boxShadow:isMobile&&mobileOpen?C.shadowLg:"none"}}>
      {isMobile&&mobileOpen&&<div onClick={()=>setMobileOpen(false)} style={{position:"fixed",inset:0,background:"rgba(20,22,28,0.35)",zIndex:-1}}/>}
      <div style={{padding:"26px 22px 22px"}}>
        <SquadraLogo size={24}/>
      </div>
      <nav style={{flex:1,overflowY:"auto",padding:"6px 14px"}}>
        {gruppi.map((g,gi)=>{
          if(!g.titolo) return g.items.map(renderItem); // voci senza gruppo (Dashboard, Calendario)
          const isOpen = aperti.includes(g.titolo);
          const contieneAttivo = g.items.some(it=>it.id===tab);
          return(
            <div key={gi}>
              <div onClick={()=>toggleGruppo(g.titolo)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 10px 6px",cursor:"pointer"}}>
                <span style={{fontSize:11,fontWeight:600,color:C.textMuted,letterSpacing:0.2,textTransform:"none"}}>{g.titolo.charAt(0)+g.titolo.slice(1).toLowerCase()}</span>
                <span style={{fontSize:9,color:C.textMuted,transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.15s"}}>▾</span>
              </div>
              {isOpen && g.items.map(renderItem)}
            </div>
          );
        })}
      </nav>
      <div style={{padding:"12px 14px",margin:"0 8px 14px",borderTop:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 6px"}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",flexShrink:0}}>{user?.email?.[0]?.toUpperCase()}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12.5,fontWeight:500,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.email?.split("@")[0]}</div>
            <div style={{fontSize:10.5,color:C.textMuted}}>Amministratore</div>
          </div>
          <span onClick={onLogout} style={{fontSize:11,color:C.textMuted,cursor:"pointer",fontWeight:500}}
            onMouseEnter={e=>{e.currentTarget.style.color=C.red;}}
            onMouseLeave={e=>{e.currentTarget.style.color=C.textMuted;}}
          >Esci</span>
        </div>
      </div>
    </div>
  );
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
function Login(){
  const [loading,setLoading]=useState(false);
  const [modo,setModo]=useState("google"); // "google" | "password"
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [errore,setErrore]=useState("");

  async function accediConPassword(){
    setErrore(""); setLoading(true);
    const {error} = await supabase.auth.signInWithPassword({email:email.trim().toLowerCase(), password});
    if(error) setErrore("Email o password non corrette.");
    setLoading(false);
  }

  return(
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:C.font}}>
      <div style={{textAlign:"center",padding:56,background:C.card,borderRadius:C.radiusXl,boxShadow:C.shadowMd,border:`1px solid ${C.border}`,width:340}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:14}}><SquadraMark size={48}/></div>
        <div style={{fontFamily:C.fontBrand,fontSize:30,fontWeight:600,color:C.text,marginBottom:6,letterSpacing:-1}}>squadra</div>
        <div style={{fontSize:13.5,color:C.textMuted,marginBottom:32}}>Il gestionale dei tuoi cantieri</div>

        {modo==="google" ? (
          <>
            <button onClick={async()=>{setLoading(true);await supabase.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin}});}} disabled={loading}
              style={{display:"flex",alignItems:"center",gap:12,background:C.bg,color:C.text,border:`1px solid ${C.border}`,borderRadius:C.radiusSm,padding:"13px 26px",fontSize:14.5,fontWeight:500,cursor:"pointer",margin:"0 auto",opacity:loading?0.7:1,fontFamily:"inherit",width:"100%",justifyContent:"center"}}>
              <svg width="19" height="19" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              {loading?"Accesso…":"Accedi con Google"}
            </button>
            <button onClick={()=>setModo("password")} style={{background:"none",border:"none",color:C.textMuted,fontSize:12.5,cursor:"pointer",marginTop:16,textDecoration:"underline",fontFamily:"inherit"}}>Accedi con email e password</button>
          </>
        ) : (
          <>
            <div style={{display:"flex",flexDirection:"column",gap:10,textAlign:"left"}}>
              <Input label="Email" value={email} onChange={setEmail} placeholder="nome@esempio.com"/>
              <Input label="Password" type="password" value={password} onChange={setPassword}/>
            </div>
            {errore && <div style={{color:C.red,fontSize:12,marginTop:10}}>{errore}</div>}
            <Btn onClick={accediConPassword} disabled={loading||!email.trim()||!password} style={{width:"100%",marginTop:16,justifyContent:"center"}}>{loading?"Accesso…":"Accedi"}</Btn>
            <button onClick={()=>{setModo("google");setErrore("");}} style={{background:"none",border:"none",color:C.textMuted,fontSize:12.5,cursor:"pointer",marginTop:14,textDecoration:"underline",fontFamily:"inherit"}}>Accedi con Google</button>
          </>
        )}
      </div>
    </div>
  );
}

// Chi entra ma non fa parte di nessuna azienda: o ne crea una sua (vuota) o chiede di essere aggiunto
function SenzaAzienda({user, onCreata}){
  const [nome,setNome]=useState("");
  const [loading,setLoading]=useState(false);
  const [errore,setErrore]=useState("");
  async function crea(){
    setErrore(""); setLoading(true);
    const {error} = await supabase.rpc("crea_azienda",{p_nome:nome.trim()});
    setLoading(false);
    if(error){ setErrore(error.message||"Impossibile creare l'azienda."); return; }
    onCreata();
  }
  return(
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:C.font,padding:16}}>
      <div style={{padding:"44px 36px",background:C.card,borderRadius:C.radiusXl,boxShadow:C.shadowMd,border:`1px solid ${C.border}`,width:380,maxWidth:"100%"}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:14}}><SquadraMark size={40}/></div>
        <div style={{fontSize:13,color:C.textMuted,textAlign:"center",marginBottom:22}}>Hai effettuato l'accesso come<br/><b style={{color:C.text}}>{user?.email}</b></div>
        <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>Lavori in un'azienda che usa già Squadra?</div>
        <div style={{fontSize:12.5,color:C.textMuted,marginBottom:22}}>Chiedi all'amministratore di aggiungere la tua email da Account › Accessi, poi ricarica la pagina.</div>
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:18}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>Oppure crea la tua azienda</div>
          <div style={{fontSize:12.5,color:C.textMuted,marginBottom:12}}>Partirai da zero: cantieri, clienti e contatti saranno solo tuoi.</div>
          <Input label="Nome azienda" value={nome} onChange={setNome} placeholder="Es. Rossi Pavimenti Srl"/>
          {errore && <div style={{color:C.red,fontSize:12,marginTop:8}}>{errore}</div>}
          <Btn onClick={crea} disabled={loading||!nome.trim()} style={{width:"100%",marginTop:12,justifyContent:"center"}}>{loading?"Creazione…":"Crea azienda"}</Btn>
        </div>
        <button onClick={()=>supabase.auth.signOut()} style={{display:"block",margin:"18px auto 0",background:"none",border:"none",color:C.textMuted,fontSize:12.5,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit"}}>Esci</button>
      </div>
    </div>
  );
}

// ── COPY BTN ─────────────────────────────────────────────────────────────────
function waLink(tel, testo){
  const clean = (tel||"").replace(/[^\d]/g,"");
  if(!clean) return null;
  const withCC = clean.startsWith("39") ? clean : ("39"+clean.replace(/^0+/,""));
  return `https://wa.me/${withCC}?text=${encodeURIComponent(testo)}`;
}
function WhatsAppBtn({tel, testo, small=true, label="WhatsApp"}){
  const link = waLink(tel, testo);
  if(!link) return null;
  return(
    <a href={link} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{display:"inline-flex",alignItems:"center",gap:5,textDecoration:"none",background:"#25D36618",color:"#25D366",borderRadius:20,padding:small?"5px 11px":"8px 16px",fontSize:small?11.5:13,fontWeight:700}}>
      💬 {label}
    </a>
  );
}

function MeteoBadge({indirizzo}){
  const [meteo, setMeteo] = useState(null);
  useEffect(()=>{
    if(!indirizzo) return;
    const citta = indirizzo.split(",").pop().trim();
    if(!citta) return;
    let cancelled = false;
    fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(citta)}&count=1&language=it`)
      .then(r=>r.json())
      .then(g=>{
        const loc = g?.results?.[0];
        if(!loc || cancelled) return;
        return fetch(`https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}&daily=weathercode,temperature_2m_max,temperature_2m_min&timezone=Europe%2FRome&forecast_days=2`)
          .then(r=>r.json())
          .then(f=>{
            if(cancelled || !f?.daily) return;
            const i = 1; // domani
            setMeteo({code:f.daily.weathercode[i], max:Math.round(f.daily.temperature_2m_max[i]), min:Math.round(f.daily.temperature_2m_min[i])});
          });
      })
      .catch(()=>{});
    return ()=>{ cancelled = true; };
  },[indirizzo]);

  if(!meteo) return null;
  const ICONS = {0:"☀️",1:"🌤️",2:"⛅",3:"☁️",45:"🌫️",48:"🌫️",51:"🌦️",53:"🌦️",55:"🌦️",61:"🌧️",63:"🌧️",65:"🌧️",71:"🌨️",73:"🌨️",75:"🌨️",80:"🌦️",81:"🌧️",82:"⛈️",95:"⛈️",96:"⛈️",99:"⛈️"};
  const icon = ICONS[meteo.code] || "🌡️";
  return <span style={{fontSize:11.5,color:C.textMuted}}>{icon} {meteo.min}°/{meteo.max}°</span>;
}

function CopyBtn({testo}){
  const [copiato, setCopiato] = useState(false);
  function copia(){
    navigator.clipboard.writeText(testo).catch(()=>{
      const ta=document.createElement("textarea");
      ta.value=testo;document.body.appendChild(ta);ta.select();document.execCommand("copy");document.body.removeChild(ta);
    });
    setCopiato(true);
    setTimeout(()=>setCopiato(false),2000);
  }
  return(
    <button onClick={copia} style={{
      display:"flex",alignItems:"center",gap:6,
      background:copiato?C.green+"22":C.accentGlow,
      border:`1px solid ${copiato?C.green:C.accent}44`,
      borderRadius:11,padding:"6px 14px",cursor:"pointer",
      fontFamily:"inherit",fontSize:12,fontWeight:700,
      color:copiato?C.green:C.accent,transition:"all 0.2s"
    }}>
      {copiato?"✓ Copiato!":"📋 Copia per WhatsApp"}
    </button>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
// ── INCASSI: FATTURATO / DIRETTO ─────────────────────────────────────────────
// Ogni lavoro può avere due importi incassati: importo_fatturato (con fattura) e importo_diretto.
// La loro somma è l'incassato e tiene allineati "saldato" e "acconto_importo" (usati da Da incassare e Contabilità).
const SQL_INCASSI = "alter table lavori add column if not exists importo_fatturato numeric default 0, add column if not exists importo_diretto numeric default 0;";
const numF = (l)=>parseFloat(l?.importo_fatturato)||0;
const numD = (l)=>parseFloat(l?.importo_diretto)||0;
function incassatoLavoro(l){ return l.saldato ? (parseFloat(l.preventivo)||0) : (parseFloat(l.acconto_importo)||0); }
function daRipartire(l){ return incassatoLavoro(l) - (numF(l)+numD(l)) > 1; }
// Payload che salva fatturato/diretto e ricalcola saldato/acconto dall'incassato (F+D).
function payloadIncassi(lavoro, fatt, dir){
  const f = Math.max(0, parseFloat(fatt)||0), d = Math.max(0, parseFloat(dir)||0);
  const tot = f+d, importo = parseFloat(lavoro.preventivo)||0;
  const p = {importo_fatturato:f, importo_diretto:d};
  if(tot>0){
    if(importo>0 && tot>=importo-0.01){ p.saldato=true; p.acconto_importo=0; }
    else { p.saldato=false; p.acconto_importo=tot; }
  }
  return p;
}
async function aggiornaLavoroIncassi(db, id, payload, setLavori){
  const {error} = await db.from("lavori").update(payload).eq("id",id);
  if(error){
    alert("Errore nel salvataggio: "+error.message+(/column|schema cache/i.test(error.message)?"\n\nMancano le colonne Fatturato/Diretto. In Supabase → SQL Editor esegui:\n\n"+SQL_INCASSI:""));
    return false;
  }
  setLavori(ls=>ls.map(l=>l.id===id?{...l,...payload}:l));
  return true;
}

function ModalIncassi({lavori,setLavori,db,onClose,soloDaRipartire=false}){
  const [filtro,setFiltro] = useState(soloDaRipartire?"ripartire":"tutti");
  const [draft,setDraft] = useState({});   // id -> {f,d}
  const [search,setSearch] = useState("");
  const base = lavori
    .filter(l=>!["Annullato","Rifiutato","Preventivo"].includes(l.stato))
    .filter(l=>l.stato==="Completato" || incassatoLavoro(l)>0 || numF(l)+numD(l)>0);
  const lista = base
    .filter(l=>filtro==="tutti" || daRipartire(l))
    .filter(l=>{ const q=search.toLowerCase(); return !q||l.titolo?.toLowerCase().includes(q)||l.cliente?.toLowerCase().includes(q); })
    .sort((a,b)=>(b.data_fine||b.data_inizio||"").localeCompare(a.data_fine||a.data_inizio||""));
  const nRip = base.filter(daRipartire).length;
  const inp = {width:"100%",boxSizing:"border-box",fontSize:13.5,padding:"8px 9px",borderRadius:9,border:`1px solid ${C.border}`,background:C.card,color:C.text,outline:"none"};
  async function salva(l){
    const dr = draft[l.id]; if(!dr) return;
    const ok = await aggiornaLavoroIncassi(db, l.id, payloadIncassi(l, dr.f, dr.d), setLavori);
    if(ok) setDraft(x=>{ const n={...x}; delete n[l.id]; return n; });
  }
  return(
    <Modal title="Fatturato e diretto" onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",background:C.bg,borderRadius:10,padding:3,gap:2}}>
            {[{id:"tutti",label:"Tutti"},{id:"ripartire",label:`Da ripartire${nRip?` (${nRip})`:""}`}].map(o=>(
              <button key={o.id} onClick={()=>setFiltro(o.id)} style={{border:"none",borderRadius:8,padding:"6px 12px",fontSize:12.5,fontWeight:600,cursor:"pointer",background:filtro===o.id?C.card:"transparent",boxShadow:filtro===o.id?C.shadowSm:"none",color:filtro===o.id?C.text:C.textMuted}}>{o.label}</button>
            ))}
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Cerca…" style={{...inp,width:180,flex:"0 0 auto"}}/>
        </div>
        <div style={{fontSize:12,color:C.textMuted}}>Scrivi quanto hai incassato in fattura e quanto diretto. La somma aggiorna da sola "Pagato" e "Da incassare".</div>
        {lista.length===0&&<div style={{textAlign:"center",color:C.textMuted,padding:30}}>{filtro==="ripartire"?"Tutti gli incassi sono ripartiti ✓":"Nessun lavoro"}</div>}
        {lista.map(l=>{
          const dr = draft[l.id];
          const f = dr ? dr.f : (numF(l)||""), d = dr ? dr.d : (numD(l)||"");
          const imp = parseFloat(l.preventivo)||0;
          const tot = (parseFloat(f)||0)+(parseFloat(d)||0);
          const rip = !dr && daRipartire(l);
          return(
            <div key={l.id} style={{background:C.bg,borderRadius:14,padding:"12px 14px",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{flex:"1 1 200px",minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</div>
                <div style={{fontSize:11.5,color:C.textMuted,marginTop:2}}>{l.cliente||"—"} · importo {euro(imp)}{tot>0&&imp>0?` · incassato ${Math.round(tot/imp*100)}%`:""}</div>
                {rip&&<div style={{fontSize:11,color:C.orange,marginTop:2}}>Incassati {euro(incassatoLavoro(l))} da ripartire</div>}
              </div>
              <div style={{display:"flex",gap:8,alignItems:"flex-end",flex:"0 1 330px"}}>
                <label style={{flex:1,fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Fattura €
                  <input type="number" value={f} onChange={e=>setDraft(x=>({...x,[l.id]:{f:e.target.value,d}}))} style={{...inp,marginTop:4}}/>
                </label>
                <label style={{flex:1,fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>Diretto €
                  <input type="number" value={d} onChange={e=>setDraft(x=>({...x,[l.id]:{f,d:e.target.value}}))} style={{...inp,marginTop:4}}/>
                </label>
                {rip && <Btn small variant="ghost" onClick={()=>setDraft(x=>({...x,[l.id]:{f:String(incassatoLavoro(l)-numD(l)),d:numD(l)||""}}))}>Tutto fattura</Btn>}
                {dr && <Btn small onClick={()=>salva(l)}>Salva</Btn>}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function Dashboard({lavori,setLavori,operai,rivendite,contatti=[],setTab,db}){
  const [vistaKanban, setVistaKanban] = useState(false);
  const [modalIncassare, setModalIncassare] = useState(false);
  const [modalIncassi, setModalIncassi] = useState(null); // null | "tutti" | "ripartire"

  // KPI
  const daOrganizzareStato = lavori.filter(l=>l.stato==="Da organizzare"); // stato letterale "Da organizzare"
  const confermati = lavori.filter(l=>l.stato==="Confermato");
  const programmati= lavori.filter(l=>l.stato==="Programmato");
  const inCorso     = programmati; // "In corso" = lavori Programmati
  const preventivi = lavori.filter(l=>l.stato==="Preventivo");
  const sospesi    = lavori.filter(l=>l.stato==="Sospeso");
  const completati = lavori.filter(l=>l.stato==="Completato");
  const importoLavori = completati.reduce((s,l)=>s+(l.preventivo||0),0);
  const daIncassare= completati.filter(l=>!l.saldato).reduce((s,l)=>s+(l.preventivo||0),0);
  const daOrganizzare = [...confermati,...programmati,...preventivi];

  const daIncassareLista = completati.filter(l=>!l.saldato).sort((a,b)=>(a.data_fine||"").localeCompare(b.data_fine||""));
  const lavoriValidi = lavori.filter(l=>!["Annullato","Rifiutato","Preventivo"].includes(l.stato));
  const totFatturato = lavoriValidi.reduce((s,l)=>s+numF(l),0);
  const totDiretto   = lavoriValidi.reduce((s,l)=>s+numD(l),0);
  const totIncassatoRip = totFatturato+totDiretto;
  const nDaRipartire = lavoriValidi.filter(daRipartire).length;

  async function segnaIncassato(lavoro, canale){
    // Il residuo da incassare va sul canale scelto (fattura o diretto) e il lavoro risulta pagato.
    const residuo = Math.max(0,(parseFloat(lavoro.preventivo)||0)-incassatoLavoro(lavoro));
    const payload = {saldato:true, acconto_importo:0};
    if(canale==="fattura") payload.importo_fatturato = numF(lavoro)+residuo;
    if(canale==="diretto") payload.importo_diretto = numD(lavoro)+residuo;
    await aggiornaLavoroIncassi(db, lavoro.id, payload, setLavori);
  }

  const prossimi = [...lavori]
    .filter(l=>l.data_inizio>=today&&["Confermato","Programmato","Preventivo"].includes(l.stato))
    .sort((a,b)=>a.data_inizio.localeCompare(b.data_inizio)).slice(0,5);

  // Trend importo lavori ultimi 6 mesi (per grafico)
  const trendMesi = [];
  for(let i=5;i>=0;i--){
    const d = new Date(); d.setMonth(d.getMonth()-i);
    const periodo = d.toISOString().slice(0,7);
    const ric = lavori.filter(l=>!["Annullato","Rifiutato"].includes(l.stato)&&(l.data_inizio||"").startsWith(periodo)).reduce((s,l)=>s+(l.preventivo||0),0);
    trendMesi.push({label:d.toLocaleDateString("it-IT",{month:"short"}), valore:ric});
  }
  const maxTrend = Math.max(1, ...trendMesi.map(m=>m.valore));

  // Distribuzione pipeline per stato — un blocco per ogni stato reale, ordine del flusso di lavoro
  const pipelineDist = [
    {label:"Preventivi", n:preventivi.length, color:"#8A8B90"},
    {label:"Confermati", n:confermati.length, color:"#2F6FDB"},
    {label:"In corso", n:programmati.length, color:"#E3701F"},
    {label:"Da organizzare", n:daOrganizzareStato.length, color:"#6B7A8F"},
    {label:"Sospesi", n:sospesi.length, color:"#B38B1E"},
    {label:"Completati", n:completati.length, color:"#2E8B57"},
  ].filter(d=>d.n>0);
  const totPipeline = pipelineDist.reduce((s,d)=>s+d.n,0);

  // Domani
  const domani = new Date(); domani.setDate(domani.getDate()+1);
  const domaniStr = domani.toISOString().split("T")[0];
  const domaniLabel = domani.toLocaleDateString("it-IT",{weekday:"long",day:"2-digit",month:"long"});
  const lavoriDomani = lavori.filter(l=>l.stato==="Programmato"&&domaniStr>=l.data_inizio&&domaniStr<=l.data_fine);
  const operaiDomani = operai.filter(o=>lavoriDomani.some(l=>l.operai_ids?.includes(o.id)));

  function generaMessaggio(){
    if(lavoriDomani.length===0) return "";
    return `Lavori domani ${domaniLabel}:\n`+operaiDomani.map(o=>{
      return lavoriDomani.filter(l=>l.operai_ids?.includes(o.id)).map(l=>{
        const p=[];p.push(`- @${o.nome}`);if(l.titolo)p.push(l.titolo);if(l.indirizzo)p.push(l.indirizzo);if(l.fascia_oraria)p.push(`ore ${l.fascia_oraria}`);return p.join(" ");
      }).join("\n");
    }).join("\n");
  }

  // Colonne Kanban
  const KANBAN_COLS = [
    { id:"preventivi",  label:"Preventivi",    color:"#8A8B90", lavori: preventivi,  tab:"preventivi",  icon:"◇" },
    { id:"confermati",  label:"Confermati",    color:"#2F6FDB", lavori: confermati,  tab:"confermati",  icon:"◈" },
    { id:"programmati", label:"In Corso",      color:"#E3701F", lavori: programmati, tab:"programmati", icon:"◐" },
    { id:"in-corso",    label:"Da Organizzare",color:"#6B7A8F", lavori: daOrganizzareStato, tab:"in-corso", icon:"◉" },
    { id:"sospesi",     label:"Sospesi",       color:"#B38B1E", lavori: sospesi,     tab:"sospesi",     icon:"⊘" },
    { id:"completati",  label:"Completati",    color:"#2E8B57", lavori: completati.slice(-5), tab:"archivio", icon:"✓" },
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:24}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontFamily:C.fontBrand,fontSize:32,fontWeight:600,color:C.text,letterSpacing:-1}}>Buongiorno</div>
          <div style={{color:C.textMuted,fontSize:14,marginTop:4}}>{fmtFull(today)}</div>
        </div>
        <div style={{display:"flex",background:C.card,boxShadow:C.shadowSm,borderRadius:11,padding:3,gap:2}}>
          {[{id:false,label:"📊 Riepilogo"},{id:true,label:"📋 Kanban"}].map(v=>(
            <button key={String(v.id)} onClick={()=>setVistaKanban(v.id)} style={{padding:"7px 16px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:vistaKanban===v.id?700:500,background:vistaKanban===v.id?C.accent:"transparent",color:vistaKanban===v.id?"#fff":C.textSub,transition:"all 0.15s"}}>{v.label}</button>
          ))}
        </div>
      </div>

      {/* CRM: richiami e appuntamenti di oggi */}
      {(()=>{
        const dovuti = richiamiDovuti(contatti);
        if(!dovuti.length) return null;
        const app = dovuti.filter(c=>c.stato==="Appuntamento").length;
        return(
          <div onClick={()=>{localStorage.setItem("posa_tab","contatti");setTab("contatti");}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,background:C.brandSoft,borderRadius:14,padding:"12px 16px",cursor:"pointer"}}>
            <span style={{fontSize:13.5,fontWeight:600,color:C.text}}>{dovuti.length-app>0?`${dovuti.length-app} contatti da richiamare`:""}{dovuti.length-app>0&&app?" · ":""}{app?`${app} appuntament${app===1?"o":"i"}`:""} oggi</span>
            <span style={{fontSize:12,fontWeight:600,color:C.brand}}>Apri Contatti ›</span>
          </div>
        );
      })()}

      {/* KPI importi — separate dal resto */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
        <div style={{background:C.text,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:26,fontWeight:800,color:"#fff"}}>{euro(importoLavori)}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.65)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginTop:5}}>Importo lavori</div>
        </div>
        {[
          {id:"fattura", label:"Fatturato", valore:totFatturato},
          {id:"diretto", label:"Diretto",   valore:totDiretto},
        ].map(k=>(
          <div key={k.id} onClick={()=>setModalIncassi("tutti")} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px",cursor:"pointer",transition:"background 0.15s"}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.cardHover;}}
            onMouseLeave={e=>{e.currentTarget.style.background=C.card;}}
          >
            <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:26,fontWeight:800,color:C.text}}>{euro(k.valore)}</div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:5,gap:8}}>
              <div style={{fontSize:11,color:C.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{k.label}</div>
              {totIncassatoRip>0 && <span style={{fontSize:11,color:C.textMuted,fontWeight:600}}>{Math.round(k.valore/totIncassatoRip*100)}%</span>}
            </div>
          </div>
        ))}
        <div onClick={()=>daIncassareLista.length>0&&setModalIncassare(true)} style={{background:C.card,boxShadow:C.shadowSm,border:`1px solid ${daIncassareLista.length>0?C.brand+"66":C.border}`,borderRadius:16,padding:"18px 20px",cursor:daIncassareLista.length>0?"pointer":"default",transition:"background 0.15s"}}
          onMouseEnter={e=>{if(daIncassareLista.length>0)e.currentTarget.style.background=C.cardHover;}}
          onMouseLeave={e=>{e.currentTarget.style.background=C.card;}}
        >
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:26,fontWeight:800,color:C.brand}}>{euro(daIncassare)}</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:5}}>
            <div style={{fontSize:11,color:C.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Da Incassare {daIncassareLista.length>0?`(${daIncassareLista.length})`:""}</div>
            {daIncassareLista.length>0 && <span style={{fontSize:11,color:C.brand,fontWeight:700}}>Vedi →</span>}
          </div>
        </div>
      </div>

      {nDaRipartire>0 && (
        <div onClick={()=>setModalIncassi("ripartire")} style={{marginTop:-4,fontSize:12.5,color:C.orange,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
          ◐ {nDaRipartire} lavor{nDaRipartire>1?"i":"o"} pagat{nDaRipartire>1?"i":"o"} senza divisione fattura/diretto — <span style={{fontWeight:700}}>ripartisci →</span>
        </div>
      )}

      {/* KPI stati pipeline */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10}}>
        {[
          {label:"In Corso",    value:programmati.length, color:"#E3701F", tab:"programmati"},
          {label:"Confermati",  value:confermati.length, color:"#2F6FDB", tab:"confermati"},
          {label:"Da Organizzare",value:daOrganizzareStato.length,color:"#6B7A8F", tab:"in-corso"},
          {label:"Preventivi",  value:preventivi.length, color:"#8A8B90", tab:"preventivi"},
          {label:"Sospesi",     value:sospesi.length,    color:"#B38B1E", tab:"sospesi"},
          {label:"Completati",  value:completati.length, color:"#2E8B57", tab:"archivio"},
        ].map(s=>(
          <div key={s.label} onClick={()=>s.tab&&setTab(s.tab)} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"14px 14px",cursor:s.tab?"pointer":"default",transition:"background 0.15s"}}
            onMouseEnter={e=>{if(s.tab)e.currentTarget.style.background=C.cardHover;}}
            onMouseLeave={e=>{if(s.tab)e.currentTarget.style.background=C.card;}}
          >
            <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:21,fontWeight:700,color:C.text}}>{s.value}</div>
            <div style={{display:"flex",alignItems:"center",gap:5,marginTop:5}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:s.color,flexShrink:0}}/>
              <span style={{fontSize:10,color:C.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Grafici */}
      <div style={{display:"grid",gridTemplateColumns:"1.6fr 1fr",gap:14}}>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:16}}>📈 Importo lavori ultimi 6 mesi</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:14,height:110}}>
            {trendMesi.map(m=>(
              <div key={m.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6,height:"100%",justifyContent:"flex-end"}}>
                <span style={{fontSize:10,color:C.textMuted,fontWeight:700}}>{m.valore>0?`€${(m.valore/1000).toFixed(1)}k`:""}</span>
                <div title={`€${m.valore.toFixed(0)}`} style={{width:"70%",height:`${Math.max(2,m.valore/maxTrend*70)}px`,background:C.accent,borderRadius:"5px 5px 0 0"}}/>
                <span style={{fontSize:10.5,color:C.textMuted,fontWeight:600,textTransform:"capitalize"}}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:16}}>🥧 Pipeline cantieri</div>
          {totPipeline===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>Nessun cantiere attivo.</div> : (
            <>
              <div style={{display:"flex",height:12,borderRadius:20,overflow:"hidden",marginBottom:14}}>
                {pipelineDist.map(d=>(
                  <div key={d.label} title={`${d.label}: ${d.n}`} style={{width:`${d.n/totPipeline*100}%`,background:d.color}}/>
                ))}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {pipelineDist.map(d=>(
                  <div key={d.label} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:12}}>
                    <span style={{display:"flex",alignItems:"center",gap:7,color:C.textSub}}><span style={{width:8,height:8,borderRadius:"50%",background:d.color}}/>{d.label}</span>
                    <span style={{fontWeight:700,color:C.text}}>{d.n}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* KANBAN VIEW */}
      {vistaKanban && (
        <div style={{overflowX:"auto",paddingBottom:8}}>
          <div style={{display:"flex",gap:14,minWidth:900}}>
            {KANBAN_COLS.map(col=>(
              <div key={col.id} style={{flex:1,minWidth:180,background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
                {/* Header colonna */}
                <div style={{padding:"12px 14px",borderBottom:`1px solid ${C.border}`,background:col.color+"11",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}} onClick={()=>setTab(col.tab)}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:col.color,fontSize:12}}>{col.icon}</span>
                    <span style={{fontSize:12,fontWeight:700,color:col.color}}>{col.label}</span>
                  </div>
                  <span style={{fontSize:11,background:col.color+"22",color:col.color,borderRadius:24,padding:"1px 8px",fontWeight:700}}>{col.lavori.length}</span>
                </div>
                {/* Cards */}
                <div style={{padding:"10px 10px",display:"flex",flexDirection:"column",gap:8,maxHeight:400,overflowY:"auto"}}>
                  {col.lavori.length===0 && <div style={{fontSize:11,color:C.textMuted,textAlign:"center",padding:"20px 0"}}>Nessun lavoro</div>}
                  {col.lavori.map(l=>{
                    const riv=rivendite.find(r=>r.id===l.rivendita_id);
                    const ops=(l.operai_ids||[]).map(id=>operai.find(o=>o.id===id)).filter(Boolean);
                    return(
                      <div key={l.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderLeft:`3px solid ${col.color}`,borderRadius:11,padding:"10px 12px",cursor:"pointer",transition:"border-color 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=col.color}
                        onMouseLeave={e=>{e.currentTarget.style.borderLeftColor=col.color;e.currentTarget.style.borderTopColor=C.border;e.currentTarget.style.borderRightColor=C.border;e.currentTarget.style.borderBottomColor=C.border;}}
                        onClick={()=>setTab(col.tab)}
                      >
                        <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:4,lineHeight:1.3}}>{l.titolo}</div>
                        <div style={{fontSize:11,color:C.textMuted,marginBottom:4}}>👤 {l.cliente}</div>
                        {riv&&<div style={{fontSize:10,color:C.textMuted,marginBottom:4}}>🏪 {riv.nome}</div>}
                        <div style={{fontSize:10,color:C.accent}}>{fmt(l.data_inizio)} → {fmt(l.data_fine)}</div>
                        {ops.length>0&&(
                          <div style={{display:"flex",gap:3,marginTop:6,flexWrap:"wrap"}}>
                            {ops.map(o=><div key={o.id} style={{width:18,height:18,borderRadius:"50%",background:o.colore+"33",border:`1.5px solid ${o.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:800,color:o.colore}}>{o.nome[0]}</div>)}
                          </div>
                        )}
                        {l.preventivo>0&&<div style={{fontSize:10,color:C.green,fontWeight:700,marginTop:4}}>€{l.preventivo.toLocaleString("it")}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RIEPILOGO VIEW */}
      {!vistaKanban && (
      <div style={{display:"flex",flexDirection:"column",gap:18}}>

        {/* Da seguire — raggruppato per stato, non più mischiato */}
        {daOrganizzare.length>0&&(
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:1,color:C.textMuted,marginBottom:14}}>⚡ Da seguire ({daOrganizzare.length})</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
            {[
              {stato:"Preventivo", label:"Preventivi", lista:preventivi, tab:"preventivi"},
              {stato:"Confermato", label:"Confermati",  lista:confermati, tab:"confermati"},
              {stato:"Programmato",label:"In Corso",    lista:programmati,tab:"programmati"},
            ].filter(g=>g.lista.length>0).map(g=>{
              const colore = STATI_COLOR[g.stato];
              const ordinati = [...g.lista].sort((a,b)=>(a.data_inizio||"").localeCompare(b.data_inizio||""));
              return(
                <div key={g.stato}>
                  <div onClick={()=>setTab(g.tab)} style={{display:"flex",alignItems:"center",gap:7,marginBottom:10,cursor:"pointer"}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:colore,flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:700,color:colore}}>{g.label}</span>
                    <span style={{fontSize:11,color:C.textMuted}}>({g.lista.length})</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {ordinati.slice(0,5).map(l=>{
                      const riv=rivendite.find(r=>r.id===l.rivendita_id);
                      return(
                        <div key={l.id} onClick={()=>setTab(g.tab)} style={{background:C.bg,borderRadius:10,padding:"8px 10px",cursor:"pointer"}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.cardHover}
                          onMouseLeave={e=>e.currentTarget.style.background=C.bg}>
                          <div style={{fontSize:12.5,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</div>
                          <div style={{fontSize:10.5,color:C.textMuted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>👤 {l.cliente||"—"}{riv?` · 🏪 ${riv.nome}`:""}</div>
                          <div style={{fontSize:10.5,color:colore,marginTop:2}}>📅 {fmt(l.data_inizio)}</div>
                        </div>
                      );
                    })}
                    {g.lista.length>5 && <div onClick={()=>setTab(g.tab)} style={{fontSize:11,color:C.textMuted,cursor:"pointer",padding:"2px 2px"}}>+{g.lista.length-5} altri →</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
          {/* Da organizzare */}
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
            <div style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:1,color:C.textMuted,marginBottom:14}}>🔨 In Corso</div>
            {inCorso.length===0?<div style={{color:C.textMuted,fontSize:13}}>Nessun lavoro in corso</div>:inCorso.map(l=>{
              const riv=rivendite.find(r=>r.id===l.rivendita_id);
              const ops=(l.operai_ids||[]).map(id=>operai.find(o=>o.id===id)).filter(Boolean);
              return(
                <div key={l.id} style={{borderBottom:`1px solid ${C.border}`,paddingBottom:12,marginBottom:12}}>
                  <div style={{fontWeight:700,color:C.text,fontSize:14}}>{l.titolo}</div>
                  <div style={{fontSize:12,color:C.textMuted,marginTop:2}}>{l.cliente} · {riv?.nome}</div>
                  <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap",alignItems:"center"}}>
                    {ops.map(o=><span key={o.id} style={{fontSize:10,background:o.colore+"22",color:o.colore,border:`1px solid ${o.colore}44`,borderRadius:24,padding:"1px 7px",fontWeight:600}}>{o.nome.split(" ")[0]}</span>)}
                    <span style={{fontSize:10,color:"#6B7A8F"}}>fino al {fmt(l.data_fine)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Prossimi */}
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
            <div style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:1,color:C.textMuted,marginBottom:14}}>📅 Prossimi</div>
            {prossimi.length===0?<div style={{color:C.textMuted,fontSize:13}}>Nessun lavoro programmato</div>:prossimi.map(l=>(
              <div key={l.id} style={{borderBottom:`1px solid ${C.border}`,paddingBottom:12,marginBottom:12}}>
                <div style={{fontWeight:700,color:C.text,fontSize:14}}>{l.titolo}</div>
                <div style={{fontSize:12,color:C.textMuted,marginTop:2}}>{l.cliente}</div>
                <div style={{fontSize:11,color:C.accent,marginTop:4}}>{fmt(l.data_inizio)} → {fmt(l.data_fine)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Squadre esterne attive */}
        {(()=>{
          const lavoriConSquadre = lavori.filter(l=>{
            let sq = l.squadre_esterne;
            if(typeof sq === "string"){try{sq=JSON.parse(sq);}catch{sq=[];}}
            return Array.isArray(sq) && sq.length>0 && ["Da organizzare","Confermato","Programmato"].includes(l.stato);
          });
          if(lavoriConSquadre.length===0) return null;
          return(
            <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
              <div style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:1,color:C.textMuted,marginBottom:14}}>🏗️ Squadre Esterne Attive</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {lavoriConSquadre.map(l=>{
                  let sq = l.squadre_esterne;
                  if(typeof sq === "string"){try{sq=JSON.parse(sq);}catch{sq=[];}}
                  const costoSquadre=(sq||[]).reduce((s,q)=>s+(parseFloat(q.mq)||0)*(parseFloat(q.prezzo_mq)||0),0);
                  const ricavo=parseFloat(l.preventivo)||0;
                  const margine=ricavo-costoSquadre;
                  return(
                    <div key={l.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 16px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                        <div>
                          <div style={{fontWeight:700,color:C.text,fontSize:13}}>{l.titolo}</div>
                          <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>👤 {l.cliente}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:11,color:C.textMuted}}>Margine</div>
                          <div style={{fontSize:15,fontWeight:800,color:margine>=0?C.green:C.red}}>€{margine.toLocaleString("it")}</div>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {(sq||[]).map((s2,i)=>(
                          <div key={i} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:9,padding:"5px 10px",fontSize:11}}>
                            <span style={{color:C.text,fontWeight:600}}>{s2.nome||"Squadra"}</span>
                            {s2.lavorazione&&<span style={{color:C.textMuted}}> · {s2.lavorazione}</span>}
                            {s2.mq&&s2.prezzo_mq&&<span style={{color:C.red,fontWeight:700}}> · €{((parseFloat(s2.mq)||0)*(parseFloat(s2.prezzo_mq)||0)).toLocaleString("it")}</span>}
                            {s2.note&&<span style={{color:C.orange,marginLeft:4,fontWeight:600}}>({s2.note})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Operai */}
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:1,color:C.textMuted,marginBottom:14}}>👷 Stato Operai</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
            {operai.map(o=>{
              const lav=lavori.filter(l=>l.operai_ids?.includes(o.id)&&l.stato==="Da organizzare");
              const lavTot=lavori.filter(l=>l.operai_ids?.includes(o.id)&&["Confermato","Programmato"].includes(l.stato));
              return(
                <div key={o.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <div style={{width:34,height:34,borderRadius:"50%",background:o.colore+"22",border:`2px solid ${o.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:o.colore,flexShrink:0}}>{o.nome[0]}</div>
                    <div>
                      <div style={{fontWeight:700,color:C.text,fontSize:13}}>{o.nome.split(" ")[0]}</div>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}><div style={{width:6,height:6,borderRadius:"50%",background:o.disponibile?C.green:C.red}}/><span style={{fontSize:10,color:o.disponibile?C.green:C.red,fontWeight:600}}>{o.disponibile?lav.length>0?"In cantiere":"Disponibile":"N/D"}</span></div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                    <div style={{textAlign:"center",background:C.card,borderRadius:9,padding:"5px 0"}}><div style={{fontSize:16,fontWeight:800,color:"#6B7A8F"}}>{lav.length}</div><div style={{fontSize:9,color:C.textMuted}}>Da org.</div></div>
                    <div style={{textAlign:"center",background:C.card,borderRadius:9,padding:"5px 0"}}><div style={{fontSize:16,fontWeight:800,color:C.accent}}>{lavTot.length}</div><div style={{fontSize:9,color:C.textMuted}}>In prog.</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Domani */}
        <div style={{background:C.card,border:`2px solid ${C.accent}44`,borderRadius:16,padding:"18px 20px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:C.accent}}/>
              <div style={{fontWeight:700,fontSize:13,textTransform:"uppercase",letterSpacing:1,color:C.accent}}>Domani — {domaniLabel}</div>
            </div>
            {lavoriDomani.length>0&&<CopyBtn testo={generaMessaggio()}/>}
          </div>
          {lavoriDomani.length===0
            ?<div style={{color:C.textMuted,fontSize:13}}>Nessun lavoro programmato per domani.</div>
            :<div style={{display:"flex",flexDirection:"column",gap:12}}>
              {operaiDomani.map(o=>{
                const lav=lavoriDomani.filter(l=>l.operai_ids?.includes(o.id));
                return(
                  <div key={o.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 16px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      <div style={{width:32,height:32,borderRadius:"50%",background:o.colore+"33",border:`2px solid ${o.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:o.colore}}>{o.nome[0]}</div>
                      <div style={{fontWeight:700,color:C.text,fontSize:14}}>{o.nome}</div>
                      <div style={{fontSize:11,color:C.textMuted}}>📞 {o.telefono}</div>
                      <div style={{marginLeft:"auto"}}>
                        <WhatsAppBtn tel={o.telefono} testo={`Ciao ${o.nome}, domani ${domaniLabel}:\n`+lav.map(l=>`- ${l.titolo}${l.indirizzo?` (${l.indirizzo})`:""}${l.fascia_oraria?` ore ${l.fascia_oraria}`:""}`).join("\n")}/>
                      </div>
                    </div>
                    {lav.map(l=>{
                      const riv=rivendite.find(r=>r.id===l.rivendita_id);
                      return(
                        <div key={l.id} style={{borderLeft:`3px solid ${o.colore}`,paddingLeft:12,marginBottom:8}}>
                          <div style={{fontWeight:700,color:C.text,fontSize:13}}>{l.titolo}</div>
                          <div style={{fontSize:12,color:C.textMuted,marginTop:2,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span>📍 {l.indirizzo} · 🏪 {riv?.nome}</span>
                            <MeteoBadge indirizzo={l.indirizzo}/>
                          </div>
                          {l.fascia_oraria&&<div style={{fontSize:12,color:C.textMuted,marginTop:2}}>🕐 {l.fascia_oraria}</div>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          }
        </div>

      </div>
      )}

      {modalIncassi && <ModalIncassi lavori={lavori} setLavori={setLavori} db={db} onClose={()=>setModalIncassi(null)} soloDaRipartire={modalIncassi==="ripartire"}/>}

      {/* ── Modale Da Incassare ── */}
      {modalIncassare && (
        <Modal title={`💶 Da Incassare — ${euro(daIncassare)}`} onClose={()=>setModalIncassare(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {daIncassareLista.length===0 ? (
              <div style={{textAlign:"center",color:C.textMuted,padding:30}}>Tutto incassato 🎉</div>
            ) : daIncassareLista.map(l=>{
              const residuo = Math.max(0,(l.preventivo||0)-(parseFloat(l.acconto_importo)||0));
              return(
                <div key={l.id} style={{display:"flex",alignItems:"center",gap:12,background:C.bg,borderRadius:14,padding:"12px 14px"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</div>
                    <div style={{fontSize:11.5,color:C.textMuted,marginTop:2}}>{l.cliente||"—"} · completato {fmt(l.data_fine)}</div>
                    {l.acconto_importo>0 && <div style={{fontSize:11,color:C.orange,marginTop:2}}>Acconto già ricevuto: {euro(l.acconto_importo)}</div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:15,fontWeight:800,color:C.red}}>{euro(residuo)}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    <Btn small onClick={()=>segnaIncassato(l,"fattura")}>✓ In fattura</Btn>
                    <Btn small variant="ghost" onClick={()=>segnaIncassato(l,"diretto")}>✓ Diretto</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CALENDARIO ────────────────────────────────────────────────────────────────
function Calendario({lavori,setLavori,operai,setOperai=()=>{},db}){
  const now = new Date();
  const [vista,    setVista]    = usePersistedState("cal_vista", "giorno"); // "mese" | "settimana" | "giorno" — ricordata
  const [vistaTipo, setVistaTipo] = usePersistedState("cal_vistaTipo", "cantieri"); // "cantieri" | "operai"
  // Il giorno su cui eri resta memorizzato (anche cambiando pagina o riaprendo l'app); "Oggi" riporta a oggi
  const [curDateStr, setCurDateStr] = usePersistedState("cal_data", toStr(now));
  const curDate = new Date((curDateStr||toStr(now))+"T12:00:00");
  const setCurDate = (d)=>setCurDateStr(toStr(d instanceof Date ? d : new Date(d)));
  const [selOp,    setSelOp]    = useState("tutti");
  const [calAction, setCalAction] = useState(null); // lavoro selezionato dal calendario
  const [dragOp, setDragOp] = useState(null); // {operaio, fromLavoroId} being dragged
  const [dropTarget, setDropTarget] = useState(null); // lavoroId being hovered

  // ── Vista Operai: stato e helper (giorno-granulari, con orario personalizzabile) ──
  const [picker, setPicker] = useState(null);         // {operaioId, dataStr} — cella vuota cliccata
  const [dettaglioOp, setDettaglioOp] = useState(null); // {operaioId, dataStr, lavoro} — cella piena cliccata
  const [filtroTesto, setFiltroTesto] = useState("");
  const [oraInizioPick, setOraInizioPick] = useState("07:30");
  const [oraFinePick, setOraFinePick] = useState("16:30");
  const [editOrario, setEditOrario] = useState(null); // intervento in modifica nel dettaglio {ora_inizio, ora_fine, ruolo, attivita, nota}
  const [attivitaPick, setAttivitaPick] = useState("lavoro");
  const TUTTI_STATI_CAL = Object.keys(STATI_COLOR);
  const [statiVisibiliOperai, setStatiVisibiliOperai] = usePersistedState("cal_statiVisibiliOperai", TUTTI_STATI_CAL.filter(s=>!["Annullato","Rifiutato","Sospeso"].includes(s)));
  const [statoEspanso, setStatoEspanso] = usePersistedState("cal_statoEspanso", null);
  const [mostraNonDisponibili, setMostraNonDisponibili] = usePersistedState("cal_mostraNonDisponibili", false);
  // Settimana Operai: vista compatta (default) o dettaglio, ricerca, filtro e ordine
  const [densitaSett, setDensitaSett] = usePersistedState("cal_densitaSett", "compatta"); // "compatta" | "dettaglio"
  const [filtroSettimana, setFiltroSettimana] = useState("");
  const [soloLiberiSett, setSoloLiberiSett] = usePersistedState("cal_soloLiberiSett", false);
  const [ordineSett, setOrdineSett] = usePersistedState("cal_ordineSett_v2", "nome"); // "nome" (predefinito, A→Z) | "liberi"

  // ── Assenze (stessa tabella della pagina Ferie / Assenze) ──
  const [assenze, setAssenze] = useState([]);
  useEffect(()=>{ if(!db) return; db.from("assenze_operai").select("*").then(({data})=>setAssenze(data||[])); },[]);
  const assenzaDi = (opId, ds)=>assenzaDelGiorno(assenze, opId, ds);
  const [pickerTab, setPickerTab] = useState("cantiere"); // "cantiere" | "assenza"
  const [nuovaAssenza, setNuovaAssenza] = useState({tipo:"ferie", data_fine:"", note:"", togli:true});
  const [dettaglioAss, setDettaglioAss] = useState(null); // {operaioId, dataStr, assenza}
  const [schedaOp, setSchedaOp] = useState(null);         // id operaio
  // ── Subappaltatori ──
  const [subExtra, setSubExtra] = usePersistedState("cal_subExtra", []); // aggiunti a mano, non ancora usati in un cantiere
  const [pickerSub, setPickerSub] = useState(null);       // {nome, dataStr, costo, fino, soloFeriali}
  const [dettaglioSub, setDettaglioSub] = useState(null); // {nome, dataStr, lavoro, costo}
  const [nuovoSub, setNuovoSub] = useState(null);         // {nome, costo}
  const [assegnaCal, setAssegnaCal] = useState(null);     // lavoro aperto nel modale Assegna
  useOrariLavoro(db);                                     // carica gli orari del pannello Operai (ridisegna quando arrivano)
  // Valore programmato: obiettivo giornaliero (condiviso tra i dispositivi) ed € nelle celle
  const [obiettivoGiorno, salvaObiettivoGiorno] = useImpostazione(db, "obiettivo_giorno", 1550);
  const [obiettivoDraft, setObiettivoDraft] = useState(null); // valore in modifica, salvato all'uscita dal campo
  const [mostraEuro, setMostraEuro] = usePersistedState("cal_mostraEuro", true);
  const [schedaSub, setSchedaSub] = useState(null);       // {nome, costo} — modifica / elimina
  const [gestisciSub, setGestisciSub] = useState(false);  // elenco subappaltatori con "Elimina"
  // Anagrafica subappaltatori su Supabase (tabella "subappaltatori"); se la tabella non c'è si resta sul salvataggio locale
  const [subDb, setSubDb] = useState(null);
  const [subNascosti, setSubNascosti] = usePersistedState("cal_subNascosti", []);
  useEffect(()=>{
    if(!db) return;
    db.from("subappaltatori").select("*").then(async ({data,error})=>{
      if(error) return;
      let righe = data||[];
      // Porta nel database quelli aggiunti a mano o eliminati su questo dispositivo
      const mancanti = subExtra.filter(x=>!righe.some(r=>r.nome.toLowerCase()===x.nome.toLowerCase()));
      if(mancanti.length){
        const {data:nuovi,error:e1} = await db.from("subappaltatori").insert(mancanti.map(x=>({nome:x.nome,costo_giorno:x.costo_giorno??null}))).select();
        if(!e1){ righe=[...righe,...(nuovi||[])]; setSubExtra([]); }
      } else if(subExtra.length) setSubExtra([]);
      for(const n of subNascosti){
        const r = righe.find(x=>x.nome.toLowerCase()===n.toLowerCase());
        const {data:d2,error:e2} = r ? await db.from("subappaltatori").update({eliminato:true}).eq("id",r.id).select()
                                     : await db.from("subappaltatori").insert([{nome:n,eliminato:true}]).select();
        if(!e2 && d2?.[0]) righe = r ? righe.map(x=>x.id===r.id?d2[0]:x) : [...righe,d2[0]];
      }
      if(subNascosti.length) setSubNascosti([]);
      setSubDb(righe);
    });
  },[]);
  const subEliminati = new Set([...(subDb||[]).filter(r=>r.eliminato).map(r=>r.nome.toLowerCase()), ...subNascosti.map(n=>n.toLowerCase())]);
  const subappaltatori = (()=>{
    const m = {};
    [...(subDb||[]).filter(r=>!r.eliminato).map(r=>({nome:r.nome,costo_giorno:r.costo_giorno})), ...subappaltatoriDaLavori(lavori), ...subExtra].forEach(x=>{
      if(subEliminati.has(x.nome.toLowerCase())) return;
      if(!m[x.nome]) m[x.nome] = {...x};
      else if(m[x.nome].costo_giorno==null && x.costo_giorno!=null) m[x.nome].costo_giorno = x.costo_giorno;
    });
    return Object.values(m).sort((a,b)=>a.nome.localeCompare(b.nome));
  })();
  // Aggiunge o aggiorna un subappaltatore (se era stato eliminato, lo rimette)
  async function salvaSub(nome, costo){
    const c = costo===""||costo==null ? null : (parseFloat(costo)||null);
    if(subDb){
      const esiste = subDb.find(r=>r.nome.toLowerCase()===nome.toLowerCase());
      const {data,error} = esiste
        ? await db.from("subappaltatori").update({costo_giorno:c, eliminato:false}).eq("id",esiste.id).select()
        : await db.from("subappaltatori").insert([{nome, costo_giorno:c}]).select();
      if(error){ alert("Errore nel salvataggio: "+error.message); return false; }
      const r = data?.[0]; if(r) setSubDb(l=>esiste ? l.map(x=>x.id===r.id?r:x) : [...l,r]);
    } else {
      setSubExtra(x=>[...x.filter(s=>s.nome.toLowerCase()!==nome.toLowerCase()), {nome, costo_giorno:c}]);
      setSubNascosti(n=>n.filter(s=>s.toLowerCase()!==nome.toLowerCase()));
    }
    return true;
  }
  // Elimina un subappaltatore dall'elenco (le giornate già fatte restano nei cantieri e in Contabilità)
  async function eliminaSub(nome){
    if(!window.confirm(`Eliminare "${nome}" dai subappaltatori?\n\nNon comparirà più nel calendario. Le giornate già segnate nei cantieri restano in Contabilità.`)) return false;
    if(subDb){
      const esiste = subDb.find(r=>r.nome.toLowerCase()===nome.toLowerCase());
      const {data,error} = esiste
        ? await db.from("subappaltatori").update({eliminato:true}).eq("id",esiste.id).select()
        : await db.from("subappaltatori").insert([{nome, eliminato:true}]).select();
      if(error){ alert("Errore nell'eliminazione: "+error.message); return false; }
      const r = data?.[0]; if(r) setSubDb(l=>esiste ? l.map(x=>x.id===r.id?r:x) : [...l,r]);
    } else {
      setSubNascosti(n=>[...n.filter(s=>s.toLowerCase()!==nome.toLowerCase()), nome]);
    }
    setSubExtra(x=>x.filter(s=>s.nome.toLowerCase()!==nome.toLowerCase()));
    return true;
  }
  const addGiorni = (ds,n)=>{ const d=new Date(ds+"T12:00:00"); d.setDate(d.getDate()+n); return d.toISOString().slice(0,10); };

  const lavoriAttivi = lavori.filter(l=>!["Annullato","Rifiutato","Sospeso"].includes(l.stato));
  const lavoriVisibiliOperai = lavori.filter(l=>statiVisibiliOperai.includes(l.stato));
  const operaiCalendario = (mostraNonDisponibili ? operai : operai.filter(o=>o.disponibile!==false)).slice().sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","it"));
  const isMob = typeof window!=="undefined" && window.innerWidth<768;
  // Quando assegni dal calendario: solo cantieri Da organizzare e In corso (stato Programmato).
  // "Mostra anche altri stati" li allarga a confermati e completati (es. un lavoro chiuso ieri).
  const STATI_DA_ASSEGNARE = ["Da organizzare","Programmato"];
  const [altriStatiPick, setAltriStatiPick] = useState(false);
  const lavoriAssegnabili = lavori
    .filter(l=>STATI_DA_ASSEGNARE.includes(l.stato) || (altriStatiPick && ["Confermato","Completato"].includes(l.stato)))
    .filter(l=>!filtroTesto || l.titolo?.toLowerCase().includes(filtroTesto.toLowerCase()) || l.cliente?.toLowerCase().includes(filtroTesto.toLowerCase()))
    .sort((a,b)=>(STATI_DA_ASSEGNARE.indexOf(a.stato)+1||9)-(STATI_DA_ASSEGNARE.indexOf(b.stato)+1||9) || (a.data_inizio||"9").localeCompare(b.data_inizio||"9"));
  const filtroStatiPick = (
    <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11.5,color:C.textMuted,cursor:"pointer"}}>
      <input type="checkbox" checked={altriStatiPick} onChange={e=>setAltriStatiPick(e.target.checked)}/>
      Mostra anche confermati e completati
    </label>
  );

  // Salva e aggiorna lo stato in automatico:
  // Da organizzare → In corso appena c'è qualcuno assegnato; In corso → Da organizzare se non resta nessuno.
  async function salvaCal(lavoro, campi){
    const n = {...lavoro, ...campi};
    const occupato = (n.operai_ids||[]).length>0 || Object.values(n.giorni_dettaglio||{}).some(ivs=>(ivs||[]).some(iv=>iv.subappalto));
    let stato = null;
    if(lavoro.stato==="Da organizzare" && occupato) stato = "Programmato";
    if(lavoro.stato==="Programmato" && !occupato) stato = "Da organizzare";
    return await salvaCampiLavoro(db, lavoro, stato ? {...campi, stato} : campi, setLavori);
  }

  function lavoroDelGiorno(operaioId, dataStr){
    return lavoriAttivi.find(l=>giorniOperaioSuLavoro(l,operaioId).includes(dataStr));
  }
  // Tutti i cantieri dell'operaio in quel giorno (un supervisore può averne più di uno): prima quelli dove lavora
  function lavoriDelGiornoVisibili(operaioId, dataStr){
    // in ordine di orario del primo intervento della giornata
    const primo = (l)=>interventiSuLavoro(l,operaioId,dataStr)[0]?.ora_inizio||"99:99";
    return lavoriVisibiliOperai
      .filter(l=>giorniOperaioSuLavoro(l,operaioId).includes(dataStr))
      .sort((a,b)=>primo(a).localeCompare(primo(b)));
  }
  function lavoroDelGiornoVisibile(operaioId, dataStr){
    return lavoriDelGiornoVisibili(operaioId, dataStr)[0];
  }
  // La supervisione non crea conflitti; il lavoro vero sì, ma solo con altri giorni di lavoro vero
  function conflittoGiorno(operaioId, dataStr, escludiLavoroId, ruolo="lavora", ini="00:00", fine="23:59"){
    if(ruolo==="supervisione") return null;
    return conflittoOrario(lavoriAttivi, operaioId, dataStr, ini, fine, [escludiLavoroId])?.lavoro || null;
  }
  function orarioOperaioGiorno(lavoro, operaioId, dataStr){
    const iv = (lavoro?.giorni_dettaglio||{})[dataStr];
    if(!iv) return null;
    for(const i of iv){ const o=(i.operai_orari||[]).find(x=>x.operaio_id===operaioId); if(o) return o; }
    return null;
  }

  // Aggiunge UN intervento (non sostituisce gli altri del giorno). escludiIntervento: quello che stiamo spostando.
  async function assegnaGiorno(lavoro, dataStr, operaioId, oraInizio=orarioDelGiorno(dataStr).ora_inizio, oraFine=orarioDelGiorno(dataStr).ora_fine, ruolo="lavora", escludiIntervento=null, attivita="lavoro", nota=""){
    const op = operai.find(o=>o.id===operaioId);
    if(!(oraInizio<oraFine)){ alert("L'orario di fine deve essere dopo l'inizio."); return false; }
    if(ruolo!=="supervisione"){
      const c = conflittoOrario(lavoriAttivi, operaioId, dataStr, oraInizio, oraFine, [], escludiIntervento);
      if(c){ alert(`⚠️ ${op?.nome} il ${fmt(dataStr)} è già su "${c.lavoro.titolo}" dalle ${c.intervento.ora_inizio} alle ${c.intervento.ora_fine}. Scegli un orario libero.`); return false; }
    }
    const ass = assenzaDi(operaioId, dataStr);
    if(ass && !window.confirm(`${op?.nome} il ${fmt(dataStr)} risulta in ${labelAssenza(ass.tipo)}. Assegnarlo comunque?`)) return false;
    if(op?.disponibile===false && !window.confirm(`${op.nome} è segnato come non disponibile. Assegnarlo comunque?`)) return false;
    const fresco = lavori.find(l=>l.id===lavoro.id)||lavoro;
    return await salvaCal(fresco, conIntervento(fresco, operaioId, dataStr, {ora_inizio:oraInizio, ora_fine:oraFine, ruolo, attivita, nota}));
  }

  async function rimuoviGiorno(lavoro, dataStr, operaioId){
    const fresco = lavori.find(l=>l.id===lavoro.id)||lavoro;
    return await salvaCal(fresco, senzaOperaioGiorni(fresco, operaioId, [dataStr]));
  }
  async function rimuoviIntervento(lavoro, dataStr, operaioId, rif){
    const fresco = lavori.find(l=>l.id===lavoro.id)||lavoro;
    return await salvaCal(fresco, cambiaIntervento(fresco, operaioId, dataStr, rif, null));
  }
  async function modificaIntervento(lavoro, dataStr, operaioId, rif, nuovo){
    const fresco = lavori.find(l=>l.id===lavoro.id)||lavoro;
    const finale = {...rif, ...nuovo};
    if(!(finale.ora_inizio<finale.ora_fine)){ alert("L'orario di fine deve essere dopo l'inizio."); return false; }
    if(finale.ruolo!=="supervisione"){
      const c = conflittoOrario(lavoriAttivi, operaioId, dataStr, finale.ora_inizio, finale.ora_fine, [], {lavoroId:lavoro.id, ora_inizio:rif.ora_inizio, ora_fine:rif.ora_fine});
      if(c){ alert(`⚠️ Si sovrappone a "${c.lavoro.titolo}" (${c.intervento.ora_inizio}–${c.intervento.ora_fine}).`); return false; }
    }
    return await salvaCal(fresco, cambiaIntervento(fresco, operaioId, dataStr, rif, nuovo));
  }
  async function spostaIntervento(daLavoro, aLavoro, dataStr, operaioId, rif){
    const ok = await assegnaGiorno(aLavoro, dataStr, operaioId, rif.ora_inizio, rif.ora_fine, rif.ruolo||"lavora", {lavoroId:daLavoro.id, ora_inizio:rif.ora_inizio, ora_fine:rif.ora_fine}, rif.attivita||"lavoro", rif.nota||"");
    if(!ok) return false;
    const fresco = lavori.find(l=>l.id===daLavoro.id)||daLavoro;
    return await salvaCal(fresco, cambiaIntervento(fresco, operaioId, dataStr, rif, null));
  }
  // Apre "aggiungi intervento" con l'orario proposto dopo l'ultimo intervento della giornata
  function apriPicker(operaioId, dataStr, extra={}){
    const o = orarioSuccessivo(operaioId, dataStr);
    setOraInizioPick(o.ora_inizio); setOraFinePick(o.ora_fine);
    setAttivitaPick("lavoro"); setPickerTab("cantiere"); setFiltroTesto("");
    setPicker({operaioId, dataStr, ...extra});
  }
  // Interventi visibili (secondo la legenda stati) di un operaio in un giorno, in ordine di orario
  const interventiVisibili = (operaioId, dataStr)=>interventiOperaioGiorno(lavoriVisibiliOperai, operaioId, dataStr);
  const apriGiornata = (operaioId, dataStr, it)=>{ setEditOrario(null); setDettaglioOp({operaioId, dataStr, lavoro:it.lavoro, rif:{ora_inizio:it.ora_inizio, ora_fine:it.ora_fine}}); };
  // Prossimo orario libero dell'operaio quel giorno (dopo l'ultimo intervento)
  function orarioSuccessivo(operaioId, dataStr){
    const ivs = interventiOperaioGiorno(lavoriAttivi, operaioId, dataStr);
    if(ivs.length===0){ const o=orarioDelGiorno(dataStr); return {ora_inizio:o.ora_inizio, ora_fine:o.ora_fine}; }
    const ultimo = ivs.reduce((m,x)=>x.ora_fine>m?x.ora_fine:m, "00:00");
    const [h,m] = ultimo.split(":").map(Number);
    const fine = `${String(Math.min(21,h+2)).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
    return {ora_inizio:ultimo, ora_fine: fine>ultimo?fine:"21:00"};
  }

  async function cambiaRuoloGiorno(lavoro, dataStr, operaioId, ruolo){
    if(ruolo==="lavora"){
      for(const x of interventiSuLavoro(lavoro, operaioId, dataStr)){
        const c = conflittoOrario(lavoriAttivi, operaioId, dataStr, x.ora_inizio, x.ora_fine, [lavoro.id]);
        if(c){ alert(`⚠️ ${operai.find(o=>o.id===operaioId)?.nome} il ${fmt(dataStr)} lavora su "${c.lavoro.titolo}" negli stessi orari (${c.intervento.ora_inizio}–${c.intervento.ora_fine}). Qui può solo supervisionare.`); return null; }
      }
    }
    const gd = {...(lavoro.giorni_dettaglio||{})};
    gd[dataStr] = (gd[dataStr]||[]).map(iv=>({...iv, operai_orari:(iv.operai_orari||[]).map(o=>{
      if(o.operaio_id!==operaioId) return o;
      const {ruolo:_r, ...resto} = o;
      return ruolo==="supervisione" ? {...resto, ruolo:"supervisione"} : resto;
    })}));
    const {error} = await db.from("lavori").update({giorni_dettaglio:gd}).eq("id",lavoro.id);
    if(error){ alert("Errore: "+error.message); return null; }
    setLavori(ls=>ls.map(l=>l.id===lavoro.id?{...l,giorni_dettaglio:gd}:l));
    return {...lavoro, giorni_dettaglio:gd};
  }

  async function sospendi(l){
    await db.from("lavori").update({stato:"Sospeso"}).eq("id",l.id);
    setLavori(ls=>ls.map(x=>x.id===l.id?{...x,stato:"Sospeso"}:x));
    setCalAction(null);
  }
  async function annulla(l){
    if(!window.confirm("Annullare questo lavoro?")) return;
    await db.from("lavori").update({stato:"Annullato"}).eq("id",l.id);
    setLavori(ls=>ls.map(x=>x.id===l.id?{...x,stato:"Annullato"}:x));
    setCalAction(null);
  }

  // Giorni del cantiere da oggi in avanti: il passato resta com'è (conta in Contabilità)
  function giorniFuturi(l){ return giorniPianificati(l).filter(ds=>ds>=today); }
  function giorniLiberi(operaioId, giorni, escludiIds=[]){
    const ok=[], saltati=[];
    giorni.forEach(ds=>{
      const conf = conflittoOrario(lavoriAttivi, operaioId, ds, orarioDelGiorno(ds).ora_inizio, orarioDelGiorno(ds).ora_fine, escludiIds);
      const ass = assenzaDi(operaioId, ds);
      if(conf) saltati.push(`${fmt(ds)} — già su "${conf.lavoro.titolo}" ${conf.intervento.ora_inizio}–${conf.intervento.ora_fine}`);
      else if(ass) saltati.push(`${fmt(ds)} — ${labelAssenza(ass.tipo)}`);
      else ok.push(ds);
    });
    return {ok, saltati};
  }

  async function aggiungiOperaioACantiere(operaioId, lavoroId){
    const lav = lavori.find(l=>l.id===lavoroId); if(!lav) return;
    const op = operai.find(o=>o.id===operaioId);
    const giorni = giorniFuturi(lav);
    if(giorni.length===0){ alert(`"${lav.titolo}" non ha giorni da oggi in avanti. Usa "📅 Assegna periodo" per scegliere le date.`); return; }
    if(op?.disponibile===false && !window.confirm(`${op.nome} è segnato come non disponibile. Aggiungerlo comunque?`)) return;
    const {ok, saltati} = giorniLiberi(operaioId, giorni, [lavoroId]);
    if(ok.length===0){ alert(`${op?.nome} non è libero in nessun giorno di questo cantiere:\n`+saltati.join("\n")); return; }
    const salvato = await salvaCal(lav, conOperaioGiorni(lav, operaioId, ok));
    if(salvato && saltati.length>0) alert(`${op?.nome} aggiunto su ${ok.length} giorn${ok.length>1?"i":"o"}. Saltati:\n`+saltati.join("\n"));
  }

  async function rimuoviOperaioDaCantiere(operaioId, lavoroId){
    const lav = lavori.find(l=>l.id===lavoroId); if(!lav) return;
    const giorni = giorniOperaioSuLavoro(lav, operaioId).filter(ds=>ds>=today);
    if(giorni.length===0){ alert("Nessun giorno da oggi in avanti da togliere: i giorni passati restano, servono alla Contabilità."); return; }
    await salvaCal(lav, senzaOperaioGiorni(lav, operaioId, giorni));
  }

  async function spostaOperaio(operaioId, fromLavoroId, toLavoroId){
    setDragOp(null); setDropTarget(null);
    if(fromLavoroId === toLavoroId) return;
    const fromLav = lavori.find(l=>l.id===fromLavoroId);
    const toLav   = lavori.find(l=>l.id===toLavoroId);
    if(!fromLav||!toLav) return;
    const op = operai.find(o=>o.id===operaioId);
    const giorniTo = giorniFuturi(toLav);
    if(giorniTo.length===0){ alert(`"${toLav.titolo}" non ha giorni da oggi in avanti. Aprilo e usa "📅 Assegna periodo".`); return; }
    const {ok, saltati} = giorniLiberi(operaioId, giorniTo, [fromLavoroId, toLavoroId]);
    if(ok.length===0){ alert(`${op?.nome} non è libero in nessun giorno di "${toLav.titolo}":\n`+saltati.join("\n")); return; }
    const giorniFrom = giorniOperaioSuLavoro(fromLav, operaioId).filter(ds=>ds>=today);
    if(giorniFrom.length>0){ const a = await salvaCal(fromLav, senzaOperaioGiorni(fromLav, operaioId, giorniFrom)); if(!a) return; }
    const b = await salvaCal(toLav, conOperaioGiorni(toLav, operaioId, ok));
    if(b && saltati.length>0) alert(`${op?.nome} spostato su ${ok.length} giorn${ok.length>1?"i":"o"}. Saltati:\n`+saltati.join("\n"));
  }

  // Sposta il cantiere: tutti i giorni e le squadre scorrono insieme; la fine può accorciare o allungare
  async function salvaNuoveDate(lav, s, e){
    if(!s||!e) return false;
    if(e<s){ alert("La data di fine è prima dell'inizio."); return false; }
    const semplice = !(lav.giorni_lavoro||[]).length && !Object.keys(lav.giorni_dettaglio||{}).length && !(lav.operai_ids||[]).length;
    if(semplice) return await salvaCal(lav, {data_inizio:s, data_fine:e});
    const inizioAtt = lav.data_inizio || giorniPianificati(lav)[0] || s;
    const delta = Math.round((new Date(s+"T12:00:00")-new Date(inizioAtt+"T12:00:00"))/86400000);
    let campi = delta!==0 ? spostaDateDettaglio(lav, delta) : {};
    let nuovo = {...lav, ...campi};
    const gd = dettaglioNormalizzato(nuovo);
    let gl = giorniPianificati(nuovo);
    const ultimo = gl[gl.length-1];
    if(ultimo && e<ultimo){
      Object.keys(gd).forEach(ds=>{ if(ds>e) delete gd[ds]; });
      gl = gl.filter(ds=>ds<=e);
    } else if(ultimo && e>ultimo){
      const squadra = gd[ultimo]||[];
      const aggiunti = rangeDates(addGiorni(ultimo,1), e).filter(giornoFeriale);
      aggiunti.forEach(ds=>{ if(squadra.length) gd[ds] = squadra.map(iv=>({...iv, operai_orari:[...(iv.operai_orari||[])]})); });
      gl = [...gl, ...aggiunti];
    }
    campi = {giorni_dettaglio:gd, giorni_lavoro:gl, data_inizio:s, data_fine:e,
      operai_ids:[...new Set(Object.values(gd).flatMap(ivs=>ivs.flatMap(iv=>(iv.operai_orari||[]).map(o=>o.operaio_id))))]};
    // Sovrapposizioni dopo lo spostamento
    const problemi = [];
    Object.entries(gd).forEach(([ds,ivs])=>ivs.forEach(iv=>(iv.operai_orari||[]).forEach(o=>{
      const nome = operai.find(x=>x.id===o.operaio_id)?.nome||"Operaio";
      if(o.ruolo!=="supervisione"){ const conf = conflittoOrario(lavoriAttivi, o.operaio_id, ds, o.ora_inizio, o.ora_fine, [lav.id]); if(conf) problemi.push(`${nome} ${fmt(ds)} — già su "${conf.lavoro.titolo}" ${conf.intervento.ora_inizio}–${conf.intervento.ora_fine}`); }
      const ass = assenzaDi(o.operaio_id, ds); if(ass) problemi.push(`${nome} ${fmt(ds)} — ${labelAssenza(ass.tipo)}`);
    })));
    if(problemi.length>0 && !window.confirm("Dopo lo spostamento ci sono sovrapposizioni:\n"+problemi.slice(0,8).join("\n")+(problemi.length>8?`\n…e altre ${problemi.length-8}`:"")+"\n\nSpostare comunque? (poi puoi sistemarle dalla vista Operai)")) return false;
    return await salvaCal(lav, campi);
  }

  // ── Assenze ──
  async function salvaAssenza(operaioId, dal, al, tipo, note, togli){
    if(!al || al<dal){ alert("Controlla la data di fine dell'assenza."); return false; }
    const {data:n, error} = await db.from("assenze_operai").insert([{operaio_id:operaioId, data_inizio:dal, data_fine:al, tipo, note:note||null}]).select().single();
    if(error){ alert("Errore nel salvare l'assenza: "+error.message); return false; }
    setAssenze(a=>[...a, n]);
    if(togli){
      const giorni = rangeDates(dal, al);
      for(const l of lavori.filter(x=>!["Annullato","Rifiutato"].includes(x.stato))){
        const gg = giorniOperaioSuLavoro(l, operaioId).filter(ds=>giorni.includes(ds));
        if(gg.length>0) await salvaCal(l, senzaOperaioGiorni(l, operaioId, gg));
      }
    }
    return true;
  }
  async function eliminaAssenza(a){
    const {error} = await db.from("assenze_operai").delete().eq("id",a.id);
    if(error){ alert("Errore: "+error.message); return false; }
    setAssenze(x=>x.filter(y=>y.id!==a.id));
    return true;
  }
  async function togliGiornoAssenza(a, ds){
    if(a.data_inizio===ds && a.data_fine===ds) return eliminaAssenza(a);
    const {id, created_at, ...resto} = a;
    if(a.data_inizio<ds){
      const {error} = await db.from("assenze_operai").update({data_fine:addGiorni(ds,-1)}).eq("id",a.id);
      if(error){ alert("Errore: "+error.message); return false; }
      let nuova = null;
      if(a.data_fine>ds){
        const r = await db.from("assenze_operai").insert([{...resto, data_inizio:addGiorni(ds,1)}]).select().single();
        if(r.error){ alert("Errore: "+r.error.message); return false; }
        nuova = r.data;
      }
      setAssenze(x=>[...x.map(y=>y.id===a.id?{...y,data_fine:addGiorni(ds,-1)}:y), ...(nuova?[nuova]:[])]);
    } else {
      const {error} = await db.from("assenze_operai").update({data_inizio:addGiorni(ds,1)}).eq("id",a.id);
      if(error){ alert("Errore: "+error.message); return false; }
      setAssenze(x=>x.map(y=>y.id===a.id?{...y,data_inizio:addGiorni(ds,1)}:y));
    }
    return true;
  }

  // ── Disponibilità operaio (stesso campo della pagina Operai) ──
  async function cambiaDisponibile(op, val){
    const {error} = await db.from("operai").update({disponibile:val}).eq("id",op.id);
    if(error){ alert("Errore: "+error.message); return; }
    setOperai(os=>os.map(o=>o.id===op.id?{...o,disponibile:val}:o));
    if(!val){
      const futuri = lavoriAttivi.map(l=>({l, gg:giorniOperaioSuLavoro(l, op.id).filter(ds=>ds>=today)})).filter(x=>x.gg.length>0);
      if(futuri.length>0 && window.confirm(`${op.nome} ha ancora ${futuri.reduce((t,x)=>t+x.gg.length,0)} giorni assegnati da oggi in avanti (${futuri.map(x=>x.l.titolo).join(", ")}). Toglierlo da quei giorni?`)){
        for(const x of futuri) await salvaCal(x.l, senzaOperaioGiorni(x.l, op.id, x.gg));
      }
    }
  }

  // ── Subappaltatori a giornata ──
  function lavoriSubGiorno(nome, ds){ return lavoriVisibiliOperai.filter(l=>subDelGiorno(l,ds).some(x=>x.nome===nome)); }
  async function assegnaSub(lavoro, nome, giorni, costo){
    const fresco = lavori.find(l=>l.id===lavoro.id)||lavoro;
    const ok = await salvaCal(fresco, conSubGiorno(fresco, nome, giorni, costo));
    if(ok) setSubExtra(x=>x.filter(y=>y.nome!==nome));
    return ok;
  }
  async function togliSub(lavoro, nome, giorni){
    const fresco = lavori.find(l=>l.id===lavoro.id)||lavoro;
    return await salvaCal(fresco, senzaSubGiorni(fresco, nome, giorni));
  }

  function getLavForDay(ds){
    return lavori.filter(l=>{
      if(["Annullato","Rifiutato","Sospeso"].includes(l.stato)) return false;
      if(selOp!=="tutti"&&!giorniOperaioSuLavoro(l,parseInt(selOp)).includes(ds)) return false;
      if(l.giorni_lavoro && l.giorni_lavoro.length>0) return l.giorni_lavoro.includes(ds);
      return ds>=l.data_inizio&&ds<=l.data_fine;
    });
  }
  function toStr(d){ return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }

  function navPrev(){
    const d=new Date(curDate);
    if(vista==="mese") d.setMonth(d.getMonth()-1);
    else if(vista==="settimana") d.setDate(d.getDate()-7);
    else d.setDate(d.getDate()-1);
    setCurDate(d);
  }
  function navNext(){
    const d=new Date(curDate);
    if(vista==="mese") d.setMonth(d.getMonth()+1);
    else if(vista==="settimana") d.setDate(d.getDate()+7);
    else d.setDate(d.getDate()+1);
    setCurDate(d);
  }

  // ─ VISTA MESE ─
  function VistaMese(){
    const year=curDate.getFullYear(), month=curDate.getMonth();
    const daysInMonth=getDaysInMonth(year,month);
    const firstDay=(getFirstDayOfMonth(year,month)+6)%7;
    const monthName=new Date(year,month,1).toLocaleDateString("it-IT",{month:"long",year:"numeric"});
    return(
      <div style={{display:"flex",flexDirection:"column",gap:0}}>
        <div style={{textAlign:"center",padding:"12px 0",fontSize:15,fontWeight:700,color:C.text,textTransform:"capitalize"}}>{monthName}</div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",background:C.bg}}>
            {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d=><div key={d} style={{textAlign:"center",padding:"10px 0",fontSize:11,fontWeight:700,color:C.textMuted,letterSpacing:1}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,background:C.border}}>
            {Array.from({length:firstDay}).map((_,i)=><div key={`e${i}`} style={{background:C.card,minHeight:85}}/>)}
            {Array.from({length:daysInMonth},(_,i)=>i+1).map(day=>{
              const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const dl=getLavForDay(ds);
              const isToday=ds===today;
              return(
                <div key={day} style={{background:isToday?C.accentGlow:C.card,minHeight:85,padding:"6px 7px",borderTop:isToday?`2px solid ${C.brand}`:"none"}}>
                  <div style={{fontSize:12,fontWeight:isToday?800:500,color:isToday?C.brand:C.textMuted,marginBottom:3}}>{day}</div>
                  {dl.slice(0,3).map(l=>{const col=STATI_COLOR[l.stato];return <div key={l.id} onClick={e=>{e.stopPropagation();setCalAction(l);}} style={{background:col+"22",border:`1px solid ${col}44`,borderRadius:4,padding:"2px 5px",marginBottom:2,fontSize:10,fontWeight:600,color:col,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",cursor:"pointer"}}>{l.titolo.slice(0,14)}{l.titolo.length>14?"…":""}</div>;})}
                  {dl.length>3&&<div style={{fontSize:9,color:C.textMuted}}>+{dl.length-3}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─ VISTA SETTIMANA ─
  function VistaSettimana(){
    const startOfWeek = new Date(curDate);
    const dow = (curDate.getDay()+6)%7;
    startOfWeek.setDate(curDate.getDate()-dow);
    const giorni = Array.from({length:7},(_,i)=>{ const d=new Date(startOfWeek); d.setDate(d.getDate()+i); return d; });
    const weekLabel = `${giorni[0].toLocaleDateString("it-IT",{day:"2-digit",month:"short"})} — ${giorni[6].toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric"})}`;
    return(
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <div style={{textAlign:"center",padding:"8px 0",fontSize:14,fontWeight:700,color:C.text}}>{weekLabel}</div>
        {giorni.map((d,i)=>{
          const ds=toStr(d);
          const dl=getLavForDay(ds);
          const isToday=ds===today;
          const nomeGiorno=["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato","Domenica"][i];
          return(
            <div key={i} style={{background:C.card,border:`1px solid ${isToday?C.brand:C.border}`,borderRadius:16,overflow:"hidden"}}>
              {/* Header giorno */}
              <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 16px",borderBottom:dl.length>0?`1px solid ${C.border}`:"none",background:isToday?C.accentGlow:"transparent"}}>
                <div style={{width:44,height:44,borderRadius:14,background:isToday?C.brand:C.border,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <div style={{fontSize:18,fontWeight:900,color:isToday?"#fff":C.text,lineHeight:1}}>{d.getDate()}</div>
                  <div style={{fontSize:8,fontWeight:700,color:isToday?"#fff":C.textMuted,textTransform:"uppercase",letterSpacing:0.5}}>{d.toLocaleDateString("it-IT",{month:"short"})}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:isToday?C.brand:C.text}}>{nomeGiorno}</div>
                  <div style={{fontSize:11,color:C.textMuted,marginTop:1}}>{dl.length===0?"Nessun lavoro":dl.length===1?"1 lavoro":`${dl.length} lavori`}</div>
                </div>
                {isToday&&<div style={{background:C.accent+"22",border:`1px solid ${C.accent}44`,borderRadius:24,padding:"3px 10px",fontSize:10,fontWeight:700,color:C.accent}}>Oggi</div>}
              </div>
              {/* Lavori */}
              {dl.length>0&&(
                <div style={{padding:"10px 16px",display:"flex",flexDirection:"column",gap:8}}>
                  {dl.map(l=>{
                    const col=STATI_COLOR[l.stato];
                    const ops=operai.filter(o=>giorniOperaioSuLavoro(l,o.id).includes(ds));
                    const subs=subDelGiorno(l,ds);
                    return(
                      <div key={l.id} onClick={()=>setCalAction(l)} style={{display:"flex",gap:12,alignItems:"flex-start",background:C.bg,border:`1px solid ${C.border}`,borderLeft:`3px solid ${col}`,borderRadius:11,padding:"10px 14px",cursor:"pointer"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                            <span style={{fontWeight:700,fontSize:13,color:C.text}}>{l.titolo}</span>
                            <Badge label={l.stato} color={col}/>
                            {l.fascia_oraria&&<span style={{fontSize:10,color:C.textMuted,fontWeight:600}}>🕐 {l.fascia_oraria}</span>}
                          </div>
                          <div style={{fontSize:12,color:C.textMuted,marginBottom:4}}>📍 {l.indirizzo} {l.mq_stimati?`· 📐 ${l.mq_stimati}mq`:""} {l.materiale?`· 🪵 ${l.materiale}`:""}</div>
                          {(ops.length>0||subs.length>0)&&(
                            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:4}}>
                              {ops.map(o=>(
                                <span key={o.id} style={{fontSize:11,background:o.colore+"22",color:o.colore,border:`1px solid ${assenzaDi(o.id,ds)?C.red:o.colore+"33"}`,borderRadius:24,padding:"2px 8px",fontWeight:600}}>
                                  {ruoloOperaioGiorno(l,o.id,ds)==="supervisione"?"👁":"👷"} {o.nome.split(" ")[0]}{assenzaDi(o.id,ds)?" ⚠":""}
                                </span>
                              ))}
                              {subs.map(sb=>(
                                <span key={sb.nome} style={{fontSize:11,background:C.bg,color:C.textSub,border:`1px solid ${C.border}`,borderRadius:24,padding:"2px 8px",fontWeight:600}}>🤝 {sb.nome}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ─ VISTA GIORNO ─
  function VistaGiorno(){
    const ds = toStr(curDate);
    const dl = getLavForDay(ds);
    const dayLabel = curDate.toLocaleDateString("it-IT",{weekday:"long",day:"2-digit",month:"long",year:"numeric"});
    const isToday = ds===today;
    return(
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div style={{textAlign:"center",padding:"12px 0"}}>
          <div style={{fontSize:15,fontWeight:700,color:isToday?C.brand:C.text,textTransform:"capitalize"}}>{dayLabel}</div>
          {isToday&&<div style={{fontSize:11,color:C.accent,marginTop:2}}>● Oggi</div>}
        </div>
        {dl.length===0
          ? <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:40,textAlign:"center",color:C.textMuted,fontSize:13}}>Nessun lavoro programmato per questo giorno.</div>
          : dl.map(l=>{
              const col=STATI_COLOR[l.stato];
              const ops=operai.filter(o=>giorniOperaioSuLavoro(l,o.id).includes(ds));
              const subs=subDelGiorno(l,ds);
              return(
                <div key={l.id} onClick={()=>setCalAction(l)} style={{background:C.card,boxShadow:C.shadowSm,borderLeft:`4px solid ${col}`,borderRadius:16,padding:"16px 20px",cursor:"pointer"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    <div style={{fontWeight:800,fontSize:16,color:C.text,flex:1}}>{l.titolo}</div>
                    <Badge label={l.stato} color={col}/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div style={{fontSize:13,color:C.textMuted}}>📍 {l.indirizzo}</div>
                    <div style={{fontSize:13,color:C.textMuted}}>👤 {l.cliente}</div>
                    <div style={{fontSize:13,color:C.textMuted}}>📐 {l.mq_stimati} mq</div>
                    <div style={{fontSize:13,color:C.textMuted}}>🪵 {l.materiale}</div>
                  </div>
                  {(ops.length>0||subs.length>0)&&(
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {subs.map(sb=>(
                        <div key={sb.nome} style={{display:"flex",alignItems:"center",gap:8,background:C.bg,border:`1px solid ${C.border}`,borderRadius:24,padding:"5px 12px",fontSize:12,fontWeight:600,color:C.textSub}}>🤝 {sb.nome} <span style={{fontSize:11,color:C.textMuted,fontWeight:500}}>subappalto</span></div>
                      ))}
                      {ops.map(o=>(
                        <div key={o.id} style={{display:"flex",alignItems:"center",gap:8,background:o.colore+"18",border:`1px solid ${o.colore}33`,borderRadius:24,padding:"5px 12px"}}>
                          <div style={{width:22,height:22,borderRadius:"50%",background:o.colore+"33",border:`1px solid ${o.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:o.colore}}>{o.nome[0]}</div>
                          <span style={{fontSize:12,fontWeight:600,color:o.colore}}>{o.nome}</span>
                          {(()=>{ const its=interventiSuLavoro(l,o.id,ds); return its.length?<span style={{fontSize:11,color:C.textMuted}}>🕐 {its.map(x=>`${x.ora_inizio}-${x.ora_fine}${x.ruolo==="supervisione"?" 👁":""}`).join(" / ")}</span>:null; })()}
                          {assenzaDi(o.id,ds) && <span style={{fontSize:11,color:C.red,fontWeight:700}}>⚠ {labelAssenza(assenzaDi(o.id,ds).tipo)}</span>}
                          {o.telefono && <span style={{fontSize:11,color:C.textMuted}}>📞 {o.telefono}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {l.note&&<div style={{marginTop:10,fontSize:12,color:C.textSub,fontStyle:"italic",borderTop:`1px solid ${C.border}`,paddingTop:10}}>📝 {l.note}</div>}
                </div>
              );
            })
        }
      </div>
    );
  }

  // ══ VISTA OPERAI — stessa navigazione giorno/settimana/mese, righe = operai ══
  const cellaBase = {borderRadius:9,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"transform 0.1s, box-shadow 0.1s"};
  function CellaOperaio({op, ds, compact}){
    const ivs = interventiVisibili(op.id, ds);
    const isToday = ds===today;
    const ass = assenzaDi(op.id, ds);
    const iconaIv = (it)=>it.ruolo==="supervisione"?"👁 ":it.attivita&&it.attivita!=="lavoro"?`${ATTIVITA[it.attivita]?.i||""} `:"";
    return(
      <div onClick={()=>ivs.length?apriGiornata(op.id,ds,ivs[0]):ass?setDettaglioAss({operaioId:op.id,dataStr:ds,assenza:ass}):apriPicker(op.id,ds)}
        title={ass?`${labelAssenza(ass.tipo)} ${fmt(ass.data_inizio)} → ${fmt(ass.data_fine)}`:ivs.map(it=>`${it.ora_inizio}–${it.ora_fine} ${it.lavoro.titolo}`).join("\n")||undefined}
        style={{background:isToday?C.accentGlow+"66":"transparent",minHeight:compact?46:58,padding:compact?"4px 3px":"6px 5px",...cellaBase,flexDirection:"column",alignItems:"stretch",justifyContent:"center",gap:3}}>
        {ivs.length===0 && ass ? (
          <div style={{width:"100%",height:compact?28:38,borderRadius:9,background:`repeating-linear-gradient(135deg, ${C.bg}, ${C.bg} 5px, ${C.border} 5px, ${C.border} 7px)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:compact?11:11.5,fontWeight:700,color:C.textSub,overflow:"hidden",whiteSpace:"nowrap"}}>
            {compact ? labelAssenza(ass.tipo).split(" ")[0] : labelAssenza(ass.tipo)}
          </div>
        ) : ivs.length>0 ? (
          compact ? (
            // Mese: una barretta colorata per intervento
            <div style={{display:"flex",flexDirection:"column",gap:2,width:"100%",outline:ass?`1.5px solid ${C.red}`:"none",borderRadius:4}}>
              {ivs.slice(0,3).map((it,i)=>{ const col=STATI_COLOR[it.lavoro.stato]; return (
                <div key={i} style={{height:ivs.length===1?24:ivs.length===2?11:7,borderRadius:3,background:it.ruolo==="supervisione"?`repeating-linear-gradient(90deg, ${col}55, ${col}55 3px, transparent 3px, transparent 5px)`:col+"cc"}}/>
              );})}
              {ivs.length>3 && <div style={{fontSize:8.5,color:C.textMuted,textAlign:"center",lineHeight:1}}>+{ivs.length-3}</div>}
            </div>
          ) : (<>
            {ivs.map((it,i)=>{ const col=STATI_COLOR[it.lavoro.stato]; return (
              <div key={i} onClick={e=>{e.stopPropagation();apriGiornata(op.id,ds,it);}}
                style={{width:"100%",boxSizing:"border-box",background:it.ruolo==="supervisione"?"transparent":col+"18",border:it.ruolo==="supervisione"?`1px dashed ${col}88`:"none",borderLeft:`3px solid ${col}`,borderRadius:7,padding:"3px 6px",outline:ass?`1.5px solid ${C.red}`:"none"}}
                onMouseEnter={e=>{e.currentTarget.style.boxShadow=C.shadowMd;}} onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";}}>
                <div style={{fontSize:10.5,fontWeight:700,color:col,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.25}}>{ass?"⚠ ":""}{iconaIv(it)}{it.lavoro.titolo}</div>
                <div style={{fontSize:9.5,color:C.textMuted,lineHeight:1.2}}>{it.ora_inizio}–{it.ora_fine}</div>
              </div>
            );})}
            <div onClick={e=>{e.stopPropagation();apriPicker(op.id,ds);}} title="Aggiungi un altro intervento"
              style={{alignSelf:"center",fontSize:12,lineHeight:1,color:C.borderLight,padding:"1px 8px",borderRadius:6,cursor:"pointer"}}
              onMouseEnter={e=>{e.currentTarget.style.color=C.accent;}} onMouseLeave={e=>{e.currentTarget.style.color=C.borderLight;}}>+</div>
          </>)
        ) : (
          <div style={{width:"100%",height:compact?28:38,borderRadius:9,border:`1.5px dashed ${C.borderLight}`,display:"flex",alignItems:"center",justifyContent:"center",color:C.borderLight,fontSize:compact?13:16,fontWeight:300}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accent;e.currentTarget.style.color=C.accent;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=C.borderLight;e.currentTarget.style.color=C.borderLight;}}>+</div>
        )}
      </div>
    );
  }
  function CellaSub({sub, ds, compact}){
    const lavs = lavoriSubGiorno(sub.nome, ds);
    const lav = lavs[0];
    const isToday = ds===today;
    const col = lav ? STATI_COLOR[lav.stato] : null;
    return(
      <div onClick={()=>lav?setDettaglioSub({nome:sub.nome,dataStr:ds,lavoro:lav,costo:subDelGiorno(lav,ds).find(x=>x.nome===sub.nome)?.costo_giorno??""}):setPickerSub({nome:sub.nome,dataStr:ds,costo:sub.costo_giorno??"",fino:ds,soloFeriali:true})}
        style={{background:isToday?C.accentGlow+"66":"transparent",minHeight:compact?46:58,padding:compact?"4px 3px":"7px 6px",...cellaBase}}>
        {lav ? (
          <div style={{width:"100%",background:col+"18",borderLeft:`3px solid ${col}`,borderRadius:9,padding:compact?"4px 5px":"7px 8px"}}>
            <div style={{fontSize:compact?9.5:11,fontWeight:700,color:col,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.3}}>{lav.titolo}</div>
            {!compact && lavs.length>1 && <div style={{fontSize:9.5,color:C.textMuted,marginTop:1}}>+{lavs.length-1}</div>}
          </div>
        ) : (
          <div style={{width:"100%",height:compact?28:38,borderRadius:9,border:`1.5px dashed ${C.borderLight}`,display:"flex",alignItems:"center",justifyContent:"center",color:C.borderLight,fontSize:compact?13:16,fontWeight:300}}>+</div>
        )}
      </div>
    );
  }
  function IntestazioneOperaio({op, compact}){
    return(
      <div onClick={()=>setSchedaOp(op.id)} title="Scheda operaio" style={{display:"flex",alignItems:"center",gap:compact?8:10,padding:compact?"8px 10px":"12px 14px",borderLeft:`3px solid ${op.colore}`,cursor:"pointer",opacity:op.disponibile===false?0.55:1}}>
        <div style={{width:compact?24:32,height:compact?24:32,borderRadius:"50%",background:op.colore+"22",border:`1.5px solid ${op.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:compact?10.5:12.5,fontWeight:800,color:op.colore,flexShrink:0}}>{op.nome[0]}</div>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:compact?11.5:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{op.nome}</div>
          {!compact && (op.disponibile===false
            ? <div style={{fontSize:10.5,color:C.orange,fontWeight:700}}>non disponibile</div>
            : op.specialita&&<div style={{fontSize:10.5,color:C.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{op.specialita}</div>)}
        </div>
      </div>
    );
  }
  function IntestazioneSub({sub, compact}){
    return(
      <div onClick={()=>setSchedaSub({nome:sub.nome,costo:sub.costo_giorno??""})} title="Modifica o elimina" style={{display:"flex",alignItems:"center",gap:compact?8:10,padding:compact?"8px 10px":"12px 14px",borderLeft:`3px dashed ${C.textMuted}`,cursor:"pointer"}}>
        <div style={{width:compact?24:32,height:compact?24:32,borderRadius:"50%",background:C.bg,border:`1.5px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:compact?11:13,flexShrink:0}}>🤝</div>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:compact?11.5:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub.nome}</div>
          {!compact && <div style={{fontSize:10.5,color:C.textMuted}}>{sub.costo_giorno?`${euro(sub.costo_giorno)}/giorno`:"subappalto"}</div>}
        </div>
      </div>
    );
  }
  const btnNuovoSub = (
    <div style={{display:"flex",flexWrap:"wrap",gap:8,alignSelf:isMob?"center":"flex-start",justifyContent:"center"}}>
      <button onClick={()=>setNuovoSub({nome:"",costo:""})} style={{background:"none",border:`1.5px dashed ${C.border}`,borderRadius:11,padding:"8px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:C.textSub}}>+ Subappaltatore</button>
      {subappaltatori.length>0 && <button onClick={()=>setGestisciSub(true)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:11,padding:"8px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:C.textSub}}>Gestisci / elimina</button>}
    </div>
  );

  // ══ GIORNO OPERAI — centrata e minimal (telefono e computer), barretta 7–18 con i posti liberi ══
  // ══ VALORE PROGRAMMATO — usato da Giorno, Settimana e Mese ══
  // Ogni cantiere vale (importo ÷ giorni pianificati) al giorno; quel valore si divide tra chi ci lavora
  // quel giorno in proporzione alle ore (subappaltatore = 8 ore). La supervisione non conta.
  const minuti = (t)=>{ const [h,m]=(t||"00:00").split(":").map(Number); return h*60+m; };
  function calcolaValore(giorniArr){
    const zeri = ()=>giorniArr.map(()=>0);
    const valOp = {}, valSub = {}, valGiorno = zeri();
    lavoriAttivi.forEach(l=>{
      const imp = parseFloat(l.preventivo)||0; if(!imp) return;
      const vg = imp / (giorniPianificati(l).length || 1);
      giorniArr.forEach((ds,i)=>{
        const parti = [];
        const ivs = (l.giorni_dettaglio||{})[ds];
        if(ivs && ivs.length){
          ivs.forEach(iv=>{
            (iv.operai_orari||[]).forEach(o=>{ if(o.ruolo==="supervisione") return; parti.push({op:o.operaio_id, ore:Math.max(0.5,(minuti(o.ora_fine)-minuti(o.ora_inizio))/60)}); });
            if(iv.subappalto) parti.push({sub:iv.subappalto.nome, ore:8});
          });
        } else {
          (l.operai_ids||[]).forEach(id=>{ if(giorniOperaioSuLavoro(l,id).includes(ds)) parti.push({op:id, ore:8}); });
        }
        if(!parti.length) return;
        const oreTot = parti.reduce((t,x)=>t+x.ore,0);
        valGiorno[i] += vg;
        parti.forEach(x=>{
          const quota = vg*x.ore/oreTot;
          if(x.op!=null) (valOp[x.op] = valOp[x.op]||zeri())[i] += quota;
          else (valSub[x.sub] = valSub[x.sub]||zeri())[i] += quota;
        });
      });
    });
    return {valOp, valSub, valGiorno, op:(id)=>valOp[id]||zeri(), sub:(nome)=>valSub[nome]||zeri()};
  }
  const obiettivo = Math.max(0, Number(obiettivoGiorno)||0);
  const eur0 = (n)=>Math.round(n).toLocaleString("it-IT")+" €";
  const eurK = (n)=>n>=1000 ? (Math.round(n/100)/10).toLocaleString("it-IT")+"k" : String(Math.round(n));
  const sommaArr = (a)=>a.reduce((t,x)=>t+x,0);
  // Giorni del periodo visibile (giorno, settimana o mese)
  function giorniPeriodo(){
    if(vista==="giorno") return [toStr(curDate)];
    if(vista==="settimana"){ const s=new Date(curDate); s.setDate(s.getDate()-((s.getDay()+6)%7)); return Array.from({length:7},(_,i)=>{ const d=new Date(s); d.setDate(d.getDate()+i); return toStr(d); }); }
    const y=curDate.getFullYear(), m=curDate.getMonth();
    return Array.from({length:getDaysInMonth(y,m)},(_,i)=>`${y}-${String(m+1).padStart(2,"0")}-${String(i+1).padStart(2,"0")}`);
  }
  // Riquadri in alto: obiettivo, programmato nel periodo, quanto manca
  function riquadriObiettivo(){
    const gg = giorniPeriodo();
    const {valGiorno} = calcolaValore(gg);
    const lav = gg.map(giornoLavorativo);
    const obiettivoPer = obiettivo*lav.filter(Boolean).length;
    const valPer = gg.reduce((t,ds,i)=>t+(lav[i]?valGiorno[i]:0),0);
    const manca = Math.max(0, obiettivoPer-valPer);
    const sotto = gg.filter((ds,i)=>lav[i] && ds>=today && valGiorno[i]<obiettivo).length;
    const nomePer = vista==="giorno" ? (gg[0]===today?"oggi":"questo giorno") : vista==="settimana" ? "questa settimana" : "questo mese";
    const ARANCIO_TXT = "#B4561C", ARANCIO_BORDO = "#EFB48A";
    const riposo = vista==="giorno" && !lav[0];
    return(
      <div style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"stretch"}}>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:14,padding:"12px 16px",display:"flex",flexDirection:"column",gap:6,flex:isMob?"1 1 100%":"0 0 auto"}}>
          <label style={{fontSize:11.5,color:C.textMuted,fontWeight:600}} htmlFor="sq-obiettivo">Obiettivo al giorno</label>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <input id="sq-obiettivo" type="number" inputMode="numeric" min="0" step="50"
              value={obiettivoDraft ?? obiettivoGiorno}
              onChange={e=>setObiettivoDraft(e.target.value)}
              onBlur={()=>{ if(obiettivoDraft!==null){ const v=Math.max(0,Number(obiettivoDraft)||0); setObiettivoDraft(null); if(v!==Number(obiettivoGiorno)) salvaObiettivoGiorno(v); } }}
              onKeyDown={e=>{ if(e.key==="Enter") e.currentTarget.blur(); }}
              style={{width:120,height:38,border:`1px solid ${C.border}`,borderRadius:9,padding:"0 10px",fontFamily:"inherit",fontSize:16,fontWeight:700,color:C.text,background:C.bg}}/>
            <span style={{fontSize:15,fontWeight:700}}>€</span>
          </div>
          <span style={{fontSize:11,color:C.textMuted}}>Vale per tutta l'azienda, su tutti i dispositivi</span>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:14,padding:"12px 16px",display:"flex",flexDirection:"column",gap:4,flex:"1 1 240px"}}>
          <span style={{fontSize:11.5,color:C.textMuted,fontWeight:600}}>Programmato {nomePer}</span>
          <span style={{fontSize:24,fontWeight:700,fontVariantNumeric:"tabular-nums",color:riposo||valPer>=obiettivoPer?C.text:ARANCIO_TXT}}>{eur0(riposo?valGiorno[0]:valPer)} {!riposo && <span style={{fontSize:13,fontWeight:500,color:C.textMuted}}>su {eur0(obiettivoPer)}</span>}</span>
          {!riposo && <div style={{height:6,borderRadius:3,background:C.borderLight,overflow:"hidden"}}><div style={{height:"100%",width:`${obiettivoPer?Math.min(100,valPer/obiettivoPer*100):0}%`,background:valPer>=obiettivoPer?C.text:C.brand}}/></div>}
          {riposo && <span style={{fontSize:11.5,color:C.textMuted}}>Giorno di riposo: non conta per l'obiettivo</span>}
        </div>
        {!riposo && (
          <div style={{background:manca?C.brandSoft:C.card,border:`1px solid ${manca?ARANCIO_BORDO:C.border}`,borderRadius:14,padding:"12px 16px",display:"flex",flexDirection:"column",gap:4,flex:"1 1 200px"}}>
            <span style={{fontSize:11.5,color:manca?ARANCIO_TXT:C.textMuted,fontWeight:600}}>{manca?"Da aggiungere per l'obiettivo":"Obiettivo raggiunto"}</span>
            <span style={{fontSize:24,fontWeight:700,fontVariantNumeric:"tabular-nums",color:manca?ARANCIO_TXT:C.text}}>{manca?eur0(manca):"✓"}</span>
            <span style={{fontSize:11.5,color:manca?ARANCIO_TXT:C.textSub}}>{manca?(vista==="giorno"?(gg[0]<today?"giorno già passato":"assegna altri lavori per arrivarci"):(sotto?`${sotto} giorn${sotto>1?"i":"o"} da oggi sotto l'obiettivo`:"solo nei giorni già passati")):`+${eur0(valPer-obiettivoPer)} sopra`}</span>
          </div>
        )}
      </div>
    );
  }

  function vistaOperaiGiornoTelefono(){
    const COL_NOME = isMob ? "minmax(0,118px)" : "minmax(0,220px)";
    const ds = toStr(curDate);
    const NERO = C.text, ARANCIO_TXT = "#B4561C", ARANCIO_SOFT = C.brandSoft, ARANCIO_BORDO = "#EFB48A";
    const RIGHE = `repeating-linear-gradient(135deg, #EDEDEA 0 4px, #F8F8F6 4px 8px)`;
    const min = (s)=>{ const [h,m]=(s||"00:00").split(":").map(Number); return h*60+m; };
    const hhmm = (n)=>`${Math.floor(n/60)}:${String(n%60).padStart(2,"0")}`;
    const [S_INI, S_FINE] = scalaOraria();
    const pos = (a,b)=>({left:`${Math.max(0,(a-S_INI)/(S_FINE-S_INI)*100)}%`, width:`${Math.max(2,(Math.min(b,S_FINE)-Math.max(a,S_INI))/(S_FINE-S_INI)*100)}%`});

    function statoOp(op, d){
      const og = orarioDelGiorno(d), G_INI = min(og.ora_inizio), G_FINE = min(og.ora_fine);
      const ivs = interventiVisibili(op.id, d);
      const ass = assenzaDi(op.id, d);
      const lavoro = ivs.filter(x=>x.ruolo!=="supervisione").map(x=>[min(x.ora_inizio),min(x.ora_fine)]).sort((a,b)=>a[0]-b[0]);
      const liberi = []; let cur = G_INI;
      lavoro.forEach(([a,b])=>{ if(a>cur) liberi.push([cur,Math.min(a,G_FINE)]); cur=Math.max(cur,b); });
      if(cur<G_FINE) liberi.push([cur,G_FINE]);
      const buoni = liberi.filter(([a,b])=>b-a>=120);
      let tipo;
      if(ass && ivs.length===0) tipo="assente";
      else if(op.disponibile===false && ivs.length===0) tipo="nondisp";
      else if(lavoro.length===0 && ivs.length===0) tipo="libero";
      else if(buoni.length>0) tipo="parziale";
      else tipo="pieno";
      return {tipo, ivs, ass, liberi:buoni};
    }

    // Striscia settimana con i posti liberi di ogni giorno
    const lun = new Date(curDate); lun.setDate(curDate.getDate()-((curDate.getDay()+6)%7));
    const settimana = Array.from({length:7},(_,i)=>{ const d=new Date(lun); d.setDate(d.getDate()+i); return d; });
    const contaLiberi = (d)=>operaiCalendario.filter(o=>statoOp(o,d).tipo==="libero").length + subappaltatori.filter(s=>lavoriSubGiorno(s.nome,d).length===0).length;
    const valSettimana = calcolaValore(settimana.map(toStr)).valGiorno;
    const valOggi = calcolaValore([ds]);
    const valore = (v)=>v>0 ? <span style={{fontSize:12,fontWeight:700,color:C.text,fontVariantNumeric:"tabular-nums"}}>{eur0(v)}</span> : null;

    const righeOp = operaiCalendario.map(op=>({op, s:statoOp(op, ds)}));
    const righeSub = subappaltatori.map(sb=>({sb, lavs:lavoriSubGiorno(sb.nome, ds)}));
    const nLiberi = righeOp.filter(r=>r.s.tipo==="libero").length + righeSub.filter(r=>r.lavs.length===0).length;
    const nParziali = righeOp.filter(r=>r.s.tipo==="parziale").length;
    const nOccupati = righeOp.filter(r=>r.s.tipo==="pieno").length + righeSub.filter(r=>r.lavs.length>0).length;

    const ROW = {width:"100%",display:"grid",gridTemplateColumns:`${COL_NOME} 1fr`,gap:12,alignItems:"center",padding:"12px 14px",background:"transparent",border:"none",fontFamily:"inherit",color:C.text,cursor:"pointer",textAlign:"left"};
    const pallino = (c)=><span style={{width:6,height:6,borderRadius:3,flexShrink:0,background:c}}/>;
    const nome = (t, onClick)=><span onClick={onClick} style={{fontSize:14,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t}</span>;
    const sotto = (c, t)=><span style={{fontSize:11,color:C.textMuted,display:"flex",alignItems:"center",gap:5,minWidth:0}}>{c&&pallino(c)}<span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t}</span></span>;
    const barraLibera = (a,b,label,onClick)=>(
      <div key={"l"+a} onClick={e=>{e.stopPropagation();onClick();}} style={{position:"absolute",top:0,bottom:0,...pos(a,b),background:ARANCIO_SOFT,border:`1.5px dashed ${ARANCIO_BORDO}`,borderRadius:7,boxSizing:"border-box",display:"flex",alignItems:"center",justifyContent:"center",color:ARANCIO_TXT,fontSize:10.5,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden"}}>{label}</div>
    );

    function rigaOp({op, s}, i){
      const primo = s.ivs[0];
      const col = primo ? STATI_COLOR[primo.lavoro.stato] : null;
      const click = ()=> primo ? apriGiornata(op.id,ds,primo) : s.ass ? setDettaglioAss({operaioId:op.id,dataStr:ds,assenza:s.ass}) : op.disponibile!==false && apriPicker(op.id,ds);
      let testo;
      if(primo) testo = `${primo.ora_inizio}–${primo.ora_fine} · ${primo.lavoro.titolo}${primo.ruolo==="supervisione"?" (superv.)":""}${s.ivs.length>1?` +${s.ivs.length-1}`:""}`;
      else if(s.ass) testo = `${labelAssenza(s.ass.tipo)} fino al ${fmt(s.ass.data_fine)}`;
      else if(s.tipo==="nondisp") testo = "Non disponibile";
      else testo = op.specialita || "Nessun intervento";
      return(
        <div key={op.id} onClick={click} role="button" style={{...ROW,borderTop:i?`1px solid ${C.borderLight}`:"none",opacity:op.disponibile===false?0.55:1}}>
          <div style={{display:"flex",flexDirection:"column",gap:3,minWidth:0}}>
            {nome(op.nome, e=>{e.stopPropagation();setSchedaOp(op.id);})}
            {sotto(col || (s.ass?C.red:C.borderLight), testo)}
            {valore(valOggi.op(op.id)[0])}
          </div>
          <div style={{position:"relative",height:26,borderRadius:7,overflow:"hidden",background:(s.tipo==="assente"||s.tipo==="nondisp")?RIGHE:"#EFEEEA",outline:s.ass&&s.ivs.length?`1.5px solid ${C.red}`:"none"}}>
            {s.tipo==="libero"
              ? barraLibera(S_INI,S_FINE,"+ Libero",()=>apriPicker(op.id,ds))
              : <>
                  {s.ivs.map((x,k)=>{ const sup=x.ruolo==="supervisione"; return (
                    <div key={k} onClick={e=>{e.stopPropagation();apriGiornata(op.id,ds,x);}}
                      style={{position:"absolute",top:0,bottom:0,...pos(min(x.ora_inizio),min(x.ora_fine)),borderRadius:7,boxSizing:"border-box",
                        background:sup?`repeating-linear-gradient(90deg, ${NERO} 0 3px, transparent 3px 6px)`:NERO,borderLeft:k>0?"2px solid #fff":"none"}}/>
                  );})}
                  {s.tipo==="parziale" && s.liberi.map(([a,b])=>barraLibera(a,b,b-a>=180?`+ dalle ${hhmm(a)}`:"+",()=>apriPicker(op.id,ds)))}
                </>}
          </div>
        </div>
      );
    }
    function rigaSub({sb, lavs}, i){
      const lav = lavs[0];
      const apri = ()=> lav ? setDettaglioSub({nome:sb.nome,dataStr:ds,lavoro:lav,costo:subDelGiorno(lav,ds).find(x=>x.nome===sb.nome)?.costo_giorno??""})
                            : setPickerSub({nome:sb.nome,dataStr:ds,costo:sb.costo_giorno??"",fino:ds,soloFeriali:true});
      const testo = lav ? `${lav.titolo}${lavs.length>1?` +${lavs.length-1}`:""}` : (sb.costo_giorno?`${euro(sb.costo_giorno)}/giorno`:"Subappalto");
      return(
        <div key={sb.nome} onClick={apri} role="button" style={{...ROW,borderTop:i?`1px solid ${C.borderLight}`:"none"}}>
          <div style={{display:"flex",flexDirection:"column",gap:3,minWidth:0}}>
            {nome(sb.nome, e=>{e.stopPropagation();setSchedaSub({nome:sb.nome,costo:sb.costo_giorno??""});})}
            {sotto(lav?STATI_COLOR[lav.stato]:C.borderLight, testo)}
            {valore(valOggi.sub(sb.nome)[0])}
          </div>
          <div style={{position:"relative",height:26,borderRadius:7,overflow:"hidden",background:lav?NERO:"#EFEEEA"}}>
            {!lav && barraLibera(S_INI,S_FINE,"+ Libero",apri)}
          </div>
        </div>
      );
    }
    const gruppo = (titolo, info, righe)=>(
      <section style={{display:"flex",flexDirection:"column",gap:8}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"0 4px"}}>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:0.8,color:C.textSub}}>{titolo}</span>
          <span style={{fontSize:12,fontWeight:600,color:info.c}}>{info.t}</span>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>{righe}</div>
      </section>
    );
    const infoDip = (()=>{ const l=righeOp.filter(r=>r.s.tipo==="libero").length; return l?{t:`${l} liber${l>1?"i":"o"}`,c:ARANCIO_TXT}:nParziali?{t:`${nParziali} mezz${nParziali>1?"e":"a"} giornat${nParziali>1?"e":"a"}`,c:ARANCIO_TXT}:{t:"pieno",c:C.textMuted}; })();
    const lSub = righeSub.filter(r=>r.lavs.length===0).length;
    const nomiG = ["L","M","M","G","V","S","D"];

    return(
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {!isMob && <div style={{textAlign:"center",fontSize:15,fontWeight:700,color:C.text,textTransform:"capitalize"}}>{curDate.toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>}
        {/* Settimana: tocca un giorno, sotto i posti liberi */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:6}}>
          {settimana.map((d,i)=>{
            const dS = toStr(d), sel = dS===ds, oggi = dS===today, conta = giornoLavorativo(dS) && dS>=today;
            const n = conta ? contaLiberi(dS) : null;
            const v = valSettimana[i], okV = v>=obiettivo;
            return(
              <button key={dS} onClick={()=>setCurDate(new Date(d))} style={{height:76,border:"none",borderRadius:11,cursor:"pointer",fontFamily:"inherit",background:sel?NERO:"transparent",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1}}>
                <span style={{fontSize:11,fontWeight:600,color:sel?"#fff":oggi?ARANCIO_TXT:C.textMuted}}>{nomiG[i]}</span>
                <span style={{fontSize:17,fontWeight:700,color:sel?"#fff":(i>=5?C.borderLight:C.text)}}>{d.getDate()}</span>
                <span style={{fontSize:10,fontWeight:600,height:12,color:sel?"#F3C9AA":ARANCIO_TXT}}>{n?`${n} lib.`:""}</span>
                <span style={{fontSize:10,fontWeight:700,height:12,fontVariantNumeric:"tabular-nums",color:sel?"#fff":(!giornoLavorativo(dS)?C.textMuted:okV?C.text:ARANCIO_TXT)}}>{v>0||giornoLavorativo(dS)?`${eurK(v)}€`:""}</span>
              </button>
            );
          })}
        </div>
        <div style={{textAlign:"center",fontSize:13,color:C.textSub}}>
          <span style={{color:nLiberi?ARANCIO_TXT:C.textMuted,fontWeight:600}}>{nLiberi} liber{nLiberi===1?"o":"i"}</span>
          {nParziali>0 && ` · ${nParziali} mezz${nParziali>1?"e":"a"} giornat${nParziali>1?"e":"a"}`} · {nOccupati} occupat{nOccupati===1?"o":"i"}
        </div>
        {/* Scala oraria */}
        <div style={{display:"grid",gridTemplateColumns:`${COL_NOME} 1fr`,gap:12,padding:"0 14px",marginBottom:-8}}>
          <span/>
          <div style={{position:"relative",height:12,fontSize:10,color:C.textMuted,fontVariantNumeric:"tabular-nums"}}>
            {Array.from({length:Math.floor((S_FINE-S_INI)/120)+1},(_,k)=>S_INI/60+k*2).filter(h=>h*60<S_FINE).map(h=><span key={h} style={{position:"absolute",left:`${(h*60-S_INI)/(S_FINE-S_INI)*100}%`,transform:h*60===S_INI?"none":"translateX(-50%)"}}>{h}</span>)}
          </div>
        </div>
        {righeOp.length>0
          ? gruppo("DIPENDENTI", infoDip, righeOp.map(rigaOp))
          : <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:32,textAlign:"center",color:C.textMuted}}>Nessun operaio disponibile.</div>}
        {righeSub.length>0 && gruppo("SUBAPPALTATORI", lSub?{t:`${lSub} liber${lSub>1?"i":"o"}`,c:ARANCIO_TXT}:{t:"pieno",c:C.textMuted}, righeSub.map(rigaSub))}
        <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",gap:14,fontSize:11,color:C.textSub}}>
          <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:18,height:10,borderRadius:3,background:NERO}}/>Occupato</span>
          <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:18,height:10,borderRadius:3,background:ARANCIO_SOFT,border:`1.5px dashed ${ARANCIO_BORDO}`,boxSizing:"border-box"}}/>Libero · tocca per assegnare</span>
        </div>
        <div style={{display:"flex",justifyContent:"center"}}>{btnNuovoSub}</div>
      </div>
    );
  }

  function VistaOperaiGiorno(){ return vistaOperaiGiornoTelefono(); }

  // ══ SETTIMANA OPERAI · COMPATTA — una riga per operaio, posti liberi in arancio ══
  // Chiamata come funzione (non <Componente/>) così la ricerca non perde il cursore a ogni lettera.
  function vistaOperaiSettimanaCompatta(){
    const NERO = C.text, ARANCIO_TXT = "#B4561C", ARANCIO_SOFT = C.brandSoft, ARANCIO_BORDO = "#EFB48A";
    const GRIGIO_OGGI = "#F3F3F0";
    const RIGHE = `repeating-linear-gradient(135deg, #EDEDEA 0 4px, #F8F8F6 4px 8px)`;
    const min = (s)=>{ const [h,m]=(s||"00:00").split(":").map(Number); return h*60+m; };
    const hhmm = (n)=>`${String(Math.floor(n/60)).padStart(2,"0")}:${String(n%60).padStart(2,"0")}`;
    const [S_INI, S_FINE] = scalaOraria();      // scala della barretta (dal pannello Orari)
    const pos = (a,b)=>({left:`${Math.max(0,(a-S_INI)/(S_FINE-S_INI)*100)}%`, width:`${Math.max(2,(Math.min(b,S_FINE)-Math.max(a,S_INI))/(S_FINE-S_INI)*100)}%`});

    const startOfWeek = new Date(curDate);
    startOfWeek.setDate(curDate.getDate()-((curDate.getDay()+6)%7));
    const giorni = Array.from({length:7},(_,i)=>{ const d=new Date(startOfWeek); d.setDate(d.getDate()+i); return toStr(d); });
    const nomiGiorno = ["LUN","MAR","MER","GIO","VEN","SAB","DOM"];
    const contaGiorno = (ds)=>giornoLavorativo(ds) && ds>=today; // giorni che contano per i posti liberi

    // Stato di un operaio in un giorno: libero / parziale / pieno / assente / nondisp
    function statoOp(op, ds){
      const og = orarioDelGiorno(ds), G_INI = min(og.ora_inizio), G_FINE = min(og.ora_fine);
      const ivs = interventiOperaioGiorno(lavoriAttivi, op.id, ds);
      const lavoro = ivs.filter(x=>x.ruolo!=="supervisione").map(x=>[min(x.ora_inizio),min(x.ora_fine)]).sort((a,b)=>a[0]-b[0]);
      const ass = assenzaDi(op.id, ds);
      const liberi = []; let cur = G_INI;
      lavoro.forEach(([a,b])=>{ if(a>cur) liberi.push([cur,Math.min(a,G_FINE)]); cur = Math.max(cur,b); });
      if(cur<G_FINE) liberi.push([cur,G_FINE]);
      const buoni = liberi.filter(([a,b])=>b-a>=120);
      let tipo;
      if(ass && ivs.length===0) tipo = "assente";
      else if(op.disponibile===false && ivs.length===0) tipo = "nondisp";
      else if(lavoro.length===0) tipo = "libero";
      else if(buoni.length>0) tipo = "parziale";
      else tipo = "pieno";
      return {tipo, ivs, ass, liberi:buoni};
    }
    function statoSub(sb, ds){
      const lavs = lavoriSubGiorno(sb.nome, ds);
      return {tipo: lavs.length ? "pieno" : "libero", lavs};
    }

    // Righe
    const q = (filtroSettimana||"").trim().toLowerCase();
    const corrisponde = (nome, titoli)=>!q || nome.toLowerCase().includes(q) || titoli.some(t=>(t||"").toLowerCase().includes(q));
    const righeOp = operai.filter(o=>o.disponibile!==false || mostraNonDisponibili).map(op=>{
      const stati = giorni.map(ds=>statoOp(op, ds));
      const liberiSett = stati.reduce((t,s,i)=>t+(contaGiorno(giorni[i])?(s.tipo==="libero"?1:s.tipo==="parziale"?0.5:0):0),0);
      const titoli = stati.flatMap(s=>s.ivs.map(x=>x.lavoro.titolo).concat(s.ivs.map(x=>x.lavoro.cliente)));
      return {key:"op"+op.id, op, stati, liberiSett, nome:op.nome, titoli};
    });
    const righeSub = subappaltatori.map(sb=>{
      const stati = giorni.map(ds=>statoSub(sb, ds));
      const liberiSett = stati.reduce((t,s,i)=>t+(contaGiorno(giorni[i])&&s.tipo==="libero"?1:0),0);
      const titoli = stati.flatMap(s=>s.lavs.map(l=>l.titolo).concat(s.lavs.map(l=>l.cliente)));
      return {key:"sb"+sb.nome, sb, stati, liberiSett, nome:sb.nome, titoli};
    });
    const filtra = (righe)=>righe
      .filter(r=>corrisponde(r.nome, r.titoli))
      .filter(r=>!soloLiberiSett || r.liberiSett>0)
      .sort((a,b)=>ordineSett==="liberi" ? (b.liberiSett-a.liberiSett || a.nome.localeCompare(b.nome)) : a.nome.localeCompare(b.nome));
    const gruppi = [
      {id:"dip", nome:"DIPENDENTI", righe:filtra(righeOp), tutte:righeOp},
      {id:"sub", nome:"SUBAPPALTATORI", righe:filtra(righeSub), tutte:righeSub},
    ];
    const nonDisp = operai.filter(o=>o.disponibile===false);

    // Conteggi per giorno
    const contiGiorno = (righe)=>giorni.map((ds,i)=>({
      liberi: righe.filter(r=>r.stati[i].tipo==="libero").length,
      parziali: righe.filter(r=>r.stati[i].tipo==="parziale").length,
    }));
    const totali = contiGiorno([...righeOp, ...righeSub]);
    const valore = calcolaValore(giorni);
    const valGiorno = valore.valGiorno;
    const valRiga = (r)=>r.op ? valore.op(r.op.id) : valore.sub(r.sb.nome);


    const COLS = isMob ? `minmax(0,${ORARI_LAVORO.sabato.attivo?84:96}px) repeat(${ORARI_LAVORO.sabato.attivo?6:5},minmax(0,1fr))` : "minmax(150px,220px) repeat(5,minmax(70px,1fr)) repeat(2,minmax(48px,0.55fr)) 104px";
    const giorniVis = isMob ? giorni.slice(0, ORARI_LAVORO.sabato.attivo?6:5) : giorni; // sul telefono solo lun–ven
    const colNome = {position:"sticky",left:0,zIndex:1,background:C.card};

    // Cella di un operaio: barretta 07–18 con un blocco nero per ogni intervento
    function cellaOp(r, i){
      const ds = giorni[i], s = r.stati[i], op = r.op;
      const smorza = !contaGiorno(ds);
      const base = {position:"relative",height:24,borderRadius:7,overflow:"hidden",boxSizing:"border-box",cursor:"pointer",opacity:smorza?(ds<today?0.4:0.6):1};
      if(s.tipo==="assente") return <div title={`${labelAssenza(s.ass.tipo)} ${fmt(s.ass.data_inizio)} → ${fmt(s.ass.data_fine)}`} onClick={()=>setDettaglioAss({operaioId:op.id,dataStr:ds,assenza:s.ass})} style={{...base,background:RIGHE}}/>;
      if(s.tipo==="nondisp") return <div title="Non disponibile" style={{...base,background:RIGHE,cursor:"default"}}/>;
      if(s.tipo==="libero") return (
        <div title="Libero — clicca per assegnare" onClick={()=>apriPicker(op.id,ds)}
          style={{...base,background:smorza?"transparent":ARANCIO_SOFT,border:`1.5px dashed ${smorza?C.border:ARANCIO_BORDO}`,display:"flex",alignItems:"center",justifyContent:"center",color:smorza?C.textMuted:ARANCIO_TXT,fontSize:14,fontWeight:700,lineHeight:1}}>+</div>
      );
      const tip = s.ivs.map(x=>`${x.ora_inizio}–${x.ora_fine} ${x.lavoro.titolo}${x.ruolo==="supervisione"?" (supervisione)":""}`).join("\n")
        + (s.liberi.length?`\nLibero ${s.liberi.map(([a,b])=>`${hhmm(a)}–${hhmm(b)}`).join(", ")}`:"")
        + (valRiga(r)[i]>0?`\nValore programmato: ${eur0(valRiga(r)[i])}`:"");
      return (
        <div title={tip} onClick={()=>apriPicker(op.id,ds)}
          style={{...base,background:s.tipo==="parziale"&&!smorza?ARANCIO_SOFT:"#EFEEEA",outline:s.ass?`1.5px solid ${C.red}`:"none"}}>
          {s.ivs.map((x,k)=>{
            const sup = x.ruolo==="supervisione";
            return <div key={k} onClick={e=>{e.stopPropagation();apriGiornata(op.id,ds,x);}}
              style={{position:"absolute",top:0,bottom:0,...pos(min(x.ora_inizio),min(x.ora_fine)),
                background:sup?`repeating-linear-gradient(90deg, ${NERO} 0 3px, transparent 3px 6px)`:NERO,
                borderLeft:k>0?"2px solid #fff":"none",boxSizing:"border-box"}}/>;
          })}
          {s.ivs.length>1 && <span style={{position:"absolute",right:4,top:"50%",transform:"translateY(-50%)",fontSize:10,fontWeight:700,color:"#fff",background:NERO,borderRadius:6,padding:"0 4px",lineHeight:"14px",pointerEvents:"none"}}>{s.ivs.length}</span>}
        </div>
      );
    }
    function cellaSub(r, i){
      const ds = giorni[i], s = r.stati[i], sb = r.sb;
      const smorza = !contaGiorno(ds);
      const base = {height:24,borderRadius:7,boxSizing:"border-box",cursor:"pointer",opacity:smorza?(ds<today?0.4:0.6):1};
      if(s.tipo==="libero") return (
        <div title="Libero — clicca per assegnare" onClick={()=>setPickerSub({nome:sb.nome,dataStr:ds,costo:sb.costo_giorno??"",fino:ds,soloFeriali:true})}
          style={{...base,background:smorza?"transparent":ARANCIO_SOFT,border:`1.5px dashed ${smorza?C.border:ARANCIO_BORDO}`,display:"flex",alignItems:"center",justifyContent:"center",color:smorza?C.textMuted:ARANCIO_TXT,fontSize:14,fontWeight:700,lineHeight:1}}>+</div>
      );
      const lav = s.lavs[0];
      return <div title={s.lavs.map(l=>l.titolo).join("\n")} onClick={()=>setDettaglioSub({nome:sb.nome,dataStr:ds,lavoro:lav,costo:subDelGiorno(lav,ds).find(x=>x.nome===sb.nome)?.costo_giorno??""})}
        style={{...base,background:NERO}}/>;
    }

    const pill = (attivo)=>({height:34,padding:"0 14px",borderRadius:17,fontFamily:"inherit",fontSize:12.5,fontWeight:attivo?700:500,cursor:"pointer",
      border:`1px solid ${attivo?ARANCIO_BORDO:C.border}`,background:attivo?ARANCIO_SOFT:C.card,color:attivo?ARANCIO_TXT:C.textSub});
    const fmtGg = (n)=>n ? `${String(n).replace(".",",")} gg` : "—";

    return(
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {/* Barra: ricerca, filtro, ordine */}
        <div style={{display:"flex",flexWrap:"wrap",gap:8,alignItems:"center"}}>
          <input type="search" value={filtroSettimana} onChange={e=>setFiltroSettimana(e.target.value)} placeholder="Cerca operaio o cantiere"
            style={{height:34,flex:"1 1 200px",maxWidth:300,border:`1px solid ${C.border}`,borderRadius:10,padding:"0 12px",fontFamily:"inherit",fontSize:13,background:C.card,color:C.text,outline:"none"}}/>
          <button onClick={()=>setSoloLiberiSett(v=>!v)} style={pill(soloLiberiSett)}>Solo con posti liberi</button>
          <select value={ordineSett} onChange={e=>setOrdineSett(e.target.value)} style={{height:34,border:`1px solid ${C.border}`,borderRadius:10,padding:"0 10px",fontFamily:"inherit",fontSize:12.5,background:C.card,color:C.text,cursor:"pointer"}}>
            <option value="nome">Ordina: A → Z</option>
            <option value="liberi">Ordina: più liberi prima</option>
          </select>
          <button onClick={()=>setMostraEuro(v=>!v)} style={pill(mostraEuro)}>€ nelle celle</button>
        </div>


        <div style={{background:C.card,boxShadow:C.shadowMd,borderRadius:18,overflowX:"auto"}}>
          <div style={{minWidth:isMob?0:760}}>
            {/* Intestazione: posti liberi per giorno */}
            <div style={{display:"grid",gridTemplateColumns:COLS,borderBottom:`1px solid ${C.border}`}}>
              <div style={{...colNome,padding:isMob?"12px 8px 12px 12px":"14px 16px",display:"flex",flexDirection:"column",justifyContent:"flex-end",gap:2}}>
                <span style={{fontSize:11,color:C.textMuted}}>Posti liberi</span>
                <span style={{fontSize:12.5,fontWeight:700,color:C.text}}>{righeOp.length+righeSub.length}{isMob?"":" persone"}</span>
                <span style={{fontSize:11,color:C.textMuted,marginTop:14}}>{isMob?"€":"Programmato"}</span>
                {!isMob && <span style={{fontSize:12.5,fontWeight:700,color:C.text}}>obiettivo {eur0(obiettivo)}</span>}
              </div>
              {giorniVis.map((ds,i)=>{
                const conta = contaGiorno(ds), isToday = ds===today, t = totali[i];
                return(
                  <div key={ds} style={{padding:isMob?"12px 2px 10px":"14px 8px 12px",background:isToday?GRIGIO_OGGI:"transparent",display:"flex",flexDirection:"column",alignItems:isMob?"center":"stretch",gap:1}}>
                    <span style={{fontSize:10.5,fontWeight:700,letterSpacing:.6,color:isToday?NERO:C.textMuted}}>{isMob?nomiGiorno[i][0]:nomiGiorno[i]} {new Date(ds+"T12:00:00").getDate()}{isToday&&!isMob?" · OGGI":""}</span>
                    <span style={{fontSize:isMob?20:(giornoLavorativo(ds)?26:18),fontWeight:700,lineHeight:1.1,color:conta?(t.liberi?ARANCIO_TXT:C.textMuted):C.borderLight,fontVariantNumeric:"tabular-nums"}}>{conta?t.liberi:"–"}</span>
                    <span style={{fontSize:10.5,color:C.textSub,whiteSpace:"nowrap"}}>{isMob?(ds===today?"oggi":(ds<today?"":t.parziali?`+${t.parziali}½`:"")):ds<today?"passato":!giornoLavorativo(ds)?"riposo":t.parziali?`+${t.parziali} mezze gg`:(t.liberi?"liberi":"pieno")}</span>
                    {(()=>{
                      const v = valGiorno[i], lav = giornoLavorativo(ds), ok = v>=obiettivo;
                      return(
                        <div style={{marginTop:10,paddingTop:8,borderTop:`1px solid ${C.borderLight}`,display:"flex",flexDirection:"column",alignItems:isMob?"center":"stretch",gap:3,width:"100%",opacity:ds<today?0.55:1}}>
                          {!lav && !v ? <span style={{fontSize:11,color:C.textMuted}}>–</span> : (<>
                            <span style={{fontSize:isMob?12:15,fontWeight:700,fontVariantNumeric:"tabular-nums",color:!lav||ok?NERO:ARANCIO_TXT,whiteSpace:"nowrap"}}>{isMob?eurK(v):eur0(v)}</span>
                            {lav && <div style={{height:5,width:"100%",borderRadius:3,background:C.borderLight,overflow:"hidden"}}><div style={{height:"100%",width:`${obiettivo?Math.min(100,v/obiettivo*100):0}%`,background:ok?NERO:C.brand}}/></div>}
                            {lav && !isMob && <span style={{fontSize:10.5,fontWeight:600,color:ok?C.textSub:ARANCIO_TXT,whiteSpace:"nowrap"}}>{ok?`✓ +${eur0(v-obiettivo)}`:`mancano ${eur0(obiettivo-v)}`}</span>}
                          </>)}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
              {!isMob && <div style={{padding:"14px 12px",display:"flex",flexDirection:"column",justifyContent:"flex-end",gap:2,fontSize:11,color:C.textMuted}}><span>Settimana</span><span>€ · liberi</span></div>}
            </div>

            {gruppi.map(g=>{
              if(g.tutte.length===0) return null;
              const cg = contiGiorno(g.righe);
              return(
                <div key={g.id}>
                  <div style={{display:"grid",gridTemplateColumns:COLS,background:C.bg,borderBottom:`1px solid ${C.borderLight}`}}>
                    <div style={{...colNome,background:C.bg,padding:isMob?"8px 8px 8px 12px":"8px 16px",fontSize:isMob?10.5:11,fontWeight:700,letterSpacing:.5,color:C.textSub,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{g.nome} <span style={{fontWeight:500,color:C.textMuted}}>· {g.righe.length}</span></div>
                    {giorniVis.map((ds,i)=>(
                      <div key={ds} style={{padding:isMob?"8px 2px":"8px 8px",fontSize:11,fontWeight:600,textAlign:isMob?"center":"left",color:cg[i].liberi?ARANCIO_TXT:C.textMuted}}>
                        {contaGiorno(ds) ? (cg[i].liberi ? (isMob?cg[i].liberi:`${cg[i].liberi} liberi`) : (isMob?"–":"pieno")) : ""}
                      </div>
                    ))}
                    {!isMob && <div/>}
                  </div>
                  {g.righe.length===0 && <div style={{padding:"12px 16px",fontSize:12.5,color:C.textMuted,borderBottom:`1px solid ${C.borderLight}`}}>Nessuno con questi filtri.</div>}
                  {g.righe.map(r=>(
                    <div key={r.key} style={{display:"grid",gridTemplateColumns:COLS,alignItems:"center",borderBottom:`1px solid ${C.borderLight}`,height:mostraEuro?50:36}}>
                      <div onClick={()=>r.op?setSchedaOp(r.op.id):setSchedaSub({nome:r.sb.nome,costo:r.sb.costo_giorno??""})} title={r.op?"Scheda operaio":"Modifica o elimina"}
                        style={{...colNome,height:"100%",display:"flex",alignItems:"center",gap:isMob?6:8,padding:isMob?"0 6px 0 12px":"0 16px",cursor:"pointer",opacity:r.op?.disponibile===false?0.55:1}}>
                        <span style={{width:8,height:8,borderRadius:4,flexShrink:0,background:r.op?(r.op.colore||NERO):C.textMuted}}/>
                        <span style={{fontSize:isMob?12.5:13,fontWeight:500,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{isMob?r.nome.split(" ")[0]+(r.nome.includes(" ")?" "+r.nome.split(" ").slice(-1)[0][0]+".":""):r.nome}</span>
                      </div>
                      {giorniVis.map((ds,i)=>(
                        <div key={ds} style={{padding:isMob?"0 2px":"0 4px",height:"100%",display:"flex",flexDirection:"column",justifyContent:"center",background:ds===today?GRIGIO_OGGI:"transparent"}}>
                          {r.op ? cellaOp(r,i) : cellaSub(r,i)}
                          {mostraEuro && valRiga(r)[i]>0 && <div style={{fontSize:10.5,fontWeight:600,color:C.textSub,fontVariantNumeric:"tabular-nums",marginTop:3,lineHeight:1,textAlign:isMob?"center":"left",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{isMob?eurK(valRiga(r)[i]):eur0(valRiga(r)[i])}</div>}
                        </div>
                      ))}
                      {!isMob && <div style={{padding:"0 12px",display:"flex",flexDirection:"column",gap:2,fontVariantNumeric:"tabular-nums"}}>
                        <span style={{fontSize:12.5,fontWeight:700,color:C.text}}>{sommaArr(valRiga(r))>0?eur0(sommaArr(valRiga(r))):"—"}</span>
                        <span style={{fontSize:11,fontWeight:600,color:r.liberiSett?ARANCIO_TXT:C.textMuted}}>{r.liberiSett?`${fmtGg(r.liberiSett)} liberi`:"pieno"}</span>
                      </div>}
                    </div>
                  ))}
                  {g.id==="dip" && !mostraNonDisponibili && nonDisp.length>0 && (
                    <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:8,padding:"10px 16px",borderBottom:`1px solid ${C.borderLight}`,fontSize:12.5,color:C.textSub}}>
                      <span style={{width:22,height:12,borderRadius:4,background:RIGHE,flexShrink:0}}/>
                      {nonDisp.length} non disponibil{nonDisp.length>1?"i":"e"}: {nonDisp.map(o=>o.nome).join(" · ")}
                      <button onClick={()=>setMostraNonDisponibili(true)} style={{background:"none",border:"none",padding:4,fontFamily:"inherit",fontSize:12.5,fontWeight:700,color:C.text,textDecoration:"underline",cursor:"pointer"}}>Mostra</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legenda */}
        <div style={{display:"flex",flexWrap:"wrap",gap:16,alignItems:"center",fontSize:11.5,color:C.textSub}}>
          <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:22,height:12,borderRadius:4,background:NERO}}/>Occupato</span>
          <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:22,height:12,borderRadius:4,background:`linear-gradient(90deg, ${NERO} 50%, ${ARANCIO_SOFT} 50%)`}}/>Libero una parte del giorno</span>
          <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:22,height:12,borderRadius:4,background:ARANCIO_SOFT,border:`1.5px dashed ${ARANCIO_BORDO}`,boxSizing:"border-box"}}/>Libero</span>
          <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:22,height:12,borderRadius:4,background:RIGHE}}/>Assente / non disponibile</span>
          {!isMob && <span>Ogni barretta va dalle {S_INI/60} alle {S_FINE/60}: ogni blocco nero è un intervento, il numero indica quanti sono.</span>}
          <span>Valore programmato = importo del cantiere ÷ giorni pianificati, diviso tra chi ci lavora in base alle ore. La supervisione non conta.</span>
        </div>
        {btnNuovoSub}
      </div>
    );
  }

  function VistaOperaiSettimana(){
    const startOfWeek = new Date(curDate);
    const dow = (curDate.getDay()+6)%7;
    startOfWeek.setDate(curDate.getDate()-dow);
    const giorni = Array.from({length:7},(_,i)=>{ const d=new Date(startOfWeek); d.setDate(d.getDate()+i); return d; });
    const nomiGiorno = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
    const weekLabel = `${giorni[0].toLocaleDateString("it-IT",{day:"2-digit",month:"short"})} — ${giorni[6].toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric"})}`;
    return(
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <div style={{textAlign:"center",fontSize:13.5,fontWeight:700,color:C.textSub}}>{weekLabel}</div>
        {operaiCalendario.length===0 ? (
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:20,padding:40,textAlign:"center",color:C.textMuted}}>Nessun operaio disponibile.</div>
        ) : (
          <div style={{background:C.card,boxShadow:C.shadowMd,borderRadius:20,overflow:"hidden",overflowX:"auto"}}>
            <div style={{minWidth:880}}>
              <div style={{display:"grid",gridTemplateColumns:"180px repeat(7,1fr)",background:C.bg}}>
                <div/>
                {giorni.map((d,i)=>{
                  const ds=toStr(d); const isToday=ds===today;
                  return(
                    <div key={i} style={{textAlign:"center",padding:"14px 4px 12px"}}>
                      <div style={{fontSize:10,fontWeight:800,color:isToday?C.brand:C.textMuted,letterSpacing:0.8,textTransform:"uppercase",marginBottom:6}}>{nomiGiorno[i]}</div>
                      <div style={{width:30,height:30,borderRadius:"50%",margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,background:isToday?C.brand:"transparent",color:isToday?"#fff":C.text}}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
              {operaiCalendario.map((op,idx)=>(
                <div key={op.id} style={{display:"grid",gridTemplateColumns:"180px repeat(7,1fr)",background:idx%2===1?C.bg+"80":"transparent",borderTop:`1px solid ${C.border}`}}>
                  <IntestazioneOperaio op={op}/>
                  {giorni.map((d,i)=><CellaOperaio key={i} op={op} ds={toStr(d)}/>)}
                </div>
              ))}
              {subappaltatori.length>0 && (
                <div style={{padding:"10px 14px 6px",fontSize:10.5,fontWeight:800,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6,borderTop:`1px solid ${C.border}`,background:C.bg}}>Subappaltatori</div>
              )}
              {subappaltatori.map(sb=>(
                <div key={sb.nome} style={{display:"grid",gridTemplateColumns:"180px repeat(7,1fr)",borderTop:`1px solid ${C.border}`}}>
                  <IntestazioneSub sub={sb}/>
                  {giorni.map((d,i)=><CellaSub key={i} sub={sb} ds={toStr(d)}/>)}
                </div>
              ))}
            </div>
          </div>
        )}
        {btnNuovoSub}
      </div>
    );
  }

  function VistaOperaiMese(){
    const year=curDate.getFullYear(), month=curDate.getMonth();
    const daysInMonth=getDaysInMonth(year,month);
    const giorniMese = Array.from({length:daysInMonth},(_,i)=>i+1);
    const monthName=new Date(year,month,1).toLocaleDateString("it-IT",{month:"long",year:"numeric"});
    const dsMese = giorniMese.map(day=>`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`);
    const valMese = calcolaValore(dsMese);
    const GRID = `130px repeat(${daysInMonth},34px) 78px`;
    const totCol = (v)=><div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",padding:"0 10px",fontSize:11.5,fontWeight:700,color:v>0?C.text:C.textMuted,fontVariantNumeric:"tabular-nums",whiteSpace:"nowrap"}}>{v>0?eur0(v):"—"}</div>;
    return(
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <div style={{textAlign:"center",fontSize:14,fontWeight:700,color:C.text,textTransform:"capitalize"}}>{monthName}</div>
        {operaiCalendario.length===0 ? (
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:20,padding:40,textAlign:"center",color:C.textMuted}}>Nessun operaio disponibile.</div>
        ) : (
          <div style={{background:C.card,boxShadow:C.shadowMd,borderRadius:20,overflow:"hidden",overflowX:"auto"}}>
            <div style={{minWidth:130+daysInMonth*34+78}}>
              <div style={{display:`grid`,gridTemplateColumns:GRID,background:C.bg}}>
                <div/>
                {giorniMese.map(day=>{
                  const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                  const isToday=ds===today;
                  return(
                    <div key={day} style={{textAlign:"center",padding:"8px 0",fontSize:10,fontWeight:800,color:isToday?C.brand:C.textMuted}}>{day}</div>
                  );
                })}
                <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",padding:"0 10px",fontSize:10,fontWeight:800,color:C.textMuted}}>MESE</div>
              </div>
              {/* Valore programmato per giorno */}
              <div style={{display:`grid`,gridTemplateColumns:GRID,borderTop:`1px solid ${C.border}`,background:C.card}}>
                <div style={{padding:"6px 10px",fontSize:10.5,fontWeight:700,color:C.textSub,display:"flex",flexDirection:"column",justifyContent:"center"}}>
                  <span>€ programmati</span><span style={{fontWeight:500,color:C.textMuted}}>obiettivo {eurK(obiettivo)}</span>
                </div>
                {dsMese.map((ds,i)=>{
                  const v = valMese.valGiorno[i], lav = giornoLavorativo(ds), ok = v>=obiettivo;
                  return(
                    <div key={ds} title={lav?`${eur0(v)} su ${eur0(obiettivo)}${ok?"":` · mancano ${eur0(obiettivo-v)}`}`:`${eur0(v)} · riposo`}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",gap:3,padding:"6px 2px",opacity:ds<today?0.55:1}}>
                      <span style={{fontSize:9,fontWeight:700,fontVariantNumeric:"tabular-nums",color:!lav?C.textMuted:ok?C.text:"#B4561C"}}>{v>0?eurK(v):(lav?"0":"")}</span>
                      {lav && <div style={{width:22,height:4,borderRadius:2,background:C.borderLight,overflow:"hidden"}}><div style={{height:"100%",width:`${obiettivo?Math.min(100,v/obiettivo*100):0}%`,background:ok?C.text:C.brand}}/></div>}
                    </div>
                  );
                })}
                {totCol(dsMese.reduce((t,ds,i)=>t+valMese.valGiorno[i],0))}
              </div>
              {operaiCalendario.map((op,idx)=>(
                <div key={op.id} style={{display:`grid`,gridTemplateColumns:GRID,background:idx%2===1?C.bg+"80":"transparent",borderTop:`1px solid ${C.border}`}}>
                  <IntestazioneOperaio op={op} compact/>
                  {giorniMese.map(day=>{
                    const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                    return <CellaOperaio key={day} op={op} ds={ds} compact/>;
                  })}
                  {totCol(sommaArr(valMese.op(op.id)))}
                </div>
              ))}
              {subappaltatori.length>0 && (
                <div style={{padding:"8px 10px 4px",fontSize:10,fontWeight:800,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6,borderTop:`1px solid ${C.border}`,background:C.bg}}>Subappaltatori</div>
              )}
              {subappaltatori.map(sb=>(
                <div key={sb.nome} style={{display:`grid`,gridTemplateColumns:GRID,borderTop:`1px solid ${C.border}`}}>
                  <IntestazioneSub sub={sb} compact/>
                  {giorniMese.map(day=>{
                    const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                    return <CellaSub key={day} sub={sb} ds={ds} compact/>;
                  })}
                  {totCol(sommaArr(valMese.sub(sb.nome)))}
                </div>
              ))}
            </div>
          </div>
        )}
        {btnNuovoSub}
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {isMob ? (
        /* ── Telefono: barra centrata e minimal ── */
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
          <div style={{display:"flex",background:"#ECECE8",borderRadius:12,padding:3}}>
            {[{id:"cantieri",label:"Cantieri"},{id:"operai",label:"Operai"}].map(v=>(
              <button key={v.id} onClick={()=>setVistaTipo(v.id)} style={{height:34,padding:"0 20px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:vistaTipo===v.id?700:500,background:vistaTipo===v.id?C.card:"transparent",color:vistaTipo===v.id?C.text:C.textSub,boxShadow:vistaTipo===v.id?"0 1px 2px rgba(0,0,0,.1)":"none"}}>{v.label}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:18}}>
            {[{id:"giorno",label:"Giorno"},{id:"settimana",label:"Settimana"},{id:"mese",label:"Mese"}].map(v=>(
              <button key={v.id} onClick={()=>setVista(v.id)} style={{background:"none",border:"none",padding:"6px 2px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:vista===v.id?700:500,color:vista===v.id?C.text:C.textMuted,borderBottom:`2px solid ${vista===v.id?C.brand:"transparent"}`}}>{v.label}</button>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"44px 1fr 44px",alignItems:"center",width:"100%"}}>
            <button onClick={navPrev} aria-label="Precedente" style={{width:44,height:44,border:"none",background:"none",cursor:"pointer",fontSize:20,color:C.textSub}}>‹</button>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
              <span style={{fontSize:17,fontWeight:700,color:C.text,textTransform:"capitalize",letterSpacing:-0.2}}>
                {vista==="mese" ? curDate.toLocaleDateString("it-IT",{month:"long",year:"numeric"})
                 : vista==="settimana" ? (()=>{ const s=new Date(curDate); s.setDate(s.getDate()-((s.getDay()+6)%7)); const e=new Date(s); e.setDate(e.getDate()+6); return `${s.getDate()} – ${e.toLocaleDateString("it-IT",{day:"numeric",month:"short"})}`; })()
                 : curDate.toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}
              </span>
              {curDateStr!==toStr(new Date())
                ? <button onClick={()=>setCurDate(new Date())} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,color:"#B4561C"}}>Torna a oggi</button>
                : <span style={{fontSize:12,color:C.textMuted}}>Oggi</span>}
            </div>
            <button onClick={navNext} aria-label="Successivo" style={{width:44,height:44,border:"none",background:"none",cursor:"pointer",fontSize:20,color:C.textSub}}>›</button>
          </div>
          {vistaTipo==="cantieri" && (
            <select value={selOp} onChange={e=>setSelOp(e.target.value)} style={{background:C.card,border:`1px solid ${C.border}`,color:C.text,borderRadius:11,padding:"7px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer"}}>
              <option value="tutti">Tutti gli operai</option>
              {operai.map(o=><option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          )}
        </div>
      ) : (
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {/* Toggle prospettiva: Cantieri / Operai */}
          <div style={{display:"flex",background:C.card,boxShadow:C.shadowSm,borderRadius:11,padding:3,gap:2}}>
            {[{id:"cantieri",label:"🏗️ Cantieri"},{id:"operai",label:"👷 Operai"}].map(v=>(
              <button key={v.id} onClick={()=>setVistaTipo(v.id)} style={{padding:"6px 14px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:vistaTipo===v.id?700:500,background:vistaTipo===v.id?C.text:"transparent",color:vistaTipo===v.id?"#fff":C.textSub,transition:"all 0.15s"}}>{v.label}</button>
            ))}
          </div>
          {/* Toggle viste */}
          <div style={{display:"flex",background:C.card,boxShadow:C.shadowSm,borderRadius:11,padding:3,gap:2}}>
            {[{id:"giorno",label:"Giorno"},{id:"settimana",label:"Settimana"},{id:"mese",label:"Mese"}].map(v=>(
              <button key={v.id} onClick={()=>setVista(v.id)} style={{padding:"6px 14px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:vista===v.id?700:500,background:vista===v.id?C.accent:"transparent",color:vista===v.id?"#fff":C.textSub,transition:"all 0.15s"}}>{v.label}</button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {vistaTipo==="cantieri" && (
            <select value={selOp} onChange={e=>setSelOp(e.target.value)} style={{background:C.card,boxShadow:C.shadowSm,color:C.text,borderRadius:11,padding:"7px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer"}}>
              <option value="tutti">Tutti gli operai</option>
              {operai.map(o=><option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          )}
          <Btn small variant="ghost" onClick={navPrev}>← Prec</Btn>
          {curDateStr!==toStr(new Date()) && (
            <span style={{fontSize:12.5,fontWeight:600,color:C.textSub,whiteSpace:"nowrap",textTransform:"capitalize"}}>
              {curDate.toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"})}
            </span>
          )}
          <Btn small variant={curDateStr===toStr(new Date())?"ghost":"primary"} onClick={()=>setCurDate(new Date())}>Oggi</Btn>
          <Btn small variant="ghost" onClick={navNext}>Succ →</Btn>
        </div>
      </div>
      )}
      {vistaTipo==="cantieri" ? (
        <>
          {vista==="mese"     && <VistaMese/>}
          {vista==="settimana"&& <VistaSettimana/>}
          {vista==="giorno"   && <VistaGiorno/>}
        </>
      ) : (
        <>
          {riquadriObiettivo()}
          {vista==="mese"     && <VistaOperaiMese/>}
          {vista==="settimana"&& (
            <>
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <div style={{display:"flex",background:C.card,boxShadow:C.shadowSm,borderRadius:11,padding:3,gap:2}}>
                  {[{id:"compatta",label:"Compatta"},{id:"dettaglio",label:"Dettaglio"}].map(v=>(
                    <button key={v.id} onClick={()=>setDensitaSett(v.id)} style={{padding:"6px 14px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:densitaSett===v.id?700:500,background:densitaSett===v.id?C.text:"transparent",color:densitaSett===v.id?"#fff":C.textSub}}>{v.label}</button>
                  ))}
                </div>
              </div>
              {densitaSett==="dettaglio" ? <VistaOperaiSettimana/> : vistaOperaiSettimanaCompatta()}
            </>
          )}
          {vista==="giorno"   && <VistaOperaiGiorno/>}
          {/* Legenda stati — cliccabile per mostrare/nascondere */}
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:14,padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <span style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5}}>Mostra nel calendario</span>
              <div style={{display:"flex",gap:14,alignItems:"center"}}>
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.textMuted,cursor:"pointer",fontWeight:600}}>
                  <input type="checkbox" checked={mostraNonDisponibili} onChange={e=>setMostraNonDisponibili(e.target.checked)}/>
                  Includi operai non disponibili
                </label>
                <div style={{display:"flex",gap:10,fontSize:11}}>
                  <span onClick={()=>setStatiVisibiliOperai(TUTTI_STATI_CAL)} style={{color:C.accent,cursor:"pointer",fontWeight:700}}>Tutti</span>
                  <span onClick={()=>setStatiVisibiliOperai([])} style={{color:C.textMuted,cursor:"pointer",fontWeight:700}}>Nessuno</span>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {TUTTI_STATI_CAL.map(s=>{
                const c = STATI_COLOR[s];
                const attivo = statiVisibiliOperai.includes(s);
                const nLavori = lavori.filter(l=>l.stato===s).length;
                const espanso = statoEspanso===s;
                return(
                  <div key={s} style={{display:"flex",alignItems:"center",borderRadius:20,background:attivo?c+"18":C.bg,border:`1px solid ${espanso?c:(attivo?c+"55":C.border)}`,opacity:attivo?1:0.55,transition:"all 0.12s",overflow:"hidden"}}>
                    <div onClick={()=>setStatiVisibiliOperai(v=>attivo?v.filter(x=>x!==s):[...v,s])}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"4px 8px 4px 8px",cursor:"pointer"}}>
                      <div style={{width:7,height:7,borderRadius:"50%",background:c,flexShrink:0}}/>
                      <span style={{fontSize:11,fontWeight:600,color:attivo?c:C.textMuted,whiteSpace:"nowrap"}}>{s}</span>
                    </div>
                    {nLavori>0 && (
                      <div onClick={()=>setStatoEspanso(espanso?null:s)} style={{fontSize:10.5,fontWeight:700,color:attivo?c:C.textMuted,padding:"4px 9px 4px 6px",cursor:"pointer",borderLeft:`1px solid ${attivo?c+"33":C.border}`}}>
                        {nLavori} {espanso?"▴":"▾"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {statoEspanso && (()=>{
              const elenco = lavori.filter(l=>l.stato===statoEspanso).sort((a,b)=>(a.data_inizio||"").localeCompare(b.data_inizio||""));
              const c = STATI_COLOR[statoEspanso];
              return(
                <div style={{borderTop:`1px solid ${C.border}`,paddingTop:8,display:"flex",flexDirection:"column",gap:5}}>
                  <div style={{fontSize:10.5,fontWeight:700,color:c,textTransform:"uppercase",letterSpacing:0.5}}>Cantieri — {statoEspanso} ({elenco.length})</div>
                  {elenco.map(l=>(
                    <div key={l.id} onClick={()=>{
                        if(l.data_inizio){ setCurDate(new Date(l.data_inizio+"T12:00:00")); }
                        setVista("giorno"); setVistaTipo("cantieri"); setStatoEspanso(null);
                      }}
                      style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,background:C.bg,borderRadius:9,padding:"7px 10px",cursor:"pointer"}}>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</div>
                        <div style={{fontSize:10.5,color:C.textMuted}}>{l.cliente||"—"}</div>
                      </div>
                      <div style={{fontSize:10.5,color:c,flexShrink:0,fontWeight:600}}>{fmt(l.data_inizio)}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </>
      )}

      {/* ── DRAG OPERAIO banner ── */}
      {dragOp&&(
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",background:C.accent,color:"#fff",borderRadius:16,padding:"12px 24px",fontSize:14,fontWeight:700,zIndex:10000,boxShadow:"0 8px 32px rgba(0,0,0,0.3)",display:"flex",gap:12,alignItems:"center"}}>
          <span>👷 {operai.find(o=>o.id===dragOp.operaioId)?.nome} — trascina su un cantiere</span>
          <button onClick={()=>setDragOp(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,color:"#fff",padding:"4px 10px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>✕ Annulla</button>
        </div>
      )}

      {calAction&&(()=>{ const calLav = lavori.find(l=>l.id===calAction.id)||calAction;
        const opsCant = operai.filter(o=>giorniOperaioSuLavoro(calLav,o.id).length>0);
        const subsCant = [...new Set(Object.values(calLav.giorni_dettaglio||{}).flat().filter(iv=>iv?.subappalto).map(iv=>iv.subappalto.nome))];
        const gFut = giorniFuturi(calLav);
        return(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setCalAction(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.card,borderRadius:20,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.4)"}}>
            <div style={{padding:"20px 24px",borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontWeight:800,fontSize:15,color:C.text,marginBottom:4}}>{calLav.titolo}</div>
              <div style={{fontSize:12,color:C.textMuted}}>📍 {calLav.indirizzo} · 📅 {fmt(calLav.data_inizio)} → {fmt(calLav.data_fine)} · {giorniPianificati(calLav).length} gg lavorativi</div>
            </div>
            <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:14}}>

              {/* ── Squadra: operai e subappaltatori sui giorni reali ── */}
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.textMuted,letterSpacing:.5,textTransform:"uppercase"}}>Squadra</div>
                  <button onClick={()=>{ setAssegnaCal(calLav); setCalAction(null); }} style={{background:C.accentGlow,border:`1px solid ${C.accent}44`,borderRadius:9,color:C.accent,padding:"5px 11px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>📅 Assegna periodo</button>
                </div>
                {opsCant.length===0 && subsCant.length===0
                  ? <div style={{fontSize:13,color:C.textMuted,fontStyle:"italic"}}>Nessuno assegnato</div>
                  : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {opsCant.map(op=>{
                        const gg = giorniOperaioSuLavoro(calLav, op.id);
                        const fut = gg.filter(ds=>ds>=today);
                        const assOggiInPoi = fut.filter(ds=>assenzaDi(op.id,ds));
                        return(
                          <div key={op.id} style={{display:"flex",alignItems:"center",gap:10,background:C.bg,borderRadius:11,padding:"8px 12px",border:`1px solid ${C.border}`}}>
                            <div style={{width:28,height:28,borderRadius:"50%",background:op.colore+"33",border:`1px solid ${op.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:op.colore,flexShrink:0}}>{op.nome[0]}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontWeight:700,fontSize:13,color:C.text}}>{op.nome}{calLav.responsabile_id===op.id?" ⭐":""}</div>
                              <div style={{fontSize:11,color:C.textMuted}}>{gg.length} gg{fut.length>0?` · prossimo ${fmt(fut[0])}`:" · solo giorni passati"}</div>
                              {assOggiInPoi.length>0 && <div style={{fontSize:11,color:C.red,fontWeight:600}}>⚠ assente {assOggiInPoi.map(fmt).join(", ")}</div>}
                              {op.disponibile===false && fut.length>0 && <div style={{fontSize:11,color:C.orange,fontWeight:600}}>⚠ segnato non disponibile</div>}
                            </div>
                            {fut.length>0 && <button
                              onClick={()=>setDragOp({operaioId:op.id,fromLavoroId:calLav.id})}
                              title="Sposta su altro cantiere (giorni da oggi in avanti)"
                              style={{background:C.blue+"18",border:`1px solid ${C.blue}44`,borderRadius:9,color:C.blue,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                              ↔ Sposta
                            </button>}
                            {fut.length>0 && <button
                              title="Togli dai giorni da oggi in avanti"
                              onClick={()=>{ if(window.confirm(`Togliere ${op.nome} da ${fut.length} giorn${fut.length>1?"i":"o"} (da oggi in avanti)? I giorni passati restano per la Contabilità.`)) rimuoviOperaioDaCantiere(op.id,calLav.id); }}
                              style={{background:C.red+"15",border:`1px solid ${C.red}33`,borderRadius:9,color:C.red,padding:"4px 8px",fontSize:11,cursor:"pointer"}}>✕</button>}
                          </div>
                        );
                      })}
                      {subsCant.map(nome=>{
                        const gg = giorniSubSuLavoro(calLav, nome);
                        const costoTot = gg.reduce((t,ds)=>t+(parseFloat(subDelGiorno(calLav,ds).find(x=>x.nome===nome)?.costo_giorno)||0),0);
                        return(
                          <div key={nome} style={{display:"flex",alignItems:"center",gap:10,background:C.bg,borderRadius:11,padding:"8px 12px",border:`1px dashed ${C.border}`}}>
                            <div style={{width:28,height:28,borderRadius:"50%",background:C.card,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>🤝</div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontWeight:700,fontSize:13,color:C.text}}>{nome}</div>
                              <div style={{fontSize:11,color:C.textMuted}}>{gg.length} giornat{gg.length===1?"a":"e"}{costoTot>0?` · ${euro(costoTot)} in Contabilità`:" · costo come spesa"}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                }
                {/* Aggiungi operaio sui giorni da oggi in avanti */}
                {operai.filter(o=>!opsCant.includes(o)).length>0 && gFut.length>0 && (
                  <div style={{marginTop:8}}>
                    <select defaultValue="" onChange={async e=>{
                      if(!e.target.value) return;
                      const id=parseInt(e.target.value);
                      e.target.value="";
                      await aggiungiOperaioACantiere(id, calLav.id);
                    }} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,color:C.text,padding:"8px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer"}}>
                      <option value="">+ Aggiungi operaio su tutti i {gFut.length} giorni rimasti…</option>
                      {operai.filter(o=>!opsCant.includes(o)).sort((a,b)=>(a.disponibile===false)-(b.disponibile===false)).map(o=>(
                        <option key={o.id} value={o.id}>{o.nome}{o.disponibile===false?" (non disponibile)":""}</option>
                      ))}
                    </select>
                    <div style={{fontSize:11,color:C.textMuted,marginTop:4}}>Salta da solo i giorni in cui è su un altro cantiere o assente.</div>
                  </div>
                )}
              </div>

              {/* ── Sposta su altro cantiere (se dragOp attivo) ── */}
              {dragOp&&dragOp.fromLavoroId===calLav.id&&(
                <div style={{background:C.blue+"12",border:`1px solid ${C.blue}44`,borderRadius:14,padding:"12px 14px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.blue,marginBottom:8}}>↔ Sposta {operai.find(o=>o.id===dragOp.operaioId)?.nome} su:</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {lavori.filter(l=>l.id!==calLav.id&&STATI_DA_ASSEGNARE.includes(l.stato)).map(l=>(
                      <button key={l.id} onClick={()=>{ spostaOperaio(dragOp.operaioId,dragOp.fromLavoroId,l.id); setCalAction(null); }}
                        style={{background:C.card,boxShadow:C.shadowSm,borderRadius:11,padding:"8px 12px",fontSize:13,fontWeight:600,color:C.text,cursor:"pointer",textAlign:"left",fontFamily:"inherit"}}>
                        📍 {l.titolo} {l.data_inizio?`(${fmt(l.data_inizio)} → ${fmt(l.data_fine)})`:""}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Date ── */}
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.textMuted,letterSpacing:.5,textTransform:"uppercase",marginBottom:4}}>Sposta date</div>
                <div style={{fontSize:11,color:C.textMuted,marginBottom:8}}>Squadra e giorni si spostano insieme. Se allunghi la fine, i giorni in più prendono la squadra dell'ultimo giorno.</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div>
                    <label style={{fontSize:10,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:1,display:"block",marginBottom:4}}>Inizio</label>
                    <input type="date" key={"s"+calLav.data_inizio} defaultValue={calLav.data_inizio} id="cal_start" style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,color:C.text,padding:"8px 10px",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:10,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:1,display:"block",marginBottom:4}}>Fine</label>
                    <input type="date" key={"e"+calLav.data_fine} defaultValue={calLav.data_fine} id="cal_end" style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,color:C.text,padding:"8px 10px",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                </div>
                <button onClick={async()=>{
                  const s=document.getElementById("cal_start").value;
                  const e=document.getElementById("cal_end").value;
                  if(await salvaNuoveDate(calLav, s, e)) setCalAction(null);
                }} style={{width:"100%",marginTop:8,background:C.accentGlow,border:`1px solid ${C.accent}44`,borderRadius:14,color:C.accent,padding:"10px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  💾 Salva nuove date
                </button>
              </div>

              {/* ── Azioni ── */}
              <div style={{display:"flex",flexDirection:"column",gap:6,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                <button onClick={()=>sospendi(calLav)} style={{width:"100%",background:C.purple+"18",border:`1px solid ${C.purple}44`,borderRadius:14,color:C.purple,padding:"11px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>⊘ Sospendi lavoro</button>
                <button onClick={()=>annulla(calLav)} style={{width:"100%",background:C.red+"15",border:`1px solid ${C.red}33`,borderRadius:14,color:C.red,padding:"11px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>✕ Annulla lavoro</button>
                <button onClick={()=>setCalAction(null)} style={{width:"100%",background:"transparent",border:`1px solid ${C.border}`,borderRadius:14,color:C.textMuted,padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Chiudi</button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── VISTA OPERAI: Picker — assegna cella vuota, con orario personalizzabile ── */}
      {picker && (
        <Modal title={`${operai.find(o=>o.id===picker.operaioId)?.nome} — ${fmtFull(picker.dataStr)}`} onClose={()=>{setPicker(null);setPickerTab("cantiere");setOraInizioPick("07:30");setOraFinePick("16:30");}}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",gap:4,background:C.bg,borderRadius:12,padding:4}}>
              {[{v:"cantiere",l:"🏗️ Assegna a cantiere"},{v:"assenza",l:"🌴 Segna assenza"}].map(t=>{ const att=pickerTab===t.v; return (
                <button key={t.v} onClick={()=>{ setPickerTab(t.v); if(t.v==="assenza") setNuovaAssenza({tipo:"ferie", data_fine:picker.dataStr, note:"", togli:true}); }} style={{flex:1,border:"none",borderRadius:9,padding:"9px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:700,background:att?C.card:"transparent",color:att?C.text:C.textMuted,boxShadow:att?C.shadowMd:"none"}}>{t.l}</button>
              );})}
            </div>
            {operai.find(o=>o.id===picker.operaioId)?.disponibile===false && (
              <div style={{background:C.orange+"14",borderRadius:11,padding:"8px 12px",fontSize:12,color:C.orange,fontWeight:600}}>Operaio segnato come non disponibile (cambialo dalla scheda operaio cliccando sul nome).</div>
            )}
            {pickerTab==="assenza" ? (()=>{
              const opId = picker.operaioId;
              const giorniAss = nuovaAssenza.data_fine>=picker.dataStr ? rangeDates(picker.dataStr, nuovaAssenza.data_fine) : [];
              const cantieriToccati = lavori.filter(l=>!["Annullato","Rifiutato"].includes(l.stato) && giorniOperaioSuLavoro(l,opId).some(ds=>giorniAss.includes(ds)));
              return(
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {TIPI_ASSENZA.map(t=>(
                      <button key={t.v} onClick={()=>setNuovaAssenza(a=>({...a,tipo:t.v}))} style={{border:`1px solid ${nuovaAssenza.tipo===t.v?C.text:C.border}`,background:nuovaAssenza.tipo===t.v?C.text:"transparent",color:nuovaAssenza.tipo===t.v?"#fff":C.textSub,borderRadius:20,padding:"7px 13px",fontSize:12.5,fontWeight:600,cursor:"pointer"}}>{t.l}</button>
                    ))}
                  </div>
                  <Input label="Fino al (compreso)" type="date" value={nuovaAssenza.data_fine} onChange={v=>setNuovaAssenza(a=>({...a,data_fine:v}))}/>
                  <Input label="Nota (facoltativa)" value={nuovaAssenza.note} onChange={v=>setNuovaAssenza(a=>({...a,note:v}))}/>
                  {cantieriToccati.length>0 && (
                    <label style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:12.5,color:C.text,background:C.red+"0F",borderRadius:11,padding:"10px 12px",cursor:"pointer"}}>
                      <input type="checkbox" checked={nuovaAssenza.togli} onChange={e=>setNuovaAssenza(a=>({...a,togli:e.target.checked}))} style={{marginTop:2}}/>
                      <span>Togli l'operaio da questi cantieri nei giorni di assenza: <b>{cantieriToccati.map(l=>l.titolo).join(", ")}</b></span>
                    </label>
                  )}
                  <div style={{fontSize:11.5,color:C.textMuted}}>Compare anche nella pagina Ferie / Assenze.</div>
                  <Btn onClick={async()=>{ const ok = await salvaAssenza(opId, picker.dataStr, nuovaAssenza.data_fine, nuovaAssenza.tipo, nuovaAssenza.note, nuovaAssenza.togli && cantieriToccati.length>0); if(ok){ setPicker(null); setPickerTab("cantiere"); } }}>Salva assenza</Btn>
                </div>
              );
            })() : (<>
            <div style={{display:"flex",gap:4,background:C.bg,borderRadius:12,padding:4}}>
              {[{v:"lavora",l:"Lavora"},{v:"supervisione",l:"Supervisiona"}].map(r=>{ const att=(picker.ruolo||"lavora")===r.v; return (
                <button key={r.v} onClick={()=>setPicker(p=>({...p,ruolo:r.v}))} style={{flex:1,border:"none",borderRadius:9,padding:"8px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:700,background:att?C.card:"transparent",color:att?C.text:C.textMuted,boxShadow:att?C.shadowMd:"none"}}>{r.l}</button>
              );})}
            </div>
            {(()=>{ const gia = interventiOperaioGiorno(lavoriAttivi, picker.operaioId, picker.dataStr); return gia.length>0 && (
              <div style={{background:C.bg,borderRadius:12,padding:"10px 12px"}}>
                <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Già in programma oggi</div>
                {gia.map((it,i)=>(
                  <div key={i} style={{fontSize:12.5,color:C.textSub,display:"flex",gap:8}}>
                    <span style={{color:C.textMuted,fontVariantNumeric:"tabular-nums"}}>{it.ora_inizio}–{it.ora_fine}</span>
                    <span>{it.ruolo==="supervisione"?"👁 ":""}{it.lavoro.titolo}</span>
                  </div>
                ))}
              </div>
            );})()}
            <div>
              <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Cosa fa</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {Object.entries(ATTIVITA).map(([k,a])=>(
                  <button key={k} onClick={()=>setAttivitaPick(k)} style={{border:`1px solid ${attivitaPick===k?C.text:C.border}`,background:attivitaPick===k?C.text:"transparent",color:attivitaPick===k?"#fff":C.textSub,borderRadius:20,padding:"6px 11px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{a.i} {a.l}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Orario in cantiere</div>
              <div style={{display:"flex",flexDirection:"column",gap:10,background:C.bg,borderRadius:14,padding:"12px 14px"}}>
                <TimeSlider value={oraInizioPick} onChange={setOraInizioPick}/>
                <TimeSlider value={oraFinePick} onChange={setOraFinePick}/>
              </div>
            </div>
            <input value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)} placeholder="🔍 Cerca cantiere…"
              style={{background:C.bg,border:"none",borderRadius:11,padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
            {filtroStatiPick}
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:300,overflowY:"auto"}}>
              {lavoriAssegnabili.length===0 && <div style={{fontSize:12.5,color:C.textMuted,textAlign:"center",padding:20}}>Nessun cantiere trovato.</div>}
              {lavoriAssegnabili.map(l=>(
                <button key={l.id} onClick={async()=>{ const ok=await assegnaGiorno(l, picker.dataStr, picker.operaioId, oraInizioPick, oraFinePick, picker.ruolo||"lavora", null, attivitaPick); if(ok){setPicker(null);setOraInizioPick("07:30");setOraFinePick("16:30");} }}
                  style={{display:"flex",alignItems:"center",gap:10,background:C.bg,border:"none",borderRadius:11,padding:"10px 12px",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:STATI_COLOR[l.stato],flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</div>
                    <div style={{fontSize:11,color:C.textMuted}}>{l.cliente||"—"} · {l.stato==="Programmato"?"In corso":l.stato}{(()=>{ const gia=interventiSuLavoro(l,picker.operaioId,picker.dataStr); return gia.length&&giorniOperaioSuLavoro(l,picker.operaioId).includes(picker.dataStr)?<span style={{color:C.accent,fontWeight:600}}> · già qui {gia.map(x=>`${x.ora_inizio}–${x.ora_fine}`).join(", ")}</span>:null; })()}</div>
                  </div>
                </button>
              ))}
            </div>
            </>)}
          </div>
        </Modal>
      )}

      {/* ── Assenza: dettaglio dal calendario ── */}
      {dettaglioAss && (()=>{
        const a = assenze.find(x=>x.id===dettaglioAss.assenza.id) || dettaglioAss.assenza;
        const op = operai.find(o=>o.id===dettaglioAss.operaioId);
        const chiudi = ()=>setDettaglioAss(null);
        return(
          <Modal title={`${labelAssenza(a.tipo)} — ${op?.nome||""}`} onClose={chiudi}>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:13,color:C.textSub}}>Dal {fmtFull(a.data_inizio)} al {fmtFull(a.data_fine)}{a.note?` · ${a.note}`:""}</div>
              {a.data_inizio!==a.data_fine && <Btn variant="ghost" onClick={async()=>{ if(await togliGiornoAssenza(a, dettaglioAss.dataStr)) chiudi(); }}>Togli solo il {fmt(dettaglioAss.dataStr)}</Btn>}
              <Btn variant="danger" onClick={async()=>{ if(!window.confirm("Eliminare tutta l'assenza?")) return; if(await eliminaAssenza(a)) chiudi(); }}>🗑 Elimina tutta l'assenza</Btn>
              <Btn variant="ghost" onClick={()=>{ const d=dettaglioAss; chiudi(); apriPicker(d.operaioId, d.dataStr); }}>Assegna comunque a un cantiere…</Btn>
            </div>
          </Modal>
        );
      })()}

      {/* ── Scheda operaio: disponibilità, prossimi giorni, assenze ── */}
      {schedaOp && (()=>{
        const op = operai.find(o=>o.id===schedaOp);
        if(!op) return null;
        const prossimi = Array.from({length:14},(_,i)=>addGiorni(today,i));
        const assFuture = assenze.filter(a=>a.operaio_id===op.id && a.data_fine>=today).sort((a,b)=>a.data_inizio.localeCompare(b.data_inizio));
        return(
          <Modal title={op.nome} onClose={()=>setSchedaOp(null)}>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,background:C.bg,borderRadius:14,padding:"12px 14px"}}>
                <div>
                  <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>{op.disponibile===false?"Non disponibile":"Disponibile"}</div>
                  <div style={{fontSize:11.5,color:C.textMuted,marginTop:2}}>Stesso interruttore della pagina Operai</div>
                </div>
                <button onClick={()=>cambiaDisponibile(op, op.disponibile===false)} aria-label="Disponibile"
                  style={{width:46,height:26,borderRadius:13,border:"none",cursor:"pointer",position:"relative",background:op.disponibile===false?C.border:C.green,transition:"background 0.15s"}}>
                  <span style={{position:"absolute",top:3,left:op.disponibile===false?3:23,width:20,height:20,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.25)",transition:"left 0.15s"}}/>
                </button>
              </div>
              {(op.telefono||op.costo_ora) && (
                <div style={{display:"flex",alignItems:"center",gap:10,fontSize:12.5,color:C.textSub,flexWrap:"wrap"}}>
                  {op.telefono && <span>📞 {op.telefono}</span>}
                  {op.costo_ora ? <span>· {euro(op.costo_ora)}/h</span> : <span style={{color:C.orange}}>· senza costo orario (non conta in Contabilità)</span>}
                  {op.telefono && <WhatsAppBtn tel={op.telefono} testo={`Ciao ${op.nome.split(" ")[0]}, `}/>}
                </div>
              )}
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Prossimi 14 giorni</div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {prossimi.map(ds=>{
                    const lavs = lavoriAttivi.filter(l=>giorniOperaioSuLavoro(l,op.id).includes(ds));
                    const ass = assenzaDi(op.id, ds);
                    const dow = new Date(ds+"T12:00:00").toLocaleDateString("it-IT",{weekday:"short",day:"2-digit",month:"short"});
                    return(
                      <div key={ds} onClick={()=>{ setSchedaOp(null); const iv0=interventiOperaioGiorno(lavoriAttivi,op.id,ds)[0]; if(iv0) apriGiornata(op.id,ds,iv0); else if(ass) setDettaglioAss({operaioId:op.id,dataStr:ds,assenza:ass}); else apriPicker(op.id,ds); }}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"6px 10px",borderRadius:9,cursor:"pointer",background:ds===today?C.accentGlow:"transparent",fontSize:12.5}}>
                        <span style={{width:92,color:giornoFeriale(ds)?C.textSub:C.textMuted,flexShrink:0,textTransform:"capitalize"}}>{dow}</span>
                        <span style={{flex:1,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:lavs.length?C.text:C.textMuted,fontWeight:lavs.length?600:400}}>
                          {lavs.length ? interventiOperaioGiorno(lavoriAttivi,op.id,ds).map(it=>`${it.ora_inizio} ${it.ruolo==="supervisione"?"👁 ":""}${it.lavoro.titolo}`).join(" · ") : ass ? "" : "libero"}
                          {ass && <span style={{color:lavs.length?C.red:C.textSub,fontWeight:600}}>{lavs.length?" ⚠ ":""}{labelAssenza(ass.tipo)}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {assFuture.length>0 && (
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Assenze in programma</div>
                  {assFuture.map(a=>(
                    <div key={a.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,fontSize:12.5,padding:"6px 10px",background:C.bg,borderRadius:9,marginBottom:4}}>
                      <span>{labelAssenza(a.tipo)} · {fmt(a.data_inizio)} → {fmt(a.data_fine)}</span>
                      <button onClick={()=>{ if(window.confirm("Eliminare questa assenza?")) eliminaAssenza(a); }} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:13}}>🗑</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ── Subappaltatore: nuovo ── */}
      {nuovoSub && (
        <Modal title="Nuovo subappaltatore" onClose={()=>setNuovoSub(null)}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Input label="Nome / squadra" value={nuovoSub.nome} onChange={v=>setNuovoSub(x=>({...x,nome:v}))} placeholder="Es. Squadra Rossi"/>
            <Input label="Costo a giornata € (proposto quando lo assegni)" type="number" value={nuovoSub.costo} onChange={v=>setNuovoSub(x=>({...x,costo:v}))} placeholder="Es. 250"/>
            <div style={{fontSize:11.5,color:C.textMuted}}>Compare come riga nel calendario: clicca un giorno per mandarlo su un cantiere. Ogni giornata finisce in Contabilità come costo del cantiere.</div>
            <Btn disabled={!nuovoSub.nome.trim()} onClick={async()=>{ const nome=nuovoSub.nome.trim(); if(subappaltatori.some(x=>x.nome.toLowerCase()===nome.toLowerCase())){ alert("Esiste già un subappaltatore con questo nome."); return; } if(await salvaSub(nome, nuovoSub.costo)) setNuovoSub(null); }}>Aggiungi</Btn>
          </div>
        </Modal>
      )}

      {/* ── Subappaltatori: elenco con elimina ── */}
      {gestisciSub && (
        <Modal title="Subappaltatori" onClose={()=>setGestisciSub(false)}>
          <div style={{display:"flex",flexDirection:"column"}}>
            {subappaltatori.length===0 && <div style={{fontSize:13,color:C.textMuted,textAlign:"center",padding:20}}>Nessun subappaltatore.</div>}
            {subappaltatori.map((sb,i)=>(
              <div key={sb.nome} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderTop:i?`1px solid ${C.border}`:"none"}}>
                <div onClick={()=>{setGestisciSub(false);setSchedaSub({nome:sb.nome,costo:sb.costo_giorno??""});}} style={{flex:1,minWidth:0,cursor:"pointer"}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sb.nome}</div>
                  <div style={{fontSize:11.5,color:C.textMuted}}>{sb.costo_giorno?`${euro(sb.costo_giorno)}/giorno · modifica`:"Tocca per modificare"}</div>
                </div>
                <Btn small variant="danger" onClick={()=>eliminaSub(sb.nome)}>Elimina</Btn>
              </div>
            ))}
            <div style={{fontSize:11.5,color:C.textMuted,marginTop:10}}>Chi elimini sparisce dal calendario. Le giornate già segnate nei cantieri restano in Contabilità.</div>
          </div>
        </Modal>
      )}

      {/* ── Subappaltatore: scheda (costo, elimina) ── */}
      {schedaSub && (
        <Modal title={schedaSub.nome} onClose={()=>setSchedaSub(null)}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Input label="Costo a giornata € (proposto quando lo assegni)" type="number" value={schedaSub.costo} onChange={v=>setSchedaSub(x=>({...x,costo:v}))} placeholder="Es. 250"/>
            <Btn onClick={async()=>{ if(await salvaSub(schedaSub.nome, schedaSub.costo)) setSchedaSub(null); }}>Salva</Btn>
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,display:"flex",flexDirection:"column",gap:8}}>
              <Btn variant="danger" onClick={async()=>{ if(await eliminaSub(schedaSub.nome)) setSchedaSub(null); }}>Elimina subappaltatore</Btn>
              <div style={{fontSize:11.5,color:C.textMuted}}>Sparisce dal calendario. Le giornate già segnate nei cantieri restano in Contabilità.</div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Subappaltatore: assegna a cantiere ── */}
      {pickerSub && (()=>{
        const giorni = pickerSub.fino>=pickerSub.dataStr ? rangeDates(pickerSub.dataStr, pickerSub.fino).filter(ds=>!pickerSub.soloFeriali || giornoFeriale(ds) || ds===pickerSub.dataStr) : [];
        const costo = parseFloat(pickerSub.costo)||0;
        return(
          <Modal title={`🤝 ${pickerSub.nome} — ${fmtFull(pickerSub.dataStr)}`} onClose={()=>{setPickerSub(null);setFiltroTesto("");}}>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <Input label="Costo a giornata €" type="number" value={pickerSub.costo} onChange={v=>setPickerSub(x=>({...x,costo:v}))} placeholder="Es. 250"/>
                <Input label="Fino al" type="date" value={pickerSub.fino} onChange={v=>setPickerSub(x=>({...x,fino:v}))}/>
              </div>
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.textMuted,cursor:"pointer"}}>
                <input type="checkbox" checked={pickerSub.soloFeriali} onChange={e=>setPickerSub(x=>({...x,soloFeriali:e.target.checked}))}/> Solo giorni feriali
              </label>
              <div style={{background:C.bg,borderRadius:11,padding:"9px 12px",fontSize:12.5,color:C.text,display:"flex",justifyContent:"space-between"}}>
                <span>{giorni.length} giornat{giorni.length===1?"a":"e"} × {euro(costo)}</span><b>{euro(giorni.length*costo)} in Contabilità</b>
              </div>
              <input value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)} placeholder="🔍 Cerca cantiere…" style={{background:C.bg,border:"none",borderRadius:11,padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
              {filtroStatiPick}
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:280,overflowY:"auto"}}>
                {lavoriAssegnabili.map(l=>(
                  <button key={l.id} disabled={giorni.length===0} onClick={async()=>{
                      if(costo<=0 && !window.confirm("Nessun costo a giornata: in Contabilità non verrà conteggiato nulla. Continuare?")) return;
                      const gia = giorni.filter(ds=>lavoriAttivi.some(x=>x.id!==l.id && subDelGiorno(x,ds).some(y=>y.nome===pickerSub.nome)));
                      if(gia.length>0 && !window.confirm(`${pickerSub.nome} è già su un altro cantiere il ${gia.map(fmt).join(", ")}. Continuare?`)) return;
                      if(await assegnaSub(l, pickerSub.nome, giorni, costo)){ setPickerSub(null); setFiltroTesto(""); }
                    }}
                    style={{display:"flex",alignItems:"center",gap:10,background:C.bg,border:"none",borderRadius:11,padding:"10px 12px",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:STATI_COLOR[l.stato],flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</div>
                      <div style={{fontSize:11,color:C.textMuted}}>{l.cliente||"—"} · {l.stato==="Programmato"?"In corso":l.stato}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Subappaltatore: dettaglio giorno ── */}
      {dettaglioSub && (()=>{
        const lav = lavori.find(l=>l.id===dettaglioSub.lavoro.id)||dettaglioSub.lavoro;
        const voce = subDelGiorno(lav, dettaglioSub.dataStr).find(x=>x.nome===dettaglioSub.nome);
        const daAssegna = voce && voce.costo_giorno==null;
        const giorniTot = giorniSubSuLavoro(lav, dettaglioSub.nome);
        const chiudi = ()=>setDettaglioSub(null);
        return(
          <Modal title={`🤝 ${dettaglioSub.nome} — ${lav.titolo}`} onClose={chiudi}>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:12.5,color:C.textMuted}}>📅 {fmtFull(dettaglioSub.dataStr)} · {giorniTot.length} giornat{giorniTot.length===1?"a":"e"} su questo cantiere</div>
              {daAssegna ? (
                <div style={{background:C.bg,borderRadius:11,padding:"9px 12px",fontSize:12,color:C.textSub}}>Inserito da "Assegna": il costo è già registrato come spesa in Contabilità.</div>
              ) : (
                <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                  <div style={{flex:1}}><Input label="Costo a giornata €" type="number" value={dettaglioSub.costo} onChange={v=>setDettaglioSub(x=>({...x,costo:v}))}/></div>
                  <Btn small onClick={async()=>{ if(await assegnaSub(lav, dettaglioSub.nome, [dettaglioSub.dataStr], dettaglioSub.costo)) chiudi(); }}>Salva</Btn>
                  {giorniTot.length>1 && <Btn small variant="ghost" onClick={async()=>{ if(await assegnaSub(lav, dettaglioSub.nome, giorniTot, dettaglioSub.costo)) chiudi(); }}>Su tutti i {giorniTot.length} gg</Btn>}
                </div>
              )}
              <Btn variant="danger" onClick={async()=>{ if(await togliSub(lav, dettaglioSub.nome, [dettaglioSub.dataStr])) chiudi(); }}>✕ Togli da questo giorno</Btn>
              {giorniTot.filter(ds=>ds>=today).length>1 && <Btn variant="ghost" onClick={async()=>{ const fut=giorniTot.filter(ds=>ds>=today); if(!window.confirm(`Togliere ${dettaglioSub.nome} da ${fut.length} giorni (da oggi in avanti)?`)) return; if(await togliSub(lav, dettaglioSub.nome, fut)) chiudi(); }}>Togli da tutti i giorni futuri</Btn>}
            </div>
          </Modal>
        );
      })()}

      {/* ── Assegna periodo (stesso modale delle liste lavori) ── */}
      {assegnaCal && (
        <QuickAssign lavoro={lavori.find(l=>l.id===assegnaCal.id)||assegnaCal} operai={operai} lavori={lavori} db={db}
          onClose={()=>setAssegnaCal(null)}
          onSave={async(d)=>{ const ok = await salvaAssegnazione(db, lavori.find(l=>l.id===assegnaCal.id)||assegnaCal, d, setLavori); if(ok) setAssegnaCal(null); }}/>
      )}

      {/* ── VISTA OPERAI: la giornata di un operaio — più interventi, anche su cantieri diversi ── */}
      {dettaglioOp && (()=>{
        const opId = dettaglioOp.operaioId, ds = dettaglioOp.dataStr;
        const op = operai.find(o=>o.id===opId);
        const ivs = interventiOperaioGiorno(lavoriAttivi, opId, ds);
        const sel = ivs.find(it=>it.lavoro.id===dettaglioOp.lavoro?.id && (!dettaglioOp.rif || (it.ora_inizio===dettaglioOp.rif.ora_inizio && it.ora_fine===dettaglioOp.rif.ora_fine)))
          || ivs.find(it=>it.lavoro.id===dettaglioOp.lavoro?.id) || ivs[0];
        const chiudi = ()=>{ setDettaglioOp(null); setEditOrario(null); };
        if(!sel) return (
          <Modal title={`${op?.nome||""} — ${fmtFull(ds)}`} onClose={chiudi}>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:13,color:C.textMuted}}>Nessun intervento in questa giornata.</div>
              <Btn onClick={()=>{ chiudi(); apriPicker(opId, ds); }}>+ Aggiungi intervento</Btn>
            </div>
          </Modal>
        );
        const rif = {ora_inizio:sel.ora_inizio, ora_fine:sel.ora_fine};
        const ed = editOrario || {ora_inizio:sel.ora_inizio, ora_fine:sel.ora_fine, ruolo:sel.ruolo||"lavora", attivita:sel.attivita||"lavoro", nota:sel.nota||""};
        const setEd = (patch)=>setEditOrario({...ed, ...patch});
        const ore = ivs.reduce((t,it)=>t+oreTraOrari(it.ora_inizio,it.ora_fine),0);
        const seleziona = (it)=>{ setEditOrario(null); setDettaglioOp(d=>({...d, lavoro:it.lavoro, rif:{ora_inizio:it.ora_inizio, ora_fine:it.ora_fine}})); };
        return(
        <Modal title={`${op?.nome||""} — ${fmtFull(ds)}`} onClose={chiudi}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Giornata */}
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5}}>La giornata · {ivs.length} intervent{ivs.length===1?"o":"i"}</div>
                <div style={{fontSize:11.5,color:C.textMuted}}>{String(Math.round(ore*10)/10).replace(".",",")} h</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {ivs.map((it,i)=>{ const on = it===sel; const col = STATI_COLOR[it.lavoro.stato]; const sup = it.ruolo==="supervisione"; return (
                  <button key={i} onClick={()=>seleziona(it)}
                    style={{display:"flex",alignItems:"center",gap:10,background:on?C.card:C.bg,border:`1.5px solid ${on?C.text:"transparent"}`,borderLeft:`4px solid ${col}`,borderRadius:11,padding:"8px 12px",cursor:"pointer",fontFamily:"inherit",textAlign:"left",boxShadow:on?C.shadowSm:"none"}}>
                    <span style={{fontSize:12.5,color:C.textMuted,fontVariantNumeric:"tabular-nums",flexShrink:0,width:86}}>{it.ora_inizio}–{it.ora_fine}</span>
                    <span style={{flex:1,minWidth:0,fontSize:13,fontWeight:600,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sup?"👁 ":it.attivita&&it.attivita!=="lavoro"?`${ATTIVITA[it.attivita]?.i} `:"🔨 "}{it.lavoro.titolo}</span>
                    <span style={{fontSize:11,color:C.textMuted,flexShrink:0}}>{sup?"supervisione":it.attivita&&it.attivita!=="lavoro"?ATTIVITA[it.attivita]?.l:""}</span>
                  </button>
                );})}
                <button onClick={()=>{ chiudi(); apriPicker(opId, ds); }}
                  style={{background:"none",border:`1.5px dashed ${C.border}`,borderRadius:11,padding:"8px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:600,color:C.textSub}}>
                  + Aggiungi un altro intervento
                </button>
              </div>
              {assenzaDi(opId, ds) && <div style={{fontSize:12,color:C.red,fontWeight:600,marginTop:6}}>⚠ Risulta in {labelAssenza(assenzaDi(opId, ds).tipo)}</div>}
            </div>

            {/* Intervento selezionato */}
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,display:"flex",flexDirection:"column",gap:12}}>
              <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>{sel.lavoro.titolo}{sel.lavoro.responsabile_id===opId?" ⭐":""}<span style={{fontWeight:500,color:C.textMuted,fontSize:12}}> · {sel.lavoro.cliente||"—"}</span></div>
              <div style={{display:"flex",gap:4,background:C.bg,borderRadius:12,padding:4}}>
                {[{v:"lavora",l:"Lavora"},{v:"supervisione",l:"Supervisiona"}].map(r=>{ const att=(ed.ruolo||"lavora")===r.v; return (
                  <button key={r.v} onClick={()=>setEd({ruolo:r.v})} style={{flex:1,border:"none",borderRadius:9,padding:"8px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:700,background:att?C.card:"transparent",color:att?C.text:C.textMuted,boxShadow:att?C.shadowMd:"none"}}>{r.l}</button>
                );})}
              </div>
              {(ed.ruolo||"lavora")!=="supervisione" && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {Object.entries(ATTIVITA).map(([k,a])=>(
                    <button key={k} onClick={()=>setEd({attivita:k})} style={{border:`1px solid ${ed.attivita===k?C.text:C.border}`,background:ed.attivita===k?C.text:"transparent",color:ed.attivita===k?"#fff":C.textSub,borderRadius:20,padding:"5px 10px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{a.i} {a.l}</button>
                  ))}
                </div>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:10,background:C.bg,borderRadius:14,padding:"12px 14px"}}>
                <TimeSlider value={ed.ora_inizio} onChange={v=>setEd({ora_inizio:v})}/>
                <TimeSlider value={ed.ora_fine} onChange={v=>setEd({ora_fine:v})}/>
              </div>
              <Input label="Nota (facoltativa)" value={ed.nota||""} onChange={v=>setEd({nota:v})} placeholder="Es. finitura seconda mano"/>
              {editOrario && (
                <Btn onClick={async()=>{ const ok = await modificaIntervento(sel.lavoro, ds, opId, rif, {ora_inizio:ed.ora_inizio, ora_fine:ed.ora_fine, ruolo:ed.ruolo, attivita:ed.ruolo==="supervisione"?"lavoro":ed.attivita, nota:ed.nota}); if(ok){ setEditOrario(null); setDettaglioOp(d=>({...d, rif:{ora_inizio:ed.ora_inizio, ora_fine:ed.ora_fine}})); } }}>💾 Salva intervento</Btn>
              )}
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5}}>Sposta questo intervento su un altro cantiere</div>
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto"}}>
                {lavoriAssegnabili.filter(l=>l.id!==sel.lavoro.id).map(l=>(
                  <button key={l.id} onClick={async()=>{ const ok = await spostaIntervento(sel.lavoro, l, ds, opId, {...rif, ruolo:sel.ruolo, attivita:sel.attivita, nota:sel.nota}); if(ok) seleziona({...sel, lavoro:l}); }}
                    style={{display:"flex",alignItems:"center",gap:10,background:C.bg,border:"none",borderRadius:11,padding:"8px 12px",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                    <div style={{width:7,height:7,borderRadius:"50%",background:STATI_COLOR[l.stato],flexShrink:0}}/>
                    <span style={{fontSize:12.5,fontWeight:600,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</span>
                  </button>
                ))}
              </div>
            </div>
            <button onClick={async()=>{ if(await rimuoviIntervento(sel.lavoro, ds, opId, rif)){ if(ivs.length<=1) chiudi(); else seleziona(ivs.find(x=>x!==sel)); } }}
              style={{width:"100%",background:C.red+"15",border:`1px solid ${C.red}33`,borderRadius:14,color:C.red,padding:"11px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              ✕ Togli questo intervento ({sel.ora_inizio}–{sel.ora_fine})
            </button>
            <Btn variant="ghost" onClick={chiudi}>Chiudi</Btn>
          </div>
        </Modal>
        );
      })()}
    </div>
  );
}

// ── RAPPORTO MODAL ────────────────────────────────────────────────────────────
function RapportoModal({lavoro,onClose,onAdd,db}){
  const [testo,setTesto]=useState("");
  const [fotoFiles,setFotoFiles]=useState([]); // File[] in coda da caricare
  const [uploading,setUploading]=useState(false);
  const [lightbox,setLightbox]=useState(null);

  function aggiungiFileLocali(fileList){
    const nuovi = Array.from(fileList).slice(0, 5-fotoFiles.length);
    setFotoFiles(f=>[...f, ...nuovi]);
  }

  function rimuoviFileLocale(i){
    setFotoFiles(f=>f.filter((_,idx)=>idx!==i));
  }

  const [ultimoTesto,setUltimoTesto]=useState("");

  async function invia(){
    setUploading(true);
    const urls=[];
    for(const file of fotoFiles){
      const path = `${lavoro.id}/${Date.now()}_${file.name}`;
      const {error} = await db.storage.from("rapporti-foto").upload(path, file);
      if(!error){
        const {data:pub} = db.storage.from("rapporti-foto").getPublicUrl(path);
        urls.push(pub.publicUrl);
      }
    }
    setUploading(false);
    setUltimoTesto(testo);
    onAdd(testo, urls);
    setTesto(""); setFotoFiles([]);
  }

  const canSave = (testo.trim()||fotoFiles.length>0) && !uploading;

  return(
    <Modal title={`📋 Rapporto — ${lavoro.titolo}`} onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {(lavoro.rapporti||[]).length===0&&<div style={{color:C.textMuted,fontSize:13}}>Nessun rapporto ancora.</div>}
        {(lavoro.rapporti||[]).map((r,i)=>(
          <div key={i} style={{background:C.bg,borderRadius:11,padding:"12px 14px"}}>
            <div style={{fontSize:11,color:C.accent,fontWeight:700,marginBottom:6}}>{fmtFull(r.data)}</div>
            {r.testo&&<div style={{fontSize:13,color:C.text,lineHeight:1.6,marginBottom:(r.foto||[]).length?8:0}}>{r.testo}</div>}
            {(r.foto||[]).length>0&&(
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {r.foto.map((url,fi)=>(
                  <img key={fi} src={url} alt="foto rapporto" onClick={()=>setLightbox(url)}
                    style={{width:64,height:64,objectFit:"cover",borderRadius:8,cursor:"pointer"}}/>
                ))}
              </div>
            )}
          </div>
        ))}
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
          <Textarea label="Nuovo rapporto" value={testo} onChange={setTesto} rows={4} placeholder="Avanzamento lavori, problemi, materiali…"/>

          {fotoFiles.length>0&&(
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
              {fotoFiles.map((f,i)=>(
                <div key={i} style={{position:"relative",width:64,height:64}}>
                  <img src={URL.createObjectURL(f)} alt="" style={{width:64,height:64,objectFit:"cover",borderRadius:8}}/>
                  <button onClick={()=>rimuoviFileLocale(i)} style={{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:C.red,color:"#fff",border:"none",cursor:"pointer",fontSize:12,lineHeight:1}}>×</button>
                </div>
              ))}
            </div>
          )}

          <div style={{display:"flex",gap:10,marginTop:12}}>
            <label style={{display:"flex",alignItems:"center",gap:6,background:C.bg,borderRadius:9,padding:"8px 14px",fontSize:12.5,fontWeight:600,color:C.textSub,cursor:fotoFiles.length>=5?"not-allowed":"pointer",opacity:fotoFiles.length>=5?0.5:1}}>
              📷 Scatta foto
              <input type="file" accept="image/*" capture="environment" style={{display:"none"}} disabled={fotoFiles.length>=5}
                onChange={e=>{if(e.target.files.length)aggiungiFileLocali(e.target.files); e.target.value="";}}/>
            </label>
            <label style={{display:"flex",alignItems:"center",gap:6,background:C.bg,borderRadius:9,padding:"8px 14px",fontSize:12.5,fontWeight:600,color:C.textSub,cursor:fotoFiles.length>=5?"not-allowed":"pointer",opacity:fotoFiles.length>=5?0.5:1}}>
              🖼️ Da galleria
              <input type="file" accept="image/*" multiple style={{display:"none"}} disabled={fotoFiles.length>=5}
                onChange={e=>{if(e.target.files.length)aggiungiFileLocali(e.target.files); e.target.value="";}}/>
            </label>
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",alignItems:"center",marginTop:14,flexWrap:"wrap"}}>
            {ultimoTesto && lavoro.telefono_cliente && (
              <WhatsAppBtn tel={lavoro.telefono_cliente} label="Avvisa cliente" testo={`Ciao, aggiornamento sui lavori a ${lavoro.indirizzo||lavoro.titolo}:\n${ultimoTesto}`}/>
            )}
            <Btn variant="ghost" onClick={onClose}>Chiudi</Btn>
            <Btn disabled={!canSave} onClick={invia}>{uploading?"Caricamento…":"+ Aggiungi"}</Btn>
          </div>
        </div>
      </div>
      {lightbox&&(
        <div onClick={()=>setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(20,22,28,0.85)",zIndex:10001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <img src={lightbox} alt="" style={{maxWidth:"92vw",maxHeight:"88vh",borderRadius:14}}/>
        </div>
      )}
    </Modal>
  );
}

// ── LAVORO FORM ───────────────────────────────────────────────────────────────
function LavoroForm({form,setForm,onSave,onClose,operai,rivendite,isNew,tuttiLavori=[],db}){
  const [tab, setTab] = useState("info");
  const toggleOp=(id)=>setForm(f=>{
    const rimosso = f.operai_ids?.includes(id);
    return {...f, operai_ids: rimosso?f.operai_ids.filter(x=>x!==id):[...(f.operai_ids||[]),id],
      responsabile_id: f.responsabile_id};
  });

  // Import automatico: se il lavoro ha data_inizio/data_fine ma giorni_lavoro vuoto,
  // offriamo di importare tutte le date del range
  const giorni = form.giorni_lavoro || [];
  const giorniDettaglio = form.giorni_dettaglio || {};
  const hasRange = form.data_inizio && form.data_fine;
  const hasDays = giorni.length > 0;

  const importaDateRange = () => {
    const start = new Date(form.data_inizio);
    const end = new Date(form.data_fine);
    const nuoviGiorni = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const str = d.toISOString().split("T")[0];
      if (!giorni.includes(str)) nuoviGiorni.push(str);
    }
    const tuttiGiorni = [...giorni, ...nuoviGiorni].sort();
    setForm(f => ({ ...f, giorni_lavoro: tuttiGiorni }));
  };

  const aggiungiGiorno = (dateStr) => {
    if (giorni.includes(dateStr)) {
      const nd = { ...giorniDettaglio };
      delete nd[dateStr];
      setForm(f => ({ ...f, giorni_lavoro: giorni.filter(d => d !== dateStr), giorni_dettaglio: nd }));
    } else {
      setForm(f => ({
        ...f,
        giorni_lavoro: [...giorni, dateStr].sort(),
        giorni_dettaglio: { ...giorniDettaglio, [dateStr]: [{ descrizione: "", operai_orari: [] }] }
      }));
    }
  };

  // Mini calendario per selezione giorni
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [giornoEspanso, setGiornoEspanso] = useState(null);

  function updOperaioOrario(dataStr, operaioId, field, val){
    setForm(f=>{
      const gd = {...(f.giorni_dettaglio||{})};
      const interventi = gd[dataStr] ? [...gd[dataStr]] : [{descrizione:"",operai_orari:[]}];
      const interv = {...interventi[0]};
      let orari = [...(interv.operai_orari||[])];
      const idx = orari.findIndex(o=>o.operaio_id===operaioId);
      if(idx===-1) orari.push({operaio_id:operaioId, ora_inizio:"08:00", ora_fine:"18:00", [field]:val});
      else orari[idx] = {...orari[idx], [field]:val};
      interv.operai_orari = orari;
      interventi[0] = interv;
      gd[dataStr] = interventi;
      return {...f, giorni_dettaglio:gd};
    });
  }
  function toggleOperaioGiorno(dataStr, operaioId){
    const gdAttuale = form.giorni_dettaglio||{};
    const giaPresente = (gdAttuale[dataStr]||[{}])[0]?.operai_orari?.some(o=>o.operaio_id===operaioId);
    if(!giaPresente){
      const c = conflittoOrario(tuttiLavori.filter(l=>l.id!==form.id && !["Annullato","Rifiutato","Sospeso","Completato"].includes(l.stato)), operaioId, dataStr, "08:00", "18:00");
      if(c && !window.confirm(`Questo operaio il ${fmt(dataStr)} ha già "${c.lavoro.titolo}" (${etichettaIntervento(c.intervento)}). Aggiungerlo per tutta la giornata? Poi puoi sistemare gli orari dal Calendario.`)) return;
    }
    setForm(f=>{
      const gd = {...(f.giorni_dettaglio||{})};
      const interventi = gd[dataStr] ? [...gd[dataStr]] : [{descrizione:"",operai_orari:[]}];
      const interv = {...interventi[0]};
      let orari = [...(interv.operai_orari||[])];
      const idx = orari.findIndex(o=>o.operaio_id===operaioId);
      if(idx===-1) orari.push({operaio_id:operaioId, ora_inizio:"08:00", ora_fine:"18:00"});
      else orari.splice(idx,1);
      interv.operai_orari = orari;
      interventi[0] = interv;
      gd[dataStr] = interventi;
      const tuttiOperaiCoinvolti = new Set(f.operai_ids||[]);
      Object.values(gd).forEach(iv=>{ (iv[0]?.operai_orari||[]).forEach(o=>tuttiOperaiCoinvolti.add(o.operaio_id)); });
      return {...f, giorni_dettaglio:gd, operai_ids:[...tuttiOperaiCoinvolti]};
    });
  }
  function updDescrizioneGiorno(dataStr, val){
    setForm(f=>{
      const gd = {...(f.giorni_dettaglio||{})};
      const interventi = gd[dataStr] ? [...gd[dataStr]] : [{descrizione:"",operai_orari:[]}];
      interventi[0] = {...interventi[0], descrizione:val};
      gd[dataStr] = interventi;
      return {...f, giorni_dettaglio:gd};
    });
  }
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay = (getFirstDayOfMonth(calYear, calMonth) + 6) % 7;
  const mName = new Date(calYear, calMonth, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  const [uploadingPlan, setUploadingPlan] = useState(false);
  async function caricaPlanimetria(files){
    setUploadingPlan(true);
    const urls = [...(form.planimetria_urls||[])];
    for(const file of files){
      const path = `${form.id||"nuovo"}/${Date.now()}_${file.name}`;
      const {error} = await db.storage.from("planimetrie").upload(path, file);
      if(!error){
        const {data:pub} = db.storage.from("planimetrie").getPublicUrl(path);
        urls.push(pub.publicUrl);
      }
    }
    setForm(f=>({...f, planimetria_urls:urls}));
    setUploadingPlan(false);
  }
  function rimuoviPlanimetria(i){
    setForm(f=>({...f, planimetria_urls:(f.planimetria_urls||[]).filter((_,idx)=>idx!==i)}));
  }

  return(
    <Modal title={isNew?"Nuovo Lavoro":"Modifica Lavoro"} onClose={onClose} wide>
      {/* Tab switcher */}
      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`1px solid ${C.border}`}}>
        {[
          {id:"info",      label:"📋 Info"},
          {id:"giorni",    label:`📅 Giornate${hasDays?` (${giorni.length})`:""}`},
          {id:"operai_tab",label:"👷 Operai"},
          {id:"squadre",   label:`🏗️ Squadre${(form.squadre_esterne||[]).length>0?` (${form.squadre_esterne.length})`:""}`},
        ].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"10px 18px",border:"none",borderBottom:tab===t.id?`2px solid ${C.accent}`:"2px solid transparent",background:"none",color:tab===t.id?C.accent:C.textMuted,fontFamily:"inherit",fontWeight:700,fontSize:13,cursor:"pointer"}}>{t.label}</button>
        ))}
      </div>

      {/* Tab INFO */}
      {tab==="info"&&(
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <Input label="Titolo" value={form.titolo||""} onChange={v=>setForm(f=>({...f,titolo:v}))}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="Cliente" value={form.cliente||""} onChange={v=>setForm(f=>({...f,cliente:v}))}/>
          <Input label="Telefono cliente" value={form.telefono_cliente||""} onChange={v=>setForm(f=>({...f,telefono_cliente:v}))}/>
        </div>
        <Input label="Indirizzo" value={form.indirizzo||""} onChange={v=>setForm(f=>({...f,indirizzo:v}))}/>

        {form.cliente && form.cliente.trim().length>=3 && (()=>{
          const storico = tuttiLavori.filter(l=>l.id!==form.id && l.cliente?.toLowerCase().trim()===form.cliente.toLowerCase().trim());
          if(storico.length===0) return null;
          return(
            <div style={{background:C.bg,borderRadius:11,padding:"10px 14px"}}>
              <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>📜 Storico cliente ({storico.length} lavor{storico.length>1?"i":"o"})</div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {storico.slice(0,4).map(l=>(
                  <div key={l.id} style={{fontSize:12,color:C.textSub,display:"flex",justifyContent:"space-between",gap:8}}>
                    <span>{l.titolo}{l.materiale?` (${l.materiale})`:""}</span>
                    <span style={{fontWeight:600,color:C.text}}>{l.preventivo?"€"+l.preventivo:"—"}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Select label="Rivendita" value={form.rivendita_id||""} onChange={v=>setForm(f=>({...f,rivendita_id:parseInt(v)}))} options={rivendite.map(r=>({value:r.id,label:r.nome}))}/>
          <Select label="Stato" value={form.stato||"Preventivo"} onChange={v=>setForm(f=>({...f,stato:v}))} options={["Preventivo","Confermato","Programmato","Da organizzare","Sospeso","Completato","Annullato","Rifiutato"].map(s=>({value:s,label:s}))}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="Data inizio" type="date" value={form.data_inizio||today} onChange={v=>setForm(f=>({...f,data_inizio:v}))}/>
          <Input label="Data fine" type="date" value={form.data_fine||today} onChange={v=>setForm(f=>({...f,data_fine:v}))}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          <Input label="MQ stimati" type="number" value={form.mq_stimati||""} onChange={v=>setForm(f=>({...f,mq_stimati:v}))}/>
          <Input label="MQ effettivi" type="number" value={form.mq_effettivi||""} onChange={v=>setForm(f=>({...f,mq_effettivi:v}))}/>
          <Input label="Preventivo €" type="number" value={form.preventivo||""} onChange={v=>setForm(f=>({...f,preventivo:v}))}/>
        </div>
        <Input label="Materiale" value={form.materiale||""} onChange={v=>setForm(f=>({...f,materiale:v}))} />

        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:12,alignItems:"flex-end"}}>
            <Input label="Km trasferta (a/r)" type="number" value={form.km_trasferta||""} onChange={v=>setForm(f=>({...f,km_trasferta:v}))} placeholder="0"/>
            {parseFloat(form.km_trasferta)>0 && (
              <div style={{display:"flex",alignItems:"center",gap:10,paddingBottom:2}}>
                <span style={{fontSize:12.5,color:C.textMuted}}>Stima costo: <b style={{color:C.text}}>€{(parseFloat(form.km_trasferta)*0.40).toFixed(2)}</b> (€0,40/km)</span>
                {form.id && db && (
                  <button type="button" onClick={async()=>{
                    await db.from("spese_cantiere").insert([{lavoro_id:form.id, categoria:"trasporto", descrizione:`Trasferta ${form.km_trasferta} km`, importo:parseFloat(form.km_trasferta)*0.40, data:today}]);
                    alert("Spesa trasporto aggiunta in Contabilità.");
                  }} style={{background:C.bg,border:"none",borderRadius:9,color:C.accent,fontSize:11.5,fontWeight:700,cursor:"pointer",padding:"5px 10px"}}>+ Aggiungi come spesa</button>
                )}
              </div>
            )}
          </div>
        </div>

        <Textarea label="Note" value={form.note||""} onChange={v=>setForm(f=>({...f,note:v}))} rows={3}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="Incassato in fattura €" type="number" value={form.importo_fatturato||""} onChange={v=>setForm(f=>({...f,importo_fatturato:v}))} placeholder="0"/>
          <Input label="Incassato diretto €" type="number" value={form.importo_diretto||""} onChange={v=>setForm(f=>({...f,importo_diretto:v}))} placeholder="0"/>
        </div>
        {((parseFloat(form.importo_fatturato)||0)+(parseFloat(form.importo_diretto)||0))>0 && (
          <div style={{fontSize:12,color:C.textMuted,marginTop:-4}}>
            Incassato {euro((parseFloat(form.importo_fatturato)||0)+(parseFloat(form.importo_diretto)||0))} su {euro(parseFloat(form.preventivo)||0)} — "Pagato" si aggiorna da solo al salvataggio.
          </div>
        )}
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <input type="checkbox" id="saldato" checked={!!form.saldato} onChange={e=>setForm(f=>({...f,saldato:e.target.checked}))}/>
          <label htmlFor="saldato" style={{color:C.textSub,fontSize:13,cursor:"pointer"}}>Pagato / saldato</label>
        </div>

        <div style={{background:C.bg,borderRadius:11,padding:"12px 14px"}}>
          <label style={fieldLabel}>💰 Acconto</label>
          <div style={{display:"flex",gap:10,alignItems:"center",marginTop:8,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="checkbox" id="acconto_richiesto" checked={!!form.acconto_richiesto} onChange={e=>setForm(f=>({...f,acconto_richiesto:e.target.checked}))}/>
              <label htmlFor="acconto_richiesto" style={{color:C.textSub,fontSize:12.5,cursor:"pointer"}}>Richiesto</label>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="checkbox" id="acconto_pagato" checked={!!form.acconto_pagato} onChange={e=>setForm(f=>({...f,acconto_pagato:e.target.checked}))}/>
              <label htmlFor="acconto_pagato" style={{color:C.textSub,fontSize:12.5,cursor:"pointer"}}>Pagato</label>
            </div>
            <input type="number" placeholder="Importo €" value={form.acconto_importo||""} onChange={e=>setForm(f=>({...f,acconto_importo:e.target.value}))}
              style={{width:100,background:C.card,border:"none",boxShadow:C.shadowSm,borderRadius:8,padding:"6px 10px",fontSize:12.5,fontFamily:"inherit",outline:"none"}}/>
            {form.telefono_cliente && form.acconto_importo && (
              <WhatsAppBtn tel={form.telefono_cliente} label="Richiedi acconto" testo={`Ciao, per procedere con "${form.titolo}" le chiedo un acconto di €${form.acconto_importo}. Grazie!`}/>
            )}
          </div>
        </div>

        {form.stato==="Completato" && (
          <div style={{background:C.bg,borderRadius:11,padding:"12px 14px"}}>
            <label style={fieldLabel}>⭐ Recensione</label>
            <div style={{display:"flex",gap:10,alignItems:"center",marginTop:8,flexWrap:"wrap"}}>
              <div style={{display:"flex",gap:2}}>
                {[1,2,3,4,5].map(n=>(
                  <span key={n} onClick={()=>setForm(f=>({...f,recensione_stelle:f.recensione_stelle===n?null:n}))} style={{cursor:"pointer",fontSize:18,color:(form.recensione_stelle||0)>=n?C.accent:C.borderLight}}>★</span>
                ))}
              </div>
              {form.telefono_cliente && !form.recensione_stelle && (
                <WhatsAppBtn tel={form.telefono_cliente} label="Chiedi recensione" testo={`Ciao, i lavori a "${form.titolo}" sono conclusi! Se è soddisfatto, le sarei grato per una recensione su Google. Grazie mille!`}/>
              )}
            </div>
          </div>
        )}

        <div>
          <label style={fieldLabel}>Checklist pre-cantiere</label>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
            {[{k:"materiali",l:"Materiali caricati"},{k:"misure",l:"Misure confermate"},{k:"accesso",l:"Accesso concordato col cliente"}].map(item=>(
              <div key={item.k} style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="checkbox" id={`chk_${item.k}`} checked={!!(form.checklist||{})[item.k]}
                  onChange={e=>setForm(f=>({...f, checklist:{...(f.checklist||{}), [item.k]:e.target.checked}}))}/>
                <label htmlFor={`chk_${item.k}`} style={{color:C.textSub,fontSize:13,cursor:"pointer"}}>{item.l}</label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label style={fieldLabel}>Planimetria / misure</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:8}}>
            {(form.planimetria_urls||[]).map((url,i)=>(
              <div key={i} style={{position:"relative",width:64,height:64}}>
                <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="planimetria" style={{width:64,height:64,objectFit:"cover",borderRadius:9}}/></a>
                <button onClick={()=>rimuoviPlanimetria(i)} style={{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:C.red,color:"#fff",border:"none",cursor:"pointer",fontSize:12,lineHeight:1}}>×</button>
              </div>
            ))}
            <label style={{width:64,height:64,display:"flex",alignItems:"center",justifyContent:"center",background:C.bg,borderRadius:9,cursor:"pointer",fontSize:20,color:C.textMuted}}>
              {uploadingPlan?"…":"+"}
              <input type="file" accept="image/*,application/pdf" multiple style={{display:"none"}} disabled={uploadingPlan}
                onChange={e=>{if(e.target.files.length)caricaPlanimetria([...e.target.files]); e.target.value="";}}/>
            </label>
          </div>
        </div>
      </div>
      )}

      {/* Tab GIORNATE */}
      {tab==="giorni"&&(
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {/* Banner import date range */}
        {hasRange && !hasDays && (
          <div style={{background:C.accent+"15",border:`1px solid ${C.accent}44`,borderRadius:14,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.accent}}>📅 Questo lavoro ha date di inizio/fine</div>
              <div style={{fontSize:12,color:C.textMuted,marginTop:2}}>Vuoi importare le date del range come giornate separate?</div>
            </div>
            <Btn small onClick={importaDateRange}>Importa date</Btn>
          </div>
        )}
        {hasRange && hasDays && (
          <div style={{background:C.blue+"15",border:`1px solid ${C.blue}44`,borderRadius:14,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:12,color:C.blue}}>💡 Puoi aggiungere nuove giornate cliccando sul calendario — quelle esistenti restano invariate</div>
            <Btn small variant="ghost" onClick={importaDateRange}>+ Aggiungi range</Btn>
          </div>
        )}

        {/* Mini calendario */}
        <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <span style={{fontSize:13,fontWeight:700,color:C.text,textTransform:"capitalize"}}>{mName}</span>
            <div style={{display:"flex",gap:6}}>
              <Btn small variant="ghost" onClick={()=>{if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1);}}>←</Btn>
              <Btn small variant="ghost" onClick={()=>{if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1);}}>→</Btn>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:6}}>
            {["L","M","M","G","V","S","D"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:10,fontWeight:700,color:C.textMuted,padding:"3px 0"}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {Array.from({length:firstDay}).map((_,i)=><div key={`e-${i}`}/>)}
            {Array.from({length:daysInMonth},(_,i)=>i+1).map(day=>{
              const ds=`${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const sel=giorni.includes(ds);
              const isToday=ds===today;
              return(
                <div key={day} onClick={()=>aggiungiGiorno(ds)} style={{textAlign:"center",padding:"7px 2px",borderRadius:9,fontSize:12,fontWeight:sel?800:400,cursor:"pointer",transition:"all 0.1s",background:sel?C.accent:isToday?C.accentGlow:"transparent",color:sel?"#fff":isToday?C.brand:C.textSub,border:isToday&&!sel?`1px solid ${C.accent}`:"1px solid transparent"}}>{day}</div>
              );
            })}
          </div>
          {giorni.length>0&&(
            <div style={{marginTop:10,fontSize:11,color:C.textMuted,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>📅 {giorni.length} giornate selezionate</span>
              <span onClick={()=>setForm(f=>({...f,giorni_lavoro:[],giorni_dettaglio:{}}))} style={{color:C.red,cursor:"pointer",fontWeight:700}}>× Cancella tutto</span>
            </div>
          )}
        </div>

        {/* Riepilogo giornate */}
        {giorni.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1}}>Giornate programmate — clicca per assegnare operai e orari</div>
            {giorni.map(d=>{
              const interventi=giorniDettaglio[d]||[];
              const orariGiorno = interventi[0]?.operai_orari||[];
              const totOp=orariGiorno.length;
              const isOpen = giornoEspanso===d;
              return(
                <div key={d} style={{background:C.card,borderRadius:11,overflow:"hidden"}}>
                  <div onClick={()=>setGiornoEspanso(isOpen?null:d)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",cursor:"pointer"}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.text,textTransform:"capitalize"}}>{new Date(d+"T00:00:00").toLocaleDateString("it-IT",{weekday:"short",day:"2-digit",month:"short"})}</span>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:11,color:totOp>0?C.accent:C.textMuted,fontWeight:600}}>{totOp>0?`👷 ${totOp}`:"Nessun operaio"}</span>
                      <span style={{fontSize:11,color:C.textMuted}}>{isOpen?"▲":"▼"}</span>
                      <span onClick={e=>{e.stopPropagation();aggiungiGiorno(d);}} style={{cursor:"pointer",color:C.red,fontSize:14,fontWeight:700,padding:"0 4px"}}>×</span>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{padding:"4px 12px 14px",display:"flex",flexDirection:"column",gap:10}}>
                      <Input label="Cosa si fa questo giorno (opzionale)" value={interventi[0]?.descrizione||""} onChange={v=>updDescrizioneGiorno(d,v)} placeholder="Es. Posa cucina, solo mattina"/>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {operai.map(o=>{
                          const assegnato = orariGiorno.find(x=>x.operaio_id===o.id);
                          return(
                            <div key={o.id} style={{background:C.bg,borderRadius:9,padding:"8px 10px"}}>
                              <div onClick={()=>toggleOperaioGiorno(d,o.id)} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                                <div style={{width:16,height:16,borderRadius:5,background:assegnato?o.colore:"transparent",border:`1.5px solid ${assegnato?o.colore:C.borderLight}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                  {assegnato&&<span style={{color:"#fff",fontSize:10,fontWeight:800,lineHeight:1}}>✓</span>}
                                </div>
                                <span style={{fontSize:12.5,fontWeight:600,color:assegnato?C.text:C.textSub}}>{o.nome}</span>
                              </div>
                              {assegnato && (
                                <div style={{display:"flex",gap:8,marginTop:8,paddingLeft:24}}>
                                  <input type="time" value={assegnato.ora_inizio||"08:00"} onChange={e=>updOperaioOrario(d,o.id,"ora_inizio",e.target.value)} style={{background:C.card,border:"none",boxShadow:C.shadowSm,borderRadius:8,color:C.text,padding:"5px 8px",fontSize:12,fontFamily:"inherit",outline:"none"}}/>
                                  <span style={{color:C.textMuted,fontSize:12,alignSelf:"center"}}>→</span>
                                  <input type="time" value={assegnato.ora_fine||"18:00"} onChange={e=>updOperaioOrario(d,o.id,"ora_fine",e.target.value)} style={{background:C.card,border:"none",boxShadow:C.shadowSm,borderRadius:8,color:C.text,padding:"5px 8px",fontSize:12,fontFamily:"inherit",outline:"none"}}/>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Tab OPERAI */}
      {tab==="operai_tab"&&(
      <div>
        <div style={{fontSize:13,color:C.textMuted,marginBottom:14}}>Operai assegnati al lavoro in generale</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {operai.map(o=>{const sel=(form.operai_ids||[]).includes(o.id);return(
            <div key={o.id} onClick={()=>toggleOp(o.id)} style={{padding:"8px 16px",borderRadius:24,cursor:"pointer",fontSize:13,fontWeight:600,background:sel?o.colore+"22":C.bg,border:`2px solid ${sel?o.colore:C.border}`,color:sel?o.colore:C.textSub,transition:"all 0.15s"}}>{sel?"✓ ":""}{o.nome}</div>
          );})}
        </div>

        {(
          <div style={{marginTop:20}}>
            <label style={fieldLabel}>⭐ Responsabile cantiere</label>
            <div style={{fontSize:11.5,color:C.textMuted,marginTop:4}}>Può anche non lavorare sul cantiere: se lo supervisiona soltanto, assegnalo con "👁 Supervisiona".</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
              {operai.map(o=>{
                const isResp = form.responsabile_id===o.id;
                return(
                  <div key={o.id} onClick={()=>setForm(f=>({...f,responsabile_id:isResp?null:o.id}))}
                    style={{padding:"8px 16px",borderRadius:24,cursor:"pointer",fontSize:13,fontWeight:600,background:isResp?C.accent:C.bg,border:`2px solid ${isResp?C.accent:C.border}`,color:isResp?"#fff":C.textSub,transition:"all 0.15s"}}>
                    {isResp?"⭐ ":""}{o.nome}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Tab SQUADRE ESTERNE */}
      {tab==="squadre"&&(()=>{
        const squadre = form.squadre_esterne || [];
        const addSquadra = () => setForm(f=>({...f, squadre_esterne:[...(f.squadre_esterne||[]), {nome:"", lavorazione:"", mq:"", prezzo_mq:"", note:""}]}));
        const updSquadra = (i,field,val) => setForm(f=>{ const s=[...(f.squadre_esterne||[])]; s[i]={...s[i],[field]:val}; return {...f,squadre_esterne:s}; });
        const delSquadra = (i) => setForm(f=>{ const s=[...(f.squadre_esterne||[])]; s.splice(i,1); return {...f,squadre_esterne:s}; });
        const costoTot = squadre.reduce((s,q)=>s+(parseFloat(q.mq)||0)*(parseFloat(q.prezzo_mq)||0),0);
        const ricavo = parseFloat(form.preventivo)||0;
        const margine = ricavo - costoTot;
        const LAVORAZIONI = ["Posa prefinito","Battiscopa","Decking","Lamatura","Rivestimento parete","Autolivellante","Facchinaggio","Altro"];

        // Controlla conflitti: squadra già occupata in altri lavori nelle stesse date
        const conflittiSquadre = [];
        squadre.forEach((sq,i) => {
          if(!sq.nome) return;
          const altriLavori = (tuttiLavori||[]).filter(l => {
            if(l.id === form.id) return false;
            if(["Completato","Annullato","Rifiutato"].includes(l.stato)) return false;
            let sqAltro = l.squadre_esterne;
            if(typeof sqAltro==="string"){try{sqAltro=JSON.parse(sqAltro);}catch{sqAltro=[];}}
            return (sqAltro||[]).some(s2=>s2.nome&&s2.nome.toLowerCase()===sq.nome.toLowerCase());
          });
          if(altriLavori.length>0){
            conflittiSquadre.push({idx:i, nome:sq.nome, lavori:altriLavori});
          }
        });
        return(
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontSize:13,color:C.textMuted}}>Aggiungi le squadre esterne pagate a mq per questo lavoro.</div>

            {/* Warning conflitti */}
            {conflittiSquadre.length>0&&(
              <div style={{background:C.red+"15",border:`1px solid ${C.red}44`,borderRadius:14,padding:"12px 16px"}}>
                <div style={{fontWeight:700,color:C.red,fontSize:13,marginBottom:6}}>⚠️ Squadre già occupate su altri cantieri</div>
                {conflittiSquadre.map((c,i)=>(
                  <div key={i} style={{fontSize:12,color:C.textSub,marginBottom:3}}>
                    <span style={{color:C.red,fontWeight:600}}>🏗️ {c.nome}</span> — già assegnata su: {c.lavori.map(l=>`"${l.titolo}"`).join(", ")}
                  </div>
                ))}
              </div>
            )}
            {(ricavo>0||costoTot>0)&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 14px",textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:C.accent}}>€{ricavo.toLocaleString("it")}</div>
                  <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>Importo lavori</div>
                </div>
                <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 14px",textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:C.red}}>€{costoTot.toLocaleString("it")}</div>
                  <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>Costo squadre</div>
                </div>
                <div style={{background:margine>=0?C.green+"15":C.red+"15",border:`1px solid ${margine>=0?C.green:C.red}44`,borderRadius:14,padding:"12px 14px",textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:margine>=0?C.green:C.red}}>€{margine.toLocaleString("it")}</div>
                  <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>Margine</div>
                </div>
              </div>
            )}

            {/* Lista squadre */}
            {squadre.map((sq,i)=>(
              <div key={i} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <span style={{fontSize:12,fontWeight:700,color:C.accent}}>Squadra {i+1}</span>
                  <div onClick={()=>delSquadra(i)} style={{cursor:"pointer",color:C.red,fontSize:18,lineHeight:1}}>×</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <Input label="Nome / Referente" value={sq.nome} onChange={v=>updSquadra(i,"nome",v)} placeholder="Es. Mario Rossi" />
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    <label style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1}}>Lavorazione</label>
                    <select value={sq.lavorazione} onChange={e=>updSquadra(i,"lavorazione",e.target.value)} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:11,color:C.text,padding:"9px 10px",fontSize:13,fontFamily:"inherit",outline:"none",cursor:"pointer"}}>
                      <option value="">Seleziona…</option>
                      {LAVORAZIONI.map(l=><option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"end"}}>
                  <Input label="Mq assegnati" type="number" value={sq.mq} onChange={v=>updSquadra(i,"mq",v)} placeholder="Es. 45" />
                  <Input label="Prezzo €/mq" type="number" value={sq.prezzo_mq} onChange={v=>updSquadra(i,"prezzo_mq",v)} placeholder="Es. 12" />
                  <div style={{paddingBottom:1}}>
                    <div style={{fontSize:10,color:C.textMuted,marginBottom:4}}>TOTALE</div>
                    <div style={{fontSize:16,fontWeight:800,color:C.red}}>
                      {sq.mq&&sq.prezzo_mq ? `€${((parseFloat(sq.mq)||0)*(parseFloat(sq.prezzo_mq)||0)).toLocaleString("it")}` : "—"}
                    </div>
                  </div>
                </div>
                <div style={{marginTop:10}}>
                  <Input label="Note pagamento" value={sq.note||""} onChange={v=>updSquadra(i,"note",v)} placeholder="Es. Pagato, da pagare, acconto 50%…" />
                </div>
              </div>
            ))}

            <Btn variant="ghost" onClick={addSquadra}>+ Aggiungi squadra esterna</Btn>
          </div>
        );
      })()}

      <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:24,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
        <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
        <Btn onClick={onSave}>💾 Salva</Btn>
      </div>
    </Modal>
  );
}

// ── LISTA LAVORI ──────────────────────────────────────────────────────────────
function ListaLavori({lavori,setLavori,operai,rivendite,statoFilter,db,titoloPagina}){
  const [search,    setSearch]    = useState("");
  const [formModal, setFormModal] = useState(null);   // null | "new" | "edit"
  const [formData,  setFormData]  = useState({});
  const [rapporto,  setRapporto]  = useState(null);
  const [assegna,   setAssegna]   = useState(null);   // lavoro da assegnare
  const [meseArchivio, setMeseArchivio] = useState("tutti");
  const [rifiutaLav, setRifiutaLav] = useState(null);   // preventivo da segnare come rifiutato

  const empty = {titolo:"",rivendita_id:rivendite[0]?.id||null,operai_ids:[],cliente:"",telefono_cliente:"",indirizzo:"",data_inizio:today,data_fine:today,mq_stimati:"",mq_effettivi:"",materiale:"",stato:statoFilter==="arch"?"Completato":statoFilter,preventivo:"",saldato:false,note:"",rapporti:[]};

  const mesiArchivio = statoFilter==="arch"
    ? Array.from(new Set(lavori.filter(l=>["Completato","Annullato"].includes(l.stato)).map(l=>(l.data_fine||l.data_inizio||"").slice(0,7)).filter(Boolean))).sort().reverse()
    : [];

  const filtered = lavori
    .filter(l => statoFilter==="arch" ? ["Completato","Annullato"].includes(l.stato) : l.stato===statoFilter)
    .filter(l => statoFilter!=="arch" || meseArchivio==="tutti" || (l.data_fine||l.data_inizio||"").startsWith(meseArchivio))
    .filter(l => { const q=search.toLowerCase(); return !q||l.titolo?.toLowerCase().includes(q)||l.cliente?.toLowerCase().includes(q)||l.indirizzo?.toLowerCase().includes(q); });

  async function salva(){
    const {id:_id, ...rest} = formData;
    let d={...rest,preventivo:parseFloat(formData.preventivo)||0,mq_stimati:parseFloat(formData.mq_stimati)||0,mq_effettivi:formData.mq_effettivi?parseFloat(formData.mq_effettivi):null,giorni_lavoro:formData.giorni_lavoro||[],giorni_dettaglio:formData.giorni_dettaglio||{},squadre_esterne:formData.squadre_esterne||[]};
    const haIncassi = ["importo_fatturato","importo_diretto"].some(k=>k in formData);
    if(haIncassi) d = {...d, ...payloadIncassi(d, formData.importo_fatturato, formData.importo_diretto)};
    const erroreMsg = (e)=>"Errore nel salvataggio: "+e.message+(/column|schema cache/i.test(e.message)?"\n\nSe riguarda Fatturato/Diretto, in Supabase → SQL Editor esegui:\n\n"+SQL_INCASSI:"");
    if(formModal==="new"){ const{data:n,error}=await db.from("lavori").insert([d]).select().single(); if(error){alert(erroreMsg(error));return;} if(n) setLavori(ls=>[...ls,n]); }
    else { const {error}=await db.from("lavori").update(d).eq("id",formData.id); if(error){alert(erroreMsg(error));return;} setLavori(ls=>ls.map(l=>l.id===formData.id?{...l,...d,id:formData.id}:l)); }
    setFormModal(null);
  }
  async function elimina(id){ if(!window.confirm("Eliminare?")) return; await db.from("lavori").delete().eq("id",id); setLavori(ls=>ls.filter(l=>l.id!==id)); }
  async function cambiaStato(id,stato){ await db.from("lavori").update({stato}).eq("id",id); setLavori(ls=>ls.map(l=>l.id===id?{...l,stato}:l)); }
  async function salvaRifiuto(l,motivo,nota){
    const ok = await segnaPreventivoRifiutato(db,l,motivo,nota,setLavori);
    if(ok) setRifiutaLav(null);
  }
  async function salvaAssegna(lavoro,data){
    const ok = await salvaAssegnazione(db, lavoro, data, setLavori);
    if(ok) setAssegna(null);
  }
  async function aggiungiRapporto(lavoro,testo,foto=[]){
    const nr=[...(lavoro.rapporti||[]),{data:today,testo,foto}];
    await db.from("lavori").update({rapporti:nr}).eq("id",lavoro.id);
    setLavori(ls=>ls.map(l=>l.id===lavoro.id?{...l,rapporti:nr}:l));
    setRapporto(r=>r?{...r,rapporti:nr}:null);
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>{titoloPagina}</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{filtered.length} lavori</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {statoFilter==="arch"&&mesiArchivio.length>0&&(
            <select value={meseArchivio} onChange={e=>setMeseArchivio(e.target.value)} style={{background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"9px 14px",fontSize:13,fontWeight:600,color:C.text,fontFamily:"inherit",cursor:"pointer"}}>
              <option value="tutti">Tutti i mesi</option>
              {mesiArchivio.map(m=><option key={m} value={m}>{new Date(m+"-01").toLocaleDateString("it-IT",{month:"long",year:"numeric"})}</option>)}
            </select>
          )}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Cerca…" style={{background:C.card,boxShadow:C.shadowSm,borderRadius:11,color:C.text,padding:"8px 14px",fontSize:13,fontFamily:"inherit",outline:"none",width:200}}/>
          {statoFilter!=="arch"&&<Btn onClick={()=>{setFormData({...empty});setFormModal("new");}}>+ Nuovo Lavoro</Btn>}
        </div>
      </div>

      {statoFilter==="Preventivo" && (()=>{
        const nonAnnullati = lavori.filter(l=>l.stato!=="Annullato");
        const aperti = nonAnnullati.filter(l=>l.stato==="Preventivo").length;
        const rifiutati = nonAnnullati.filter(l=>l.stato==="Rifiutato").length;
        const convertiti = nonAnnullati.filter(l=>!["Preventivo","Rifiutato"].includes(l.stato)).length;
        const totale = aperti+convertiti+rifiutati;
        const tasso = totale>0 ? Math.round(convertiti/totale*100) : null;
        if(tasso===null) return null;
        return(
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"14px 18px",display:"flex",alignItems:"center",gap:14}}>
            <span style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.accent}}>{tasso}%</span>
            <span style={{fontSize:12.5,color:C.textMuted}}>tasso di conversione stimato — {convertiti} lavori confermati su {totale} preventivati{rifiutati>0?` · ${rifiutati} rifiutati`:""} (esclusi annullati)</span>
          </div>
        );
      })()}

      {statoFilter==="Preventivo" && (()=>{
        const fermi = lavori.filter(l=>l.stato==="Preventivo" && l.data_inizio && Math.floor((new Date(today)-new Date(l.data_inizio))/86400000) > 7);
        if(fermi.length===0) return null;
        return(
          <div style={{background:C.orange+"10",borderRadius:16,padding:"14px 18px",display:"flex",flexDirection:"column",gap:8}}>
            <div style={{fontSize:12.5,color:C.orange,fontWeight:700}}>⏳ {fermi.length} preventivo{fermi.length>1?"i":""} fermo{fermi.length>1?"i":""} da oltre 7 giorni senza risposta</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {fermi.map(l=>{
                const giorni = Math.floor((new Date(today)-new Date(l.data_inizio))/86400000);
                return(
                  <div key={l.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:12.5,gap:10,flexWrap:"wrap"}}>
                    <span style={{color:C.text}}>{l.titolo} <span style={{color:C.textMuted}}>· {giorni} giorni fa</span></span>
                    {l.telefono_cliente && <WhatsAppBtn tel={l.telefono_cliente} label="Follow-up" testo={`Ciao, volevo sapere se ha avuto modo di valutare il preventivo per "${l.titolo}". Resto a disposizione per qualsiasi chiarimento.`}/>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {filtered.length===0&&<div style={{color:C.textMuted,textAlign:"center",padding:60,background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>Nessun lavoro</div>}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.map(l=>{
          const riv=rivendite.find(r=>r.id===l.rivendita_id);
          const ops=(l.operai_ids||[]).map(id=>operai.find(o=>o.id===id)).filter(Boolean);
          const col=STATI_COLOR[l.stato];
          return(
            <div key={l.id} style={{background:C.card,boxShadow:C.shadowSm,borderLeft:`3px solid ${col}`,borderRadius:16,padding:"16px 20px"}}>
              <div style={{display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
                    <span style={{fontWeight:800,fontSize:15,color:C.text}}>{l.titolo}</span>
                    <Badge label={l.stato} color={col}/>
                    {l.saldato&&<Badge label="✓ Pagato" color={C.green}/>}
                  </div>
                  <div style={{fontSize:13,color:C.textMuted,marginBottom:4}}>👤 {l.cliente} · 📍 {l.indirizzo}</div>
                  <div style={{fontSize:12,color:C.textSub}}>🏪 {riv?.nome} · 📅 {fmt(l.data_inizio)} → {fmt(l.data_fine)} · 📐 {l.mq_stimati} mq · 🪵 {l.materiale}{l.fascia_oraria?` · 🕐 ${l.fascia_oraria}`:""}</div>
                  <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                    {(()=>{ const resp = operai.find(o=>o.id===l.responsabile_id); return resp && !ops.some(o=>o.id===resp.id) ? <span style={{fontSize:11,background:resp.colore+"22",color:resp.colore,border:`1px solid ${resp.colore}33`,borderRadius:24,padding:"2px 8px",fontWeight:600}}>⭐ {resp.nome.split(" ")[0]}</span> : null; })()}
                    {ops.map(o=><span key={o.id} style={{fontSize:11,background:o.colore+"22",color:o.colore,border:`1px solid ${o.colore}33`,borderRadius:24,padding:"2px 8px",fontWeight:600}}>{l.responsabile_id===o.id?"⭐":"👷"} {o.nome.split(" ")[0]}</span>)}
                    {(l.rapporti||[]).length>0&&<span style={{fontSize:11,color:C.textMuted}}>📋 {l.rapporti.length} rapporti</span>}
                    {!["Completato","Annullato"].includes(l.stato) && (()=>{
                      const chk = l.checklist||{};
                      const done = ["materiali","misure","accesso"].filter(k=>chk[k]).length;
                      if(done===0) return null;
                      return <span style={{fontSize:11,color:done===3?C.green:C.orange,fontWeight:600}}>{done===3?"✓":"◐"} checklist {done}/3</span>;
                    })()}
                    {l.stato==="Confermato" && l.acconto_richiesto && (
                      <span style={{fontSize:11,fontWeight:600,color:l.acconto_pagato?C.green:C.orange}}>{l.acconto_pagato?"✓ Acconto pagato":"◐ Acconto da incassare"}</span>
                    )}
                    {l.stato==="Completato" && l.recensione_stelle>0 && (
                      <span style={{fontSize:11,color:C.accent}}>{"★".repeat(l.recensione_stelle)}{"☆".repeat(5-l.recensione_stelle)}</span>
                    )}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontWeight:800,fontSize:18,color:C.accent}}>€{parseFloat(l.preventivo||0).toLocaleString("it")}</div>
                  <div style={{fontSize:11,color:C.textMuted,marginBottom:10}}>preventivo</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      <Btn small variant="ghost" onClick={()=>setRapporto(l)}>📋 Rapporto</Btn>
                      {statoFilter!=="arch"&&<>
                        <Btn small variant="ghost" onClick={()=>setAssegna(l)} style={{background:C.accentGlow,color:C.accent,border:`1px solid ${C.accent}44`}}>📅 Assegna</Btn>
                        <Btn small variant="ghost" onClick={()=>{
                          let sq = l.squadre_esterne;
                          if(typeof sq === "string"){try{sq=JSON.parse(sq);}catch{sq=[];}}
                          setFormData({...l, squadre_esterne: Array.isArray(sq)?sq:[], giorni_lavoro: l.giorni_lavoro||[], giorni_dettaglio: l.giorni_dettaglio||{}});
                          setFormModal("edit");
                        }}>✏️ Modifica</Btn>
                      </>}
                      {statoFilter==="arch"&&<Btn small variant="ghost" onClick={()=>cambiaStato(l.id,"Confermato")}>↩️ Ripristina</Btn>}
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      {statoFilter==="Da organizzare"&&<Btn small variant="success" onClick={()=>cambiaStato(l.id,"Completato")}>✓ Completa</Btn>}
                      {statoFilter==="Confermato"&&<Btn small variant="ghost" onClick={()=>cambiaStato(l.id,"Da organizzare")}>▶ Avvia</Btn>}
                      {statoFilter==="Preventivo"&&<Btn small variant="success" onClick={()=>cambiaStato(l.id,"Confermato")}>✓ Conferma</Btn>}
                      {statoFilter==="Preventivo"&&<Btn small variant="ghost" onClick={()=>setRifiutaLav(l)}>✕ Rifiutato</Btn>}
                      {statoFilter!=="arch"&&<Btn small variant="danger" onClick={()=>elimina(l.id)}>🗑 Elimina</Btn>}
                      {statoFilter==="arch"&&<Btn small variant="danger" onClick={()=>elimina(l.id)}>🗑 Elimina</Btn>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {formModal&&<LavoroForm form={formData} setForm={setFormData} onSave={salva} onClose={()=>setFormModal(null)} operai={operai} rivendite={rivendite} isNew={formModal==="new"} tuttiLavori={lavori} db={db}/>}
      {rapporto&&<RapportoModal lavoro={rapporto} onClose={()=>setRapporto(null)} onAdd={(t,f)=>aggiungiRapporto(rapporto,t,f)} db={db}/>}
      {assegna&&<QuickAssign lavoro={assegna} operai={operai} lavori={lavori} onClose={()=>setAssegna(null)} onSave={(d)=>salvaAssegna(assegna,d)} db={db}/>}
      {rifiutaLav&&<RifiutoModal lavoro={rifiutaLav} onClose={()=>setRifiutaLav(null)} onSave={(m,n)=>salvaRifiuto(rifiutaLav,m,n)}/>}
    </div>
  );
}

// ── PREVENTIVI RIFIUTATI (ARCHIVIO) ──────────────────────────────────────────
// Segna un preventivo come rifiutato salvando motivo e data.
// Se le colonne motivo_rifiuto / nota_rifiuto / data_rifiuto non esistono ancora su Supabase,
// salva comunque lo stato e scrive il motivo nelle note, così nulla va perso.
async function segnaPreventivoRifiutato(db,l,motivo,nota,setLavori){
  const extra = {motivo_rifiuto:motivo||"Altro", nota_rifiuto:nota||null, data_rifiuto:today};
  let {error} = await db.from("lavori").update({stato:"Rifiutato",...extra}).eq("id",l.id);
  if(error && /column|schema cache/i.test(error.message)){
    const riga = `[Rifiutato ${new Date().toLocaleDateString("it-IT")} — ${motivo||"Altro"}${nota?`: ${nota}`:""}]`;
    const note = [l.note||"", riga].filter(Boolean).join("\n\n");
    ({error} = await db.from("lavori").update({stato:"Rifiutato", note}).eq("id",l.id));
    if(!error){ setLavori(ls=>ls.map(x=>x.id===l.id?{...x,stato:"Rifiutato",note}:x)); return true; }
  }
  if(error){
    alert("Errore nel segnare il preventivo come rifiutato: "+error.message+(/check constraint/i.test(error.message)?"\n\nIl database non accetta ancora lo stato \"Rifiutato\": va aggiornato il vincolo sulla colonna stato in Supabase.":""));
    return false;
  }
  setLavori(ls=>ls.map(x=>x.id===l.id?{...x,stato:"Rifiutato",...extra}:x));
  return true;
}

// Motivo del rifiuto: dalla colonna dedicata, oppure (fallback) dalle note.
function motivoRifiuto(l){
  if(l.motivo_rifiuto) return l.motivo_rifiuto;
  const m = (l.note||"").match(/\[Rifiutato [^—\]]*— ([^:\]]+)/);
  return m ? m[1].trim() : "Non specificato";
}
function dataRifiuto(l){
  if(l.data_rifiuto) return l.data_rifiuto;
  const m = (l.note||"").match(/\[Rifiutato (\d{2})\/(\d{2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (l.data_inizio||"");
}

function RifiutoModal({lavoro,onClose,onSave}){
  const [motivo,setMotivo] = useState(MOTIVI_RIFIUTO[0]);
  const [nota,setNota] = useState("");
  const [saving,setSaving] = useState(false);
  return(
    <Modal title="Preventivo rifiutato" onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{fontSize:13,color:C.textMuted}}>{lavoro.titolo} · {lavoro.cliente} · €{parseFloat(lavoro.preventivo||0).toLocaleString("it")}</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {MOTIVI_RIFIUTO.map(m=>(
            <button key={m} onClick={()=>setMotivo(m)} style={{border:`1px solid ${motivo===m?STATI_COLOR.Rifiutato:C.border}`,background:motivo===m?STATI_COLOR.Rifiutato+"18":"transparent",color:motivo===m?STATI_COLOR.Rifiutato:C.textSub,borderRadius:20,padding:"7px 13px",fontSize:12.5,fontWeight:600,cursor:"pointer"}}>{m}</button>
          ))}
        </div>
        <Textarea label="Nota (facoltativa)" value={nota} onChange={setNota} rows={2} placeholder="Es. ha preso un preventivo a 28 €/mq"/>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
          <Btn disabled={saving} onClick={async()=>{setSaving(true);await onSave(motivo,nota.trim());setSaving(false);}}>Sposta in archivio</Btn>
        </div>
      </div>
    </Modal>
  );
}

function PreventiviRifiutati({lavori,setLavori,db}){
  const [search,setSearch] = useState("");
  const [motivoF,setMotivoF] = useState("tutti");
  const rifiutati = lavori.filter(l=>l.stato==="Rifiutato");
  const motiviPresenti = Array.from(new Set(rifiutati.map(motivoRifiuto)));
  const lista = rifiutati
    .filter(l=>motivoF==="tutti"||motivoRifiuto(l)===motivoF)
    .filter(l=>{ const q=search.toLowerCase(); return !q||l.titolo?.toLowerCase().includes(q)||l.cliente?.toLowerCase().includes(q)||l.indirizzo?.toLowerCase().includes(q); })
    .sort((a,b)=>dataRifiuto(b).localeCompare(dataRifiuto(a)));

  const valorePerso = rifiutati.reduce((s,l)=>s+(parseFloat(l.preventivo)||0),0);
  const conteggio = {};
  rifiutati.forEach(l=>{ const m=motivoRifiuto(l); conteggio[m]=(conteggio[m]||0)+1; });
  const motiviOrdinati = Object.entries(conteggio).sort((a,b)=>b[1]-a[1]);

  async function riapri(l){
    let {error} = await db.from("lavori").update({stato:"Preventivo",motivo_rifiuto:null,nota_rifiuto:null,data_rifiuto:null}).eq("id",l.id);
    if(error && /column|schema cache/i.test(error.message)) ({error} = await db.from("lavori").update({stato:"Preventivo"}).eq("id",l.id));
    if(error){ alert("Errore nel riaprire il preventivo: "+error.message); return; }
    setLavori(ls=>ls.map(x=>x.id===l.id?{...x,stato:"Preventivo",motivo_rifiuto:null,nota_rifiuto:null,data_rifiuto:null}:x));
  }
  async function duplica(l){
    const {id:_id, created_at:_c, motivo_rifiuto:_m, nota_rifiuto:_n, data_rifiuto:_d, ...rest} = l;
    const note = (l.note||"").replace(/\n*\[Rifiutato [^\]]*\]/g,"").trim();
    const copia = {...rest, titolo:`${l.titolo} (nuova versione)`, stato:"Preventivo", data_inizio:today, data_fine:today,
      note, saldato:false, rapporti:[], operai_ids:[], giorni_lavoro:[], giorni_dettaglio:{}};
    const {data:n, error} = await db.from("lavori").insert([copia]).select().single();
    if(error){ alert("Errore nel duplicare il preventivo: "+error.message); return; }
    if(n){ setLavori(ls=>[n,...ls]); alert("Creato un nuovo preventivo in \"Preventivi inviati\". Aprilo da lì per modificare importi e voci."); }
  }
  async function elimina(l){
    if(!window.confirm(`Eliminare definitivamente "${l.titolo}"?`)) return;
    const {error} = await db.from("lavori").delete().eq("id",l.id);
    if(error){ alert("Errore: "+error.message); return; }
    setLavori(ls=>ls.filter(x=>x.id!==l.id));
  }

  const col = STATI_COLOR.Rifiutato;
  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>Preventivi rifiutati</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{lista.length} preventivi in archivio</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          {motiviPresenti.length>1&&(
            <select value={motivoF} onChange={e=>setMotivoF(e.target.value)} style={{background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"9px 14px",fontSize:13,fontWeight:600,color:C.text,cursor:"pointer"}}>
              <option value="tutti">Tutti i motivi</option>
              {motiviPresenti.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Cerca…" style={{background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,color:C.text,padding:"8px 14px",fontSize:13,outline:"none",width:200}}/>
        </div>
      </div>

      {rifiutati.length>0&&(
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px 20px",display:"flex",gap:28,flexWrap:"wrap",alignItems:"flex-start"}}>
          <div>
            <div style={{fontFamily:C.fontBrand,fontSize:22,fontWeight:800,color:C.text,letterSpacing:-0.5}}>{rifiutati.length}</div>
            <div style={{fontSize:12,color:C.textMuted}}>rifiutati</div>
          </div>
          <div>
            <div style={{fontFamily:C.fontBrand,fontSize:22,fontWeight:800,color:col,letterSpacing:-0.5}}>€{Math.round(valorePerso).toLocaleString("it")}</div>
            <div style={{fontSize:12,color:C.textMuted}}>valore non acquisito</div>
          </div>
          <div style={{flex:1,minWidth:220,display:"flex",flexDirection:"column",gap:6}}>
            {motiviOrdinati.slice(0,4).map(([m,n])=>(
              <div key={m} style={{display:"flex",alignItems:"center",gap:10,fontSize:12.5}}>
                <span style={{width:150,color:C.textSub,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m}</span>
                <div style={{flex:1,height:6,background:C.border,borderRadius:4,overflow:"hidden"}}><div style={{width:`${n/rifiutati.length*100}%`,height:"100%",background:col}}/></div>
                <span style={{width:24,textAlign:"right",color:C.textMuted,fontWeight:600}}>{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {lista.length===0&&<div style={{color:C.textMuted,textAlign:"center",padding:60,background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>{rifiutati.length===0?"Nessun preventivo rifiutato. Dalla pagina Preventivi inviati usa \"✕ Rifiutato\" per archiviarne uno.":"Nessun risultato"}</div>}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {lista.map(l=>(
          <div key={l.id} style={{background:C.card,boxShadow:C.shadowSm,borderLeft:`3px solid ${col}`,borderRadius:16,padding:"16px 20px"}}>
            <div style={{display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:200}}>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
                  <span style={{fontWeight:800,fontSize:15,color:C.text}}>{l.titolo}</span>
                  <Badge label={motivoRifiuto(l)} color={col}/>
                </div>
                <div style={{fontSize:13,color:C.textMuted,marginBottom:4}}>👤 {l.cliente}{l.indirizzo?` · 📍 ${l.indirizzo}`:""}</div>
                <div style={{fontSize:12,color:C.textSub}}>Rifiutato il {fmtFull(dataRifiuto(l))}{l.mq_stimati?` · 📐 ${l.mq_stimati} mq`:""}{l.materiale?` · 🪵 ${l.materiale}`:""}</div>
                {l.nota_rifiuto&&<div style={{fontSize:12.5,color:C.textSub,marginTop:6,fontStyle:"italic"}}>"{l.nota_rifiuto}"</div>}
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontWeight:800,fontSize:18,color:C.textMuted,textDecoration:"line-through"}}>€{parseFloat(l.preventivo||0).toLocaleString("it")}</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end",marginTop:10}}>
                  {l.telefono_cliente&&<WhatsAppBtn tel={l.telefono_cliente} label="Ricontatta" testo={`Ciao, ci eravamo sentiti per "${l.titolo}". Se fosse ancora interessato, possiamo rivedere insieme il preventivo.`}/>}
                  <Btn small variant="ghost" onClick={()=>riapri(l)}>↩️ Riapri</Btn>
                  <Btn small variant="ghost" onClick={()=>duplica(l)}>⧉ Duplica</Btn>
                  <Btn small variant="danger" onClick={()=>elimina(l)}>🗑</Btn>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── OPERAI ────────────────────────────────────────────────────────────────────
function Operai({operai,setOperai,lavori,db}){
  const [modal,setModal]=useState(false);
  const ef={nome:"",telefono:"",specialita:"",disponibile:true,colore:C.accent,costo_ora:"",competenze:[]};
  const [form,setForm]=useState(ef);
  const cols=[C.accent,C.blue,C.green,C.purple,C.orange,C.red,"#e05cc8"];
  const COMPETENZE_DISP = ["Posa prefinito","Posa massello","Levigatura","Verniciatura","Decking","Scale","Battiscopa","Restauro","Autolivellante"];
  function toggleCompetenza(c){ setForm(f=>{ const cur=f.competenze||[]; return {...f, competenze: cur.includes(c)?cur.filter(x=>x!==c):[...cur,c]}; }); }
  async function salva(){
    const {id, ...rest} = form;
    const d={...rest, costo_ora: form.costo_ora?parseFloat(form.costo_ora):null};
    if(id){
      const {error} = await db.from("operai").update(d).eq("id",id);
      if(error){ window.alert("Errore nel salvataggio: "+error.message); return; }
      setOperai(os=>os.map(o=>o.id===id?{...d,id}:o));
    } else {
      const {data:n,error} = await db.from("operai").insert([d]).select().single();
      if(error){ window.alert("Errore nel salvataggio: "+error.message); return; }
      if(n) setOperai(os=>[...os,n]);
    }
    setModal(false);
  }
  async function del(id){ if(!window.confirm("Eliminare?")) return; await db.from("operai").delete().eq("id",id); setOperai(os=>os.filter(o=>o.id!==id)); }
  // ── Pannello orari di lavoro ──
  const [orari, salvaOrari] = useOrariLavoro(db);
  const [bozzaOrari, setBozzaOrari] = useState(orari);
  const [orariSalvati, setOrariSalvati] = useState(false);
  useEffect(()=>{ setBozzaOrari(orari); },[orari]);
  const orariModificati = JSON.stringify(bozzaOrari)!==JSON.stringify(orari);
  const setOra = (gruppo, campo, v)=>{ setOrariSalvati(false); setBozzaOrari(b=>({...b,[gruppo]:{...b[gruppo],[campo]:v}})); };
  const durata = (a,b)=>{ const m=(t)=>{const [h,mm]=t.split(":").map(Number);return h*60+mm;}; const d=m(b)-m(a); return d>0?`${Math.floor(d/60)}h${d%60?String(d%60).padStart(2,"0"):""}`:"—"; };
  async function confermaOrari(){
    const {feriali, sabato} = bozzaOrari;
    if(!(feriali.inizio<feriali.fine) || (sabato.attivo && !(sabato.inizio<sabato.fine))){ alert("L'orario di fine deve essere dopo l'inizio."); return; }
    if(await salvaOrari(bozzaOrari)) setOrariSalvati(true);
  }
  const campoOra = (gruppo, campo, disab)=>(
    <input type="time" step={900} value={bozzaOrari[gruppo][campo]} disabled={disab} onChange={e=>setOra(gruppo,campo,e.target.value)}
      style={{height:40,border:`1px solid ${C.border}`,borderRadius:10,padding:"0 10px",fontFamily:"inherit",fontSize:15,fontWeight:600,background:disab?C.bg:C.card,color:disab?C.textMuted:C.text,fontVariantNumeric:"tabular-nums",minWidth:0}}/>
  );
  const rigaOrario = (titolo, gruppo, extra, disab)=>(
    <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) auto",alignItems:"center",gap:12,padding:"14px 0",borderTop:gruppo==="sabato"?`1px solid ${C.border}`:"none"}}>
      <div style={{minWidth:0}}>
        <div style={{fontSize:14,fontWeight:700,color:C.text}}>{titolo}</div>
        <div style={{fontSize:12,color:C.textMuted,marginTop:2}}>{disab?"Riposo":durata(bozzaOrari[gruppo].inizio,bozzaOrari[gruppo].fine)}</div>
        {extra}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        {campoOra(gruppo,"inizio",disab)}<span style={{color:C.textMuted}}>–</span>{campoOra(gruppo,"fine",disab)}
      </div>
    </div>
  );
  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:"16px 18px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:0.8,color:C.textSub}}>ORARI DI LAVORO</div>
          <div style={{fontSize:11.5,color:C.textMuted}}>Usati nel calendario per proporre gli orari e calcolare chi è libero</div>
        </div>
        {rigaOrario("Lunedì – Venerdì","feriali",null,false)}
        {rigaOrario("Sabato","sabato",
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.textSub,marginTop:6,cursor:"pointer"}}>
            <input type="checkbox" checked={!!bozzaOrari.sabato.attivo} onChange={e=>setOra("sabato","attivo",e.target.checked)}/> Si lavora il sabato
          </label>, !bozzaOrari.sabato.attivo)}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
          <span style={{fontSize:12,color:C.textMuted}}>Domenica: riposo</span>
          {orariModificati ? <Btn small onClick={confermaOrari}>Salva orari</Btn>
            : <span style={{fontSize:12,fontWeight:600,color:orariSalvati?C.green:C.textMuted}}>{orariSalvati?"✓ Salvati":""}</span>}
        </div>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div><div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>Gestione Operai</div><div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{operai.length} operai</div></div>
        <Btn onClick={()=>{setForm({...ef});setModal(true);}}>+ Nuovo Operaio</Btn>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
        {operai.map(o=>{
          const lav=lavori.filter(l=>l.operai_ids?.includes(o.id));
          return(
            <div key={o.id} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px",borderTop:`3px solid ${o.colore}`}}>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
                <div style={{width:48,height:48,borderRadius:"50%",background:o.colore+"22",border:`2px solid ${o.colore}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:900,color:o.colore}}>{o.nome[0]}</div>
                <div style={{flex:1}}><div style={{fontWeight:800,color:C.text,fontSize:15}}>{o.nome}</div><div style={{fontSize:12,color:C.textMuted}}>{o.specialita}</div></div>
                <div style={{display:"flex",gap:4}}>
                  <Btn small variant="ghost" onClick={()=>{setForm({...o});setModal(true);}}>✏️</Btn>
                  <Btn small variant="danger" onClick={()=>del(o.id)}>🗑</Btn>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}><div style={{width:7,height:7,borderRadius:"50%",background:o.disponibile?C.green:C.red}}/><span style={{fontSize:12,color:o.disponibile?C.green:C.red,fontWeight:600}}>{o.disponibile?"Disponibile":"Non disponibile"}</span></div>
              <div style={{fontSize:13,color:C.textMuted,marginBottom:10}}>📞 {o.telefono}{o.costo_ora?` · €${o.costo_ora}/h`:""}</div>
              {(o.competenze||[]).length>0&&(
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:12}}>
                  {o.competenze.map(c=><span key={c} style={{fontSize:10.5,fontWeight:600,background:C.bg,color:C.textSub,borderRadius:20,padding:"3px 9px"}}>{c}</span>)}
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[{l:"Totali",v:lav.length,c:C.textSub},{l:"Da organizzare",v:lav.filter(l=>l.stato==="Da organizzare").length,c:C.orange},{l:"Completati",v:lav.filter(l=>l.stato==="Completato").length,c:C.green}].map(s=>(
                  <div key={s.l} style={{textAlign:"center",background:C.bg,borderRadius:11,padding:"8px 0"}}><div style={{fontWeight:800,fontSize:18,color:s.c}}>{s.v}</div><div style={{fontSize:10,color:C.textMuted}}>{s.l}</div></div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {modal&&(
        <Modal title={form.id?"Modifica Operaio":"Nuovo Operaio"} onClose={()=>setModal(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Input label="Nome" value={form.nome||""} onChange={v=>setForm(f=>({...f,nome:v}))}/>
            <Input label="Telefono" value={form.telefono||""} onChange={v=>setForm(f=>({...f,telefono:v}))}/>
            <Input label="Specialità" value={form.specialita||""} onChange={v=>setForm(f=>({...f,specialita:v}))}/>
            <Input label="Costo orario (€/h)" type="number" value={form.costo_ora||""} onChange={v=>setForm(f=>({...f,costo_ora:v}))}/>
            <div>
              <label style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.8,display:"block",marginBottom:8}}>Competenze</label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {COMPETENZE_DISP.map(c=>{
                  const attiva=(form.competenze||[]).includes(c);
                  return <span key={c} onClick={()=>toggleCompetenza(c)} style={{cursor:"pointer",fontSize:11.5,fontWeight:600,borderRadius:20,padding:"5px 12px",background:attiva?C.accent:C.bg,color:attiva?"#fff":C.textSub}}>{c}</span>;
                })}
              </div>
            </div>
            <div><label style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,display:"block",marginBottom:8}}>Colore</label><div style={{display:"flex",gap:8}}>{cols.map(col=><div key={col} onClick={()=>setForm(f=>({...f,colore:col}))} style={{width:28,height:28,borderRadius:"50%",background:col,cursor:"pointer",border:`3px solid ${form.colore===col?"#fff":"transparent"}`,transform:form.colore===col?"scale(1.2)":"scale(1)",transition:"transform 0.15s"}}/>)}</div></div>
            <div style={{display:"flex",alignItems:"center",gap:10}}><input type="checkbox" id="disp" checked={!!form.disponibile} onChange={e=>setForm(f=>({...f,disponibile:e.target.checked}))}/><label htmlFor="disp" style={{color:C.textSub,fontSize:13,cursor:"pointer"}}>Disponibile (deseleziona per sospendere — non comparirà più di default nel Calendario)</label></div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>setModal(false)}>Annulla</Btn><Btn onClick={salva}>💾 Salva</Btn></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CRM ───────────────────────────────────────────────────────────────────────
function Clienti({lavori, db}){
  const [extra, setExtra] = useState({}); // {nomeCliente: {email, note, tipo}}
  const [espanso, setEspanso] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState({email:"",note:"",tipo:"privato"});
  const [search, setSearch] = useState("");
  const [scheda, setScheda] = usePersistedState("clienti_scheda", "imprese"); // "imprese" | "privati"
  const [soloDaVerificare, setSoloDaVerificare] = useState(false);

  useEffect(()=>{
    db.from("clienti_extra").select("*").then(({data})=>{
      // tipoSalvato: scelto da te; se manca, il tipo è dedotto dal nome
      const m={}; (data||[]).forEach(c=>{ m[c.nome]={email:c.email||"",note:c.note||"",tipo:c.tipo||"privato",tipoSalvato:!!c.tipo}; });
      setExtra(m);
    });
  },[]);

  async function scriviExtra(nome, dati){
    const {error} = await db.from("clienti_extra").upsert({nome, email:dati.email||"", note:dati.note||"", tipo:dati.tipo},{onConflict:"azienda_id,nome"});
    if(error){ alert("Errore nel salvataggio del cliente: "+error.message); return false; }
    setExtra(e=>({...e, [nome]:{email:dati.email||"", note:dati.note||"", tipo:dati.tipo, tipoSalvato:true}}));
    return true;
  }
  async function salvaExtra(){
    if(await scriviExtra(editModal, form)) setEditModal(null);
  }
  async function cambiaTipo(c, tipo){
    const ex = extra[c.nome]||{};
    await scriviExtra(c.nome, {email:ex.email, note:ex.note, tipo});
  }

  const SUFFISSI_IMPRESA = /\b(srl|s\.r\.l|spa|s\.p\.a|snc|s\.n\.c|sas|s\.a\.s|società|soc\.|ditta|impresa|costruzioni|edil|group|gruppo)\b/i;

  const gruppi = {};
  lavori.filter(l=>l.cliente?.trim()).forEach(l=>{
    const nome = l.cliente.trim();
    if(!gruppi[nome]) gruppi[nome] = {nome, lavori:[], telefono:"", indirizzi:new Set()};
    gruppi[nome].lavori.push(l);
    if(l.telefono_cliente) gruppi[nome].telefono = l.telefono_cliente;
    if(l.indirizzo) gruppi[nome].indirizzi.add(l.indirizzo);
  });
  let clienti = Object.values(gruppi).map(c=>({
    ...c,
    nLavori: c.lavori.length,
    fatturato: c.lavori.filter(l=>!["Annullato","Rifiutato"].includes(l.stato)).reduce((s,l)=>s+(l.preventivo||0),0),
    ultimaData: c.lavori.reduce((max,l)=>l.data_inizio>max?l.data_inizio:max,""),
    tipo: extra[c.nome]?.tipoSalvato ? extra[c.nome].tipo : (SUFFISSI_IMPRESA.test(c.nome) ? "impresa" : "privato"),
    tipoAuto: !extra[c.nome]?.tipoSalvato,
    attivi: c.lavori.filter(l=>["Confermato","Programmato","Da organizzare"].includes(l.stato)).length,
  })).sort((a,b)=>b.fatturato-a.fatturato);

  if(search.trim()){
    const q=search.toLowerCase();
    clienti = clienti.filter(c=>c.nome.toLowerCase().includes(q)||[...c.indirizzi].some(i=>i.toLowerCase().includes(q)));
  }

  const imprese = clienti.filter(c=>c.tipo==="impresa");
  const privati = clienti.filter(c=>c.tipo!=="impresa");
  const isImprese = scheda==="imprese";
  const elencoScheda = (isImprese?imprese:privati).filter(c=>!soloDaVerificare || c.tipoAuto);
  const daVerificare = (isImprese?imprese:privati).filter(c=>c.tipoAuto).length;
  const totScheda = (isImprese?imprese:privati).reduce((t,c)=>t+c.fatturato,0);
  const totTutti = clienti.reduce((t,c)=>t+c.fatturato,0);
  const attiviScheda = (isImprese?imprese:privati).reduce((t,c)=>t+c.attivi,0);

  function RigaCliente({c}){
    const isOpen = espanso===c.nome;
    const ex = extra[c.nome]||{};
    return(
      <div key={c.nome} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:14,overflow:"hidden"}}>
        <div onClick={()=>setEspanso(isOpen?null:c.nome)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px",cursor:"pointer",flexWrap:"wrap",gap:10}}>
          <div style={{minWidth:0}}>
            <div style={{fontWeight:700,fontSize:14,color:C.text,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {c.nome}
              {c.tipoAuto && <span title="Tipo dedotto dal nome: confermalo o spostalo" style={{fontSize:10,fontWeight:700,color:C.orange,background:C.orange+"14",borderRadius:20,padding:"2px 7px"}}>da verificare</span>}
              {c.attivi>0 && <span style={{fontSize:10,fontWeight:700,color:C.blue,background:C.blue+"14",borderRadius:20,padding:"2px 7px"}}>{c.attivi} in corso</span>}
            </div>
            <div style={{fontSize:11.5,color:C.textMuted,marginTop:2}}>{c.telefono||"senza telefono"}{ex.email?` · ${ex.email}`:""}</div>
          </div>
          <div style={{display:"flex",gap:20,alignItems:"center"}}>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:9.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase"}}>Lavori</div>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{c.nLavori}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:9.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase"}}>Importo lavori</div>
              <div style={{fontSize:13,fontWeight:800,color:C.accent}}>{euro(c.fatturato)}</div>
            </div>
            {c.telefono && <WhatsAppBtn tel={c.telefono} testo={`Ciao ${c.nome.split(" ")[0]}, `}/>}
            <span style={{color:C.textMuted,fontSize:12,transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.15s"}}>▾</span>
          </div>
        </div>
        {isOpen && (
          <div style={{borderTop:`1px solid ${C.border}`,padding:"14px 18px",background:C.bg,display:"flex",flexDirection:"column",gap:12}}>
            {ex.note && <div style={{fontSize:12.5,color:C.textSub,fontStyle:"italic"}}>📝 {ex.note}</div>}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {c.lavori.sort((a,b)=>b.data_inizio.localeCompare(a.data_inizio)).map(l=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 12px",background:C.card,borderRadius:9,fontSize:12.5,flexWrap:"wrap",gap:6}}>
                  <span style={{color:C.text}}>{l.titolo}{l.materiale?` · ${l.materiale}`:""}</span>
                  <span style={{color:C.textMuted}}>{fmt(l.data_inizio)} · <Badge label={l.stato} color={STATI_COLOR[l.stato]||C.textMuted}/></span>
                  <span style={{fontWeight:700,color:C.text}}>{l.preventivo?euro(l.preventivo):"—"}</span>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {c.tipoAuto && <Btn small onClick={()=>cambiaTipo(c, c.tipo)}>✓ Conferma {c.tipo==="impresa"?"impresa":"privato"}</Btn>}
              <Btn small variant="ghost" onClick={()=>cambiaTipo(c, c.tipo==="impresa"?"privato":"impresa")}>{c.tipo==="impresa"?"→ Sposta in Privati":"→ Sposta in Imprese"}</Btn>
              <Btn small variant="ghost" onClick={()=>{setForm({email:ex.email||"",note:ex.note||"",tipo:c.tipo});setEditModal(c.nome);}}>✏️ Modifica contatto/note</Btn>
            </div>
          </div>
        )}
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div>
        <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>👤 Clienti</div>
        <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{imprese.length} imprese · {privati.length} privati</div>
      </div>

      {/* Imprese / Privati: due elenchi separati */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {[
          {id:"imprese", label:"🏢 Imprese", lista:imprese},
          {id:"privati", label:"🙍 Privati", lista:privati},
        ].map(t=>{
          const att = scheda===t.id;
          const tot = t.lista.reduce((x,c)=>x+c.fatturato,0);
          return(
            <button key={t.id} onClick={()=>{setScheda(t.id);setEspanso(null);setSoloDaVerificare(false);}}
              style={{textAlign:"left",border:`1.5px solid ${att?C.text:"transparent"}`,background:C.card,boxShadow:att?C.shadowMd:C.shadowSm,borderRadius:16,padding:"14px 16px",cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
              <div style={{fontSize:13.5,fontWeight:700,color:att?C.text:C.textSub}}>{t.label} <span style={{color:C.textMuted,fontWeight:600}}>({t.lista.length})</span></div>
              <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:att?C.text:C.textSub,marginTop:6}}>{euro(tot)}</div>
              <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>{totTutti>0?`${Math.round(tot/totTutti*100)}% dell'importo lavori`:"importo lavori"}</div>
            </button>
          );
        })}
      </div>

      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`🔍 Cerca tra ${isImprese?"le imprese":"i privati"}…`}
          style={{flex:1,minWidth:200,background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
        {daVerificare>0 && (
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12.5,color:C.orange,fontWeight:600,cursor:"pointer"}}>
            <input type="checkbox" checked={soloDaVerificare} onChange={e=>setSoloDaVerificare(e.target.checked)}/>
            Solo da verificare ({daVerificare})
          </label>
        )}
      </div>

      {attiviScheda>0 && !soloDaVerificare && (
        <div style={{fontSize:12.5,color:C.textMuted,marginTop:-6}}>{attiviScheda} lavor{attiviScheda>1?"i":"o"} in corso con {isImprese?"imprese":"privati"}</div>
      )}

      {elencoScheda.length===0 ? (
        <div style={{textAlign:"center",color:C.textMuted,padding:60,background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>
          {search.trim()||soloDaVerificare ? "Nessun cliente trovato" : isImprese ? "Nessuna impresa. Apri un privato e usa \"→ Sposta in Imprese\"." : "Nessun privato."}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {elencoScheda.map(c=><RigaCliente key={c.nome} c={c}/>)}
        </div>
      )}

      {editModal && (
        <Modal title={`Contatto — ${editModal}`} onClose={()=>setEditModal(null)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Select label="Tipo cliente" value={form.tipo} onChange={v=>setForm(f=>({...f,tipo:v}))} options={[{value:"privato",label:"🙍 Privato"},{value:"impresa",label:"🏢 Impresa"}]}/>
            <Input label="Email" type="email" value={form.email} onChange={v=>setForm(f=>({...f,email:v}))}/>
            <Textarea label="Note" value={form.note} onChange={v=>setForm(f=>({...f,note:v}))} rows={3} placeholder="Preferenze, dettagli utili…"/>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn variant="ghost" onClick={()=>setEditModal(null)}>Annulla</Btn>
              <Btn onClick={salvaExtra}>💾 Salva</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CRM({rivendite,setRivendite,lavori,db}){
  const [modal,setModal]=useState(false);
  const ef={nome:"",referente:"",telefono:"",email:"",zona:"",attivo:true};
  const [form,setForm]=useState(ef);
  async function salva(){
    const {id, ...rest} = form;
    if(id){
      const {error} = await db.from("rivendite").update(rest).eq("id",id);
      if(error){ window.alert("Errore nel salvataggio: "+error.message); return; }
      setRivendite(rs=>rs.map(r=>r.id===id?{...rest,id}:r));
    } else {
      const {data:n,error} = await db.from("rivendite").insert([rest]).select().single();
      if(error){ window.alert("Errore nel salvataggio: "+error.message); return; }
      if(n) setRivendite(rs=>[...rs,n]);
    }
    setModal(false);
  }
  async function del(id){ if(!window.confirm("Eliminare?")) return; await db.from("rivendite").delete().eq("id",id); setRivendite(rs=>rs.filter(r=>r.id!==id)); }
  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div><div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>CRM Rivenditori</div><div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{rivendite.length} rivenditori</div></div>
        <Btn onClick={()=>{setForm({...ef});setModal(true);}}>+ Nuovo Rivenditore</Btn>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14}}>
        {rivendite.map(r=>{
          const lav=lavori.filter(l=>l.rivendita_id===r.id);
          const fatt=lav.filter(l=>l.stato==="Completato").reduce((s,l)=>s+l.preventivo,0);
          return(
            <div key={r.id} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px",borderLeft:`4px solid ${r.attivo?C.accent:C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}><div><div style={{fontWeight:800,fontSize:15,color:C.text}}>{r.nome}</div><div style={{fontSize:12,color:C.textMuted,marginTop:2}}>📍 {r.zona}</div></div><Badge label={r.attivo?"Attivo":"Inattivo"} color={r.attivo?C.green:C.textMuted}/></div>
              <div style={{fontSize:13,color:C.textSub,marginBottom:4}}>👤 {r.referente}</div>
              <div style={{fontSize:13,color:C.textSub,marginBottom:4}}>📞 {r.telefono}</div>
              <div style={{fontSize:13,color:C.textSub,marginBottom:14}}>✉️ {r.email}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
                <div style={{textAlign:"center",background:C.bg,borderRadius:11,padding:"10px 0"}}><div style={{fontWeight:800,fontSize:20,color:C.accent}}>{lav.length}</div><div style={{fontSize:11,color:C.textMuted}}>Lavori</div></div>
                <div style={{textAlign:"center",background:C.bg,borderRadius:11,padding:"10px 0"}}><div style={{fontWeight:800,fontSize:20,color:C.green}}>€{(fatt/1000).toFixed(1)}k</div><div style={{fontSize:11,color:C.textMuted}}>Importo lavori</div></div>
              </div>
              <div style={{display:"flex",gap:8}}><Btn small variant="ghost" style={{flex:1}} onClick={()=>{setForm({...r});setModal(true);}}>✏️ Modifica</Btn><Btn small variant="danger" onClick={()=>del(r.id)}>🗑</Btn></div>
            </div>
          );
        })}
      </div>
      {modal&&(
        <Modal title="Rivenditore" onClose={()=>setModal(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Input label="Nome" value={form.nome||""} onChange={v=>setForm(f=>({...f,nome:v}))}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}><Input label="Referente" value={form.referente||""} onChange={v=>setForm(f=>({...f,referente:v}))}/><Input label="Zona" value={form.zona||""} onChange={v=>setForm(f=>({...f,zona:v}))}/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}><Input label="Telefono" value={form.telefono||""} onChange={v=>setForm(f=>({...f,telefono:v}))}/><Input label="Email" type="email" value={form.email||""} onChange={v=>setForm(f=>({...f,email:v}))}/></div>
            <div style={{display:"flex",alignItems:"center",gap:10}}><input type="checkbox" id="att" checked={!!form.attivo} onChange={e=>setForm(f=>({...f,attivo:e.target.checked}))}/><label htmlFor="att" style={{color:C.textSub,fontSize:13,cursor:"pointer"}}>Attivo</label></div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>setModal(false)}>Annulla</Btn><Btn onClick={salva}>💾 Salva</Btn></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CRM CONTATTI ──────────────────────────────────────────────────────────────
const LISTE_CRM = [
  {id:"rivendite_parquet", label:"Rivendite parquet", gruppo:"rivendite"},
  {id:"bagno_ceramiche",   label:"Bagno e ceramiche", gruppo:"rivendite"},
  {id:"arredamento",       label:"Arredamento",       gruppo:"rivendite"},
  {id:"imprese_edili",     label:"Imprese edili",     gruppo:"imprese"},
  {id:"grandi_imprese",    label:"Grandi imprese",    gruppo:"imprese"},
  {id:"architetti",        label:"Architetti",        gruppo:"progettisti"},
  {id:"geometri",          label:"Geometri",          gruppo:"progettisti"},
  {id:"ingegneri",         label:"Ingegneri",         gruppo:"progettisti"},
  {id:"privati",           label:"Privati",           gruppo:"privati"},
];
const LISTA_LABEL = Object.fromEntries(LISTE_CRM.map(l=>[l.id,l.label]));
const GRUPPO_LISTA = Object.fromEntries(LISTE_CRM.map(l=>[l.id,l.gruppo]));
const GRUPPI_CRM = [
  {id:"rivendite",   label:"Rivendite"},
  {id:"imprese",     label:"Imprese"},
  {id:"progettisti", label:"Progettisti"},
  {id:"privati",     label:"Privati"},
  {id:"tutti",       label:"Tutte le liste"},
];
const STATI_CRM = [
  {id:"Da contattare",   color:"#A3A4A8"},
  {id:"Contattato",      color:"#2F6FDB"},
  {id:"Interessato",     color:"#E3701F"},
  {id:"Appuntamento",    color:"#7A5AA6"},
  {id:"Partner attivo",  color:"#2E8B57"},
  {id:"Non interessato", color:"#C2412F"},
];
function nomeStato(stato, gruppo){ return gruppo==="privati" && stato==="Partner attivo" ? "Cliente" : stato; }
const FONTI_PRIVATI = ["Passaparola","Ex cliente","Sito parquetti.it","Instagram / Facebook","Google","Rivendita","Impresa / architetto","Altro"];
const STATO_CRM_COLOR = Object.fromEntries(STATI_CRM.map(s=>[s.id,s.color]));
const PROVINCE_CRM = ["Roma","Latina","Frosinone","Viterbo","Rieti"];

const MODELLI_PRIVATI = [
  {gruppo:"privati", canale:"whatsapp", titolo:"Risposta a richiesta preventivo", oggetto:"",
   testo:"{saluto}, sono Fabio di Parquetti Group, grazie per averci contattato! Per prepararle un preventivo preciso possiamo fare un sopralluogo: misuriamo, vediamo il sottofondo e le consigliamo il materiale giusto. Quando le farebbe comodo? Intanto, se vuole, mi mandi qualche foto degli ambienti."},
  {gruppo:"privati", canale:"whatsapp", titolo:"Follow-up preventivo", oggetto:"",
   testo:"{saluto}, sono Fabio di Parquetti Group. Volevo sapere se ha avuto modo di guardare il preventivo e se ha domande sul materiale o sui tempi. Resto a disposizione, anche per passare a far vedere qualche campione."},
  {gruppo:"privati", canale:"whatsapp", titolo:"Ex cliente: recensione e passaparola", oggetto:"",
   testo:"{saluto}, sono Fabio di Parquetti Group. Spero che il parquet le stia dando soddisfazione! Se si è trovato bene, ci aiuterebbe molto una recensione su Google. E se un amico o un parente deve rifare i pavimenti, ci faccia il nostro nome: lo trattiamo con la stessa cura. Grazie!"},
  {gruppo:"privati", canale:"email", titolo:"Email preventivo privato", oggetto:"Il suo preventivo per il parquet – Parquetti Group",
   testo:"{saluto},\n\ngrazie per averci contattato. Come d'accordo le invio i riferimenti di Parquetti Group: posiamo parquet prefinito e massello, facciamo levigature e restauri in tutto il Lazio con squadre nostre.\n\nPer un preventivo preciso proponiamo un sopralluogo, così misuriamo gli ambienti e verifichiamo il sottofondo.\n\nCordiali saluti,\nFabio – Parquetti Group Srl"},
];
const MODELLI_CRM_DEFAULT = [
  {gruppo:"rivendite", canale:"whatsapp", titolo:"Presentazione rivendita", oggetto:"",
   testo:"{saluto}, sono Fabio di Parquetti Group (Fonte Nuova). Siamo posatori specializzati di parquet prefinito e massello, levigature, battiscopa e decking in tutto il Lazio, con squadre interne.\nCerchiamo showroom come {nome} a cui offrire un servizio di posa affidabile per i vostri clienti: sopralluoghi veloci, tempi certi e prezzi da posatore di fiducia.\nPossiamo passare in negozio a presentarci? Grazie!"},
  {gruppo:"imprese", canale:"whatsapp", titolo:"Presentazione impresa", oggetto:"",
   testo:"{saluto}, sono Fabio, titolare di Parquetti Group Srl. Ci occupiamo solo di parquet: posa, levigatura, battiscopa, scale e decking, in tutto il Lazio con squadre nostre.\nCerchiamo imprese a cui fare da posatore di riferimento anche in subappalto, con prezzi chiari e tempi certi.\nPossiamo sentirci 5 minuti? Grazie, Fabio – Parquetti Group"},
  {gruppo:"progettisti", canale:"whatsapp", titolo:"Presentazione progettista", oggetto:"",
   testo:"{saluto}, sono Fabio di Parquetti Group (Fonte Nuova). Siamo posatori specializzati di parquet (prefinito, massello, spina, decking) e facciamo levigature e restauri in tutto il Lazio.\nLavoriamo spesso con progettisti e direzioni lavori: sopralluoghi rapidi, preventivi chiari, cantiere pulito e tempi rispettati.\nLe andrebbe di conoscerci per i suoi prossimi progetti? Posso mandarle qualche lavoro fatto."},
  {gruppo:"tutti", canale:"whatsapp", titolo:"Richiamo dopo la telefonata", oggetto:"",
   testo:"{saluto}, come d'accordo al telefono le lascio i contatti di Parquetti Group. Qui trova alcuni nostri lavori: www.parquetti.it\nResto a disposizione per sopralluoghi e preventivi. Fabio"},
  {gruppo:"rivendite", canale:"email", titolo:"Email rivendita", oggetto:"Servizio di posa parquet per i vostri clienti",
   testo:"{saluto},\n\nsono Fabio, titolare di Parquetti Group Srl. Posiamo parquet (prefinito, massello, spina, decking), facciamo levigature e lavoriamo in tutto il Lazio con squadre nostre.\n\nVorremmo diventare il posatore di riferimento di {nome}: sopralluogo e preventivo veloci, posa a regola d'arte, garanzia sul lavoro.\n\nSarei felice di passare in showroom per presentarci.\n\nCordiali saluti,\nFabio – Parquetti Group Srl"},
  {gruppo:"imprese", canale:"email", titolo:"Email impresa", oggetto:"Posa parquet in subappalto – Parquetti Group",
   testo:"{saluto},\n\nsono Fabio, titolare di Parquetti Group Srl, impresa specializzata nella posa e levigatura di parquet in tutto il Lazio con squadre interne.\n\nSiamo disponibili come posatori di riferimento per i vostri cantieri, anche in subappalto, con prezzi chiari e tempi certi.\n\nPossiamo fissare una breve chiamata?\n\nCordiali saluti,\nFabio – Parquetti Group Srl"},
  {gruppo:"progettisti", canale:"email", titolo:"Email progettista", oggetto:"Posa parquet per i suoi progetti – Parquetti Group",
   testo:"{saluto},\n\nsono Fabio di Parquetti Group Srl. Siamo posatori specializzati di parquet e lavoriamo spesso con architetti, geometri e direzioni lavori in tutto il Lazio.\n\nPer i suoi prossimi progetti possiamo offrirle sopralluoghi rapidi, preventivi chiari e posa a regola d'arte.\n\nLe farebbe piacere ricevere qualche lavoro realizzato?\n\nCordiali saluti,\nFabio – Parquetti Group Srl"},
];

function normCRM(s){ return (s||"").toLowerCase().replace(/[^a-z0-9]/g,""); }
function cifre(s){ return (s||"").replace(/\D/g,""); }
function numeroWhatsApp(c){
  if(c.whatsapp) return c.whatsapp;
  if(c.cellulare) return c.cellulare;
  return cifre(c.telefono).replace(/^39/,"").startsWith("3") ? c.telefono : "";
}
function telLink(n){ const d=cifre(n); if(!d) return null; return "tel:+"+(d.startsWith("39")&&d.length>10?d:"39"+d); }
function compilaModello(testo, c){
  const ref = (c.referente||"").trim();
  return (testo||"")
    .replaceAll("{saluto}", ref ? `Buongiorno ${ref}` : "Buongiorno")
    .replaceAll("{nome}", c.nome||"")
    .replaceAll("{referente}", ref)
    .replaceAll("{comune}", c.comune||"");
}
function addGiorni(n){ const d=new Date(); d.setDate(d.getDate()+n); return d.toISOString().split("T")[0]; }
function fmtQuando(ts){
  if(!ts) return "";
  const d=new Date(ts);
  return d.toLocaleDateString("it-IT",{day:"2-digit",month:"short"})+" "+d.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"});
}

// Carica tutte le righe (Supabase restituisce max 1000 righe per richiesta)
async function caricaTuttiContatti(db){
  let tutti=[], da=0;
  while(true){
    const {data,error} = await db.from("contatti").select("*").order("nome").range(da, da+999);
    if(error){ console.warn("contatti:", error.message); return null; }
    tutti = tutti.concat(data||[]);
    if(!data || data.length<1000) break;
    da += 1000;
  }
  return tutti;
}

function richiamiDovuti(contatti){
  return contatti.filter(c=>c.prossimo_richiamo && c.prossimo_richiamo<=today && !["Partner attivo","Non interessato"].includes(c.stato));
}

function Contatti({contatti, setContatti, lavori, setLavori, rivendite, setRivendite, db, setTab, user}){
  const [gruppo, setGruppo]   = usePersistedState("crm_gruppo", "tutti");
  const [lista, setLista]     = usePersistedState("crm_lista", "tutte");
  const [vista, setVista]     = usePersistedState("crm_vista", "elenco");
  const [provincia, setProvincia] = usePersistedState("crm_provincia", "");
  const [statoF, setStatoF]   = usePersistedState("crm_stato", "");
  const [soloRichiami, setSoloRichiami] = useState(false);
  const [search, setSearch]   = useState("");
  const [mostra, setMostra]   = useState(60);
  const [apertoId, setApertoId] = useState(null);
  const [azione, setAzione]   = useState(null); // "chiamata" | "whatsapp" all'apertura della scheda
  const [sequenza, setSequenza] = useState([]); // ordine bloccato mentre scorri le schede con "Successivo"
  const [nuovo, setNuovo]     = useState(false);
  // aperto da Messaggi › "Scheda contatto"
  useEffect(()=>{ try{ const id=localStorage.getItem("crm_apri"); if(id){ localStorage.removeItem("crm_apri"); setSequenza([Number(id)]); setApertoId(Number(id)); } }catch(e){} },[]);
  const [importa, setImporta] = useState(false);
  const [modelliAperti, setModelliAperti] = useState(false);
  const [modelli, setModelli] = useState([]);
  const isMobile = typeof window!=="undefined" && window.innerWidth<768;

  useEffect(()=>{
    db.from("crm_modelli").select("*").order("id").then(async({data,error})=>{
      if(error){ console.warn("crm_modelli:", error.message); setModelli([...MODELLI_CRM_DEFAULT, ...MODELLI_PRIVATI].map((m,i)=>({...m,id:"def"+i}))); return; }
      if(data && data.length){
        if(data.some(m=>m.gruppo==="privati")){ setModelli(data); return; }
        const {data:priv} = await db.from("crm_modelli").insert(MODELLI_PRIVATI).select(); // aggiunti una volta sola
        setModelli([...data, ...(priv||[])]); return;
      }
      const {data:ins} = await db.from("crm_modelli").insert([...MODELLI_CRM_DEFAULT, ...MODELLI_PRIVATI]).select();
      setModelli(ins && ins.length ? ins : [...MODELLI_CRM_DEFAULT, ...MODELLI_PRIVATI].map((m,i)=>({...m,id:"def"+i})));
    });
  },[]);

  async function aggiorna(id, campi){
    const {id:_x, ...pulito} = campi;
    const {error} = await db.from("contatti").update(pulito).eq("id", id);
    if(error){ alert("Errore nel salvataggio del contatto: "+error.message); return false; }
    setContatti(cs=>cs.map(c=>c.id===id?{...c,...pulito}:c));
    return true;
  }
  async function registra(contattoId, tipo, testo="", esito=""){
    const riga = {contatto_id:contattoId, tipo, testo, esito, utente:(user?.email||"").split("@")[0]};
    const {data,error} = await db.from("contatti_attivita").insert([riga]).select().single();
    if(error){ console.warn("attività:", error.message); return null; }
    return data;
  }

  // ── Filtri ──
  const listeGruppo = gruppo==="tutti" ? LISTE_CRM : LISTE_CRM.filter(l=>l.gruppo===gruppo);
  const nelGruppo = contatti.filter(c=>gruppo==="tutti" || GRUPPO_LISTA[c.lista]===gruppo);
  const listaValida = lista==="tutte" || listeGruppo.some(l=>l.id===lista);
  let filtrati = nelGruppo.filter(c=>!listaValida || lista==="tutte" || c.lista===lista);
  if(provincia) filtrati = filtrati.filter(c=>c.provincia===provincia);
  if(statoF) filtrati = filtrati.filter(c=>c.stato===statoF);
  if(soloRichiami) filtrati = filtrati.filter(c=>c.prossimo_richiamo && c.prossimo_richiamo<=today && !["Partner attivo","Non interessato"].includes(c.stato));
  if(search.trim()){
    const q = search.toLowerCase();
    filtrati = filtrati.filter(c=>[c.nome,c.referente,c.comune,c.indirizzo,c.telefono,c.cellulare,c.email].some(v=>(v||"").toLowerCase().includes(q)));
  }
  const chiuso = (c)=>["Partner attivo","Non interessato"].includes(c.stato);
  const ordinePriorita = (c)=> chiuso(c) ? 3 : (c.prossimo_richiamo && c.prossimo_richiamo<=today) ? 0 : c.stato==="Da contattare" ? 1 : 2;
  filtrati = [...filtrati].sort((a,b)=> ordinePriorita(a)-ordinePriorita(b) || (a.prossimo_richiamo||"9").localeCompare(b.prossimo_richiamo||"9") || a.nome.localeCompare(b.nome));

  const dovuti = richiamiDovuti(contatti).length;
  const partner = contatti.filter(c=>c.stato==="Partner attivo").length;
  const contattati = contatti.filter(c=>c.stato!=="Da contattare").length;

  function apri(id, az=null){ setSequenza(filtrati.map(c=>c.id)); setAzione(az); setApertoId(id); }
  const indiceAperto = sequenza.indexOf(apertoId);
  const aperto = contatti.find(c=>c.id===apertoId);

  // Porta nella lista Privati i clienti privati che hai già in Squadra (dai lavori), senza doppioni
  async function importaPrivatiSquadra(){
    const {data:extra} = await db.from("clienti_extra").select("*");
    const tipi = Object.fromEntries((extra||[]).map(x=>[x.nome, x]));
    const IMPRESA = /\b(srl|s\.r\.l|spa|s\.p\.a|snc|s\.n\.c|sas|s\.a\.s|società|soc\.|ditta|impresa|costruzioni|edil|group|gruppo)\b/i;
    const gruppi = {};
    lavori.filter(l=>l.cliente?.trim()).forEach(l=>{
      const nome = l.cliente.trim();
      const tipo = tipi[nome]?.tipo || (IMPRESA.test(nome) ? "impresa" : "privato");
      if(tipo!=="privato") return;
      (gruppi[nome] = gruppi[nome] || {nome, lavori:[], telefono:"", indirizzo:""});
      gruppi[nome].lavori.push(l);
      if(l.telefono_cliente) gruppi[nome].telefono = l.telefono_cliente;
      if(l.indirizzo) gruppi[nome].indirizzo = l.indirizzo;
    });
    const nomi = new Set(contatti.map(c=>normCRM(c.nome)));
    const tels = new Set(contatti.flatMap(c=>[c.telefono,c.cellulare].map(cifre)).filter(t=>t.length>=6));
    const nuovi = Object.values(gruppi).filter(g=>!nomi.has(normCRM(g.nome)) && !(cifre(g.telefono).length>=6 && tels.has(cifre(g.telefono)))).map(g=>{
      const st = g.lavori.map(l=>l.stato);
      const stato = st.some(s=>["Completato","Programmato","Confermato","Da organizzare"].includes(s)) ? "Partner attivo"
        : st.includes("Preventivo") ? "Interessato" : st.includes("Rifiutato") ? "Non interessato" : "Contattato";
      const tel = g.telefono||""; const mob = cifre(tel).replace(/^39/,"").startsWith("3");
      const parti = g.indirizzo.split(",").map(x=>x.trim()).filter(Boolean);
      return {lista:"privati", nome:g.nome, telefono:mob?"":tel, cellulare:mob?tel:"", indirizzo:parti.length>1?parti.slice(0,-1).join(", "):g.indirizzo,
        comune:parti.length>1?parti[parti.length-1]:"", provincia:"", fonte:stato==="Partner attivo"?"Ex cliente":"Preventivo in Squadra", stato, cliente_nome:g.nome, email:tipi[g.nome]?.email||"",
        note:"Lavori in Squadra: "+g.lavori.map(l=>`${l.titolo} (${l.stato})`).join("; ")};
    });
    if(!nuovi.length){ alert("Tutti i clienti privati di Squadra sono già nella lista Privati."); return; }
    if(!window.confirm(`Aggiungo ${nuovi.length} clienti privati di Squadra alla lista Privati?`)) return;
    const {error} = await db.from("contatti").insert(nuovi);
    if(error){ alert("Errore: "+error.message); return; }
    const t = await caricaTuttiContatti(db); if(t) setContatti(t);
    setLista("privati");
  }

  async function cambiaStato(c, stato){
    if(c.stato===stato) return;
    const extra = stato==="Non interessato"||stato==="Partner attivo" ? {prossimo_richiamo:null} : {};
    if(await aggiorna(c.id, {stato, ...extra})) await registra(c.id, "stato", `${c.stato} → ${stato}`);
  }

  // ── UI ──
  const chip = (on)=>({padding:"6px 12px",borderRadius:999,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:on?600:500,background:on?C.text:C.card,color:on?"#fff":C.textSub,boxShadow:on?"none":`inset 0 0 0 1px ${C.border}`,whiteSpace:"nowrap"});
  const selStyle = {background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"9px 12px",fontSize:12.5,fontFamily:"inherit",color:C.text,outline:"none",cursor:"pointer"};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>Contatti</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{contatti.length} contatti · {contattati} contattati · {partner} partner</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn small variant="ghost" onClick={()=>setModelliAperti(true)}>Modelli messaggi</Btn>
          {gruppo==="privati" && <Btn small variant="ghost" onClick={importaPrivatiSquadra}>Importa clienti privati di Squadra</Btn>}
          <Btn small variant="ghost" onClick={()=>setImporta(true)}>Importa Excel</Btn>
          <Btn small onClick={()=>setNuovo(true)}>+ Nuovo</Btn>
        </div>
      </div>

      {contatti.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 24px",background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>
          <div style={{fontFamily:C.fontBrand,fontSize:18,fontWeight:600,color:C.text}}>Nessun contatto</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:6,marginBottom:18}}>Importa le liste Excel di rivendite, imprese e professionisti per iniziare.</div>
          <Btn onClick={()=>setImporta(true)}>Importa Excel</Btn>
        </div>
      ) : (<>
        {/* Richiami del giorno */}
        {dovuti>0 && (
          <button onClick={()=>{setSoloRichiami(v=>!v);setMostra(60);}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",textAlign:"left",background:soloRichiami?C.text:C.brandSoft,color:soloRichiami?"#fff":C.text,border:"none",borderRadius:14,padding:"12px 16px",cursor:"pointer",fontFamily:"inherit"}}>
            <span style={{fontSize:13.5,fontWeight:600}}>{dovuti} {dovuti===1?"contatto da richiamare":"contatti da richiamare"} oggi</span>
            <span style={{fontSize:12,fontWeight:600,opacity:0.8}}>{soloRichiami?"Mostra tutti":"Mostra"}</span>
          </button>
        )}

        {/* Gruppi */}
        <Segmenti value={gruppo} onChange={g=>{setGruppo(g);setLista("tutte");setMostra(60);}}
          items={GRUPPI_CRM.map(g=>({id:g.id,label:g.label,n:g.id==="tutti"?contatti.length:contatti.filter(c=>GRUPPO_LISTA[c.lista]===g.id).length}))}/>

        {/* Liste del gruppo */}
        <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
          <button onClick={()=>{setLista("tutte");setMostra(60);}} style={chip(lista==="tutte"||!listaValida)}>Tutte · {nelGruppo.length}</button>
          {listeGruppo.map(l=>{
            const n = contatti.filter(c=>c.lista===l.id).length;
            return <button key={l.id} onClick={()=>{setLista(l.id);setMostra(60);}} style={chip(lista===l.id)}>{l.label} · {n}</button>;
          })}
        </div>

        {/* Ricerca e filtri */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <input value={search} onChange={e=>{setSearch(e.target.value);setMostra(60);}} placeholder="Cerca nome, comune, telefono…"
            style={{flex:1,minWidth:200,background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
          <select value={provincia} onChange={e=>setProvincia(e.target.value)} style={selStyle}>
            <option value="">Tutte le province</option>
            {PROVINCE_CRM.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          {vista==="elenco" && (
            <select value={statoF} onChange={e=>setStatoF(e.target.value)} style={selStyle}>
              <option value="">Tutti gli stati</option>
              {STATI_CRM.map(s=><option key={s.id} value={s.id}>{nomeStato(s.id, gruppo)}</option>)}
            </select>
          )}
          <Segmenti value={vista} onChange={setVista} items={[{id:"elenco",label:"Elenco"},{id:"pipeline",label:"Pipeline"}]}/>
        </div>

        {vista==="elenco" ? (
          filtrati.length===0 ? (
            <div style={{textAlign:"center",color:C.textMuted,padding:50,background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>Nessun contatto con questi filtri</div>
          ) : (
            <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
              {filtrati.slice(0,mostra).map((c,i)=>{
                const wa = numeroWhatsApp(c);
                const tel = c.telefono || c.cellulare;
                const scaduto = c.prossimo_richiamo && c.prossimo_richiamo<=today && !["Partner attivo","Non interessato"].includes(c.stato);
                return(
                  <div key={c.id} onClick={()=>apri(c.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderTop:i?`1px solid ${C.borderLight}`:"none",cursor:"pointer"}}
                    onMouseEnter={e=>{e.currentTarget.style.background=C.bg;}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                    <span title={c.stato} style={{width:8,height:8,borderRadius:"50%",background:STATO_CRM_COLOR[c.stato]||C.textMuted,flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13.5,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nome}</div>
                      <div style={{fontSize:11.5,color:C.textMuted,marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {LISTA_LABEL[c.lista]||c.lista}{c.comune?` · ${c.comune}`:""}{!isMobile && c.stato!=="Da contattare"?` · ${nomeStato(c.stato, GRUPPO_LISTA[c.lista])}`:""}
                      </div>
                    </div>
                    {c.prossimo_richiamo && !["Partner attivo","Non interessato"].includes(c.stato) && (
                      <span style={{fontSize:11.5,fontWeight:600,color:scaduto?C.brand:C.textMuted,whiteSpace:"nowrap"}}>{c.stato==="Appuntamento"?"App. ":"Richiamo "}{fmt(c.prossimo_richiamo)}</span>
                    )}
                    <div style={{display:"flex",gap:6}} onClick={e=>e.stopPropagation()}>
                      {tel && <a href={telLink(tel)} onClick={()=>apri(c.id,"chiamata")} title="Chiama" style={iconaAzione}>☎</a>}
                      {wa && <span title="WhatsApp" onClick={()=>apri(c.id,"whatsapp")} style={{...iconaAzione,color:"#25D366"}}>💬</span>}
                    </div>
                  </div>
                );
              })}
              {filtrati.length>mostra && (
                <div style={{padding:12,textAlign:"center",borderTop:`1px solid ${C.borderLight}`}}>
                  <Btn small variant="ghost" onClick={()=>setMostra(m=>m+100)}>Mostra altri ({filtrati.length-mostra})</Btn>
                </div>
              )}
            </div>
          )
        ) : (
          <PipelineContatti contatti={filtrati} gruppo={gruppo} onApri={(id)=>apri(id)} onSposta={cambiaStato}/>
        )}
      </>)}

      {aperto && (
        <SchedaContatto key={aperto.id} c={aperto} azioneIniziale={azione} modelli={modelli} lavori={lavori} rivendite={rivendite} db={db}
          aggiorna={aggiorna} registra={registra} cambiaStato={cambiaStato}
          posizione={indiceAperto>=0?`${indiceAperto+1} di ${sequenza.length}`:""}
          onPrev={indiceAperto>0?()=>{setAzione(null);setApertoId(sequenza[indiceAperto-1]);}:null}
          onNext={indiceAperto>=0&&indiceAperto<sequenza.length-1?()=>{setAzione(null);setApertoId(sequenza[indiceAperto+1]);}:null}
          onClose={()=>{setAzione(null);setApertoId(null);}}
          onElimina={async()=>{
            if(!window.confirm(`Eliminare ${aperto.nome} dai contatti?`)) return;
            const {error} = await db.from("contatti").delete().eq("id",aperto.id);
            if(error){ alert("Errore: "+error.message); return; }
            setContatti(cs=>cs.filter(x=>x.id!==aperto.id)); setApertoId(null);
          }}
          onCreaPreventivo={async()=>{
            const c = aperto;
            const nuovoLavoro = {
              titolo:`Preventivo – ${c.nome}`, cliente:c.nome, telefono_cliente:c.cellulare||c.telefono||"",
              indirizzo:[c.indirizzo,c.comune].filter(Boolean).join(", "), stato:"Preventivo",
              data_inizio:today, data_fine:today, preventivo:null, materiale:"",
              note:`Da Contatti (${LISTA_LABEL[c.lista]||c.lista})`, operai_ids:[],
              rivendita_id:c.rivendita_id||rivendite[0]?.id||null, saldato:false, rapporti:[],
            };
            const {data:n,error} = await db.from("lavori").insert([nuovoLavoro]).select().single();
            if(error){ alert("Errore nella creazione del preventivo: "+error.message); return; }
            setLavori(ls=>[n,...ls]);
            await db.from("clienti_extra").upsert({nome:c.nome, email:c.email||"", note:c.note||"", tipo:GRUPPO_LISTA[c.lista]==="privati"?"privato":"impresa"},{onConflict:"azienda_id,nome"});
            const campi = {cliente_nome:c.nome};
            if(["Da contattare","Contattato","Interessato"].includes(c.stato)) campi.stato="Appuntamento";
            await aggiorna(c.id, campi);
            await registra(c.id, "nota", "Creato preventivo in Squadra");
            setApertoId(null); setTab("preventivi");
          }}
          onAggiungiRivenditore={async()=>{
            const c = aperto;
            const {data:n,error} = await db.from("rivendite").insert([{nome:c.nome, referente:c.referente||"", telefono:c.cellulare||c.telefono||"", email:c.email||"", zona:c.comune||c.provincia||"", attivo:true}]).select().single();
            if(error){ alert("Errore: "+error.message); return; }
            setRivendite(rs=>[...rs,n]);
            await aggiorna(c.id, {rivendita_id:n.id, stato:"Partner attivo", prossimo_richiamo:null});
            await registra(c.id, "nota", "Aggiunto ai Rivenditori come partner");
          }}
        />
      )}

      {nuovo && (
        <ContattoForm iniziale={{lista:lista!=="tutte"&&listaValida?lista:(listeGruppo[0]?.id||"rivendite_parquet"),provincia:provincia||"Roma"}} onClose={()=>setNuovo(false)}
          onSave={async(dati)=>{
            const {data:n,error} = await db.from("contatti").insert([{...dati, stato:"Da contattare"}]).select().single();
            if(error){ alert("Errore nel salvataggio: "+error.message); return; }
            setContatti(cs=>[...cs,n]); setNuovo(false); setSequenza([n.id]); setApertoId(n.id);
          }}/>
      )}
      {importa && <ImportaContatti contatti={contatti} db={db} onClose={()=>setImporta(false)} onFatto={async()=>{ const t=await caricaTuttiContatti(db); if(t) setContatti(t); }}/>}
      {modelliAperti && <ModelliCRM modelli={modelli} setModelli={setModelli} db={db} onClose={()=>setModelliAperti(false)}/>}
    </div>
  );
}
const iconaAzione = {width:32,height:32,borderRadius:"50%",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,textDecoration:"none",color:C.text,cursor:"pointer"};

function PipelineContatti({contatti, gruppo, onApri, onSposta}){
  const [trascinato, setTrascinato] = useState(null);
  const [sopra, setSopra] = useState(null);
  return(
    <div style={{display:"grid",gridAutoFlow:"column",gridAutoColumns:"minmax(220px,1fr)",gap:10,overflowX:"auto",paddingBottom:6}}>
      {STATI_CRM.map(s=>{
        const col = contatti.filter(c=>c.stato===s.id);
        return(
          <div key={s.id}
            onDragOver={e=>{e.preventDefault();setSopra(s.id);}} onDragLeave={()=>setSopra(null)}
            onDrop={e=>{e.preventDefault();setSopra(null);const c=contatti.find(x=>x.id===trascinato);if(c)onSposta(c,s.id);setTrascinato(null);}}
            style={{background:sopra===s.id?C.brandSoft:C.sidebar,borderRadius:14,padding:8,minHeight:200,transition:"background 0.12s"}}>
            <div style={{display:"flex",alignItems:"center",gap:7,padding:"6px 6px 10px"}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:s.color}}/>
              <span style={{fontSize:12.5,fontWeight:600,color:C.text}}>{nomeStato(s.id, gruppo)}</span>
              <span style={{fontSize:11.5,color:C.textMuted,marginLeft:"auto"}}>{col.length}</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {col.slice(0,40).map(c=>(
                <div key={c.id} draggable onDragStart={()=>setTrascinato(c.id)} onClick={()=>onApri(c.id)}
                  style={{background:C.card,borderRadius:10,padding:"9px 11px",boxShadow:C.shadowSm,cursor:"grab"}}>
                  <div style={{fontSize:12.5,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nome}</div>
                  <div style={{fontSize:11,color:C.textMuted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{LISTA_LABEL[c.lista]}{c.comune?` · ${c.comune}`:""}</div>
                  {c.prossimo_richiamo && <div style={{fontSize:10.5,fontWeight:600,color:c.prossimo_richiamo<=today?C.brand:C.textMuted,marginTop:4}}>{s.id==="Appuntamento"?"Appuntamento":"Richiamo"} {fmt(c.prossimo_richiamo)}</div>}
                </div>
              ))}
              {col.length>40 && <div style={{fontSize:11.5,color:C.textMuted,textAlign:"center",padding:6}}>+{col.length-40} altri · usa i filtri</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── SCRIPT DI CHIAMATA per lista ──────────────────────────────────────────────
const CHIUSURA_CONSENSO = "Le posso mandare su WhatsApp due foto di lavori nostri e il mio numero diretto, così ce l'ha sotto mano? (Se dice sì: spunta \"Ok a ricevere WhatsApp\")";
const SCRIPT_CHIAMATA = {
  rivendite_parquet: {
    chiedi: "il titolare o il responsabile dello showroom",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. Le rubo un minuto: parlo con chi segue la posa per i vostri clienti?",
    presentazione: "Siamo un'azienda di famiglia, seconda generazione: facciamo solo parquet. Posa di prefinito e massello, spina, levigature, battiscopa e decking, in tutto il Lazio con squadre nostre.",
    domande: [
      "Quando vendete il parquet, la posa la proponete voi o il cliente si trova il posatore da solo?",
      "Avete già posatori di fiducia? Riescono a starvi dietro con i tempi, soprattutto nei periodi pieni?",
      "Vi capita di perdere una vendita perché il cliente non trova chi lo posa?",
    ],
    proposta: "Vorremmo essere il vostro posatore di riferimento: voi vendete il materiale, noi facciamo sopralluogo, preventivo di posa e lavoro. Tempi certi, cantiere pulito, e il cliente torna da voi contento.",
    obiezioni: [
      ["Abbiamo già i nostri posatori", "Benissimo, non vogliamo sostituire nessuno. Ci tenga come squadra di riserva per quando i suoi sono pieni o per i lavori fuori zona."],
      ["Quanto costate?", "Dipende dal tipo di posa e dal sottofondo; ai rivenditori facciamo prezzi da posatore di fiducia. Le preparo un listino posa per i suoi clienti?"],
      ["Mandatemi una mail", "Certo. Intanto le mando su WhatsApp due lavori e il listino, così li vede subito. Qual è il numero migliore?"],
      ["Non ho tempo adesso", "Nessun problema: quando la posso richiamare, domani mattina o nel pomeriggio?"],
    ],
    chiusura: "Possiamo passare in showroom 15 minuti questa settimana per conoscerci e lasciarle campioni di lavori fatti? Che giorno le va meglio?",
  },
  bagno_ceramiche: {
    chiedi: "il titolare o il responsabile vendite",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. Parlo con il titolare o con chi segue le vendite?",
    presentazione: "Noi facciamo solo parquet: posa, levigatura, battiscopa e scale, in tutto il Lazio con squadre nostre.",
    domande: [
      "Vendete anche parquet o pavimenti in legno, oppure i vostri clienti per il legno vanno altrove?",
      "Quando un cliente rifà il bagno spesso rifà anche la casa: chi gli consigliate per i pavimenti in legno?",
      "Avete un posatore di legno a cui mandare i clienti?",
    ],
    proposta: "Potremmo collaborare: quando un vostro cliente chiede il legno ce lo mandate, noi facciamo sopralluogo e posa a regola d'arte, e voi ci fate bella figura. Se vendete anche parquet, possiamo posarlo noi per voi.",
    obiezioni: [
      ["Noi facciamo solo ceramica", "Proprio per questo: quando il cliente chiede il legno avete qualcuno di serio a cui mandarlo, e non lo perdete."],
      ["Abbiamo già qualcuno", "Perfetto, teneteci come alternativa quando sono pieni o per i lavori di levigatura e restauro."],
      ["Mandatemi qualcosa", "Le mando su WhatsApp due lavori e i miei contatti, così ce li ha."],
    ],
    chiusura: "Passo in negozio a presentarmi e lasciarle i biglietti per i clienti? Quale giorno va bene?",
  },
  arredamento: {
    chiedi: "il titolare o l'arredatore / progettista interno",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. Parlo con chi segue i progetti di arredo completo?",
    presentazione: "Facciamo solo parquet: posa, levigatura, battiscopa, scale e rivestimenti in legno, in tutto il Lazio.",
    domande: [
      "Vi capita di seguire case intere, dove il cliente rifà anche i pavimenti?",
      "In quei casi chi si occupa del pavimento: lo proponete voi o lo lasciate al cliente?",
      "Avete un posatore di riferimento per il legno?",
    ],
    proposta: "Possiamo essere il vostro partner per i pavimenti: voi arredate, noi posiamo o levigano il parquet prima della consegna dei mobili, coordinandoci con i vostri tempi di montaggio.",
    obiezioni: [
      ["Noi vendiamo solo mobili", "Certo, ma quando il cliente rifà casa il pavimento viene prima dei mobili: se lo segnalate a noi, i tempi del vostro montaggio non saltano."],
      ["Non ci interessa", "Capisco. Le lascio comunque il mio numero su WhatsApp nel caso un cliente glielo chieda?"],
    ],
    chiusura: "Le va di conoscerci? Passo in showroom con qualche foto di lavori: che giorno è più tranquillo?",
  },
  imprese_edili: {
    chiedi: "il titolare o il responsabile di cantiere / acquisti",
    apertura: "Buongiorno, sono Fabio, titolare di Parquetti Group, posatori di parquet di Fonte Nuova. Parlo con il titolare o con chi segue i cantieri?",
    presentazione: "Facciamo solo parquet: posa, levigatura, battiscopa, scale e decking. Lavoriamo in tutto il Lazio con squadre interne, anche in subappalto.",
    domande: [
      "Nei vostri cantieri il parquet lo fate fare a una squadra fissa o cambiate di volta in volta?",
      "Qual è il problema più grosso con i posatori: tempi, qualità, prezzo?",
      "Avete cantieri in partenza nei prossimi mesi con pavimenti in legno?",
    ],
    proposta: "Vorremmo essere il vostro posatore di riferimento: prezzi chiari a metro, tempi certi che si incastrano con le altre lavorazioni, cantiere lasciato pulito. Ci pensate voi al cliente, noi al pavimento.",
    obiezioni: [
      ["Abbiamo già il nostro parquettista", "Ottimo, teneteci come seconda squadra per quando lui è pieno: un cantiere fermo costa più di un preventivo in più."],
      ["Siete cari?", "Mi dica il prossimo lavoro e le faccio un preventivo a metro: se non conviene, nessun problema."],
      ["Mandami il listino", "Glielo mando su WhatsApp adesso, con un paio di lavori fatti."],
      ["Richiamami più avanti", "Va bene: quando partono i prossimi cantieri? La richiamo in quel periodo."],
    ],
    chiusura: "Avete un cantiere su cui possiamo fare un sopralluogo e un preventivo? Così vede come lavoriamo.",
  },
  grandi_imprese: {
    chiedi: "l'ufficio acquisti o il responsabile subappalti / fornitori",
    apertura: "Buongiorno, sono Fabio, titolare di Parquetti Group. Siamo un'impresa specializzata nella posa di parquet. Mi può passare l'ufficio acquisti o chi gestisce l'albo fornitori e i subappalti?",
    presentazione: "Siamo specializzati solo nel parquet: posa, levigatura, battiscopa, scale e decking, con squadre interne e capacità di seguire più cantieri insieme in tutto il Lazio.",
    domande: [
      "Avete un albo fornitori o una procedura per qualificare i subappaltatori? Cosa serve?",
      "Sui vostri cantieri residenziali chi segue oggi i pavimenti in legno?",
      "Ci sono gare o cantieri in partenza dove servono finiture in legno?",
    ],
    proposta: "Vorremmo entrare nel vostro albo come impresa di posa parquet: documenti in regola, squadre interne, tempi rispettati e capacità di lavorare su più lotti.",
    obiezioni: [
      ["Mandi la documentazione", "Certo: a quale indirizzo email e a chi la indirizzo? Mi dice quali documenti servono? (Segna email e referente nella scheda)"],
      ["Abbiamo già fornitori", "Capisco. Possiamo comunque qualificarci per essere pronti alla prossima occasione o come seconda squadra?"],
    ],
    chiusura: "Mi dice il nome del referente e l'email giusta? Le mando la nostra presentazione e la richiamo tra una settimana per un confronto.",
  },
  architetti: {
    chiedi: "l'architetto titolare dello studio",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. Parlo con l'architetto? Le rubo solo un minuto.",
    presentazione: "Facciamo solo parquet: posa di prefinito, massello, spina e pavimenti a disegno, levigature e restauri di parquet antichi, in tutto il Lazio.",
    domande: [
      "Nei suoi progetti di ristrutturazione usa spesso il legno? Che tipo: spina, plance larghe, restauri?",
      "Per la posa ha già una squadra di fiducia o la sceglie il cliente o l'impresa?",
      "Qual è la cosa che più le dà fastidio quando un posatore lavora su un suo progetto?",
    ],
    proposta: "Ci piacerebbe essere i posatori a cui pensa per i suoi progetti: sopralluogo con lei, consigli tecnici su sottofondo e materiale, e una posa che rispetta il progetto, anche per pose a disegno e restauri.",
    obiezioni: [
      ["Lo decide l'impresa", "Capisco: se però lei ci segnala, l'impresa ha un riferimento serio e lei è più tranquilla sul risultato."],
      ["Ho già i miei posatori", "Benissimo. Ci tenga presenti per i lavori particolari, come spine, restauri o quando i suoi sono pieni."],
      ["Mandi del materiale", "Le mando su WhatsApp alcuni lavori, soprattutto pose a disegno e restauri, così vede il livello."],
    ],
    chiusura: "Le va un caffè in studio per mostrarle qualche lavoro e campione? Oppure ha un cantiere in corso dove ci vediamo per un sopralluogo?",
  },
  geometri: {
    chiedi: "il geometra titolare dello studio",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. Parlo con il geometra? Le rubo un minuto.",
    presentazione: "Facciamo solo parquet: posa, levigatura, battiscopa, scale, in tutto il Lazio con squadre nostre.",
    domande: [
      "Segue ristrutturazioni o direzioni lavori dove si mette o si recupera il parquet?",
      "I clienti le chiedono spesso a chi rivolgersi per i pavimenti?",
      "Le capita di avere problemi di tempi con i posatori che bloccano la consegna dei lavori?",
    ],
    proposta: "Può indicarci ai suoi clienti come posatori di fiducia: sopralluogo veloce, preventivo chiaro e tempi rispettati, così i suoi lavori chiudono senza ritardi.",
    obiezioni: [
      ["I clienti scelgono da soli", "Certo, ma spesso chiedono a lei un consiglio: le basta avere il nostro numero quando succede."],
      ["Ho già qualcuno", "Benissimo, ci tenga come alternativa quando è pieno."],
    ],
    chiusura: "Le mando su WhatsApp i miei contatti e qualche lavoro? E se ha un cantiere in corso, posso passare per un sopralluogo.",
  },
  ingegneri: {
    chiedi: "l'ingegnere o il responsabile della direzione lavori",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. Parlo con l'ingegnere o con chi segue le direzioni lavori?",
    presentazione: "Siamo specializzati solo nel parquet: posa, levigatura, controllo dei sottofondi e dell'umidità prima della posa, in tutto il Lazio.",
    domande: [
      "Segue cantieri residenziali o ristrutturazioni con pavimenti in legno?",
      "Vi sono capitati problemi di parquet che si alza o si muove per umidità del sottofondo o tempi di posa sbagliati?",
      "Chi sceglie la ditta di posa nei vostri cantieri?",
    ],
    proposta: "Possiamo essere un riferimento tecnico per il legno nei suoi cantieri: verifica dell'umidità del massetto prima di posare, scelta del sistema di posa giusto e lavoro documentato.",
    obiezioni: [
      ["Non seguo la parte finiture", "Capisco. Chi se ne occupa nel suo studio o nelle imprese con cui lavora? Posso contattarlo da parte sua?"],
      ["Mandi una presentazione", "Gliela mando via email o WhatsApp, come preferisce?"],
    ],
    chiusura: "Se ha un cantiere con parquet in programma, posso fare un sopralluogo e misurare l'umidità del sottofondo, così lo vede direttamente.",
  },
  privati: {
    chiedi: "la persona che ha chiesto il preventivo",
    apertura: "Buongiorno, sono Fabio di Parquetti Group, posatori di parquet di Fonte Nuova. La chiamo per la sua richiesta sul parquet: ha due minuti?",
    presentazione: "Siamo un'azienda di famiglia: facciamo solo parquet, posa nuova, levigature e restauri, in tutto il Lazio con squadre nostre.",
    domande: [
      "Che lavoro vorrebbe fare?",
      "Più o meno quanti metri quadri sono?",
      "Quando vorrebbe iniziare?",
      "Il materiale ce l'ha già o le serve un consiglio?",
    ],
    proposta: "Per darle un prezzo preciso passiamo a fare un sopralluogo: misuriamo, controlliamo il sottofondo e le facciamo vedere qualche campione. Poi le mandiamo il preventivo scritto.",
    obiezioni: [
      ["Quanto costa al metro?", "Dipende dal tipo di posa, dal materiale e dal sottofondo: per non darle un numero sbagliato preferiamo vederlo. Il sopralluogo serve proprio a questo."],
      ["Ho già altri preventivi", "Benissimo, così può confrontare. Le chiedo solo di guardare anche cosa è compreso: preparazione del fondo, battiscopa, smaltimento."],
      ["Devo sentire in famiglia", "Certo. Fissiamo il sopralluogo quando ci siete tutti e due, così decidete con calma vedendo i campioni."],
      ["Per ora sto solo guardando", "Nessun problema: le mando su WhatsApp qualche lavoro e i nostri contatti, e quando è pronto ci sente."],
    ],
    chiusura: "Fissiamo il sopralluogo: le va meglio in settimana o il sabato mattina? Mi conferma l'indirizzo?",
  },
};

// Risposte rapide per ogni domanda dello script (stesso ordine delle domande)
const RISPOSTE_SCRIPT = {
  rivendite_parquet: [["La proponiamo noi, con posatori nostri","Il cliente si trova il posatore","Dipende dal cliente"],["Sì, e bastano","Sì, ma sono spesso pieni","Non abbiamo posatori fissi"],["Sì, capita","Raramente","No, mai"]],
  bagno_ceramiche:   [["Sì, vendiamo anche legno","No, solo ceramica","Solo su richiesta"],["Un posatore di fiducia","Nessuno, si arrangiano","Lo chiedono all'impresa"],["Sì, ce l'abbiamo","No","Ne cerchiamo uno"]],
  arredamento:       [["Sì, spesso","Ogni tanto","Raramente"],["Lo proponiamo noi","Lo lasciamo al cliente","Se ne occupa l'impresa"],["Sì","No","Ne cerchiamo uno"]],
  imprese_edili:     [["Squadra fissa","Cambiamo di volta in volta","Lo facciamo internamente"],["Tempi","Qualità","Prezzo","Nessun problema"],["Sì, a breve","Fra qualche mese","Al momento no"]],
  grandi_imprese:    [["Sì, ci mandano i requisiti","Sì, ma ora l'albo è chiuso","No, chiamano a necessità"],["Un subappaltatore fisso","Più ditte","Non fanno legno"],["Sì, ci sono cantieri","Fra qualche mese","No"]],
  architetti:        [["Sì, spesso","Ogni tanto","Raramente"],["Ha la sua squadra","Lo sceglie il cliente","Lo sceglie l'impresa"],["Tempi non rispettati","Poca cura nei dettagli","Cantiere sporco","Niente in particolare"]],
  geometri:          [["Sì, spesso","Ogni tanto","No"],["Sì, spesso","A volte","No"],["Sì, capita","Raramente","No"]],
  ingegneri:         [["Sì","Ogni tanto","No"],["Sì, è capitato","No"],["Il committente","L'impresa","Lo studio"]],
  privati:           [["Posa parquet nuovo","Levigatura / restauro","Togliere il vecchio e rifare","Battiscopa / scale / decking"],["Meno di 30 mq","Da 30 a 80 mq","Più di 80 mq"],["Subito","Entro 1-3 mesi","Più avanti"],["Ce l'ha già","Gli serve un consiglio","Lo compra in rivendita"]],
};
const REAZIONI_PROPOSTA = ["Gli interessa","Ci deve pensare","Non gli interessa"];

// Chiamata guidata: un passo alla volta, risposte da toccare, alla fine esito e storico
function GuidaChiamata({c, onFine, onConsenso, onChiudi}){
  const s = SCRIPT_CHIAMATA[c.lista];
  const opzioni = RISPOSTE_SCRIPT[c.lista] || [];
  const [passo, setPasso] = useState(0);
  const [referente, setReferente] = useState(c.referente||"");
  const [risposte, setRisposte] = useState({});      // {indiceDomanda: "risposta"}
  const [noteDomande, setNoteDomande] = useState({}); // {indiceDomanda: "nota"}
  const [reazione, setReazione] = useState("");
  const [obiezioni, setObiezioni] = useState([]);
  const [obiezioneAperta, setObiezioneAperta] = useState(null);
  const [nota, setNota] = useState("");
  const [data, setData] = useState(c.prossimo_richiamo||addGiorni(3));
  const [salvando, setSalvando] = useState(false);
  if(!s) return null;

  // passi: 0 apertura · 1 chi siamo · 2..(2+n-1) domande · proposta · chiusura
  const nD = s.domande.length;
  const P_PROPOSTA = 2+nD, P_CHIUSURA = 3+nD, TOT = P_CHIUSURA+1;
  const avanti = ()=>setPasso(p=>Math.min(p+1, P_CHIUSURA));
  const indietro = ()=>setPasso(p=>Math.max(p-1, 0));

  function rispondi(i, r){
    setRisposte(x=>({...x,[i]:r}));
    setTimeout(()=>setPasso(p=>p===2+i?p+1:p), 250); // passa da sola alla domanda dopo
  }
  function riepilogo(){
    const righe = [];
    if(referente.trim()) righe.push(`Parlato con: ${referente.trim()}`);
    s.domande.forEach((d,i)=>{
      const r = risposte[i], n = (noteDomande[i]||"").trim();
      if(r || n) righe.push(`${d}\n→ ${[r,n].filter(Boolean).join(" · ")}`);
    });
    if(reazione) righe.push(`Proposta: ${reazione}`);
    if(obiezioni.length) righe.push(`Obiezioni: ${obiezioni.join(", ")}`);
    if(nota.trim()) righe.push(`Note: ${nota.trim()}`);
    return righe.join("\n\n");
  }
  async function concludi(esito, dataEsito=data){
    setSalvando(true);
    await onFine(esito, riepilogo(), dataEsito, referente.trim());
    setSalvando(false);
  }

  const t = {fontSize:15,color:C.text,lineHeight:1.55};
  const etichetta = {fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.8,marginBottom:8};
  const chip = (on)=>({textAlign:"left",padding:"11px 14px",borderRadius:12,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13.5,fontWeight:on?600:500,background:on?C.text:C.card,color:on?"#fff":C.text,boxShadow:on?"none":`inset 0 0 0 1px ${C.border}`});

  let corpo;
  if(passo===0){
    corpo = (<>
      <div style={etichetta}>Apertura · chiedi di {s.chiedi}</div>
      <div style={t}>{s.apertura}</div>
      <input value={referente} onChange={e=>setReferente(e.target.value)} placeholder="Con chi stai parlando? (nome e ruolo)"
        style={{...fieldBase,background:C.card,marginTop:4}}/>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Btn onClick={avanti}>Ho la persona giusta ›</Btn>
      </div>
    </>);
  } else if(passo===1){
    corpo = (<>
      <div style={etichetta}>Chi siamo</div>
      <div style={t}>{s.presentazione}</div>
      <div><Btn onClick={avanti}>Avanti ›</Btn></div>
    </>);
  } else if(passo<P_PROPOSTA){
    const i = passo-2;
    corpo = (<>
      <div style={etichetta}>Domanda {i+1} di {nD}</div>
      <div style={{...t,fontWeight:600}}>{s.domande[i]}</div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {(opzioni[i]||[]).map(o=><button key={o} onClick={()=>rispondi(i,o)} style={chip(risposte[i]===o)}>{o}</button>)}
      </div>
      <input value={noteDomande[i]||""} onChange={e=>setNoteDomande(x=>({...x,[i]:e.target.value}))} placeholder="Altro / dettagli (facoltativo)"
        onKeyDown={e=>{if(e.key==="Enter")avanti();}} style={{...fieldBase,background:C.card}}/>
      <div style={{display:"flex",gap:8}}>
        <Btn variant="ghost" onClick={avanti}>{risposte[i]||noteDomande[i]?"Avanti ›":"Salta ›"}</Btn>
      </div>
    </>);
  } else if(passo===P_PROPOSTA){
    corpo = (<>
      <div style={etichetta}>Proposta</div>
      <div style={t}>{s.proposta}</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {REAZIONI_PROPOSTA.map(r=><button key={r} onClick={()=>setReazione(r)} style={{...chip(reazione===r),padding:"8px 12px"}}>{r}</button>)}
      </div>
      <div>
        <div style={{...etichetta,marginTop:6}}>Se ti dice…</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {s.obiezioni.map(([o,r])=>{
            const segnata = obiezioni.includes(o);
            return(
              <div key={o}>
                <button onClick={()=>{ setObiezioneAperta(obiezioneAperta===o?null:o); if(!segnata) setObiezioni(x=>[...x,o]); }} style={{...chip(segnata),width:"100%",padding:"9px 12px"}}>“{o}”</button>
                {obiezioneAperta===o && <div style={{...t,fontSize:14,padding:"8px 4px 2px",color:C.textSub}}>{r}</div>}
              </div>
            );
          })}
        </div>
      </div>
      <div><Btn onClick={avanti}>Vai alla chiusura ›</Btn></div>
    </>);
  } else {
    corpo = (<>
      <div style={etichetta}>Chiusura</div>
      <div style={t}>{s.chiusura}</div>
      <div style={{...t,fontSize:13.5,color:C.textSub}}>{CHIUSURA_CONSENSO}</div>
      <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:13,color:C.text}}>
        <label style={{display:"flex",gap:6,alignItems:"center",cursor:"pointer"}}><input type="checkbox" checked={!!c.consenso_whatsapp} onChange={e=>onConsenso("consenso_whatsapp",e.target.checked)}/>Ok a WhatsApp</label>
        <label style={{display:"flex",gap:6,alignItems:"center",cursor:"pointer"}}><input type="checkbox" checked={!!c.consenso_email} onChange={e=>onConsenso("consenso_email",e.target.checked)}/>Ok a email</label>
      </div>
      <Textarea value={nota} onChange={setNota} rows={2} placeholder="Note finali (cosa gli serve, quando richiamare…)"/>
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:12.5,color:C.textSub}}>Appuntamento / richiamo il</span>
        <input type="date" value={data} onChange={e=>setData(e.target.value)} style={{...fieldBase,background:C.card,padding:"7px 10px",fontSize:13}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:6}}>
        <Btn disabled={salvando} onClick={()=>concludi("Appuntamento")}>Appuntamento</Btn>
        <Btn disabled={salvando} onClick={()=>concludi("Interessato")}>Interessato</Btn>
        <Btn disabled={salvando} variant="ghost" onClick={()=>concludi("Da richiamare")}>Da richiamare</Btn>
        <Btn disabled={salvando} variant="danger" onClick={()=>concludi("Non interessato")}>Non interessato</Btn>
      </div>
    </>);
  }

  return(
    <div style={{background:C.bg,borderRadius:16,padding:16,display:"flex",flexDirection:"column",gap:14}}>
      {/* Barra: progresso e uscite rapide */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <span style={{fontSize:13,fontWeight:600}}>Chiamata guidata · {LISTA_LABEL[c.lista]}</span>
        <div style={{display:"flex",gap:6}}>
          <Btn small variant="ghost" disabled={salvando} onClick={()=>concludi("Non risponde", addGiorni(2))}>Non risponde</Btn>
          <Btn small variant="ghost" disabled={salvando} onClick={()=>concludi("Da richiamare")}>Richiamare</Btn>
          <button onClick={onChiudi} aria-label="Chiudi" style={{width:30,height:30,borderRadius:"50%",border:"none",background:C.card,color:C.textMuted,cursor:"pointer",fontSize:16}}>×</button>
        </div>
      </div>
      <div style={{display:"flex",gap:4}}>
        {Array.from({length:TOT}).map((_,k)=>(
          <button key={k} onClick={()=>setPasso(k)} aria-label={`Passo ${k+1}`} style={{flex:1,height:5,border:"none",padding:0,borderRadius:3,cursor:"pointer",background:k<passo?C.text:k===passo?C.brand:C.border}}/>
        ))}
      </div>
      {corpo}
      {passo>0 && <button onClick={indietro} style={{alignSelf:"flex-start",background:"none",border:"none",color:C.textMuted,fontSize:12.5,cursor:"pointer",fontFamily:"inherit",padding:0}}>‹ Indietro</button>}
    </div>
  );
}

function SchedaContatto({c, azioneIniziale, modelli, lavori, rivendite, db, aggiorna, registra, cambiaStato, onClose, onPrev, onNext, posizione, onElimina, onCreaPreventivo, onAggiungiRivenditore}){
  const [attivita, setAttivita] = useState([]);
  const [modifica, setModifica] = useState(false);
  const [esitoAperto, setEsitoAperto] = useState(false);
  const [dataRichiamo, setDataRichiamo] = useState(c.prossimo_richiamo||addGiorni(3));
  const [nota, setNota] = useState("");
  const [canale, setCanale] = useState(null); // "whatsapp" | "email"
  const [modelloId, setModelloId] = useState(null);
  const [testo, setTesto] = useState("");
  const [oggetto, setOggetto] = useState("");

  useEffect(()=>{
    db.from("contatti_attivita").select("*").eq("contatto_id",c.id).order("created_at",{ascending:false}).limit(100)
      .then(({data})=>setAttivita(data||[]));
    if(azioneIniziale==="chiamata") setEsitoAperto(true);
    if(azioneIniziale==="whatsapp") apriComposer("whatsapp");
  },[c.id]);

  async function log(tipo, t="", esito=""){
    const r = await registra(c.id, tipo, t, esito);
    if(r) setAttivita(a=>[r,...a]);
  }
  const gruppo = GRUPPO_LISTA[c.lista];
  const modelliCanale = (ch)=>modelli.filter(m=>m.canale===ch && (m.gruppo===gruppo||m.gruppo==="tutti"));
  function apriComposer(ch){
    const ms = modelliCanale(ch);
    const m = ms[0];
    setCanale(ch); setModelloId(m?.id||null);
    setTesto(m?compilaModello(m.testo,c):""); setOggetto(m?compilaModello(m.oggetto,c):"");
  }
  function scegliModello(id){
    const m = modelli.find(x=>String(x.id)===String(id));
    setModelloId(id); setTesto(m?compilaModello(m.testo,c):""); setOggetto(m?compilaModello(m.oggetto,c):"");
  }
  async function dopoContatto(extra={}){
    const campi = {ultimo_contatto:new Date().toISOString(), ...extra};
    if(c.stato==="Da contattare" && !campi.stato) campi.stato="Contattato";
    if(!c.prossimo_richiamo && campi.prossimo_richiamo===undefined && !["Partner attivo","Non interessato"].includes(campi.stato||c.stato)) campi.prossimo_richiamo=addGiorni(7);
    await aggiorna(c.id, campi);
  }
  async function inviaWhatsApp(){
    const link = waLink(numeroWhatsApp(c), testo);
    if(!link) return;
    window.open(link, "_blank");
    await log("whatsapp", testo);
    await dopoContatto();
    setCanale(null);
  }
  async function inviaEmail(){
    if(!c.email) return;
    window.location.href = `mailto:${c.email}?subject=${encodeURIComponent(oggetto)}&body=${encodeURIComponent(testo)}`;
    await log("email", (oggetto?oggetto+"\n\n":"")+testo);
    await dopoContatto();
    setCanale(null);
  }
  async function esitoGuida(e, testoChiamata, data, referente){
    const mappa = {
      "Non risponde":   {prossimo_richiamo:addGiorni(2)},
      "Da richiamare":  {prossimo_richiamo:data},
      "Interessato":    {stato:"Interessato", prossimo_richiamo:data},
      "Appuntamento":   {stato:"Appuntamento", prossimo_richiamo:data},
      "Non interessato":{stato:"Non interessato", prossimo_richiamo:null},
    };
    await log("chiamata", testoChiamata, e);
    if(mappa[e].stato && mappa[e].stato!==c.stato) await log("stato", `${c.stato} → ${mappa[e].stato}`);
    const extra = {...mappa[e]};
    if(referente && referente!==c.referente) extra.referente = referente;
    await dopoContatto(extra);
    setEsitoAperto(false);
  }
  async function consenso(campo, val){
    await aggiorna(c.id, {[campo]:val, consenso_data:val?new Date().toISOString():c.consenso_data});
    await log("nota", `${campo==="consenso_whatsapp"?"Consenso WhatsApp":"Consenso email"} ${val?"dato":"tolto"}`);
  }
  async function salvaNota(){
    if(!nota.trim()) return;
    await log("nota", nota.trim()); setNota("");
  }

  const tel = c.telefono || c.cellulare;
  const wa = numeroWhatsApp(c);
  const lavoriCollegati = lavori.filter(l=>l.cliente && (l.cliente===c.cliente_nome || normCRM(l.cliente)===normCRM(c.nome)));
  const rivendita = rivendite.find(r=>r.id===c.rivendita_id);
  const isRivendita = GRUPPO_LISTA[c.lista]==="rivendite";
  const ICONE = {chiamata:"☎",whatsapp:"💬",email:"✉",nota:"✎",stato:"→"};
  const ultimaChiamata = attivita.find(a=>a.tipo==="chiamata" && (a.testo||"").trim());
  const riga = {display:"flex",justifyContent:"space-between",gap:12,fontSize:13,padding:"7px 0",borderBottom:`1px solid ${C.borderLight}`};

  return(
    <Modal title={c.nome} onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:20}}>
        {/* Navigazione e stato */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:-8}}>
          <div style={{fontSize:12.5,color:C.textMuted}}>{LISTA_LABEL[c.lista]}{c.comune?` · ${c.comune}`:""}{c.provincia&&c.provincia!==c.comune?` (${c.provincia})`:""}</div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {posizione && <span style={{fontSize:11.5,color:C.textMuted,marginRight:4}}>{posizione}</span>}
            <Btn small variant="ghost" disabled={!onPrev} onClick={onPrev}>‹</Btn>
            <Btn small variant="ghost" disabled={!onNext} onClick={onNext}>Successivo ›</Btn>
          </div>
        </div>

        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {STATI_CRM.map(s=>{
            const on = c.stato===s.id;
            return <button key={s.id} onClick={()=>cambiaStato(c,s.id).then(()=>db.from("contatti_attivita").select("*").eq("contatto_id",c.id).order("created_at",{ascending:false}).limit(100).then(({data})=>setAttivita(data||[])))}
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 11px",borderRadius:999,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:on?600:500,background:on?s.color+"1A":C.bg,color:on?s.color:C.textSub}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:s.color}}/>{nomeStato(s.id, GRUPPO_LISTA[c.lista])}
            </button>;
          })}
        </div>

        {/* Azioni */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
          <a href={tel?telLink(tel):undefined} onClick={()=>{ if(tel) setEsitoAperto(true); }}
            style={{...bottoneAzione, opacity:tel?1:0.4, pointerEvents:tel?"auto":"none"}}>☎<span style={{fontSize:12.5}}>Chiama</span></a>
          <button onClick={()=>wa&&apriComposer("whatsapp")} style={{...bottoneAzione, color:"#1DA851", opacity:wa?1:0.4}} disabled={!wa}>💬<span style={{fontSize:12.5}}>WhatsApp</span></button>
          <button onClick={()=>c.email&&apriComposer("email")} style={{...bottoneAzione, opacity:c.email?1:0.4}} disabled={!c.email}>✉<span style={{fontSize:12.5}}>Email</span></button>
        </div>

        {/* Chiamata guidata: si apre con "Chiama" o da qui senza telefonare */}
        {esitoAperto ? (
          <GuidaChiamata c={c} onFine={esitoGuida} onConsenso={consenso} onChiudi={()=>setEsitoAperto(false)}/>
        ) : SCRIPT_CHIAMATA[c.lista] && (
          <button onClick={()=>setEsitoAperto(true)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card,border:"none",boxShadow:`inset 0 0 0 1px ${C.border}`,borderRadius:12,padding:"12px 14px",cursor:"pointer",fontFamily:"inherit"}}>
            <span style={{fontSize:13,fontWeight:600,color:C.text}}>Apri lo script di chiamata guidato</span>
            <span style={{fontSize:12,color:C.textMuted}}>{LISTA_LABEL[c.lista]} ›</span>
          </button>
        )}

        {/* Cosa ha risposto l'ultima volta */}
        {!esitoAperto && ultimaChiamata && (
          <div style={{background:C.bg,borderRadius:12,padding:"12px 14px"}}>
            <div style={{...fieldLabel,marginBottom:6}}>Ultima chiamata · {fmtQuando(ultimaChiamata.created_at)} · {ultimaChiamata.esito}</div>
            <div style={{fontSize:12.5,color:C.textSub,whiteSpace:"pre-wrap",lineHeight:1.5}}>{ultimaChiamata.testo}</div>
          </div>
        )}

        {/* Composer WhatsApp / Email */}
        {canale && (
          <div style={{background:C.bg,borderRadius:14,padding:16,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <div style={{fontSize:13.5,fontWeight:600}}>{canale==="whatsapp"?`WhatsApp a ${wa}`:`Email a ${c.email}`}</div>
              <select value={modelloId||""} onChange={e=>scegliModello(e.target.value)} style={{...fieldBase,padding:"7px 10px",fontSize:12.5,background:C.card}}>
                {modelliCanale(canale).map(m=><option key={m.id} value={m.id}>{m.titolo}</option>)}
                <option value="">Messaggio libero</option>
              </select>
            </div>
            {canale==="email" && <Input value={oggetto} onChange={setOggetto} placeholder="Oggetto"/>}
            <Textarea value={testo} onChange={setTesto} rows={7}/>
            {((canale==="whatsapp"&&!c.consenso_whatsapp)||(canale==="email"&&!c.consenso_email)) && (
              <div style={{fontSize:11.5,color:C.textMuted}}>Non hai ancora segnato il consenso di questo contatto. Il primo contatto conviene farlo con una telefonata.</div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn small variant="ghost" onClick={()=>setCanale(null)}>Annulla</Btn>
              <Btn small onClick={canale==="whatsapp"?inviaWhatsApp:inviaEmail} disabled={!testo.trim()}>{canale==="whatsapp"?"Apri WhatsApp e invia":"Apri email e invia"}</Btn>
            </div>
          </div>
        )}

        {/* Dati */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:24}}>
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <span style={fieldLabel}>Dati</span>
              <button onClick={()=>setModifica(true)} style={{background:"none",border:"none",color:C.textSub,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Modifica</button>
            </div>
            {[["Referente",c.referente],["Come ci ha conosciuto",GRUPPO_LISTA[c.lista]==="privati"?c.fonte:""],["Telefono",c.telefono],["Cellulare",c.cellulare],["WhatsApp",c.whatsapp],["Email",c.email],["Indirizzo",[c.indirizzo,c.comune].filter(Boolean).join(", ")],["Sito",c.sito]].filter(([,v])=>v).map(([k,v])=>(
              <div key={k} style={riga}><span style={{color:C.textMuted}}>{k}</span><span style={{color:C.text,textAlign:"right",wordBreak:"break-word"}}>{k==="Sito"?<a href={v.startsWith("http")?v:"https://"+v} target="_blank" rel="noreferrer" style={{color:C.blue}}>{v}</a>:v}</span></div>
            ))}
            <div style={riga}><span style={{color:C.textMuted}}>Prossimo richiamo</span>
              <input type="date" value={c.prossimo_richiamo||""} onChange={e=>aggiorna(c.id,{prossimo_richiamo:e.target.value||null})} style={{border:"none",background:"transparent",fontFamily:"inherit",fontSize:13,color:C.text,textAlign:"right"}}/>
            </div>
            <div style={riga}><span style={{color:C.textMuted}}>Consensi</span><span style={{color:C.text}}>{[c.consenso_whatsapp&&"WhatsApp",c.consenso_email&&"Email"].filter(Boolean).join(" · ")||"—"}</span></div>
            {c.note && <div style={{fontSize:12.5,color:C.textSub,marginTop:10,whiteSpace:"pre-wrap"}}>{c.note}</div>}

            {/* Collegamenti con Squadra */}
            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:18}}>
              <span style={fieldLabel}>In Squadra</span>
              {lavoriCollegati.map(l=>(
                <div key={l.id} style={{display:"flex",justifyContent:"space-between",gap:8,fontSize:12.5,background:C.bg,borderRadius:9,padding:"8px 10px"}}>
                  <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.titolo}</span>
                  <Badge label={l.stato} color={STATI_COLOR[l.stato]||C.textMuted}/>
                </div>
              ))}
              {rivendita && <div style={{fontSize:12.5,color:C.green,fontWeight:600}}>✓ Nei Rivenditori come {rivendita.nome}</div>}
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Btn small variant="ghost" onClick={onCreaPreventivo}>+ Crea preventivo</Btn>
                {isRivendita && !rivendita && <Btn small variant="ghost" onClick={onAggiungiRivenditore}>+ Aggiungi ai Rivenditori</Btn>}
              </div>
            </div>
          </div>

          {/* Storico */}
          <div>
            <span style={fieldLabel}>Storico</span>
            <div style={{display:"flex",gap:8,margin:"8px 0 12px"}}>
              <input value={nota} onChange={e=>setNota(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")salvaNota();}} placeholder="Aggiungi una nota…"
                style={{...fieldBase,flex:1,padding:"8px 12px",fontSize:13}}/>
              <Btn small variant="ghost" onClick={salvaNota} disabled={!nota.trim()}>Aggiungi</Btn>
            </div>
            {attivita.length===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>Nessuna attività. Inizia con una chiamata.</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:10,maxHeight:340,overflowY:"auto"}}>
                {attivita.map(a=>(
                  <div key={a.id} style={{display:"flex",gap:10}}>
                    <span style={{width:26,height:26,borderRadius:"50%",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0,color:a.tipo==="whatsapp"?"#1DA851":C.textSub}}>{ICONE[a.tipo]||"•"}</span>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:12.5,color:C.text,fontWeight:500}}>
                        {a.tipo==="chiamata"?`Chiamata · ${a.esito}`:a.tipo==="whatsapp"?"WhatsApp inviato":a.tipo==="email"?"Email inviata":a.tipo==="stato"?a.testo:"Nota"}
                      </div>
                      {a.tipo!=="stato" && a.testo && <div style={{fontSize:12,color:C.textSub,whiteSpace:"pre-wrap",marginTop:2,display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{a.testo}</div>}
                      <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>{fmtQuando(a.created_at)}{a.utente?` · ${a.utente}`:""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{display:"flex",justifyContent:"flex-start"}}>
          <button onClick={onElimina} style={{background:"none",border:"none",color:C.textMuted,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Elimina contatto</button>
        </div>
      </div>

      {modifica && (
        <ContattoForm iniziale={c} onClose={()=>setModifica(false)}
          onSave={async(dati)=>{ if(await aggiorna(c.id, dati)) setModifica(false); }}/>
      )}
    </Modal>
  );
}
const bottoneAzione = {display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"12px 8px",borderRadius:14,background:C.bg,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:18,color:C.text,textDecoration:"none",fontWeight:600};

function ContattoForm({iniziale, onSave, onClose}){
  const campiBase = {lista:"rivendite_parquet",nome:"",referente:"",telefono:"",cellulare:"",whatsapp:"",email:"",sito:"",indirizzo:"",comune:"",provincia:"Roma",fonte:"",note:""};
  const [f, setF] = useState(()=>{ const o={}; Object.keys(campiBase).forEach(k=>{ o[k]=iniziale?.[k]??campiBase[k]; }); return o; });
  const set = (k)=>(v)=>setF(x=>({...x,[k]:v}));
  return(
    <Modal title={iniziale?.id?"Modifica contatto":"Nuovo contatto"} onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Select label="Lista" value={f.lista} onChange={set("lista")} options={LISTE_CRM.map(l=>({value:l.id,label:l.label}))}/>
        <Input label={f.lista==="privati"?"Nome e cognome":"Nome / azienda / studio"} value={f.nome} onChange={set("nome")}/>
        <Input label="Referente" value={f.referente} onChange={set("referente")} placeholder="Es. Marco (titolare)"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="Telefono" value={f.telefono} onChange={set("telefono")}/>
          <Input label="Cellulare" value={f.cellulare} onChange={set("cellulare")}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="WhatsApp (se diverso)" value={f.whatsapp} onChange={set("whatsapp")}/>
          <Input label="Email" type="email" value={f.email} onChange={set("email")}/>
        </div>
        <Input label="Indirizzo" value={f.indirizzo} onChange={set("indirizzo")}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="Comune" value={f.comune} onChange={set("comune")}/>
          <Select label="Provincia" value={f.provincia} onChange={set("provincia")} options={PROVINCE_CRM.map(p=>({value:p,label:p}))}/>
        </div>
        {f.lista==="privati"
          ? <Select label="Come ci ha conosciuto" value={f.fonte} onChange={set("fonte")} options={[{value:"",label:"—"},...FONTI_PRIVATI.map(x=>({value:x,label:x}))]}/>
          : <Input label="Sito" value={f.sito} onChange={set("sito")}/>}
        <Textarea label="Note" value={f.note} onChange={set("note")} rows={2}/>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
          <Btn onClick={()=>f.nome.trim()&&onSave({...f,nome:f.nome.trim()})} disabled={!f.nome.trim()}>Salva</Btn>
        </div>
      </div>
    </Modal>
  );
}

function ModelliCRM({modelli, setModelli, db, onClose}){
  const [edit, setEdit] = useState(null);
  async function salva(){
    const {id, created_at, ...rest} = edit;
    if(id && !String(id).startsWith("def")){
      const {error} = await db.from("crm_modelli").update(rest).eq("id",id);
      if(error){ alert("Errore: "+error.message); return; }
      setModelli(ms=>ms.map(m=>m.id===id?{...m,...rest}:m));
    } else {
      const {data:n,error} = await db.from("crm_modelli").insert([rest]).select().single();
      if(error){ alert("Errore: "+error.message); return; }
      setModelli(ms=>[...ms.filter(m=>m.id!==id),n]);
    }
    setEdit(null);
  }
  async function elimina(m){
    if(!window.confirm(`Eliminare il modello "${m.titolo}"?`)) return;
    if(!String(m.id).startsWith("def")) await db.from("crm_modelli").delete().eq("id",m.id);
    setModelli(ms=>ms.filter(x=>x.id!==m.id)); setEdit(null);
  }
  return(
    <Modal title="Modelli messaggi" onClose={onClose} wide>
      {edit ? (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Input label="Nome modello" value={edit.titolo} onChange={v=>setEdit(e=>({...e,titolo:v}))}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Select label="Per" value={edit.gruppo} onChange={v=>setEdit(e=>({...e,gruppo:v}))} options={GRUPPI_CRM.map(g=>({value:g.id,label:g.label}))}/>
            <Select label="Canale" value={edit.canale} onChange={v=>setEdit(e=>({...e,canale:v}))} options={[{value:"whatsapp",label:"WhatsApp"},{value:"email",label:"Email"}]}/>
          </div>
          {edit.canale==="email" && <Input label="Oggetto" value={edit.oggetto||""} onChange={v=>setEdit(e=>({...e,oggetto:v}))}/>}
          <Textarea label="Testo" value={edit.testo} onChange={v=>setEdit(e=>({...e,testo:v}))} rows={9}/>
          <div style={{fontSize:11.5,color:C.textMuted}}>Campi automatici: {"{saluto}"} · {"{nome}"} · {"{referente}"} · {"{comune}"}</div>
          <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
            {edit.id ? <Btn variant="danger" onClick={()=>elimina(edit)}>Elimina</Btn> : <span/>}
            <div style={{display:"flex",gap:10}}>
              <Btn variant="ghost" onClick={()=>setEdit(null)}>Annulla</Btn>
              <Btn onClick={salva} disabled={!edit.titolo?.trim()||!edit.testo?.trim()}>Salva</Btn>
            </div>
          </div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {GRUPPI_CRM.map(g=>{
            const ms = modelli.filter(m=>m.gruppo===g.id);
            if(!ms.length) return null;
            return(
              <div key={g.id} style={{marginBottom:8}}>
                <div style={{...fieldLabel,marginBottom:6}}>{g.label}</div>
                {ms.map(m=>(
                  <div key={m.id} onClick={()=>setEdit({...m})} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:C.bg,borderRadius:10,marginBottom:6,cursor:"pointer"}}>
                    <span style={{fontSize:13,fontWeight:500}}>{m.titolo}</span>
                    <span style={{fontSize:11.5,color:m.canale==="whatsapp"?"#1DA851":C.textSub,fontWeight:600}}>{m.canale==="whatsapp"?"WhatsApp":"Email"}</span>
                  </div>
                ))}
              </div>
            );
          })}
          <div><Btn small onClick={()=>setEdit({titolo:"",gruppo:"tutti",canale:"whatsapp",oggetto:"",testo:""})}>+ Nuovo modello</Btn></div>
        </div>
      )}
    </Modal>
  );
}

// ── Import Excel / CSV ──
const MAPPA_CATEGORIE_CRM = [
  [/parquet|pavimenti/i, "rivendite_parquet"],
  [/bagno|ceramich/i, "bagno_ceramiche"],
  [/arredament|mobili/i, "arredamento"],
  [/grande impresa|general contractor/i, "grandi_imprese"],
  [/impresa edile|edilizia|ristruttur/i, "imprese_edili"],
  [/architett/i, "architetti"],
  [/geometr/i, "geometri"],
  [/ingegner/i, "ingegneri"],
  [/privat/i, "privati"],
];
const MAPPA_STATI_IMPORT = {
  "chiamato - richiamare":"Contattato","messaggio whatsapp inviato":"Contattato","email inviata":"Contattato",
  "interessato - appuntamento":"Interessato","già collabora":"Partner attivo","non interessato":"Non interessato","numero errato/chiuso":"Non interessato",
};
function caricaSheetJS(){
  if(window.XLSX) return Promise.resolve(window.XLSX);
  return new Promise((ok,ko)=>{
    const s=document.createElement("script");
    s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload=()=>ok(window.XLSX); s.onerror=()=>ko(new Error("Impossibile caricare il lettore Excel"));
    document.head.appendChild(s);
  });
}
function rigaInContatto(r, listaFissa){
  const trova = (re)=>{ const k=Object.keys(r).find(k=>re.test(k)); return k? String(r[k]??"").trim() : ""; };
  const cat = trova(/^categoria/i);
  let lista = listaFissa;
  if(!lista){ const m = MAPPA_CATEGORIE_CRM.find(([re])=>re.test(cat)); lista = m?m[1]:""; }
  const statoX = trova(/^stato/i).toLowerCase();
  const dataX = trova(/^data contatto/i);
  return {
    lista,
    nome: trova(/^(azienda|nome|ragione)/i),
    referente: trova(/^referente/i),
    indirizzo: trova(/^indirizzo/i),
    comune: trova(/^comune|^citt/i),
    provincia: trova(/^provincia/i),
    telefono: trova(/^telefono/i),
    cellulare: trova(/^cellulare/i),
    whatsapp: trova(/^whatsapp/i),
    email: trova(/^e-?mail/i),
    sito: trova(/^sito/i),
    fonte: trova(/^fonte/i),
    note: [trova(/^note/i), dataX?`Ultimo contatto: ${dataX}`:""].filter(Boolean).join("\n"),
    stato: MAPPA_STATI_IMPORT[statoX] || "Da contattare",
  };
}

function ImportaContatti({contatti, db, onClose, onFatto}){
  const [anteprima, setAnteprima] = useState(null);
  const [listaFissa, setListaFissa] = useState("");
  const [righeGrezze, setRigheGrezze] = useState(null);
  const [errore, setErrore] = useState("");
  const [stato, setStato] = useState(""); // "" | "lettura" | "import" | "fatto"
  const [progresso, setProgresso] = useState(0);

  function prepara(righe, fissa){
    const chiavi = new Set(), telefoni = new Set();
    contatti.forEach(c=>{ chiavi.add(normCRM(c.nome)+"|"+normCRM(c.comune)); [c.telefono,c.cellulare].map(cifre).filter(x=>x.length>=6).forEach(t=>telefoni.add(t)); });
    const nuovi=[], doppi=[], senzaLista=[];
    righe.map(r=>rigaInContatto(r,fissa)).filter(c=>c.nome).forEach(c=>{
      const k = normCRM(c.nome)+"|"+normCRM(c.comune);
      const tels = [c.telefono,c.cellulare].map(cifre).filter(x=>x.length>=6);
      if(chiavi.has(k) || tels.some(t=>telefoni.has(t))){ doppi.push(c); return; }
      if(!c.lista){ senzaLista.push(c); return; }
      chiavi.add(k); tels.forEach(t=>telefoni.add(t)); nuovi.push(c);
    });
    setAnteprima({nuovi, doppi, senzaLista});
  }
  async function leggi(file){
    setErrore(""); setStato("lettura"); setAnteprima(null);
    try{
      const XLSX = await caricaSheetJS();
      const wb = XLSX.read(await file.arrayBuffer(), {type:"array"});
      const nomeFoglio = wb.SheetNames.find(n=>/contatti/i.test(n)) || wb.SheetNames[0];
      const righe = XLSX.utils.sheet_to_json(wb.Sheets[nomeFoglio], {defval:"", raw:false});
      if(!righe.length) throw new Error("Il foglio è vuoto");
      setRigheGrezze(righe); prepara(righe, listaFissa); setStato("");
    }catch(e){ setErrore(e.message||"File non leggibile"); setStato(""); }
  }
  async function importa(){
    const tutti = anteprima.nuovi;
    setStato("import"); setProgresso(0);
    for(let i=0;i<tutti.length;i+=400){
      const {error} = await db.from("contatti").insert(tutti.slice(i,i+400));
      if(error){ setErrore("Errore durante l'import: "+error.message); setStato(""); return; }
      setProgresso(Math.min(tutti.length,i+400));
    }
    await onFatto(); setStato("fatto");
  }
  const perLista = anteprima ? LISTE_CRM.map(l=>({...l,n:anteprima.nuovi.filter(c=>c.lista===l.id).length})).filter(l=>l.n) : [];

  return(
    <Modal title="Importa contatti da Excel" onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {stato==="fatto" ? (<>
          <div style={{fontSize:14,color:C.text}}>Fatto: {anteprima.nuovi.length} contatti importati.</div>
          <Btn onClick={onClose}>Chiudi</Btn>
        </>) : (<>
          <div style={{fontSize:13,color:C.textSub,lineHeight:1.5}}>Carica un file .xlsx o .csv. La lista giusta viene scelta dalla colonna "Categoria"; i contatti già presenti (stesso nome e comune o stesso telefono) vengono saltati.</div>
          <Select label="Lista" value={listaFissa} onChange={v=>{setListaFissa(v); if(righeGrezze) prepara(righeGrezze,v);}}
            options={[{value:"",label:"Automatica dalla colonna Categoria"},...LISTE_CRM.map(l=>({value:l.id,label:"Tutti in: "+l.label}))]}/>
          <label style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"22px",border:`1.5px dashed ${C.border}`,borderRadius:14,cursor:"pointer",fontSize:13.5,fontWeight:600,color:C.textSub,background:C.bg}}>
            {stato==="lettura"?"Lettura del file…":"Scegli file Excel o CSV"}
            <input type="file" accept=".xlsx,.xls,.csv" style={{display:"none"}} onChange={e=>e.target.files?.[0]&&leggi(e.target.files[0])}/>
          </label>
          {errore && <div style={{fontSize:12.5,color:C.red}}>{errore}</div>}
          {anteprima && (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:14,fontWeight:600}}>{anteprima.nuovi.length} nuovi contatti</div>
              {perLista.map(l=><div key={l.id} style={{display:"flex",justifyContent:"space-between",fontSize:13,color:C.textSub}}><span>{l.label}</span><span>{l.n}</span></div>)}
              {anteprima.doppi.length>0 && <div style={{fontSize:12.5,color:C.textMuted}}>{anteprima.doppi.length} già presenti, verranno saltati</div>}
              {anteprima.senzaLista.length>0 && <div style={{fontSize:12.5,color:C.orange}}>{anteprima.senzaLista.length} senza categoria riconosciuta: scegli una lista sopra per importarli</div>}
            </div>
          )}
          {stato==="import" && <div style={{fontSize:12.5,color:C.textSub}}>Importazione… {progresso} di {anteprima.nuovi.length}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
            <Btn onClick={importa} disabled={!anteprima||!anteprima.nuovi.length||stato==="import"}>Importa {anteprima?.nuovi.length||""}</Btn>
          </div>
        </>)}
      </div>
    </Modal>
  );
}

// ── MESSAGGI: email, WhatsApp, Telegram… tutto in un posto ──────────────────────
const CANALI_INFO = {
  whatsapp: {label:"WhatsApp", colore:"#25D366"},
  email:    {label:"Email",    colore:"#2F6FDB"},
  telegram: {label:"Telegram", colore:"#229ED9"},
  instagram:{label:"Instagram",colore:"#C13584", presto:true},
  facebook: {label:"Facebook", colore:"#1877F2", presto:true},
};

async function chiamaApi(db, percorso, dati){
  const {data:{session}} = await db.auth.getSession();
  const r = await fetch(percorso,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${session?.access_token||""}`},body:JSON.stringify(dati||{})});
  const j = await r.json().catch(()=>({}));
  if(!r.ok) throw new Error(j.error||"Qualcosa non ha funzionato, riprova.");
  return j;
}

function oraBreve(ts){
  if(!ts) return "";
  const d=new Date(ts), oggi=new Date();
  if(d.toDateString()===oggi.toDateString()) return d.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"});
  const ieri=new Date(oggi); ieri.setDate(oggi.getDate()-1);
  if(d.toDateString()===ieri.toDateString()) return "Ieri";
  return d.toLocaleDateString("it-IT",{day:"2-digit",month:"short"});
}
const PuntoCanale = ({tipo,size=8}) => <span title={CANALI_INFO[tipo]?.label} style={{width:size,height:size,borderRadius:"50%",background:CANALI_INFO[tipo]?.colore||C.border,flexShrink:0,display:"inline-block"}}/>;

function Messaggi({db, isAdmin, contatti, setContatti, setTab, onNonLetti}){
  const [canali, setCanali]   = useState([]);
  const [conv, setConv]       = useState([]);
  const [pronto, setPronto]   = useState(false);
  const [manca, setManca]     = useState(false); // tabelle non ancora create
  const [filtro, setFiltro]   = usePersistedState("msg_filtro","tutti");
  const [search, setSearch]   = useState("");
  const [apertaId, setApertaId] = useState(null);
  const [thread, setThread]   = useState([]);
  const [testo, setTesto]     = useState("");
  const [invio, setInvio]     = useState(false);
  const [errore, setErrore]   = useState("");
  const [collegamenti, setCollegamenti] = useState(false);
  const [aggiorno, setAggiorno] = useState(false);
  const fondo = useRef(null);
  const isMobile = typeof window!=="undefined" && window.innerWidth<768;

  const carica = useCallback(async()=>{
    const [{data:ca,error:e1},{data:co}] = await Promise.all([
      db.from("canali").select("*").order("created_at"),
      db.from("conversazioni").select("*").order("ultimo_at",{ascending:false}).limit(400),
    ]);
    if(e1 && /relation|does not exist|schema cache/i.test(e1.message||"")){ setManca(true); setPronto(true); return; }
    setCanali(ca||[]); setConv(co||[]); setPronto(true);
    onNonLetti && onNonLetti((co||[]).reduce((s,c)=>s+(c.non_letti||0),0));
  },[db]);

  const caricaThread = useCallback(async(id)=>{
    if(!id) return;
    const {data} = await db.from("messaggi").select("*").eq("conversazione_id",id).order("created_at",{ascending:true});
    setThread(t=> (data||[]).length!==t.length || (data||[]).some((m,i)=>m.id!==t[i]?.id) ? (data||[]) : t);
  },[db]);

  const leggiEmail = useCallback(async(mostra)=>{
    if(mostra) setAggiorno(true);
    try{ await chiamaApi(db,"/api/email-sync"); }catch(e){}
    await carica(); if(mostra) setAggiorno(false);
  },[db,carica]);

  useEffect(()=>{ carica(); },[carica]);
  // nuovi messaggi WhatsApp/Telegram arrivano da soli: ricarico ogni 15 secondi
  useEffect(()=>{ const t=setInterval(()=>{ carica(); caricaThread(apertaId); },15000); return ()=>clearInterval(t); },[carica,caricaThread,apertaId]);
  // le email vanno lette dalla casella: ogni 2 minuti
  const haEmail = canali.some(c=>c.tipo==="email");
  useEffect(()=>{ if(!haEmail) return; leggiEmail(false); const t=setInterval(()=>leggiEmail(false),120000); return ()=>clearInterval(t); },[haEmail,leggiEmail]);
  useEffect(()=>{ fondo.current?.scrollIntoView({block:"end"}); },[thread.length,apertaId]);

  async function apri(c){
    setApertaId(c.id); setThread([]); setErrore(""); setTesto("");
    await caricaThread(c.id);
    if(c.non_letti>0){
      await db.from("conversazioni").update({non_letti:0}).eq("id",c.id);
      setConv(cs=>{ const n=cs.map(x=>x.id===c.id?{...x,non_letti:0}:x); onNonLetti&&onNonLetti(n.reduce((s,x)=>s+(x.non_letti||0),0)); return n; });
    }
  }
  const aperta = conv.find(c=>c.id===apertaId);

  async function invia(){
    const t=testo.trim(); if(!t||!aperta) return;
    setInvio(true); setErrore("");
    try{
      const r = await chiamaApi(db,"/api/invia",{conversazione_id:aperta.id,testo:t});
      setTesto("");
      if(r.messaggio) setThread(th=>[...th,r.messaggio]);
      setConv(cs=>cs.map(x=>x.id===aperta.id?{...x,ultimo_testo:t,ultimo_at:new Date().toISOString(),non_letti:0}:x).sort((a,b)=>new Date(b.ultimo_at)-new Date(a.ultimo_at)));
    }catch(e){ setErrore(e.message); }
    setInvio(false);
  }

  async function salvaContatto(){
    if(!aperta) return;
    const tel = aperta.tipo==="whatsapp" ? aperta.esterno : "";
    const nuovo = {lista:"privati", nome:aperta.nome||aperta.esterno, stato:"Da contattare", fonte:CANALI_INFO[aperta.tipo]?.label||"",
      ...(aperta.tipo==="email"?{email:aperta.esterno}:{}), ...(tel?{cellulare:tel}:{})};
    const {data:n,error} = await db.from("contatti").insert([nuovo]).select().single();
    if(error||!n){ setErrore("Non sono riuscito a salvare il contatto."); return; }
    await db.from("conversazioni").update({contatto_id:n.id}).eq("id",aperta.id);
    setContatti && setContatti(cs=>[...cs,n]);
    setConv(cs=>cs.map(x=>x.id===aperta.id?{...x,contatto_id:n.id}:x));
  }
  function apriScheda(){
    if(!aperta?.contatto_id) return;
    try{ localStorage.setItem("crm_apri",String(aperta.contatto_id)); }catch(e){}
    setTab("contatti");
  }

  const tipiPresenti = [...new Set(canali.map(c=>c.tipo))];
  const q = search.trim().toLowerCase();
  const visibili = conv.filter(c=>(filtro==="tutti"||c.tipo===filtro) && (!q || (c.nome||"").toLowerCase().includes(q) || (c.esterno||"").toLowerCase().includes(q) || (c.ultimo_testo||"").toLowerCase().includes(q)));
  const totNonLetti = conv.reduce((s,c)=>s+(c.non_letti||0),0);
  const fuori24h = aperta?.tipo==="whatsapp" && (!aperta.ultimo_in_at || Date.now()-new Date(aperta.ultimo_in_at).getTime()>24*3600e3);

  if(!pronto) return null;

  const header = (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>Messaggi</div>
        <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{conv.length} conversazioni{totNonLetti>0 && <> · <span style={{color:C.brand,fontWeight:600}}>{totNonLetti} da leggere</span></>}</div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {haEmail && <Btn small variant="ghost" onClick={()=>leggiEmail(true)} disabled={aggiorno}>{aggiorno?"Controllo…":"↻ Controlla email"}</Btn>}
        {isAdmin && <Btn small variant="ghost" onClick={()=>setCollegamenti(true)}>Canali collegati</Btn>}
      </div>
    </div>
  );

  if(manca) return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>{header}
      <div style={{textAlign:"center",padding:"60px 24px",background:C.card,boxShadow:C.shadowSm,borderRadius:16,fontSize:13,color:C.textMuted}}>Manca un passaggio nel database: esegui lo script <b>messaggi.sql</b> in Supabase.</div>
    </div>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {header}

      {canali.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 24px",background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>
          <div style={{fontFamily:C.fontBrand,fontSize:18,fontWeight:600,color:C.text}}>Tutti i messaggi dei clienti, qui</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:6,marginBottom:18}}>Collega email, WhatsApp e Telegram: leggi e rispondi senza uscire da Squadra.</div>
          {isAdmin ? <Btn onClick={()=>setCollegamenti(true)}>Collega un canale</Btn> : <div style={{fontSize:12.5,color:C.textMuted}}>Chiedi all'amministratore di collegare i canali.</div>}
        </div>
      ) : (
        <div style={{display:"flex",background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden",height:isMobile?"calc(100vh - 190px)":"calc(100vh - 210px)",minHeight:420}}>
          {/* Elenco conversazioni */}
          {(!isMobile || !aperta) && (
          <div style={{width:isMobile?"100%":320,flexShrink:0,borderRight:isMobile?"none":`1px solid ${C.border}`,display:"flex",flexDirection:"column",minWidth:0}}>
            <div style={{padding:12,display:"flex",flexDirection:"column",gap:8,borderBottom:`1px solid ${C.border}`}}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca nome, numero, testo…" style={{...fieldBase,padding:"8px 12px",fontSize:13}}/>
              {tipiPresenti.length>1 && <Segmenti value={filtro} onChange={setFiltro} items={[{id:"tutti",label:"Tutti"},...tipiPresenti.map(t=>({id:t,label:CANALI_INFO[t]?.label||t,n:conv.filter(c=>c.tipo===t&&c.non_letti>0).length||null}))]}/>}
            </div>
            <div style={{flex:1,overflowY:"auto"}}>
              {visibili.length===0 && <div style={{padding:"30px 16px",textAlign:"center",fontSize:12.5,color:C.textMuted}}>{conv.length===0?"Ancora nessun messaggio. I nuovi arriveranno qui.":"Nessuna conversazione trovata."}</div>}
              {visibili.map(c=>{
                const on=c.id===apertaId;
                return(
                  <div key={c.id} onClick={()=>apri(c)} style={{display:"flex",gap:10,padding:"11px 14px",cursor:"pointer",background:on?C.bg:"transparent",borderBottom:`1px solid ${C.borderLight}`}}>
                    <div style={{paddingTop:5}}><PuntoCanale tipo={c.tipo}/></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
                        <span style={{fontSize:13.5,fontWeight:c.non_letti>0?700:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nome||c.esterno}</span>
                        <span style={{fontSize:11,color:c.non_letti>0?C.brand:C.textMuted,flexShrink:0}}>{oraBreve(c.ultimo_at)}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",gap:8,marginTop:2}}>
                        <span style={{fontSize:12.5,color:c.non_letti>0?C.text:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.ultimo_testo||"—"}</span>
                        {c.non_letti>0 && <span style={{background:C.brand,color:"#fff",borderRadius:999,fontSize:10.5,fontWeight:700,minWidth:18,height:18,padding:"0 5px",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{c.non_letti}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>)}

          {/* Conversazione aperta */}
          {(!isMobile || aperta) && (
          <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
            {!aperta ? (
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:C.textMuted}}>Scegli una conversazione</div>
            ) : (<>
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:`1px solid ${C.border}`}}>
                {isMobile && <button onClick={()=>setApertaId(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.text,padding:"0 6px 0 0"}}>‹</button>}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14.5,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{aperta.nome||aperta.esterno}</div>
                  <div style={{fontSize:11.5,color:C.textMuted,display:"flex",alignItems:"center",gap:6,marginTop:1}}><PuntoCanale tipo={aperta.tipo} size={6}/>{CANALI_INFO[aperta.tipo]?.label} · {aperta.esterno}</div>
                </div>
                {aperta.contatto_id
                  ? <Btn small variant="ghost" onClick={apriScheda}>Scheda contatto</Btn>
                  : <Btn small variant="ghost" onClick={salvaContatto}>+ Salva nei contatti</Btn>}
              </div>
              <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:8,background:C.bg}}>
                {thread.map((m,i)=>{
                  const mio=m.direzione==="out";
                  const nuovoGiorno = i===0 || new Date(m.created_at).toDateString()!==new Date(thread[i-1].created_at).toDateString();
                  return(
                    <Fragment key={m.id}>
                      {nuovoGiorno && <div style={{textAlign:"center",fontSize:11,color:C.textMuted,margin:"6px 0"}}>{new Date(m.created_at).toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}</div>}
                      <div style={{alignSelf:mio?"flex-end":"flex-start",maxWidth:"78%"}}>
                        <div style={{background:mio?C.accent:C.card,color:mio?"#fff":C.text,boxShadow:mio?"none":`inset 0 0 0 1px ${C.border}`,borderRadius:14,borderBottomRightRadius:mio?4:14,borderBottomLeftRadius:mio?14:4,padding:"8px 12px",fontSize:13.5,lineHeight:1.45,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                          {m.oggetto && aperta.tipo==="email" && <div style={{fontSize:11.5,fontWeight:700,opacity:.75,marginBottom:4}}>{m.oggetto}</div>}
                          {m.testo}
                          {(m.allegati||[]).length>0 && <div style={{fontSize:11.5,opacity:.75,marginTop:6}}>📎 {(m.allegati||[]).map(a=>a.nome).join(", ")}</div>}
                        </div>
                        <div style={{fontSize:10.5,color:C.textMuted,marginTop:3,textAlign:mio?"right":"left"}}>{new Date(m.created_at).toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}{mio&&m.inviato_da?` · ${m.inviato_da.split("@")[0]}`:""}</div>
                      </div>
                    </Fragment>
                  );
                })}
                <div ref={fondo}/>
              </div>
              <div style={{borderTop:`1px solid ${C.border}`,padding:12}}>
                {fuori24h && <div style={{fontSize:11.5,color:C.textSub,background:C.brandSoft,borderRadius:8,padding:"7px 10px",marginBottom:8}}>Il cliente non scrive da più di 24 ore: WhatsApp potrebbe non consegnare un messaggio libero. Se non parte, rispondi dall'app WhatsApp Business.</div>}
                {errore && <div style={{color:C.red,fontSize:12,marginBottom:8}}>{errore}</div>}
                <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                  <textarea value={testo} onChange={e=>setTesto(e.target.value)} rows={Math.min(6,Math.max(1,testo.split("\n").length))}
                    onKeyDown={e=>{ if(e.key==="Enter" && !e.shiftKey && !isMobile){ e.preventDefault(); invia(); } }}
                    placeholder={`Rispondi su ${CANALI_INFO[aperta.tipo]?.label||""}…`} style={{...fieldBase,flex:1,resize:"none",fontSize:13.5,padding:"9px 12px"}}/>
                  <Btn onClick={invia} disabled={invio||!testo.trim()} style={{padding:"9px 18px"}}>{invio?"…":"Invia"}</Btn>
                </div>
              </div>
            </>)}
          </div>)}
        </div>
      )}

      {collegamenti && <CanaliCollegati db={db} canali={canali} onClose={()=>setCollegamenti(false)} onCambio={carica}/>}
    </div>
  );
}

// Facebook serve solo per la finestra ufficiale "Collega WhatsApp" di Meta
function caricaFacebook(appId){
  return new Promise((ok,ko)=>{
    const init = ()=>{ window.FB.init({appId, autoLogAppEvents:true, xfbml:false, version:"v23.0"}); ok(window.FB); };
    if(window.FB) return init();
    window.fbAsyncInit = init;
    if(document.getElementById("fb-sdk")) return;
    const sc = document.createElement("script");
    sc.id="fb-sdk"; sc.src="https://connect.facebook.net/en_US/sdk.js"; sc.async=true; sc.defer=true; sc.crossOrigin="anonymous";
    sc.onerror = ()=>ko(new Error("Impossibile caricare Facebook: controlla la connessione o eventuali blocchi pubblicità."));
    document.body.appendChild(sc);
  });
}

// Solo amministratore: collega le caselle email, il numero WhatsApp, il bot Telegram
function CanaliCollegati({db, canali, onClose, onCambio}){
  const [tipo, setTipo] = useState(null);
  const [f, setF] = useState({});
  const [avanzate, setAvanzate] = useState(false);
  const [lavoro, setLavoro] = useState(false);
  const [errore, setErrore] = useState("");
  const set = k => v => setF(x=>({...x,[k]:v}));
  const [meta, setMeta] = useState(null);      // {app_id, config_id} se il collegamento diretto è configurato
  const [fbPronto, setFbPronto] = useState(false);
  const [via360, setVia360] = useState(false);
  const [viaToken, setViaToken] = useState(false);
  const infoMeta = useRef({});

  useEffect(()=>{
    if(tipo!=="whatsapp" || meta) return;
    chiamaApi(db,"/api/canali",{azione:"meta_config"}).then(m=>{
      setMeta(m);
      if(m.app_id && m.config_id) caricaFacebook(m.app_id).then(()=>setFbPronto(true)).catch(e=>setErrore(e.message));
    }).catch(()=>setMeta({}));
  },[tipo]);

  useEffect(()=>{
    // la finestra di Meta comunica account e numero scelti
    const ascolta = e=>{
      if(!String(e.origin||"").endsWith("facebook.com")) return;
      try{
        const d = typeof e.data==="string" ? JSON.parse(e.data) : e.data;
        if(d?.type!=="WA_EMBEDDED_SIGNUP") return;
        if(String(d.event||"").startsWith("FINISH")) Object.assign(infoMeta.current, d.data||{});
        if(d.event==="ERROR") infoMeta.current.errore = d.data?.error_message;
      }catch(x){}
    };
    window.addEventListener("message",ascolta);
    return ()=>window.removeEventListener("message",ascolta);
  },[]);

  function collegaConMeta(){
    // FB.login va chiamato subito al click, altrimenti il browser blocca la finestra
    setErrore(""); infoMeta.current = {};
    window.FB.login(r=>{
      const code = r?.authResponse?.code;
      if(!code){ setErrore(infoMeta.current.errore || "Collegamento annullato."); return; }
      setLavoro(true);
      setTimeout(async()=>{
        try{
          const i = infoMeta.current;
          await chiamaApi(db,"/api/canali",{azione:"aggiungi",tipo:"whatsapp",code,waba_id:i.waba_id,phone_number_id:i.phone_number_id});
          setTipo(null); await onCambio();
        }catch(e){ setErrore(e.message); }
        setLavoro(false);
      },700);
    },{config_id:meta.config_id, response_type:"code", override_default_response_type:true, extras:{setup:{}, featureType:"whatsapp_business_app_onboarding", sessionInfoVersion:"3"}});
  }

  async function collega(){
    setLavoro(true); setErrore("");
    try{ await chiamaApi(db,"/api/canali",{azione:"aggiungi",tipo,...f}); setTipo(null); setF({}); await onCambio(); }
    catch(e){ setErrore(e.message); }
    setLavoro(false);
  }
  async function rimuovi(c){
    if(!window.confirm(`Scollegare ${c.nome}? Le conversazioni di questo canale verranno cancellate da Squadra (non dal telefono o dalla casella).`)) return;
    try{ await chiamaApi(db,"/api/canali",{azione:"rimuovi",id:c.id}); await onCambio(); }catch(e){ window.alert(e.message); }
  }
  const Passo = ({n,children}) => <div style={{display:"flex",gap:10,fontSize:12.5,color:C.textSub,lineHeight:1.5}}><span style={{width:18,height:18,borderRadius:"50%",background:C.bg,color:C.text,fontSize:11,fontWeight:700,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{n}</span><span>{children}</span></div>;
  const pronto = tipo==="email" ? (f.email&&f.password) : tipo==="whatsapp" ? ((via360 && f.api_key) || (viaToken && f.meta_token && f.phone_number_id && f.waba_id)) : tipo==="telegram" ? f.token : false;
  const metaDiretto = tipo==="whatsapp" && !via360 && !viaToken;

  return(
    <Modal title="Canali collegati" onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
        {canali.length===0 && <div style={{fontSize:12.5,color:C.textMuted}}>Nessun canale collegato.</div>}
        {canali.map(c=>(
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,background:C.bg,borderRadius:10,padding:"10px 12px"}}>
            <PuntoCanale tipo={c.tipo}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{c.nome}</div>
              <div style={{fontSize:11,color:c.stato==="errore"?C.red:C.textMuted}}>{CANALI_INFO[c.tipo]?.label} · {c.stato==="errore"?(c.ultimo_errore||"Errore"):"Attivo"}</div>
            </div>
            <button onClick={()=>rimuovi(c)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:12,fontWeight:600}}>Scollega</button>
          </div>
        ))}
      </div>

      <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16}}>
        <div style={{fontSize:12.5,fontWeight:700,color:C.text,marginBottom:10}}>Collega un canale</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {Object.entries(CANALI_INFO).map(([k,v])=>(
            <button key={k} disabled={v.presto} onClick={()=>{setTipo(k);setF({});setErrore("");setViaToken(false);}} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 12px",borderRadius:999,border:`1px solid ${tipo===k?C.accent:C.border}`,background:tipo===k?C.accentGlow:C.card,color:v.presto?C.textMuted:C.text,fontSize:12.5,fontWeight:600,cursor:v.presto?"default":"pointer",fontFamily:"inherit"}}>
              <PuntoCanale tipo={k} size={7}/>{v.label}{v.presto && <span style={{fontSize:10.5,fontWeight:500}}>· presto</span>}
            </button>
          ))}
        </div>

        {tipo==="email" && <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Input label="Indirizzo email" value={f.email||""} onChange={set("email")} placeholder="info@tuaazienda.it"/>
          <Input label="Password della casella" type="password" value={f.password||""} onChange={set("password")}/>
          <Input label="Nome che vedono i clienti (facoltativo)" value={f.nome_mittente||""} onChange={set("nome_mittente")} placeholder="Parquetti Group"/>
          <button onClick={()=>setAvanzate(a=>!a)} style={{alignSelf:"flex-start",background:"none",border:"none",color:C.textMuted,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>{avanzate?"Nascondi":"Non è una casella Aruba?"}</button>
          {avanzate && <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}><Input label="Server in arrivo (IMAP)" value={f.imap_host||""} onChange={set("imap_host")} placeholder="imaps.aruba.it"/></div>
            <div style={{flex:1}}><Input label="Server in uscita (SMTP)" value={f.smtp_host||""} onChange={set("smtp_host")} placeholder="smtps.aruba.it"/></div>
          </div>}
          <div style={{fontSize:11.5,color:C.textMuted}}>La password resta solo sul server di Squadra: nessuno, nemmeno chi usa il gestionale, può leggerla. Arrivano solo le mail delle persone: newsletter e notifiche automatiche vengono saltate.</div>
        </div>}

        {tipo==="whatsapp" && !via360 && !viaToken && <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {meta===null && <div style={{fontSize:12.5,color:C.textMuted}}>Un attimo…</div>}
          {meta && !(meta.app_id && meta.config_id) && <div style={{fontSize:12.5,color:C.textSub,background:C.bg,borderRadius:10,padding:"10px 12px"}}>Il collegamento diretto con Meta non è ancora attivo: mancano i dati dell'app Meta su Vercel (META_APP_ID, META_CONFIG_ID).</div>}
          {meta?.app_id && meta?.config_id && <>
            <Passo n={1}>Tieni a portata di mano il telefono con <b>WhatsApp Business</b> aggiornato.</Passo>
            <Passo n={2}>Premi il pulsante qui sotto: si apre la finestra ufficiale di Meta. Accedi con Facebook, inserisci il numero e scegli di <b>collegare l'app WhatsApp Business</b> che già usi.</Passo>
            <Passo n={3}>Sul telefono ti arriva un messaggio: aprilo e <b>inquadra il QR</b>. Accetta la condivisione della cronologia per avere in Squadra anche le chat passate.</Passo>
            <div style={{fontSize:11.5,color:C.textMuted}}>Il numero resta sul telefono e continui a usarlo come sempre. Arrivano qui i messaggi ricevuti e quelli che scrivi dall'app. Apri WhatsApp Business almeno ogni 2 settimane.</div>
          </>}
          <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
            <button onClick={()=>{setViaToken(true);setErrore("");}} style={{background:"none",border:"none",color:C.textMuted,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>Collega con token</button>
            <button onClick={()=>setVia360(true)} style={{background:"none",border:"none",color:C.textMuted,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>Usi già 360dialog?</button>
          </div>
        </div>}

        {tipo==="whatsapp" && viaToken && <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Passo n={1}>Per un numero già presente nel tuo portfolio Meta (anche il <b>numero di prova</b>). Phone Number ID e ID dell'account li trovi in developers.facebook.com › WhatsApp.</Passo>
          <Passo n={2}>Il token lo crei in Business Suite › Impostazioni › <b>Utenti di sistema</b> (scadenza: mai, autorizzazioni WhatsApp).</Passo>
          <Input label="Phone Number ID" value={f.phone_number_id||""} onChange={set("phone_number_id")} placeholder="1383778884827226"/>
          <Input label="ID dell'account WhatsApp" value={f.waba_id||""} onChange={set("waba_id")} placeholder="3003908…"/>
          <Input label="Token permanente" type="password" value={f.meta_token||""} onChange={set("meta_token")}/>
          <div style={{fontSize:11.5,color:C.textMuted}}>Il token resta solo sul server di Squadra. Con questo collegamento non arriva lo storico delle chat passate.</div>
          <button onClick={()=>setViaToken(false)} style={{alignSelf:"flex-start",background:"none",border:"none",color:C.textMuted,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>Collega con la finestra di Meta</button>
        </div>}

        {tipo==="whatsapp" && via360 && <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Passo n={1}>Nel pannello di 360dialog genera la <b>chiave API</b> del numero.</Passo>
          <Input label="Numero (per riconoscerlo)" value={f.numero||""} onChange={set("numero")} placeholder="+39 333 1234567"/>
          <Input label="Chiave API 360dialog" type="password" value={f.api_key||""} onChange={set("api_key")}/>
          <button onClick={()=>setVia360(false)} style={{alignSelf:"flex-start",background:"none",border:"none",color:C.textMuted,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>Collega direttamente con Meta</button>
        </div>}

        {tipo==="telegram" && <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Passo n={1}>In Telegram apri <b>@BotFather</b>, scrivi <b>/newbot</b>, scegli un nome e copia il <b>token</b> che ti dà.</Passo>
          <Passo n={2}>Incollalo qui sotto e premi Collega.</Passo>
          <Passo n={3}>Per ricevere i messaggi del tuo account: Telegram › Impostazioni › <b>Telegram Business › Chatbot</b>, aggiungi il bot e dagli il permesso di rispondere.</Passo>
          <Input label="Token del bot" type="password" value={f.token||""} onChange={set("token")} placeholder="123456:ABC-…"/>
        </div>}

        {errore && <div style={{color:C.red,fontSize:12,marginTop:10}}>{errore}</div>}
        {metaDiretto && meta?.app_id && meta?.config_id && <Btn onClick={collegaConMeta} disabled={lavoro||!fbPronto} style={{marginTop:14}}>{lavoro?"Collegamento…":(fbPronto?"Collega WhatsApp Business":"Preparo…")}</Btn>}
        {tipo && !metaDiretto && <Btn onClick={collega} disabled={lavoro||!pronto} style={{marginTop:14}}>{lavoro?"Collegamento…":"Collega"}</Btn>}
      </div>
    </Modal>
  );
}

// ── PREVENTIVI ────────────────────────────────────────────────────────────────
const TARIFFE = [
  { id:"prefinito",    label:"Posa prefinito",           unita:"mq",  prezzo:20,   mat:3.5,  note:"colla" },
  { id:"battiscopa",   label:"Battiscopa",               unita:"ml",  prezzo:6,    mat:1.2,  note:"silicone+chiodi" },
  { id:"decking",      label:"Decking esterno",          unita:"mq",  prezzo:47,   mat:0,    note:"" },
  { id:"lamatura",     label:"Lamatura",                 unita:"mq",  prezzo:28,   mat:6,    note:"abrasivi" },
  { id:"rivestimento", label:"Rivestimento a parete",    unita:"mq",  prezzo:35,   mat:0,    note:"" },
  { id:"autolivellante",label:"Autolivellante",          unita:"mq",  prezzo:12,   mat:0,    note:"" },
  { id:"facchinaggio_pref", label:"Facchinaggio prefinito", unita:"mq", prezzo:7,  mat:0,    note:"" },
  { id:"facchinaggio_deck", label:"Facchinaggio decking/rivestimento", unita:"mq", prezzo:12, mat:0, note:"" },
  { id:"sopralluogo",  label:"Sopralluogo",              unita:"cad", prezzo:100,  mat:0,    note:"" },
];

function Preventivi({lavori=[], setLavori=()=>{}, operai=[], rivendite=[], db=null, setTab=()=>{}}) {
  // ── AI Import state ──
  const [aiModal, setAiModal] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiFile, setAiFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState("");
  const [righe, setRighe] = useState(
    TARIFFE.map(t => ({ ...t, qty: "", conMateriali: false, attiva: false }))
  );
  const [nuovaVoce, setNuovaVoce] = useState(false);
  const [formVoce, setFormVoce] = useState({label:"",unita:"mq",prezzo:"",mat:"",note:"",permanente:true});

  useEffect(()=>{
    if(!db) return;
    db.from("tariffe_custom").select("*").order("label").then(({data})=>{
      if(data?.length) setRighe(rs=>[...rs, ...data.map(t=>({id:"custom_"+t.id, label:t.label, unita:t.unita, prezzo:t.prezzo, mat:t.mat||0, note:t.note||"", qty:"", conMateriali:false, attiva:false}))]);
    });
  },[]);

  async function salvaNuovaVoce(){
    if(!formVoce.label.trim()||!formVoce.prezzo) return;
    const voce = {label:formVoce.label.trim(), unita:formVoce.unita||"mq", prezzo:parseFloat(formVoce.prezzo)||0, mat:parseFloat(formVoce.mat)||0, note:formVoce.note||""};
    if(formVoce.permanente && db){
      const {data:n} = await db.from("tariffe_custom").insert([voce]).select().single();
      setRighe(rs=>[...rs, {id:"custom_"+n.id, ...voce, qty:"", conMateriali:false, attiva:true}]);
    } else {
      setRighe(rs=>[...rs, {id:"temp_"+Date.now(), ...voce, qty:"", conMateriali:false, attiva:true}]);
    }
    setFormVoce({label:"",unita:"mq",prezzo:"",mat:"",note:"",permanente:true});
    setNuovaVoce(false);
  }
  const [cliente, setCliente] = useState("");
  const [indirizzo, setIndirizzo] = useState("");
  const [note, setNote] = useState("");
  const [conIva, setConIva] = useState(false);
  const [opzioniMultiple, setOpzioniMultiple] = useState(false);
  const [opzioni, setOpzioni] = useState([{titolo:"",prezzo:"",descrizione:""}]);
  function addOpzione(){ if(opzioni.length<3) setOpzioni(o=>[...o,{titolo:"",prezzo:"",descrizione:""}]); }
  function updOpzione(i,field,val){ setOpzioni(o=>{ const n=[...o]; n[i]={...n[i],[field]:val}; return n; }); }
  function delOpzione(i){ setOpzioni(o=>o.filter((_,idx)=>idx!==i)); }


  // ── AI Import functions ──
  async function analizzaPreventivo(){
    setAiLoading(true); setAiError(""); setAiResult(null);
    try {
      let messages;

      if(aiFile){
        // Legge il PDF come base64 e lo manda alla route Vercel
        const base64 = await new Promise((res,rej)=>{
          const r=new FileReader();
          r.onload=()=>res(r.result.split(",")[1]);
          r.onerror=()=>rej(new Error("Lettura file fallita"));
          r.readAsDataURL(aiFile);
        });
        messages = [{role:"user", content:[
          {type:"document", source:{type:"base64", media_type:"application/pdf", data:base64}},
          {type:"text", text:"Analizza questo preventivo italiano di lavori di pavimentazione/parquet, leggendo l'intero documento con estrema attenzione prima di rispondere. Rispondi SOLO con un oggetto JSON valido, niente markdown, niente testo prima o dopo.\n\nREGOLE IMPORTANTI PER I NUMERI: i documenti italiani usano il punto come separatore delle migliaia e la virgola per i decimali (es. \"1.250,00\" = 1250.00). Converti sempre in numero puro con il punto come separatore decimale, mai il punto delle migliaia. Se il documento mostra più totali (es. imponibile, IVA, totale finale, eventuali sconti), individua con precisione quale sia l'importo imponibile netto e quale il totale con IVA — non confonderli. Se ci sono più pagine o sezioni con importi diversi, usa il totale finale del documento, non un subtotale parziale. Ricontrolla che l'importo estratto corrisponda esattamente a quanto scritto, cifra per cifra.\n\nCampi richiesti: titolo (stringa breve tipo Posa decking - NomeCliente), cliente (nome esatto come scritto nel documento), indirizzo, importo (numero, imponibile netto), importo_iva (numero o null, totale con IVA se indicato), data_preventivo (YYYY-MM-DD o null), numero_preventivo, stato (sempre Preventivo), note (descrizione breve lavori), mq_stimati (numero o null), materiale (tipo di pavimento o null), voci (array di oggetti con descrizione, quantita, unita, prezzo_unitario, importo — trascrivi ogni riga del documento, non riassumere), analisi (oggetto con: completezza — stringa breve su cosa manca o è ambiguo nel documento tipo misure, tempistiche, condizioni di pagamento; rischi — array di massimo 3 stringhe brevi su possibili criticità o clausole da chiarire col cliente; suggerimenti — array di massimo 3 stringhe brevi su cosa verificare o aggiungere prima di confermare il lavoro).\n\nSe un campo non è presente nel documento, usa null invece di inventare un valore."}
        ]}];
      } else {
        if(!aiInput.trim()){ setAiError("Carica un PDF o incolla il testo."); setAiLoading(false); return; }
        messages = [{role:"user", content:"Analizza questo preventivo italiano di lavori di pavimentazione/parquet, leggendolo con estrema attenzione prima di rispondere. Rispondi SOLO con JSON valido senza markdown.\n\nREGOLE PER I NUMERI: i documenti italiani usano il punto per le migliaia e la virgola per i decimali (es. \"1.250,00\" = 1250.00). Converti sempre in numero puro con punto decimale, senza il punto delle migliaia. Se ci sono più totali (imponibile, IVA, totale finale), non confonderli: importo = imponibile netto, importo_iva = totale con IVA. Usa il totale finale del documento, non un subtotale parziale. Ricontrolla ogni cifra prima di rispondere.\n\nCampi: titolo, cliente (nome esatto come scritto), indirizzo, importo, importo_iva, data_preventivo, numero_preventivo, stato (sempre Preventivo), note, mq_stimati, materiale, voci:[{descrizione,quantita,unita,prezzo_unitario,importo}] (trascrivi ogni riga, non riassumere), analisi:{completezza (stringa breve su cosa manca o è ambiguo, tipo misure/tempistiche/condizioni pagamento), rischi (array max 3 stringhe brevi su criticità da chiarire), suggerimenti (array max 3 stringhe brevi su cosa verificare prima di confermare)}. Se un campo non è presente usa null, non inventare.\n\nPreventivo:\n"+aiInput}];
      }

      const resp = await fetch("/api/analyze", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({messages})
      });

      if(!resp.ok){
        const err = await resp.text();
        setAiError("Errore server: "+err);
        setAiLoading(false);
        return;
      }

      const data = await resp.json();
      if(data.error){ setAiError("Errore API: "+data.error); setAiLoading(false); return; }

      const raw = (data.content||[]).map(b=>b.text||"").join("").trim();
      const clean = raw.replace(/^```json\s*/,"").replace(/^```\s*/,"").replace(/\s*```$/,"").trim();
      const parsed = JSON.parse(clean);
      setAiResult(parsed);

    } catch(e){
      setAiError("Errore: "+e.message);
    }
    setAiLoading(false);
  }
  async function creaLavoroDaAI(){
    if(!aiResult||!db) return;
    const today = new Date().toISOString().split("T")[0];
    const importoBase = parseFloat(aiResult.importo)||0;
    const scontoVal = parseFloat(aiResult.sconto_valore)||0;
    const scontoEuro = scontoVal<=0 ? 0 : (aiResult.sconto_tipo==="fisso" ? scontoVal : importoBase*scontoVal/100);
    const totFinale = Math.max(0, importoBase - scontoEuro);
    const nuovoLavoro = {
      titolo: aiResult.titolo||"Preventivo importato",
      cliente: aiResult.cliente||"",
      indirizzo: aiResult.indirizzo||"",
      preventivo: totFinale||null,
      stato: "Preventivo",
      data_inizio: aiResult.data_preventivo||today,
      data_fine: aiResult.data_preventivo||today,
      mq_stimati: aiResult.mq_stimati||null,
      materiale: aiResult.materiale||"",
      note: [
        aiResult.numero_preventivo?`N° preventivo: ${aiResult.numero_preventivo}`:"",
        aiResult.note||"",
        scontoEuro>0?`Sconto applicato: -€${scontoEuro.toFixed(2)} (importo pieno €${importoBase.toFixed(2)})`:"",
        aiResult.voci?.length ? "VOCI:\n"+aiResult.voci.map(v=>`- ${v.descrizione}: ${v.quantita} ${v.unita} × €${v.prezzo_unitario} = €${v.importo}`).join("\n") : ""
      ].filter(Boolean).join("\n\n"),
      operai_ids:[],
      rivendita_id: rivendite[0]?.id||null,
      saldato:false,
      rapporti:[],
    };
    const {data:n} = await db.from("lavori").insert([nuovoLavoro]).select().single();
    if(n){
      setLavori(ls=>[n,...ls]);
      setAiModal(false);
      setAiResult(null);
      setAiInput("");
      setAiFile(null);
      setTab("preventivi");
    }
  }

    const toggleRiga = (id) => setRighe(rs => rs.map(r => r.id === id ? { ...r, attiva: !r.attiva, qty: r.attiva ? "" : r.qty } : r));
  const setQty = (id, v) => setRighe(rs => rs.map(r => r.id === id ? { ...r, qty: v } : r));
  const toggleMat = (id) => setRighe(rs => rs.map(r => r.id === id ? { ...r, conMateriali: !r.conMateriali } : r));

  const righeAttive = righe.filter(r => r.attiva && parseFloat(r.qty) > 0);

  const totaleManodopera = righeAttive.reduce((s, r) => s + parseFloat(r.qty) * r.prezzo, 0);
  const totaleMateriali = righeAttive.reduce((s, r) => s + (r.conMateriali ? parseFloat(r.qty) * r.mat : 0), 0);
  const totaleNetto = totaleManodopera + totaleMateriali;
  const iva = conIva ? totaleNetto * 0.22 : 0;
  const totaleLordo = totaleNetto + iva;

  const generaPDF = () => {
    const dataOggi = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    const righeHTML = righeAttive.map(r => {
      const sub = parseFloat(r.qty) * r.prezzo;
      const subMat = r.conMateriali ? parseFloat(r.qty) * r.mat : 0;
      return `
        <tr>
          <td>${r.label}</td>
          <td style="text-align:center">${r.qty} ${r.unita}</td>
          <td style="text-align:right">€${r.prezzo.toFixed(2)}/${r.unita}</td>
          <td style="text-align:right">€${sub.toFixed(2)}</td>
          ${r.conMateriali && r.mat > 0 ? `<td style="text-align:right;color:#888">+ €${subMat.toFixed(2)} mat.</td>` : '<td></td>'}
        </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8">
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; color: #222; margin: 0; padding: 40px; font-size: 13px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 3px solid #E3701F; padding-bottom: 20px; }
      .logo { font-size: 28px; font-weight: 900; color: #16171A; letter-spacing: 2px; }
      .logo-sub { font-size: 11px; color: #888; letter-spacing: 3px; text-transform: uppercase; }
      .info { text-align: right; font-size: 12px; color: #555; }
      .num-prev { font-size: 22px; font-weight: 700; color: #222; margin-bottom: 30px; }
      .cliente { background: #f8f6f2; border-left: 4px solid #E3701F; padding: 16px 20px; border-radius: 4px; margin-bottom: 30px; }
      .cliente-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 4px; }
      .cliente-nome { font-size: 16px; font-weight: 700; }
      .cliente-ind { font-size: 13px; color: #555; margin-top: 4px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      th { background: #16171A; color: #FAFAF8; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
      td { padding: 10px 12px; border-bottom: 1px solid #eee; }
      tr:nth-child(even) td { background: #fafafa; }
      .totali { margin-left: auto; width: 280px; }
      .tot-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
      .tot-sep { border-top: 1px solid #ddd; margin: 6px 0; }
      .tot-finale { display: flex; justify-content: space-between; padding: 10px 0; font-size: 18px; font-weight: 900; color: #16171A; }
      .note-box { margin-top: 30px; font-size: 12px; color: #666; border-top: 1px solid #eee; padding-top: 16px; }
      .footer { margin-top: 50px; text-align: center; font-size: 11px; color: #aaa; border-top: 1px solid #eee; padding-top: 16px; }
      .opzioni-title { font-size: 15px; font-weight: 700; color: #222; margin: 30px 0 14px; text-align: center; }
      .opzioni-grid { display: flex; gap: 16px; justify-content: center; }
      .opzione-card { flex: 1; max-width: 220px; border: 2px solid #eee; border-radius: 10px; padding: 18px 16px; text-align: center; }
      .opzione-titolo { font-size: 14px; font-weight: 700; color: #222; margin-bottom: 6px; }
      .opzione-desc { font-size: 11px; color: #888; margin-bottom: 14px; line-height: 1.5; }
      .opzione-prezzo { font-size: 22px; font-weight: 900; color: #16171A; }
    </style></head><body>
    <div class="header">
      <div><img src="https://parquetti.it/wp-content/uploads/2026/03/logo-parquetti-header.png" alt="Parquetti" style="height:36px;object-fit:contain;"/></div>
      <div class="info">
        <div>Data: ${dataOggi}</div>
        <div>Preventivo N° ${Math.floor(Math.random() * 9000) + 1000}</div>
      </div>
    </div>
    ${cliente ? `<div class="cliente"><div class="cliente-label">Cliente</div><div class="cliente-nome">${cliente}</div>${indirizzo ? `<div class="cliente-ind">📍 ${indirizzo}</div>` : ""}</div>` : ""}
    <table>
      <thead><tr><th>Lavorazione</th><th style="text-align:center">Quantità</th><th style="text-align:right">Prezzo unit.</th><th style="text-align:right">Manodopera</th><th style="text-align:right">Materiali</th></tr></thead>
      <tbody>${righeHTML}</tbody>
    </table>
    <div class="totali">
      <div class="tot-row"><span>Manodopera</span><span>€${totaleManodopera.toFixed(2)}</span></div>
      ${totaleMateriali > 0 ? `<div class="tot-row"><span>Materiali</span><span>€${totaleMateriali.toFixed(2)}</span></div>` : ""}
      <div class="tot-sep"></div>
      <div class="tot-row"><span>Totale netto</span><span>€${totaleNetto.toFixed(2)}</span></div>
      ${conIva ? `<div class="tot-row"><span>IVA 22%</span><span>€${iva.toFixed(2)}</span></div>` : ""}
      ${(!opzioniMultiple||opzioni.filter(o=>o.titolo&&o.prezzo).length===0) ? `<div class="tot-finale"><span>TOTALE</span><span>€${totaleLordo.toFixed(2)}</span></div>` : ""}
    </div>
    ${opzioniMultiple && opzioni.filter(o=>o.titolo&&o.prezzo).length>0 ? `
    <div class="opzioni-title">Scegli l'opzione preferita</div>
    <div class="opzioni-grid">
      ${opzioni.filter(o=>o.titolo&&o.prezzo).map(o=>`
        <div class="opzione-card">
          <div class="opzione-titolo">${o.titolo}</div>
          ${o.descrizione ? `<div class="opzione-desc">${o.descrizione}</div>` : ""}
          <div class="opzione-prezzo">€${parseFloat(o.prezzo).toFixed(2)}</div>
        </div>
      `).join("")}
    </div>` : ""}
    ${note ? `<div class="note-box">Note: ${note}</div>` : ""}
    <div class="footer">Parquetti Service · Preventivo generato il ${dataOggi}</div>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    setTimeout(() => { w?.print(); URL.revokeObjectURL(url); }, 800);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{ fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize: 20, fontWeight: 800, color: C.text }}>💰 Preventivi</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>Crea un preventivo manuale o importa da Fatture in Cloud con l'AI</div>
        </div>
        <Btn onClick={()=>{setAiModal(true);setAiResult(null);setAiError("");setAiInput("");setAiFile(null);}}>🤖 Importa da PDF / Testo</Btn>
      </div>

      {/* ── AI Import Modal ── */}
      {aiModal&&(
        <Modal title="🤖 Importa Preventivo con AI" onClose={()=>setAiModal(false)} wide>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {!aiResult ? (
              <>
                <div style={{fontSize:13,color:C.textMuted}}>Carica il PDF del preventivo da Fatture in Cloud, oppure incolla il testo. Gemini AI estrae tutto automaticamente e crea il lavoro nel gestionale.</div>
                {/* Upload PDF */}
                <div style={{border:`2px dashed ${C.border}`,borderRadius:16,padding:"20px",textAlign:"center",cursor:"pointer",background:aiFile?C.accentGlow:C.bg}}
                  onClick={()=>document.getElementById("ai-pdf-input").click()}>
                  <input id="ai-pdf-input" type="file" accept="application/pdf" style={{display:"none"}} onChange={e=>setAiFile(e.target.files[0]||null)}/>
                  {aiFile
                    ? <div><div style={{fontSize:22}}>📄</div><div style={{fontSize:13,fontWeight:700,color:C.accent,marginTop:6}}>{aiFile.name}</div><div style={{fontSize:11,color:C.textMuted,marginTop:4}}>Clicca per cambiare</div></div>
                    : <div><div style={{fontSize:28}}>📥</div><div style={{fontSize:13,color:C.textMuted,marginTop:8}}>Clicca per caricare il PDF del preventivo</div></div>
                  }
                </div>
                <div style={{textAlign:"center",fontSize:12,color:C.textMuted}}>— oppure incolla il testo —</div>
                <textarea value={aiInput} onChange={e=>setAiInput(e.target.value)} rows={6}
                  placeholder={"Incolla qui il testo del preventivo da Fatture in Cloud...\n\nEs:\nCliente: Mario Rossi\nTotale: €4.017,75"}
                  style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,color:C.text,padding:"10px 12px",fontSize:13,fontFamily:"inherit",outline:"none",resize:"vertical",width:"100%",boxSizing:"border-box"}}/>
                {aiError&&<div style={{background:C.red+"15",border:`1px solid ${C.red}44`,borderRadius:11,padding:"10px 14px",fontSize:13,color:C.red}}>{aiError}</div>}
                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <Btn variant="ghost" onClick={()=>setAiModal(false)}>Annulla</Btn>
                  <Btn onClick={analizzaPreventivo} disabled={aiLoading||(!aiFile&&!aiInput.trim())}>
                    {aiLoading?"🔄 Analisi in corso…":"🤖 Analizza con AI"}
                  </Btn>
                </div>
              </>
            ) : (
              <>
                <div style={{background:C.green+"12",border:`1px solid ${C.green}44`,borderRadius:14,padding:"12px 16px",fontSize:13,color:C.green,fontWeight:700}}>✅ Preventivo analizzato — controlla e modifica i dati prima di importare</div>

                {(()=>{
                  const mq = parseFloat(aiResult.mq_stimati);
                  const importo = parseFloat(aiResult.importo);
                  const prezzoMq = (mq>0 && importo>0) ? importo/mq : null;
                  const tariffeMq = TARIFFE.filter(t=>t.unita==="mq");
                  const range = tariffeMq.map(t=>t.prezzo+t.mat);
                  const minRef = Math.min(...range), maxRef = Math.max(...range);
                  let confrontoPrezzo = null;
                  if(prezzoMq!==null){
                    if(prezzoMq < minRef*0.6) confrontoPrezzo = {tono:"warn", testo:`€${prezzoMq.toFixed(0)}/mq sembra basso rispetto al vostro listino (€${minRef}-€${maxRef}/mq) — verifica che non manchino voci.`};
                    else if(prezzoMq > maxRef*1.6) confrontoPrezzo = {tono:"warn", testo:`€${prezzoMq.toFixed(0)}/mq è più alto del vostro listino (€${minRef}-€${maxRef}/mq) — controlla se include extra giustificati.`};
                    else confrontoPrezzo = {tono:"ok", testo:`€${prezzoMq.toFixed(0)}/mq è in linea con il vostro listino (€${minRef}-€${maxRef}/mq).`};
                  }
                  const an = aiResult.analisi;
                  if(!an && !confrontoPrezzo) return null;
                  return(
                    <div style={{background:C.bg,borderRadius:14,padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
                      <div style={{fontSize:11,color:C.textMuted,fontWeight:700,letterSpacing:.5}}>🔎 ANALISI APPROFONDITA</div>
                      {confrontoPrezzo && <div style={{fontSize:12.5,color:confrontoPrezzo.tono==="ok"?C.green:C.orange,fontWeight:600}}>{confrontoPrezzo.tono==="ok"?"✓":"⚠️"} {confrontoPrezzo.testo}</div>}
                      {an?.completezza && <div style={{fontSize:12.5,color:C.textSub}}><b style={{color:C.text}}>Completezza:</b> {an.completezza}</div>}
                      {an?.rischi?.length>0 && (
                        <div style={{fontSize:12.5,color:C.textSub}}>
                          <b style={{color:C.text}}>Da chiarire:</b>
                          <ul style={{margin:"4px 0 0",paddingLeft:18}}>{an.rischi.map((r,i)=><li key={i}>{r}</li>)}</ul>
                        </div>
                      )}
                      {an?.suggerimenti?.length>0 && (
                        <div style={{fontSize:12.5,color:C.textSub}}>
                          <b style={{color:C.text}}>Suggerimenti:</b>
                          <ul style={{margin:"4px 0 0",paddingLeft:18}}>{an.suggerimenti.map((s,i)=><li key={i}>{s}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <Input label="Titolo lavoro" value={aiResult.titolo||""} onChange={v=>setAiResult(r=>({...r,titolo:v}))}/>
                  <Input label="Cliente" value={aiResult.cliente||""} onChange={v=>setAiResult(r=>({...r,cliente:v}))}/>
                  <Input label="Indirizzo" value={aiResult.indirizzo||""} onChange={v=>setAiResult(r=>({...r,indirizzo:v}))}/>
                  <Input label="N° Preventivo" value={aiResult.numero_preventivo||""} onChange={v=>setAiResult(r=>({...r,numero_preventivo:v}))}/>
                  <Input label="Importo (imponibile)" type="number" value={aiResult.importo||""} onChange={v=>setAiResult(r=>({...r,importo:v}))}/>
                  <Input label="Totale con IVA" type="number" value={aiResult.importo_iva||""} onChange={v=>setAiResult(r=>({...r,importo_iva:v}))}/>
                  <Input label="mq stimati" type="number" value={aiResult.mq_stimati||""} onChange={v=>setAiResult(r=>({...r,mq_stimati:v}))}/>
                  <Input label="Materiale" value={aiResult.materiale||""} onChange={v=>setAiResult(r=>({...r,materiale:v}))}/>
                </div>

                <div style={{background:C.bg,borderRadius:11,padding:"12px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div style={{fontSize:11,color:C.textMuted,fontWeight:700,letterSpacing:.5}}>VOCI PREVENTIVO — modificabili</div>
                    <button onClick={()=>setAiResult(r=>({...r, voci:[...(r.voci||[]), {descrizione:"",quantita:1,unita:"mq",prezzo_unitario:0,importo:0}]}))}
                      style={{background:"none",border:"none",color:C.accent,fontSize:12,fontWeight:700,cursor:"pointer"}}>+ Lavorazione extra</button>
                  </div>
                  {(aiResult.voci||[]).length===0 && <div style={{fontSize:12.5,color:C.textMuted}}>Nessuna voce. Aggiungine una con il pulsante sopra.</div>}
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {(aiResult.voci||[]).map((v,i)=>(
                      <div key={i} style={{background:C.card,borderRadius:9,padding:9,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                        <input value={v.descrizione||""} onChange={e=>setAiResult(r=>{const vv=[...r.voci];vv[i]={...vv[i],descrizione:e.target.value};return {...r,voci:vv};})}
                          placeholder="Descrizione lavorazione" style={{...fieldBase,flex:"2 1 140px",padding:"6px 9px",fontSize:12.5}}/>
                        <input type="number" value={v.quantita??""} onChange={e=>setAiResult(r=>{const vv=[...r.voci];vv[i]={...vv[i],quantita:e.target.value};return {...r,voci:vv};})}
                          placeholder="Qtà" style={{...fieldBase,width:56,padding:"6px 7px",fontSize:12.5}}/>
                        <input value={v.unita||""} onChange={e=>setAiResult(r=>{const vv=[...r.voci];vv[i]={...vv[i],unita:e.target.value};return {...r,voci:vv};})}
                          placeholder="u.m." style={{...fieldBase,width:52,padding:"6px 7px",fontSize:12.5}}/>
                        <input type="number" value={v.prezzo_unitario??""} onChange={e=>setAiResult(r=>{const vv=[...r.voci];vv[i]={...vv[i],prezzo_unitario:e.target.value};return {...r,voci:vv};})}
                          placeholder="€/u" style={{...fieldBase,width:66,padding:"6px 7px",fontSize:12.5}}/>
                        <input type="number" value={v.importo??""} onChange={e=>setAiResult(r=>{const vv=[...r.voci];vv[i]={...vv[i],importo:e.target.value};return {...r,voci:vv};})}
                          placeholder="Tot €" style={{...fieldBase,width:72,padding:"6px 7px",fontSize:12.5,fontWeight:700}}/>
                        <button onClick={()=>setAiResult(r=>({...r, voci:r.voci.filter((_,idx)=>idx!==i)}))} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:15}}>×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sconto */}
                <div style={{background:C.bg,borderRadius:11,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:C.textMuted,fontWeight:700,letterSpacing:.5,marginBottom:10}}>SCONTO (opzionale)</div>
                  <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                    <Select label="Tipo" value={aiResult.sconto_tipo||"percentuale"} onChange={v=>setAiResult(r=>({...r,sconto_tipo:v}))}
                      options={[{value:"percentuale",label:"% percentuale"},{value:"fisso",label:"€ fisso"}]}/>
                    <Input label="Valore sconto" type="number" value={aiResult.sconto_valore||""} onChange={v=>setAiResult(r=>({...r,sconto_valore:v}))} placeholder="0"/>
                  </div>
                  {(()=>{
                    const importoBase = parseFloat(aiResult.importo)||0;
                    const scontoVal = parseFloat(aiResult.sconto_valore)||0;
                    if(scontoVal<=0) return null;
                    const scontoEuro = aiResult.sconto_tipo==="fisso" ? scontoVal : importoBase*scontoVal/100;
                    const totScontato = Math.max(0, importoBase - scontoEuro);
                    return(
                      <div style={{marginTop:10,fontSize:13,display:"flex",justifyContent:"space-between"}}>
                        <span style={{color:C.textMuted}}>Sconto: −€{scontoEuro.toFixed(2)}</span>
                        <span style={{fontWeight:800,color:C.accent}}>Totale scontato: €{totScontato.toFixed(2)}</span>
                      </div>
                    );
                  })()}
                </div>

                <Textarea label="Note" value={aiResult.note||""} onChange={v=>setAiResult(r=>({...r,note:v}))} rows={3}/>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <Btn variant="ghost" onClick={()=>setAiResult(null)}>← Rianalizza</Btn>
                  <Btn onClick={creaLavoroDaAI}>✅ Crea lavoro nel gestionale</Btn>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Dati cliente */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Dati cliente</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="Nome cliente" value={cliente} onChange={setCliente} placeholder="Es. Sig. Rossi" />
          <Input label="Indirizzo cantiere" value={indirizzo} onChange={setIndirizzo} placeholder="Es. Via Roma 1, Napoli" />
        </div>
      </div>

      {/* Tariffario */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>Lavorazioni</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Attiva → inserisci quantità → scegli con/senza materiali</div>
        </div>
        <div>
          {righe.map((r, i) => (
            <div key={r.id} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto auto auto", gap: 12, alignItems: "center", padding: "12px 20px", borderBottom: i < righe.length - 1 ? `1px solid ${C.border}` : "none", background: r.attiva ? C.accentGlow : "transparent", transition: "background 0.2s" }}>
              {/* Toggle attiva */}
              <div onClick={() => toggleRiga(r.id)} style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${r.attiva ? C.accent : C.border}`, background: r.attiva ? C.accent : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                {r.attiva && <span style={{ color: "#fff", fontSize: 13, fontWeight: 900 }}>✓</span>}
              </div>
              {/* Label */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: r.attiva ? C.text : C.textMuted }}>{r.label}</div>
                {r.note && <div style={{ fontSize: 11, color: C.textMuted }}>({r.note})</div>}
              </div>
              {/* Prezzo */}
              <div style={{ textAlign: "right", fontSize: 12, color: C.textSub, minWidth: 70 }}>
                <div style={{ color: C.accent, fontWeight: 700 }}>€{r.prezzo}/{r.unita}</div>
                {r.mat > 0 && <div style={{ color: C.textMuted, fontSize: 11 }}>+€{r.mat} mat.</div>}
              </div>
              {/* Toggle materiali */}
              {r.mat > 0 ? (
                <div onClick={() => r.attiva && toggleMat(r.id)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: r.attiva ? "pointer" : "default", opacity: r.attiva ? 1 : 0.3 }}>
                  <div style={{ width: 32, height: 18, borderRadius: 9, background: r.conMateriali ? C.green : C.border, transition: "background 0.2s", position: "relative", flexShrink: 0 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: r.conMateriali ? 16 : 2, transition: "left 0.2s" }} />
                  </div>
                  <span style={{ fontSize: 11, color: r.conMateriali ? C.green : C.textMuted, whiteSpace: "nowrap" }}>Con mat.</span>
                </div>
              ) : <div style={{ width: 90 }} />}
              {/* Input quantità */}
              <input
                type="number" min="0" value={r.qty} onChange={e => setQty(r.id, e.target.value)}
                placeholder={r.unita} disabled={!r.attiva}
                style={{ width: 80, background: C.bg, border: `1px solid ${r.attiva ? C.border : "transparent"}`, borderRadius: 8, color: C.text, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", textAlign: "right", opacity: r.attiva ? 1 : 0.3 }}
              />
              {/* Subtotale */}
              <div style={{ minWidth: 80, textAlign: "right", fontSize: 13, fontWeight: 700, color: r.attiva && r.qty ? C.accent : C.textMuted }}>
                {r.attiva && r.qty ? `€${((parseFloat(r.qty) * r.prezzo) + (r.conMateriali ? parseFloat(r.qty) * r.mat : 0)).toFixed(0)}` : "—"}
              </div>
            </div>
          ))}
        </div>
        <div style={{padding:"14px 20px",borderTop:`1px solid ${C.border}`}}>
          {!nuovaVoce ? (
            <button onClick={()=>setNuovaVoce(true)} style={{background:"none",border:"none",color:C.accent,fontSize:12.5,fontWeight:700,cursor:"pointer"}}>+ Aggiungi voce al listino</button>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8}}>
                <Input label="Nome lavorazione" value={formVoce.label} onChange={v=>setFormVoce(f=>({...f,label:v}))} placeholder="Es. Posa scala in rovere"/>
                <Select label="Unità" value={formVoce.unita} onChange={v=>setFormVoce(f=>({...f,unita:v}))} options={[{value:"mq",label:"mq"},{value:"ml",label:"ml"},{value:"cad",label:"cad"},{value:"ore",label:"ore"}]}/>
                <Input label="Prezzo (€)" type="number" value={formVoce.prezzo} onChange={v=>setFormVoce(f=>({...f,prezzo:v}))} placeholder="0"/>
                <Input label="Materiale (€, opz.)" type="number" value={formVoce.mat} onChange={v=>setFormVoce(f=>({...f,mat:v}))} placeholder="0"/>
              </div>
              <div style={{display:"flex",gap:14,alignItems:"center"}}>
                <div onClick={()=>setFormVoce(f=>({...f,permanente:true}))} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                  <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${formVoce.permanente?C.accent:C.borderLight}`,background:formVoce.permanente?C.accent:"transparent"}}/>
                  <span style={{fontSize:12.5,color:formVoce.permanente?C.text:C.textMuted}}>Permanente (resta nel listino sempre)</span>
                </div>
                <div onClick={()=>setFormVoce(f=>({...f,permanente:false}))} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                  <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${!formVoce.permanente?C.accent:C.borderLight}`,background:!formVoce.permanente?C.accent:"transparent"}}/>
                  <span style={{fontSize:12.5,color:!formVoce.permanente?C.text:C.textMuted}}>Solo per questo preventivo</span>
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <Btn small variant="ghost" onClick={()=>setNuovaVoce(false)}>Annulla</Btn>
                <Btn small onClick={salvaNuovaVoce} disabled={!formVoce.label.trim()||!formVoce.prezzo}>+ Aggiungi</Btn>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Totali + opzioni */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 20, alignItems: "start" }}>
        {/* Note */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Note preventivo</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Es. Il preventivo include smontaggio del vecchio pavimento…"
            style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        </div>

        {/* Riepilogo totali */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, minWidth: 260 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Riepilogo</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textSub }}>
              <span>Manodopera</span><span style={{ fontWeight: 600 }}>€{totaleManodopera.toFixed(2)}</span>
            </div>
            {totaleMateriali > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textSub }}>
                <span>Materiali</span><span style={{ fontWeight: 600 }}>€{totaleMateriali.toFixed(2)}</span>
              </div>
            )}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textSub }}>
              <span>Totale netto</span><span style={{ fontWeight: 600 }}>€{totaleNetto.toFixed(2)}</span>
            </div>
            {/* Toggle IVA */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => setConIva(!conIva)}>
                <div style={{ width: 32, height: 18, borderRadius: 9, background: conIva ? C.green : C.border, position: "relative", transition: "background 0.2s" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: conIva ? 16 : 2, transition: "left 0.2s" }} />
                </div>
                <span style={{ fontSize: 12, color: conIva ? C.green : C.textMuted }}>IVA 22%</span>
              </div>
              {conIva && <span style={{ fontSize: 12, color: C.textSub }}>€{iva.toFixed(2)}</span>}
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>TOTALE</span>
              <span style={{ fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize: 22, fontWeight: 900, color: C.accent }}>€{totaleLordo.toFixed(2)}</span>
            </div>
          </div>

          {/* Opzioni multiple */}
          <div style={{marginTop:16,background:C.bg,borderRadius:11,padding:14}}>
            <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>setOpzioniMultiple(v=>!v)}>
              <div style={{width:32,height:18,borderRadius:9,background:opzioniMultiple?C.green:C.border,position:"relative",transition:"background 0.2s"}}>
                <div style={{width:14,height:14,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:opzioniMultiple?16:2,transition:"left 0.2s"}}/>
              </div>
              <span style={{fontSize:12.5,fontWeight:600,color:opzioniMultiple?C.green:C.textMuted}}>Preventivo con opzioni multiple (max 3, invece del totale unico)</span>
            </div>
            {opzioniMultiple && (
              <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:12}}>
                {opzioni.map((o,i)=>(
                  <div key={i} style={{background:C.card,borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:6}}>
                    <div style={{display:"flex",gap:8}}>
                      <input value={o.titolo} onChange={e=>updOpzione(i,"titolo",e.target.value)} placeholder="Es. Rovere naturale" style={{...fieldBase,flex:1,padding:"7px 10px",fontSize:12.5}}/>
                      <input value={o.prezzo} onChange={e=>updOpzione(i,"prezzo",e.target.value)} placeholder="€" type="number" style={{...fieldBase,width:80,padding:"7px 10px",fontSize:12.5}}/>
                      {opzioni.length>1&&<button onClick={()=>delOpzione(i)} style={{background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:16}}>×</button>}
                    </div>
                    <input value={o.descrizione} onChange={e=>updOpzione(i,"descrizione",e.target.value)} placeholder="Breve descrizione (opzionale)" style={{...fieldBase,padding:"7px 10px",fontSize:12}}/>
                  </div>
                ))}
                {opzioni.length<3&&<Btn small variant="ghost" onClick={addOpzione}>+ Aggiungi opzione</Btn>}
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            <Btn onClick={generaPDF} disabled={righeAttive.length === 0}>📄 Genera PDF</Btn>
            <Btn variant="ghost" onClick={() => { setRighe(TARIFFE.map(t => ({ ...t, qty: "", conMateriali: false, attiva: false }))); setCliente(""); setIndirizzo(""); setNote(""); setOpzioniMultiple(false); setOpzioni([{titolo:"",prezzo:"",descrizione:""}]); }}>🗑 Azzera</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}


// Sezioni che si possono assegnare a un accesso limitato (escluse le voci divisorie e "Account", sempre visibile a tutti)
const PAGINE_SELEZIONABILI = NAV.filter(n=>!n.divider && n.id!=="account");

function SelettorePagine({selezionate, onToggle}){
  return(
    <div style={{display:"flex",flexDirection:"column",gap:2,maxHeight:260,overflowY:"auto",background:C.bg,borderRadius:10,padding:10}}>
      {NAV.map((n,i)=> n.divider
        ? <div key={"d"+i} style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:.5,margin:"8px 4px 2px"}}>{n.label}</div>
        : n.id==="account" ? null : (
          <label key={n.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:C.text,cursor:"pointer",padding:"4px 4px"}}>
            <input type="checkbox" checked={selezionate.includes(n.id)} onChange={()=>onToggle(n.id)}/>
            <span>{n.icon} {n.label}</span>
          </label>
        )
      )}
    </div>
  );
}

function GestioneAccessi({db, user}){
  const mia = (user?.email||"").toLowerCase();
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metodo, setMetodo] = useState("google"); // "google" | "password"
  const [tipo, setTipo] = useState("limitato");   // "admin" (socio, vede tutto) | "limitato"
  const [nuovaEmail, setNuovaEmail] = useState("");
  const [nuovaPassword, setNuovaPassword] = useState("");
  const [nuovePagine, setNuovePagine] = useState([]);
  const [creazioneErrore, setCreazioneErrore] = useState("");
  const [creando, setCreando] = useState(false);
  const [editEmail, setEditEmail] = useState(null);
  const [editTipo, setEditTipo] = useState("limitato");
  const [editPagine, setEditPagine] = useState([]);

  useEffect(()=>{ load(); },[]);
  async function load(){
    setLoading(true);
    const {data} = await db.from("utenti_ruoli").select("*").order("email");
    // Compatibilità con vecchi accessi salvati solo con ruolo "contabile" senza elenco pagine
    setUtenti((data||[]).map(u=>({...u, pagine: (u.pagine&&u.pagine.length>0) ? u.pagine : (u.ruolo==="contabile" ? ["contabilita"] : [])})));
    setLoading(false);
  }

  function toggleNuova(id){ setNuovePagine(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]); }
  function toggleEdit(id){ setEditPagine(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]); }

  async function aggiungi(){
    const email = nuovaEmail.trim().toLowerCase();
    if(!email || !email.includes("@") || (tipo==="limitato" && nuovePagine.length===0)) return;
    if(utenti.some(u=>u.email.toLowerCase()===email)){ setCreazioneErrore("Questa email ha già un accesso."); return; }
    setCreazioneErrore(""); setCreando(true);

    // prima si registra la persona nella tua azienda, poi (se serve) si crea la password
    const pagine = tipo==="admin" ? [] : nuovePagine;
    const {data:n, error} = await db.from("utenti_ruoli").insert([{email, ruolo:tipo, pagine, metodo}]).select().single();
    if(error){
      setCreazioneErrore(/duplicate|unique/i.test(error.message) ? "Questa email fa già parte di un'altra azienda." : "Non hai i permessi per aggiungere persone.");
      setCreando(false); return;
    }
    if(metodo==="password"){
      let msg = "";
      if(nuovaPassword.length<6) msg = "La password deve avere almeno 6 caratteri.";
      else try{
        const {data:{session}} = await db.auth.getSession();
        const res = await fetch("/api/create-user",{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${session?.access_token||""}`},body:JSON.stringify({email,password:nuovaPassword})});
        const out = await res.json().catch(()=>({}));
        if(!res.ok) msg = out.error||"Impossibile creare l'account.";
        else if(out.avviso) window.alert(out.avviso);
      }catch(e){ msg = "Impossibile contattare il server."; }
      if(msg){ await db.from("utenti_ruoli").delete().eq("email",email); setCreazioneErrore(msg); setCreando(false); return; }
    }
    if(n) setUtenti(u=>[...u,{...n,pagine}]);
    setNuovaEmail(""); setNuovaPassword(""); setNuovePagine([]); setTipo("limitato"); setCreando(false);
  }
  async function rimuovi(email){
    if(!window.confirm(`Rimuovere l'accesso di ${email}?`)) return;
    await db.from("utenti_ruoli").delete().eq("email",email);
    setUtenti(u=>u.filter(x=>x.email!==email));
  }
  function apriModifica(u){ setEditEmail(u.email); setEditTipo(u.ruolo==="admin"?"admin":"limitato"); setEditPagine(u.pagine); }
  async function salvaModifica(email){
    const pagine = editTipo==="admin" ? [] : editPagine;
    await db.from("utenti_ruoli").update({pagine, ruolo:editTipo}).eq("email",email);
    setUtenti(u=>u.map(x=>x.email===email?{...x,pagine,ruolo:editTipo}:x));
    setEditEmail(null);
  }

  if(loading) return null;

  const SceltaTipo = ({valore,onChange})=>(
    <div style={{display:"flex",gap:6}}>
      {[["admin","Accesso completo"],["limitato","Solo alcune sezioni"]].map(([k,l])=>(
        <button key={k} onClick={()=>onChange(k)} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${valore===k?C.accent:C.border}`,background:valore===k?C.accentGlow:C.bg,color:valore===k?C.accent:C.textMuted,fontSize:12.5,fontWeight:600,cursor:"pointer"}}>{l}</button>
      ))}
    </div>
  );
  const descr = u => u.ruolo==="admin" ? "Accesso completo" : (u.pagine.length>0 ? u.pagine.map(id=>NAV.find(n=>n.id===id)?.label||id).join(" · ") : "Nessuna sezione assegnata");

  return(
    <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"24px"}}>
      <div style={{fontSize:13,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Accessi</div>
      <div style={{fontSize:12.5,color:C.textMuted,marginBottom:14}}>Solo le persone in questo elenco vedono i dati della tua azienda. Chiunque altro entri in Squadra parte da un'azienda sua, vuota.</div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
        {utenti.map(u=>{
          const io = u.email.toLowerCase()===mia;
          return(
          <div key={u.email} style={{background:C.bg,borderRadius:10,padding:"9px 12px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis"}}>{u.email}{io && <span style={{color:C.textMuted,fontWeight:500}}> (tu)</span>} <span style={{fontSize:10.5,color:C.textMuted,fontWeight:500}}>· {u.metodo==="password"?"email/password":"Google"}</span></div>
                <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>{descr(u)}</div>
              </div>
              {!io && <div style={{display:"flex",gap:12,flexShrink:0}}>
                <button onClick={()=>editEmail===u.email?setEditEmail(null):apriModifica(u)} style={{background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:12,fontWeight:600}}>{editEmail===u.email?"Chiudi":"Modifica"}</button>
                <button onClick={()=>rimuovi(u.email)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:12,fontWeight:600}}>Rimuovi</button>
              </div>}
            </div>
            {editEmail===u.email && (
              <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                <SceltaTipo valore={editTipo} onChange={setEditTipo}/>
                {editTipo==="limitato" && <SelettorePagine selezionate={editPagine} onToggle={toggleEdit}/>}
                <div><Btn small onClick={()=>salvaModifica(u.email)} disabled={editTipo==="limitato"&&editPagine.length===0}>Salva modifiche</Btn></div>
              </div>
            )}
          </div>
        );})}
      </div>
      <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16}}>
        <div style={{fontSize:12.5,fontWeight:700,color:C.text,marginBottom:10}}>Aggiungi una persona</div>
        <div style={{display:"flex",gap:6,marginBottom:10}}>
          <button onClick={()=>setMetodo("google")} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${metodo==="google"?C.accent:C.border}`,background:metodo==="google"?C.accentGlow:C.bg,color:metodo==="google"?C.accent:C.textMuted,fontSize:12.5,fontWeight:600,cursor:"pointer"}}>Account Google</button>
          <button onClick={()=>setMetodo("password")} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${metodo==="password"?C.accent:C.border}`,background:metodo==="password"?C.accentGlow:C.bg,color:metodo==="password"?C.accent:C.textMuted,fontSize:12.5,fontWeight:600,cursor:"pointer"}}>Email e password</button>
        </div>
        <Input label={metodo==="google"?"Email Google della persona":"Email di accesso"} value={nuovaEmail} onChange={setNuovaEmail} placeholder={metodo==="google"?"nome@gmail.com":"nome@esempio.com"}/>
        {metodo==="password" && <div style={{marginTop:10}}><Input label="Password (min. 6 caratteri)" type="password" value={nuovaPassword} onChange={setNuovaPassword}/></div>}
        <div style={{fontSize:11.5,color:C.textMuted,margin:"12px 0 6px"}}>Cosa può vedere:</div>
        <SceltaTipo valore={tipo} onChange={setTipo}/>
        {tipo==="limitato" && <div style={{marginTop:8}}><SelettorePagine selezionate={nuovePagine} onToggle={toggleNuova}/></div>}
        {tipo==="admin" && <div style={{fontSize:11.5,color:C.textMuted,marginTop:6}}>Per i soci: vede tutto e può aggiungere o togliere persone.</div>}
        {creazioneErrore && <div style={{color:C.red,fontSize:12,marginTop:8}}>{creazioneErrore}</div>}
        <Btn small onClick={aggiungi} disabled={creando||!nuovaEmail.trim()||(tipo==="limitato"&&nuovePagine.length===0)||(metodo==="password"&&!nuovaPassword)} style={{marginTop:10}}>{creando?"Creazione…":"+ Aggiungi"}</Btn>
      </div>
    </div>
  );
}

function Account({user, db, isAdmin}){
  const [nome,     setNome]     = useState(user?.user_metadata?.full_name || "");
  const [telefono, setTelefono] = useState(user?.user_metadata?.telefono  || "");
  const [azienda,  setAzienda]  = useState(user?.user_metadata?.azienda   || "");
  const [ruolo,    setRuolo]    = useState(user?.user_metadata?.ruolo     || "");
  const [password, setPassword] = useState("");
  const [confPass, setConfPass] = useState("");
  const [msg,      setMsg]      = useState(null);
  const [loading,  setLoading]  = useState(false);

  function mostraMsg(tipo,testo){ setMsg({tipo,testo}); setTimeout(()=>setMsg(null),3500); }

  async function salvaProfilo(){
    setLoading(true);
    const {error} = await db.auth.updateUser({data:{full_name:nome,telefono,azienda,ruolo}});
    setLoading(false);
    if(error) mostraMsg("err","Errore: "+error.message);
    else mostraMsg("ok","Profilo aggiornato!");
  }

  async function salvaPassword(){
    if(!password) return mostraMsg("err","Inserisci una password.");
    if(password.length<6) return mostraMsg("err","Minimo 6 caratteri.");
    if(password!==confPass) return mostraMsg("err","Le password non coincidono.");
    setLoading(true);
    const {error} = await db.auth.updateUser({password});
    setLoading(false);
    if(error) mostraMsg("err","Errore: "+error.message);
    else { mostraMsg("ok","Password aggiornata!"); setPassword(""); setConfPass(""); }
  }

  const initiali = (nome||user?.email||"?").slice(0,2).toUpperCase();

  return(
    <div style={{display:"flex",flexDirection:"column",gap:24,maxWidth:600}}>
      <div>
        <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>Account</div>
        <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>Gestisci il tuo profilo e le impostazioni</div>
      </div>

      {msg&&(
        <div style={{background:msg.tipo==="ok"?C.green+"22":C.red+"22",border:`1px solid ${msg.tipo==="ok"?C.green:C.red}44`,borderRadius:14,padding:"12px 16px",fontSize:13,fontWeight:600,color:msg.tipo==="ok"?C.green:C.red}}>
          {msg.tipo==="ok"?"✓ ":"✗ "}{msg.testo}
        </div>
      )}

      {/* Avatar */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"24px"}}>
        <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:24}}>
          <div style={{width:72,height:72,borderRadius:"50%",background:C.accent+"33",border:`3px solid ${C.accent}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:900,color:C.accent,flexShrink:0}}>{initiali}</div>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.text}}>{nome||"—"}</div>
            <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{user?.email}</div>
            <div style={{fontSize:11,color:C.textMuted,marginTop:4}}>Accesso tramite Google</div>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Input label="Nome completo" value={nome} onChange={setNome} placeholder="Es. Mario Rossi"/>
            <Input label="Telefono" value={telefono} onChange={setTelefono} placeholder="Es. 348 123 4567"/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Input label="Azienda / Ditta" value={azienda} onChange={setAzienda} placeholder="Es. Parquetti Srl"/>
            <Input label="Ruolo" value={ruolo} onChange={setRuolo} placeholder="Es. Titolare"/>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:4}}>
            <Btn onClick={salvaProfilo} disabled={loading}>💾 Salva Profilo</Btn>
          </div>
        </div>
      </div>

      {/* Email */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"24px"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Email</div>
        <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:11,padding:"10px 14px",fontSize:14,color:C.textSub}}>{user?.email}</div>
        <div style={{fontSize:11,color:C.textMuted,marginTop:8}}>L'email è collegata al tuo account Google e non può essere modificata qui.</div>
      </div>

      {/* Password */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"24px"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Cambia Password</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Input label="Nuova password" type="password" value={password} onChange={setPassword} placeholder="Minimo 6 caratteri"/>
          <Input label="Conferma password" type="password" value={confPass} onChange={setConfPass} placeholder="Ripeti la password"/>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:4}}>
            <Btn onClick={salvaPassword} disabled={loading||!password}>🔒 Aggiorna Password</Btn>
          </div>
        </div>
      </div>

      {isAdmin && <GestioneAccessi db={db} user={user}/>}

      {/* Logout */}
      <div style={{background:C.card,border:`1px solid ${C.red}33`,borderRadius:16,padding:"24px"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Zona Pericolosa</div>
        <div style={{fontSize:13,color:C.textMuted,marginBottom:14}}>Il logout ti disconnette da tutti i dispositivi.</div>
        <Btn variant="danger" onClick={()=>db.auth.signOut()}>⏻ Disconnetti Account</Btn>
      </div>
    </div>
  );
}


// ── MATERIALI ─────────────────────────────────────────────────────────────────
const STATO_MAT_COLOR = {
  da_ordinare: "#d04545", ordinato: "#2F6FDB", in_cantiere: "#2e6fd4", consumato: "#2da063"
};
const STATO_MAT_LABEL = {
  da_ordinare: "Da ordinare", ordinato: "Ordinato", in_cantiere: "In cantiere", consumato: "Consegnato/Usato"
};

// ── MAGAZZINO ────────────────────────────────────────────────────────────────
// Quattro schede: Materiali, Attrezzi, Cantieri (cosa c'è su ogni cantiere), Ordini.
// Tre azioni principali: Porta in cantiere, Rientro, Arrivo merce — un solo pannello per tutte.
// Tabelle: magazzino_stock, movimenti_magazzino, materiali (invariate); le consegne di materiale
// con prezzo diventano spese del cantiere in Contabilità (spese_cantiere).
const TIPI_MOV = {
  carico:   {label:"Carico",    segno:+1, desc:"Arrivo merce"},
  scarico:  {label:"Scarico",   segno:-1, desc:"Uso / perdita"},
  uscita:   {label:"Consegna",  segno:-1, desc:"A cantiere"},
  rientro:  {label:"Rientro",   segno:+1, desc:"Da cantiere"},
  rettifica:{label:"Rettifica", segno:0,  desc:"Conta inventario"},
};
const UNITA_MAG = ["pz","mq","ml","kg","lt","conf","sacchi","rotoli"];
const numIt = (n)=> (Math.round((parseFloat(n)||0)*100)/100).toLocaleString("it-IT");

function Segmenti({value, onChange, items}){
  return(
    <div style={{display:"flex",background:C.bg,borderRadius:11,padding:3,gap:2,flexWrap:"wrap"}}>
      {items.map(it=>{ const on = value===it.id; return (
        <button key={it.id} onClick={()=>onChange(it.id)} style={{padding:"6px 12px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:on?600:500,background:on?C.card:"transparent",boxShadow:on?C.shadowSm:"none",color:on?C.text:C.textSub,whiteSpace:"nowrap"}}>
          {it.label}{it.n!=null && <span style={{color:it.alert&&it.n>0?C.red:C.textMuted,marginLeft:5,fontWeight:600}}>{it.n}</span>}
        </button>
      );})}
    </div>
  );
}


// Quanto di ogni articolo si trova su ogni cantiere: consegne − rientri
function presenzeCantieri(movimenti){
  const m = {}; // artId -> lavoroId -> {qta, operaio, ultima}
  [...movimenti].sort((a,b)=>(a.data||"").localeCompare(b.data||"")||((a.id||0)-(b.id||0))).forEach(mv=>{
    if(!mv.lavoro_id || !mv.articolo_id || !["uscita","rientro"].includes(mv.tipo)) return;
    const a = (m[mv.articolo_id] = m[mv.articolo_id]||{});
    const p = (a[mv.lavoro_id] = a[mv.lavoro_id]||{qta:0, operaio:null, ultima:null});
    p.qta += (mv.tipo==="uscita"?1:-1)*(parseFloat(mv.quantita)||0);
    if(mv.tipo==="uscita" && mv.operaio_nome) p.operaio = mv.operaio_nome;
    p.ultima = mv.data;
  });
  Object.values(m).forEach(a=>Object.keys(a).forEach(k=>{ if(a[k].qta<=0.0001) delete a[k]; }));
  return m;
}

function Materiali({lavori, operai, db}){
  const [vista, setVista] = usePersistedState("mag_vista2", "materiali"); // materiali | attrezzi | cantieri | ordini
  const [stock, setStock] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [soloSotto, setSoloSotto] = useState(false);
  const [scheda, setScheda] = useState(null);     // id articolo aperto
  const [edit, setEdit] = useState(null);         // articolo nuovo / in modifica
  const [mov, setMov] = useState(null);           // pannello movimento
  const [storico, setStorico] = useState(false);
  const [ordFiltro, setOrdFiltro] = useState("da_ordinare");

  useEffect(()=>{ (async()=>{
    const [s,m,o] = await Promise.all([
      db.from("magazzino_stock").select("*").order("nome"),
      db.from("movimenti_magazzino").select("*").order("data",{ascending:false}).limit(3000),
      db.from("materiali").select("*").order("created_at",{ascending:false}),
    ]);
    setStock(s.data||[]); setMovimenti(m.data||[]); setOrdini(o.data||[]); setLoading(false);
  })(); },[]);

  const lavoriAperti = lavori.filter(l=>!["Completato","Annullato","Rifiutato","Preventivo"].includes(l.stato))
    .sort((a,b)=>(a.data_inizio||"9").localeCompare(b.data_inizio||"9"));
  const lavoroDi = (id)=>lavori.find(l=>String(l.id)===String(id));
  const presenze = presenzeCantieri(movimenti);
  const prezzo = (a)=>parseFloat(a?.prezzo_unitario)||0;

  // ── Movimento: aggiorna giacenza + storico (+ spesa in Contabilità per il materiale) ──
  async function registraMovimento(art, tipo, q, {lavoro=null, operaio=null, note="", nuovaQta=null}={}){
    const qta = parseFloat(q)||0;
    const prima = parseFloat(art.quantita)||0;
    const finale = tipo==="rettifica" ? Math.max(0, parseFloat(nuovaQta)||0) : Math.max(0, prima + TIPI_MOV[tipo].segno*qta);
    const {error} = await db.from("magazzino_stock").update({quantita:finale}).eq("id",art.id);
    if(error){ alert("Errore aggiornando la giacenza: "+error.message); return false; }
    setStock(s=>s.map(x=>x.id===art.id?{...x,quantita:finale}:x));
    const {data:mv, error:e2} = await db.from("movimenti_magazzino").insert([{
      articolo_id:art.id, articolo_nome:art.nome, categoria:art.categoria, unita:art.unita,
      tipo, quantita: tipo==="rettifica" ? Math.round((finale-prima)*100)/100 : qta,
      lavoro_id:lavoro?.id||null, lavoro_titolo:lavoro?.titolo||null,
      operaio_id:operaio?.id||null, operaio_nome:operaio?.nome||null, note:note||null, data:today,
    }]).select().single();
    if(e2) alert("Giacenza aggiornata, ma lo storico non è stato salvato: "+e2.message);
    if(mv) setMovimenti(m=>[mv,...m]);
    // Materiale portato o rientrato da un cantiere → costo del cantiere in Contabilità
    if(lavoro && ["uscita","rientro"].includes(tipo) && (art.categoria||"materiale")!=="attrezzo" && prezzo(art)>0){
      const segno = tipo==="uscita"?1:-1;
      const {error:e3} = await db.from("spese_cantiere").insert([{lavoro_id:lavoro.id, categoria:"materiali",
        descrizione:`Magazzino — ${tipo==="uscita"?"":"rientro "}${art.nome} (${numIt(qta)} ${art.unita})`,
        importo: Math.round(segno*qta*prezzo(art)*100)/100, data:today}]);
      if(e3) alert("Movimento salvato, ma la spesa in Contabilità no: "+e3.message);
    }
    return true;
  }

  async function salvaArticolo(){
    const {id, created_at, ...rest} = edit;
    const d = {nome:rest.nome.trim(), categoria:rest.categoria||"materiale", unita:rest.unita||"pz",
      scorta_minima:parseFloat(rest.scorta_minima)||0, prezzo_unitario:rest.prezzo_unitario!==""&&rest.prezzo_unitario!=null?parseFloat(rest.prezzo_unitario):null};
    if(id){
      const orig = stock.find(s=>s.id===id);
      const {error} = await db.from("magazzino_stock").update(d).eq("id",id);
      if(error){ alert("Errore nel salvataggio: "+error.message); return; }
      setStock(s=>s.map(x=>x.id===id?{...x,...d}:x));
      const nuovaQ = parseFloat(rest.quantita)||0;
      if(orig && nuovaQ!==(parseFloat(orig.quantita)||0)) await registraMovimento({...orig,...d}, "rettifica", 0, {nuovaQta:nuovaQ, note:"Correzione conteggio"});
    } else {
      if(stock.some(s=>s.nome.trim().toLowerCase()===d.nome.toLowerCase())){ alert(`"${d.nome}" esiste già nel magazzino.`); return; }
      const {data:n, error} = await db.from("magazzino_stock").insert([{...d, quantita:parseFloat(rest.quantita)||0}]).select().single();
      if(error){ alert("Errore nel salvataggio: "+error.message); return; }
      if(n) setStock(s=>[...s,n]);
    }
    setEdit(null);
  }
  async function eliminaArticolo(a){
    if(!window.confirm(`Eliminare "${a.nome}" dal magazzino? Lo storico resta.`)) return;
    const {error} = await db.from("magazzino_stock").delete().eq("id",a.id);
    if(error){ alert("Errore: "+error.message); return; }
    setStock(s=>s.filter(x=>x.id!==a.id)); setEdit(null); setScheda(null);
  }
  async function ordina(a){
    const min = parseFloat(a.scorta_minima)||0, qta = Math.max(1, Math.ceil(min*2 - (parseFloat(a.quantita)||0)));
    const {data:n, error} = await db.from("materiali").insert([{nome:a.nome, tipo:"consumabile", fornitore:"", quantita_necessaria:qta, quantita_ordinata:0, quantita_consegnata:0,
      prezzo_unitario:prezzo(a), costo_totale:prezzo(a)*qta, unita:a.unita, stato:"da_ordinare", note:"Riordino scorta", lavoro_id:null}]).select().single();
    if(error){ alert("Errore nel creare l'ordine: "+error.message); return; }
    if(n) setOrdini(o=>[n,...o]);
  }

  const apriMov = (tipo, extra={})=>setMov({tipo, lavoro_id:"", operaio_id:"", note:"", righe:{}, prezzi:{}, cerca:"", ddt:tipo!=="carico", ...extra});

  if(loading) return <div style={{textAlign:"center",color:C.textMuted,padding:60}}>Caricamento…</div>;

  const isSotto = (s)=>(parseFloat(s.scorta_minima)||0)>0 && (parseFloat(s.quantita)||0)<=(parseFloat(s.scorta_minima)||0);
  const inOrdine = (nome)=>ordini.some(o=>!o.lavoro_id && o.stato!=="consumato" && o.nome?.toLowerCase()===nome.toLowerCase());
  const materiali = stock.filter(s=>(s.categoria||"materiale")!=="attrezzo");
  const attrezzi  = stock.filter(s=>s.categoria==="attrezzo");
  const sottoScorta = materiali.filter(isSotto);
  const daOrdinare = ordini.filter(o=>o.stato==="da_ordinare");
  const fuoriTot = (id)=>Object.values(presenze[id]||{}).reduce((t,p)=>t+p.qta,0);
  const attrezziFuori = attrezzi.reduce((t,a)=>t+fuoriTot(a.id),0);
  const valoreMag = materiali.reduce((t,s)=>t+(parseFloat(s.quantita)||0)*prezzo(s),0);
  const cerca = (s)=>!q.trim() || s.nome.toLowerCase().includes(q.toLowerCase());

  const azioni = [
    {tipo:"uscita",  label:"Porta in cantiere", icona:"↗", primario:true},
    {tipo:"rientro", label:"Rientro",           icona:"↙"},
    {tipo:"carico",  label:"Arrivo merce",      icona:"＋"},
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:700,color:C.text}}>Magazzino</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>
            {euro(valoreMag)} di materiale{sottoScorta.length>0?<> · <span style={{color:C.red,fontWeight:600}}>{sottoScorta.length} sotto scorta</span></>:null}{attrezziFuori>0?` · ${numIt(attrezziFuori)} attrezzi fuori`:""}
          </div>
        </div>
        <button onClick={()=>setStorico(true)} style={{background:"none",border:"none",color:C.textSub,fontSize:12.5,fontWeight:600,cursor:"pointer",padding:0}}>Storico movimenti</button>
      </div>

      {/* Azioni principali */}
      <div className="mag-azioni" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        {azioni.map(a=>(
          <button key={a.tipo} onClick={()=>apriMov(a.tipo)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"14px 16px",borderRadius:16,border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left",
              background:a.primario?C.text:C.card,color:a.primario?"#fff":C.text,boxShadow:C.shadowSm}}>
            <span style={{fontSize:18,lineHeight:1,width:22,textAlign:"center"}}>{a.icona}</span>
            <span style={{fontSize:13.5,fontWeight:650}}>{a.label}</span>
          </button>
        ))}
      </div>
      <style>{`@media (max-width:560px){ .mag-azioni{grid-template-columns:1fr !important} }`}</style>

      <Segmenti value={vista} onChange={v=>{setVista(v);setQ("");}} items={[
        {id:"materiali", label:"Materiali", n:materiali.length},
        {id:"attrezzi",  label:"Attrezzi",  n:attrezzi.length},
        {id:"cantieri",  label:"Cantieri"},
        {id:"ordini",    label:"Ordini",    n:daOrdinare.length, alert:true},
      ]}/>

      {(vista==="materiali"||vista==="attrezzi") && (
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder={vista==="attrezzi"?"Cerca attrezzo…":"Cerca materiale…"} style={{...fieldBase,background:C.card,boxShadow:C.shadowSm,flex:"1 1 200px",fontSize:13.5}}/>
          {vista==="materiali" && sottoScorta.length>0 && (
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12.5,color:C.red,fontWeight:600,cursor:"pointer"}}>
              <input type="checkbox" checked={soloSotto} onChange={e=>setSoloSotto(e.target.checked)}/> Solo sotto scorta
            </label>
          )}
          <Btn small variant="ghost" onClick={()=>setEdit({nome:"",categoria:vista==="attrezzi"?"attrezzo":"materiale",unita:"pz",quantita:"",scorta_minima:"",prezzo_unitario:""})}>+ {vista==="attrezzi"?"Attrezzo":"Materiale"}</Btn>
        </div>
      )}

      {/* ── MATERIALI ── */}
      {vista==="materiali" && (()=>{
        const lista = materiali.filter(cerca).filter(s=>!soloSotto||isSotto(s)).sort((a,b)=>(isSotto(b)-isSotto(a)) || a.nome.localeCompare(b.nome));
        return(
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
            {lista.length===0 && <div style={{padding:"40px 18px",textAlign:"center",color:C.textMuted,fontSize:13}}>{materiali.length===0?"Nessun materiale: aggiungi il primo con \"+ Materiale\".":"Nessun risultato."}</div>}
            {lista.map((s,i)=>{
              const qn = parseFloat(s.quantita)||0, sotto = isSotto(s), fuori = fuoriTot(s.id);
              return(
                <div key={s.id} onClick={()=>setScheda(s.id)} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderTop:i?`1px solid ${C.border}`:"none",cursor:"pointer"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.nome}</div>
                    <div style={{fontSize:11.5,color:C.textMuted,marginTop:1}}>
                      {prezzo(s)>0?`${euro(prezzo(s))}/${s.unita}`:"senza prezzo"}{fuori>0?` · ${numIt(fuori)} ${s.unita} nei cantieri`:""}
                    </div>
                  </div>
                  {sotto && (inOrdine(s.nome)
                    ? <span style={{fontSize:11.5,color:C.textMuted}}>in ordine</span>
                    : <button onClick={e=>{e.stopPropagation();ordina(s);}} style={{background:C.red+"12",border:"none",borderRadius:20,color:C.red,fontSize:12,fontWeight:700,cursor:"pointer",padding:"5px 11px"}}>Riordina</button>)}
                  <div style={{textAlign:"right",minWidth:70}}>
                    <span style={{fontSize:17,fontWeight:700,color:sotto?C.red:C.text,fontFamily:C.fontBrand}}>{numIt(qn)}</span>
                    <span style={{fontSize:12,color:C.textMuted,marginLeft:4}}>{s.unita}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── ATTREZZI: dove sono ── */}
      {vista==="attrezzi" && (()=>{
        const lista = attrezzi.filter(cerca).sort((a,b)=>a.nome.localeCompare(b.nome));
        return(
          <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
            {lista.length===0 && <div style={{padding:"40px 18px",textAlign:"center",color:C.textMuted,fontSize:13}}>{attrezzi.length===0?"Nessun attrezzo: aggiungi levigatrici, bordatrici, aspiratori… con \"+ Attrezzo\".":"Nessun risultato."}</div>}
            {lista.map((a,i)=>{
              const dove = Object.entries(presenze[a.id]||{});
              const inMag = parseFloat(a.quantita)||0;
              return(
                <div key={a.id} onClick={()=>setScheda(a.id)} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderTop:i?`1px solid ${C.border}`:"none",cursor:"pointer",flexWrap:"wrap"}}>
                  <div style={{flex:"1 1 180px",minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:C.text}}>{a.nome}</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:5}}>
                      {inMag>0 && <span style={{fontSize:11.5,fontWeight:600,color:C.green,background:C.green+"14",borderRadius:20,padding:"3px 9px"}}>In magazzino{inMag>1?` · ${numIt(inMag)}`:""}</span>}
                      {dove.map(([lid,p])=>(
                        <span key={lid} style={{fontSize:11.5,fontWeight:600,color:C.text,background:C.bg,borderRadius:20,padding:"3px 9px"}}>
                          🏗️ {lavoroDi(lid)?.titolo||"Cantiere"}{p.qta>1?` · ${numIt(p.qta)}`:""}{p.operaio?` · ${p.operaio.split(" ")[0]}`:""}
                        </span>
                      ))}
                      {inMag<=0 && dove.length===0 && <span style={{fontSize:11.5,color:C.textMuted}}>non registrato</span>}
                    </div>
                  </div>
                  {inMag>0 && <Btn small variant="ghost" onClick={e=>{e.stopPropagation();apriMov("uscita",{righe:{[a.id]:"1"}});}}>↗ Porta</Btn>}
                  {dove.length>0 && <Btn small variant="ghost" onClick={e=>{e.stopPropagation();apriMov("rientro",{lavoro_id:dove[0][0],righe:{[a.id]:String(dove[0][1].qta)}});}}>↙ Rientrato</Btn>}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── CANTIERI: cosa c'è su ogni cantiere ── */}
      {vista==="cantieri" && <MagCantieri {...{lavori, lavoriAperti, lavoroDi, stock, presenze, ordini, apriMov, prezzo, setVista, setOrdFiltro}}/>}

      {vista==="ordini" && <MagOrdini {...{db,ordini,setOrdini,lavori,lavoriAperti,lavoroDi,stock,setStock,registraMovimento,filtro:ordFiltro,setFiltro:setOrdFiltro}}/>}

      {/* Scheda articolo */}
      {scheda && (()=>{ const a = stock.find(s=>s.id===scheda); if(!a) return null;
        const att = a.categoria==="attrezzo";
        const dove = Object.entries(presenze[a.id]||{});
        const mvArt = movimenti.filter(m=>m.articolo_id===a.id).slice(0,12);
        const qn = parseFloat(a.quantita)||0;
        return(
          <Modal title={a.nome} onClose={()=>setScheda(null)}>
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}}>
                <span style={{fontFamily:C.fontBrand,fontSize:32,fontWeight:700,letterSpacing:-1,color:isSotto(a)?C.red:C.text}}>{numIt(qn)}</span>
                <span style={{fontSize:14,color:C.textMuted}}>{a.unita} in magazzino</span>
                <span style={{fontSize:12.5,color:C.textMuted,marginLeft:"auto"}}>{att?"Attrezzo":"Materiale"}{prezzo(a)>0?` · ${euro(prezzo(a))}/${a.unita}`:""}{(parseFloat(a.scorta_minima)||0)>0?` · min ${numIt(a.scorta_minima)}`:""}</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <Btn onClick={()=>{setScheda(null);apriMov("uscita",{righe:{[a.id]:att?"1":""}});}}>↗ Porta in cantiere</Btn>
                <Btn variant="ghost" onClick={()=>{setScheda(null);apriMov("rientro",{lavoro_id:dove[0]?.[0]||"",righe:{[a.id]:dove[0]?String(dove[0][1].qta):""}});}}>↙ Rientro</Btn>
                <Btn variant="ghost" onClick={()=>{setScheda(null);apriMov("carico",{righe:{[a.id]:""},prezzi:{[a.id]:a.prezzo_unitario??""}});}}>＋ Arrivo merce</Btn>
                <Btn variant="ghost" onClick={()=>{setScheda(null);apriMov("rettifica",{righe:{[a.id]:String(qn)}});}}>✎ Correggi conteggio</Btn>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Nei cantieri</div>
                {dove.length===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>Niente fuori dal magazzino.</div> :
                  dove.map(([lid,p])=>(
                    <div key={lid} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:13,padding:"7px 10px",background:C.bg,borderRadius:9,marginBottom:4}}>
                      <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🏗️ {lavoroDi(lid)?.titolo||"Cantiere"}{p.operaio?<span style={{color:C.textMuted}}> · {p.operaio}</span>:null}</span>
                      <b style={{flexShrink:0}}>{numIt(p.qta)} {a.unita}</b>
                    </div>
                  ))}
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Ultimi movimenti</div>
                {mvArt.length===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>Nessun movimento.</div> :
                  mvArt.map(m=>{ const sg = m.tipo==="rettifica" ? ((parseFloat(m.quantita)||0)>=0?"+":"") : (TIPI_MOV[m.tipo]?.segno>0?"+":"−"); return (
                    <div key={m.id} style={{display:"grid",gridTemplateColumns:"56px 74px minmax(0,1fr)",gap:8,fontSize:12.5,padding:"5px 0",borderTop:`1px solid ${C.border}`}}>
                      <span style={{color:C.textMuted}}>{fmt(m.data)}</span>
                      <b style={{fontWeight:600}}>{sg}{numIt(Math.abs(parseFloat(m.quantita)||0))}</b>
                      <span style={{color:C.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{TIPI_MOV[m.tipo]?.label}{[m.lavoro_titolo,m.operaio_nome,m.note].filter(Boolean).length?` · ${[m.lavoro_titolo,m.operaio_nome,m.note].filter(Boolean).join(" · ")}`:""}</span>
                    </div>
                  );})}
              </div>
              <button onClick={()=>{setScheda(null);setEdit({...a,quantita:String(a.quantita??""),scorta_minima:String(a.scorta_minima??""),prezzo_unitario:a.prezzo_unitario??""});}} style={{alignSelf:"flex-start",background:"none",border:"none",color:C.textSub,fontSize:12.5,fontWeight:600,cursor:"pointer",padding:0}}>Modifica dati articolo</button>
            </div>
          </Modal>
        );
      })()}

      {/* Articolo nuovo / modifica */}
      {edit && (
        <Modal title={edit.id?"Modifica articolo":(edit.categoria==="attrezzo"?"Nuovo attrezzo":"Nuovo materiale")} onClose={()=>setEdit(null)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Segmenti value={edit.categoria||"materiale"} onChange={v=>setEdit(f=>({...f,categoria:v}))} items={[{id:"materiale",label:"Materiale"},{id:"attrezzo",label:"Attrezzo"}]}/>
            <Input label="Nome" value={edit.nome} onChange={v=>setEdit(f=>({...f,nome:v}))} placeholder={edit.categoria==="attrezzo"?"Es. Levigatrice Bona Belt":"Es. Colla MS polimero 15 kg"}/>
            <div style={{display:"grid",gridTemplateColumns:edit.categoria==="attrezzo"?"1fr 1fr":"1fr 1fr 1fr",gap:10}}>
              <Input label={edit.id?"In magazzino":"Quantità iniziale"} type="number" value={edit.quantita} onChange={v=>setEdit(f=>({...f,quantita:v}))}/>
              {edit.categoria!=="attrezzo" && <Input label="Scorta minima" type="number" value={edit.scorta_minima} onChange={v=>setEdit(f=>({...f,scorta_minima:v}))}/>}
              <Select label="Unità" value={edit.unita||"pz"} onChange={v=>setEdit(f=>({...f,unita:v}))} options={[...new Set([...UNITA_MAG, edit.unita||"pz"])].map(u=>({value:u,label:u}))}/>
            </div>
            {edit.categoria!=="attrezzo" && <Input label="Prezzo €/unità — serve per il costo dei cantieri in Contabilità" type="number" value={edit.prezzo_unitario} onChange={v=>setEdit(f=>({...f,prezzo_unitario:v}))}/>}
            {edit.id && <div style={{fontSize:11.5,color:C.textMuted,marginTop:-6}}>Se cambi la quantità, nello storico resta come correzione conteggio.</div>}
            <div style={{display:"flex",gap:10,justifyContent:"space-between",alignItems:"center"}}>
              {edit.id ? <button onClick={()=>eliminaArticolo(edit)} style={{background:"none",border:"none",color:C.red,fontSize:13,cursor:"pointer",padding:0}}>Elimina</button> : <span/>}
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>setEdit(null)}>Annulla</Btn>
                <Btn disabled={!edit.nome?.trim()} onClick={salvaArticolo}>Salva</Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {mov && <MagMovimento {...{mov, setMov, stock, setStock, db, lavori, lavoriAperti, lavoroDi, operai, presenze, registraMovimento, prezzo}}/>}

      {storico && (
        <Modal title="Storico movimenti" onClose={()=>setStorico(false)} wide>
          <MagMovimenti movimenti={movimenti}/>
        </Modal>
      )}
    </div>
  );
}

// ── Pannello unico: Porta in cantiere / Rientro / Arrivo merce / Correggi conteggio ──
function MagMovimento({mov, setMov, stock, setStock, db, lavori, lavoriAperti, lavoroDi, operai, presenze, registraMovimento, prezzo}){
  const [salvando, setSalvando] = useState(false);
  const T = {
    uscita:   {titolo:"Porta in cantiere",   bottone:"Conferma consegna"},
    rientro:  {titolo:"Rientro in magazzino", bottone:"Conferma rientro"},
    carico:   {titolo:"Arrivo merce",         bottone:"Carica in magazzino"},
    rettifica:{titolo:"Correggi conteggio",   bottone:"Salva conteggio"},
  }[mov.tipo];
  const conCantiere = mov.tipo==="uscita"||mov.tipo==="rientro";
  const set = (patch)=>setMov(m=>({...m,...patch}));
  const setQta = (id,v)=>setMov(m=>({...m,righe:{...m.righe,[id]:v}}));

  // Cantieri proposti: per il rientro quelli con qualcosa sopra; per la consegna quelli aperti (oggi in corso prima)
  const conRoba = new Set(Object.values(presenze).flatMap(p=>Object.keys(p)));
  const inCorsoOggi = (l)=>(l.giorni_lavoro||[]).includes(today) || (l.data_inizio<=today && l.data_fine>=today);
  const cantieri = mov.tipo==="rientro"
    ? [...new Set([...conRoba])].map(lavoroDi).filter(Boolean)
    : [...lavoriAperti].sort((a,b)=>(inCorsoOggi(b)-inCorsoOggi(a)));
  const lavoro = mov.lavoro_id ? lavoroDi(mov.lavoro_id) : null;

  // Articoli: nel rientro prima quelli presenti su quel cantiere
  const presenteQui = (id)=>lavoro ? (presenze[id]?.[lavoro.id]?.qta||0) : 0;
  const scelti = (id)=>parseFloat(mov.righe[id])>0 || mov.righe[id]==="";
  const elenco = stock
    .filter(s=>mov.tipo!=="rettifica" || mov.righe[s.id]!==undefined)
    .filter(s=>!mov.cerca.trim() || s.nome.toLowerCase().includes(mov.cerca.toLowerCase()))
    .filter(s=>mov.cerca.trim() || mov.righe[s.id]!==undefined || (mov.tipo==="rientro" ? presenteQui(s.id)>0 : true))
    .sort((a,b)=>((mov.righe[b.id]!==undefined)-(mov.righe[a.id]!==undefined)) || (presenteQui(b.id)-presenteQui(a.id)) || a.nome.localeCompare(b.nome));
  const righe = Object.entries(mov.righe).filter(([,v])=>parseFloat(v)>0 || (mov.tipo==="rettifica" && v!=="")).map(([id,v])=>({art:stock.find(s=>String(s.id)===String(id)), qta:v})).filter(r=>r.art);
  const valore = righe.reduce((t,r)=>t+(parseFloat(r.qta)||0)*((mov.tipo==="carico" && mov.prezzi[r.art.id]!==undefined && mov.prezzi[r.art.id]!=="") ? parseFloat(mov.prezzi[r.art.id])||0 : prezzo(r.art)),0);
  const mancanti = mov.tipo==="uscita" ? righe.filter(r=>(parseFloat(r.qta)||0)>(parseFloat(r.art.quantita)||0)) : [];

  async function conferma(){
    if(righe.length===0) return;
    if(conCantiere && !lavoro){ alert("Scegli il cantiere."); return; }
    if(mancanti.length>0 && !window.confirm(`In magazzino non basta: ${mancanti.map(r=>`${r.art.nome} (ci sono ${numIt(r.art.quantita)} ${r.art.unita})`).join(", ")}. Continuare? La giacenza andrà a zero.`)) return;
    setSalvando(true);
    const operaio = operai.find(o=>String(o.id)===String(mov.operaio_id))||null;
    for(const r of righe){
      let art = stock.find(s=>s.id===r.art.id)||r.art;
      if(mov.tipo==="carico" && mov.prezzi[art.id]!==undefined && mov.prezzi[art.id]!=="" && parseFloat(mov.prezzi[art.id])!==prezzo(art)){
        const p = parseFloat(mov.prezzi[art.id])||null;
        await db.from("magazzino_stock").update({prezzo_unitario:p}).eq("id",art.id);
        setStock(s=>s.map(x=>x.id===art.id?{...x,prezzo_unitario:p}:x));
        art = {...art, prezzo_unitario:p};
      }
      if(mov.tipo==="rettifica") await registraMovimento(art, "rettifica", 0, {nuovaQta:r.qta, note:mov.note||"Correzione conteggio"});
      else await registraMovimento(art, mov.tipo, r.qta, {lavoro, operaio, note:mov.note});
    }
    if(conCantiere && mov.ddt) stampaDDT(mov.tipo, righe.map(r=>({art:r.art, qta:r.qta})), lavoro, operaio, mov.note);
    setSalvando(false);
    setMov(null);
  }

  const passo = (a)=>a.unita==="pz"||a.categoria==="attrezzo"||a.unita==="conf"||a.unita==="sacchi"||a.unita==="rotoli" ? 1 : 1;
  const step = (a,d)=>{ const v = Math.max(0,(parseFloat(mov.righe[a.id])||0)+d*passo(a)); setQta(a.id, v>0?String(v):(mov.tipo==="rettifica"?"0":"")); };
  const chip = (on)=>({border:`1px solid ${on?C.text:C.border}`,background:on?C.text:C.card,color:on?"#fff":C.text,borderRadius:20,padding:"7px 12px",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",maxWidth:230,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"});

  return(
    <Modal title={T.titolo} onClose={()=>setMov(null)} wide>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {conCantiere && (
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>{mov.tipo==="uscita"?"Su quale cantiere?":"Da quale cantiere?"}</div>
            {cantieri.length===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>{mov.tipo==="rientro"?"Nessun cantiere ha materiale o attrezzi del magazzino.":"Nessun cantiere aperto."}</div> : (
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {cantieri.slice(0,10).map(l=>(
                  <button key={l.id} onClick={()=>{ const resp = l.responsabile_id && operai.some(o=>o.id===l.responsabile_id) ? String(l.responsabile_id) : mov.operaio_id; set({lavoro_id:String(l.id), operaio_id:mov.operaio_id||resp||""}); }} style={chip(String(mov.lavoro_id)===String(l.id))}>
                    {inCorsoOggi(l)?"● ":""}{l.titolo}
                  </button>
                ))}
              </div>
            )}
            {cantieri.length>10 && (
              <select value={mov.lavoro_id} onChange={e=>set({lavoro_id:e.target.value})} style={{...fieldBase,marginTop:8,width:"100%"}}>
                <option value="">Altri cantieri…</option>
                {cantieri.map(l=><option key={l.id} value={l.id}>{l.titolo}</option>)}
              </select>
            )}
          </div>
        )}
        {conCantiere && (
          <Select label={mov.tipo==="uscita"?"Lo porta / lo riceve":"Lo riporta"} value={mov.operaio_id} onChange={v=>set({operaio_id:v})} options={[{value:"",label:"—"},...operai.filter(o=>o.disponibile!==false||String(o.id)===String(mov.operaio_id)).map(o=>({value:String(o.id),label:o.nome}))]}/>
        )}

        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5}}>{mov.tipo==="rettifica"?"Quantità contata":"Cosa"}</div>
            {mov.tipo==="rientro" && lavoro && elenco.some(s=>presenteQui(s.id)>0) && (
              <button onClick={()=>{ const r={...mov.righe}; elenco.forEach(s=>{ if(presenteQui(s.id)>0) r[s.id]=String(presenteQui(s.id)); }); set({righe:r}); }} style={{background:"none",border:"none",color:C.brand,fontSize:12.5,fontWeight:700,cursor:"pointer",padding:0}}>Rientra tutto</button>
            )}
          </div>
          {mov.tipo!=="rettifica" && <input value={mov.cerca} onChange={e=>set({cerca:e.target.value})} placeholder="Cerca articolo…" style={{...fieldBase,width:"100%",boxSizing:"border-box",marginBottom:8}}/>}
          <div style={{display:"flex",flexDirection:"column",maxHeight:320,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:12}}>
            {mov.tipo==="rientro" && !lavoro && <div style={{padding:"18px",textAlign:"center",fontSize:12.5,color:C.textMuted}}>Scegli prima il cantiere.</div>}
            {!(mov.tipo==="rientro" && !lavoro) && elenco.length===0 && <div style={{padding:"18px",textAlign:"center",fontSize:12.5,color:C.textMuted}}>{mov.tipo==="rientro"?"Su questo cantiere non risulta niente: cerca l'articolo sopra.":"Nessun articolo. Aggiungilo da \"+ Materiale\"."}</div>}
            {!(mov.tipo==="rientro" && !lavoro) && elenco.map((s,i)=>{
              const v = mov.righe[s.id] ?? "";
              const on = parseFloat(v)>0;
              const qui = presenteQui(s.id);
              return(
                <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderTop:i?`1px solid ${C.border}`:"none",background:on?C.bg:"transparent"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,color:C.text,fontWeight:on?600:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.categoria==="attrezzo"?"🔧 ":""}{s.nome}</div>
                    <div style={{fontSize:11,color:C.textMuted}}>
                      {numIt(s.quantita)} {s.unita} in magazzino{qui>0?` · ${numIt(qui)} su questo cantiere`:""}
                    </div>
                  </div>
                  {mov.tipo==="carico" && on && (
                    <input type="number" placeholder="€/u" value={mov.prezzi[s.id]??(s.prezzo_unitario??"")} onChange={e=>setMov(m=>({...m,prezzi:{...m.prezzi,[s.id]:e.target.value}}))} title="Prezzo €/unità"
                      style={{width:70,...fieldBase,padding:"6px 8px",fontSize:12.5,textAlign:"right",background:C.card,border:`1px solid ${C.border}`}}/>
                  )}
                  <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                    {mov.tipo!=="rettifica" && <button onClick={()=>step(s,-1)} style={{width:30,height:30,borderRadius:9,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:16,cursor:"pointer",lineHeight:1}}>−</button>}
                    <input type="number" min="0" placeholder="0" value={v} onChange={e=>setQta(s.id, e.target.value)}
                      style={{width:58,...fieldBase,padding:"6px 6px",fontSize:13.5,fontWeight:600,textAlign:"center",background:C.card,border:`1px solid ${on?C.text:C.border}`}}/>
                    {mov.tipo!=="rettifica" && <button onClick={()=>step(s,+1)} style={{width:30,height:30,borderRadius:9,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:16,cursor:"pointer",lineHeight:1}}>+</button>}
                    <span style={{fontSize:11.5,color:C.textMuted,width:34}}>{s.unita}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Input label="Nota (facoltativa)" value={mov.note} onChange={v=>set({note:v})} placeholder={mov.tipo==="carico"?"Es. DDT fornitore n°…":mov.tipo==="rettifica"?"Es. inventario di fine mese":"Es. per la seconda mano"}/>

        {mancanti.length>0 && <div style={{fontSize:12.5,color:C.red,fontWeight:600}}>Non basta in magazzino: {mancanti.map(r=>r.art.nome).join(", ")}</div>}

        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",borderTop:`1px solid ${C.border}`,paddingTop:14}}>
          <span style={{fontSize:12.5,color:C.textMuted,marginRight:"auto"}}>
            {righe.length} articol{righe.length===1?"o":"i"}{valore>0 && mov.tipo!=="rettifica"?` · ${euro(valore)}`:""}
            {mov.tipo==="uscita" && valore>0 && lavoro?" → spesa del cantiere in Contabilità":""}
          </span>
          {conCantiere && (
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12.5,color:C.textSub,cursor:"pointer"}}>
              <input type="checkbox" checked={mov.ddt} onChange={e=>set({ddt:e.target.checked})}/> Stampa DDT
            </label>
          )}
          <Btn variant="ghost" onClick={()=>setMov(null)}>Annulla</Btn>
          <Btn disabled={salvando || righe.length===0 || (conCantiere && !lavoro)} onClick={conferma}>{salvando?"Salvataggio…":T.bottone}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Vista per cantiere: cosa c'è sopra, quanto vale, ordini aperti ──
function MagCantieri({lavori, lavoriAperti, lavoroDi, stock, presenze, ordini, apriMov, prezzo, setVista, setOrdFiltro}){
  const [aperto, setAperto] = useState(null);
  const perLavoro = {}; // lavoroId -> [{art, qta, operaio}]
  Object.entries(presenze).forEach(([artId,mp])=>Object.entries(mp).forEach(([lid,p])=>{
    const art = stock.find(s=>String(s.id)===String(artId)); if(!art) return;
    (perLavoro[lid] = perLavoro[lid]||[]).push({art, qta:p.qta, operaio:p.operaio});
  }));
  const ordiniAperti = (lid)=>ordini.filter(o=>String(o.lavoro_id)===String(lid) && o.stato!=="consumato");
  const ids = [...new Set([...Object.keys(perLavoro), ...lavoriAperti.filter(l=>ordiniAperti(l.id).length>0).map(l=>String(l.id))])];
  const conRoba = ids.map(lavoroDi).filter(Boolean).sort((a,b)=>(b.data_inizio||"").localeCompare(a.data_inizio||""));
  const senza = lavoriAperti.filter(l=>!ids.includes(String(l.id)));

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {conRoba.length===0 && <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"40px 18px",textAlign:"center",color:C.textMuted,fontSize:13}}>Nessun cantiere ha materiale o attrezzi del magazzino.</div>}
      {conRoba.map(l=>{
        const items = (perLavoro[l.id]||[]).sort((a,b)=>(a.art.categoria==="attrezzo")-(b.art.categoria==="attrezzo") || a.art.nome.localeCompare(b.art.nome));
        const mat = items.filter(i=>i.art.categoria!=="attrezzo");
        const att = items.filter(i=>i.art.categoria==="attrezzo");
        const valore = mat.reduce((t,i)=>t+i.qta*prezzo(i.art),0);
        const ord = ordiniAperti(l.id);
        const isOpen = aperto===l.id;
        const chiuso = ["Completato","Annullato","Rifiutato"].includes(l.stato);
        return(
          <div key={l.id} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
            <div onClick={()=>setAperto(isOpen?null:l.id)} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",cursor:"pointer",flexWrap:"wrap"}}>
              <div style={{flex:"1 1 200px",minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:STATI_COLOR[l.stato]||C.textMuted,flexShrink:0}}/>
                  <span style={{fontSize:14,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{l.titolo}</span>
                </div>
                <div style={{fontSize:11.5,color:chiuso&&att.length>0?C.red:C.textMuted,marginTop:2,fontWeight:chiuso&&att.length>0?600:400}}>
                  {l.cliente||"—"}{chiuso&&items.length>0?` · ${l.stato.toLowerCase()}: da far rientrare`:""}
                </div>
              </div>
              <div style={{display:"flex",gap:18,alignItems:"center"}}>
                {[{v:valore>0?euro(valore):mat.length, l:"materiale"},{v:att.length, l:att.length===1?"attrezzo":"attrezzi"},{v:ord.length, l:"ordini aperti"}].map(k=>(
                  <div key={k.l} style={{textAlign:"right"}}>
                    <div style={{fontSize:14,fontWeight:700,color:k.v&&k.v!=="0"?C.text:C.textMuted}}>{k.v||0}</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.4,fontWeight:700}}>{k.l}</div>
                  </div>
                ))}
                <span style={{color:C.textMuted,fontSize:12,transform:isOpen?"rotate(180deg)":"none"}}>▾</span>
              </div>
            </div>
            {isOpen && (
              <div style={{borderTop:`1px solid ${C.border}`,padding:"12px 18px 16px",background:C.bg,display:"flex",flexDirection:"column",gap:10}}>
                {items.length===0 && <div style={{fontSize:12.5,color:C.textMuted}}>Niente del magazzino su questo cantiere.</div>}
                {items.map(i=>(
                  <div key={i.art.id} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:13,padding:"7px 10px",background:C.card,borderRadius:9}}>
                    <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{i.art.categoria==="attrezzo"?"🔧 ":""}{i.art.nome}{i.operaio?<span style={{color:C.textMuted}}> · {i.operaio}</span>:null}</span>
                    <span style={{flexShrink:0}}><b>{numIt(i.qta)} {i.art.unita}</b>{prezzo(i.art)>0&&i.art.categoria!=="attrezzo"?<span style={{color:C.textMuted}}> · {euro(i.qta*prezzo(i.art))}</span>:null}</span>
                  </div>
                ))}
                {ord.length>0 && (
                  <div style={{fontSize:12.5,color:C.textSub}}>
                    Ordini aperti: {ord.map(o=>`${o.nome} (${STATI_ORD.find(s=>s.id===o.stato)?.label||o.stato})`).join(", ")} · <button onClick={()=>{setOrdFiltro("tutti");setVista("ordini");}} style={{background:"none",border:"none",color:C.brand,fontWeight:700,cursor:"pointer",padding:0,fontSize:12.5}}>vai agli ordini</button>
                  </div>
                )}
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {!chiuso && <Btn small onClick={()=>apriMov("uscita",{lavoro_id:String(l.id)})}>↗ Porta altro</Btn>}
                  {items.length>0 && <Btn small variant="ghost" onClick={()=>{ const r={}; items.forEach(i=>{ r[i.art.id]=String(i.qta); }); apriMov("rientro",{lavoro_id:String(l.id),righe:r}); }}>↙ Fai rientrare tutto</Btn>}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {senza.length>0 && (
        <div style={{fontSize:12.5,color:C.textMuted,padding:"4px 2px"}}>
          Cantieri aperti senza materiale del magazzino: {senza.slice(0,8).map((l,i)=>(
            <span key={l.id}>{i?", ":""}<button onClick={()=>apriMov("uscita",{lavoro_id:String(l.id)})} style={{background:"none",border:"none",color:C.textSub,fontWeight:600,cursor:"pointer",padding:0,fontSize:12.5,textDecoration:"underline",textUnderlineOffset:2}}>{l.titolo}</button></span>
          ))}{senza.length>8?` e altri ${senza.length-8}`:""}
        </div>
      )}
    </div>
  );
}

function stampaDDT(tipo, righe, lavoro, operaio, note){
  const dataOggi = new Date().toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"});
  const numero = `${tipo==="uscita"?"U":"R"}-${Date.now().toString().slice(-6)}`;
  const esc = (t)=>String(t??"").replace(/[&<>]/g,c=>({"&":"&amp;","<":"&lt;",">":"&gt;"}[c]));
  const righeHTML = righe.map(r=>`<tr><td>${esc(r.art?.nome)}</td><td>${r.art?.categoria==="attrezzo"?"Attrezzo":"Materiale"}</td><td style="text-align:right">${esc(r.qta)} ${esc(r.art?.unita)}</td></tr>`).join("");
  const html = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8"><title>DDT ${numero}</title>
  <style>body{font-family:Inter,'Segoe UI',Arial,sans-serif;color:#16171A;margin:0;padding:40px;font-size:13px}
  .h{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #16171A;padding-bottom:14px;margin-bottom:24px}
  h1{font-size:18px;margin:0 0 4px}.sub{font-size:11px;color:#6E6F73;text-transform:uppercase;letter-spacing:1px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px}.box{background:#FAFAF8;border:1px solid #E7E6E1;border-radius:8px;padding:12px 14px}
  table{width:100%;border-collapse:collapse}th{text-align:left;font-size:11px;text-transform:uppercase;color:#6E6F73;padding:8px 10px;border-bottom:1px solid #16171A}
  td{padding:8px 10px;border-bottom:1px solid #E7E6E1}.f{margin-top:70px;display:flex;justify-content:space-between}.firma{border-top:1px solid #999;width:200px;padding-top:6px;text-align:center;font-size:11px;color:#6E6F73}</style></head><body>
  <div class="h"><div><b style="font-size:16px">Parquetti Group</b></div><div style="text-align:right"><h1>DDT — ${tipo==="uscita"?"Consegna a cantiere":"Rientro da cantiere"}</h1><div class="sub">N° ${numero} · ${dataOggi}</div></div></div>
  <div class="meta"><div class="box"><b>Cantiere</b><br/>${esc(lavoro?.titolo)}<br/>${esc(lavoro?.indirizzo)}</div><div class="box"><b>${tipo==="uscita"?"Consegnato a":"Ritirato da"}</b><br/>${esc(operaio?.nome||"—")}</div></div>
  <table><thead><tr><th>Articolo</th><th>Tipo</th><th style="text-align:right">Quantità</th></tr></thead><tbody>${righeHTML}</tbody></table>
  ${note?`<p style="margin-top:16px;color:#6E6F73">Note: ${esc(note)}</p>`:""}
  <div class="f"><div class="firma">Firma trasportatore</div><div class="firma">Firma ricevente</div></div></body></html>`;
  const url = URL.createObjectURL(new Blob([html],{type:"text/html"}));
  const w = window.open(url,"_blank");
  setTimeout(()=>{ w?.print(); URL.revokeObjectURL(url); },800);
}

// ── ORDINI ──
const STATI_ORD = [
  {id:"da_ordinare", label:"Da ordinare", next:"ordinato",    azione:"Ordinato"},
  {id:"ordinato",    label:"In arrivo",   next:"in_cantiere", azione:"Arrivato"},
  {id:"in_cantiere", label:"Arrivato",    next:"consumato",   azione:"Chiudi"},
  {id:"consumato",   label:"Chiusi"},
];
function MagOrdini({db, ordini, setOrdini, lavori, lavoriAperti, lavoroDi, stock, setStock, registraMovimento, filtro, setFiltro}){
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [copiato, setCopiato] = useState(null);

  const cerca = (o)=>{ if(!q.trim()) return true; const t=q.toLowerCase(); const l=lavoroDi(o.lavoro_id);
    return [o.nome,o.fornitore,l?.titolo,l?.cliente,l?.indirizzo].some(x=>x?.toLowerCase().includes(t)); };
  const visibili = ordini.filter(o=>(filtro==="tutti"||o.stato===filtro) && cerca(o));

  // Gruppi: un cantiere per gruppo, ordinati per data di inizio; "Scorta magazzino" in fondo
  const gruppi = {};
  visibili.forEach(o=>{ const k = o.lavoro_id ? String(o.lavoro_id) : "mag"; (gruppi[k] = gruppi[k]||[]).push(o); });
  const chiavi = Object.keys(gruppi).sort((a,b)=>{
    if(a==="mag") return 1; if(b==="mag") return -1;
    return (lavoroDi(a)?.data_inizio||"9").localeCompare(lavoroDi(b)?.data_inizio||"9");
  });
  const fornitori = [...new Set(ordini.map(o=>o.fornitore).filter(Boolean))].sort();

  async function avanza(o, stato){
    const extra = {};
    if(stato==="ordinato" && !(parseFloat(o.quantita_ordinata)>0)) extra.quantita_ordinata = o.quantita_necessaria;
    if(stato==="in_cantiere" && !(parseFloat(o.quantita_consegnata)>0)) extra.quantita_consegnata = o.quantita_ordinata||o.quantita_necessaria;
    const {error} = await db.from("materiali").update({stato, ...extra}).eq("id",o.id);
    if(error){ alert("Errore: "+error.message); return; }
    setOrdini(os=>os.map(x=>x.id===o.id?{...x,stato,...extra}:x));
    // Ordine di scorta arrivato → entra in magazzino
    if(stato==="in_cantiere" && !o.lavoro_id){
      const qta = parseFloat(extra.quantita_consegnata ?? o.quantita_consegnata) || parseFloat(o.quantita_necessaria) || 0;
      let art = stock.find(s=>s.nome.trim().toLowerCase()===String(o.nome).trim().toLowerCase());
      if(!window.confirm(`Caricare ${numIt(qta)} ${o.unita||""} di "${o.nome}" in magazzino?`)) return;
      if(!art){
        const {data:n, error:e} = await db.from("magazzino_stock").insert([{nome:o.nome, categoria:"materiale", unita:o.unita||"pz", quantita:0, scorta_minima:0, prezzo_unitario:parseFloat(o.prezzo_unitario)||null}]).select().single();
        if(e){ alert("Errore nel creare l'articolo: "+e.message); return; }
        art = n; setStock(s=>[...s,n]);
      }
      await registraMovimento(art, "carico", qta, {note:`Ordine${o.fornitore?` da ${o.fornitore}`:""}`});
    }
  }
  async function segnaTuttiOrdinati(lista){
    for(const o of lista) await avanza(o, "ordinato");
  }
  function copiaLista(fornitore, lista){
    const testo = `Ordine Parquetti Group${fornitore?` — ${fornitore}`:""}\n` + lista.map(o=>{ const l=lavoroDi(o.lavoro_id); return `• ${o.nome}: ${numIt(o.quantita_necessaria)} ${o.unita||""}${l?` (cantiere ${l.titolo})`:""}`; }).join("\n");
    const fatto = ()=>{ setCopiato(fornitore||"—"); setTimeout(()=>setCopiato(null),2000); };
    if(navigator.clipboard?.writeText) navigator.clipboard.writeText(testo).then(fatto, ()=>{ window.prompt("Copia la lista:", testo); });
    else window.prompt("Copia la lista:", testo);
  }
  async function salva(){
    const {id, created_at, ...r} = edit;
    const qN = parseFloat(r.quantita_necessaria)||0, qO = parseFloat(r.quantita_ordinata)||0, qC = parseFloat(r.quantita_consegnata)||0, pu = parseFloat(r.prezzo_unitario)||0;
    const d = {...r, nome:r.nome.trim(), lavoro_id:r.lavoro_id||null, quantita_necessaria:qN, quantita_ordinata:qO, quantita_consegnata:qC, prezzo_unitario:pu, costo_totale:pu*(qC||qO||qN)};
    if(id){
      const {error} = await db.from("materiali").update(d).eq("id",id);
      if(error){ alert("Errore nel salvataggio: "+error.message); return; }
      setOrdini(os=>os.map(x=>x.id===id?{...x,...d}:x));
    } else {
      const {data:n, error} = await db.from("materiali").insert([d]).select().single();
      if(error){ alert("Errore nel salvataggio: "+error.message+(/lavoro_id/.test(error.message)?"\n\nIl database richiede un cantiere: sceglilo nell'elenco.":"")); return; }
      if(n) setOrdini(os=>[n,...os]);
    }
    setEdit(null);
  }
  async function elimina(o){
    if(!window.confirm(`Eliminare "${o.nome}"?`)) return;
    const {error} = await db.from("materiali").delete().eq("id",o.id);
    if(error){ alert("Errore: "+error.message); return; }
    setOrdini(os=>os.filter(x=>x.id!==o.id)); setEdit(null);
  }

  const perFornitore = filtro==="da_ordinare" ? Object.entries(visibili.reduce((m,o)=>{ const k=o.fornitore||""; (m[k]=m[k]||[]).push(o); return m; },{})).sort(([a],[b])=>(a===""?1:b===""?-1:a.localeCompare(b))) : [];
  const nuovo = (lavoro_id="")=>setEdit({nome:"",tipo:"consumabile",fornitore:"",quantita_necessaria:"",quantita_ordinata:"",quantita_consegnata:"",prezzo_unitario:"",unita:"pz",stato:"da_ordinare",note:"",lavoro_id});

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca materiale, fornitore, cantiere o cliente…" style={{...fieldBase,background:C.card,boxShadow:C.shadowSm,flex:"1 1 220px",fontSize:13.5}}/>
        <Btn small onClick={()=>nuovo(lavoriAperti[0]?.id||"")}>+ Ordine</Btn>
      </div>
      <Segmenti value={filtro} onChange={setFiltro} items={[
        ...STATI_ORD.map(s=>({id:s.id, label:s.label, n:ordini.filter(o=>o.stato===s.id).length, alert:s.id==="da_ordinare"})),
        {id:"tutti", label:"Tutti"},
      ]}/>

      {perFornitore.length>0 && (
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"12px 18px",display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6}}>Liste per fornitore</div>
          {perFornitore.map(([f,lista])=>(
            <div key={f||"_"} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",fontSize:13}}>
              <span style={{fontWeight:600,color:f?C.text:C.textMuted,flex:"1 1 160px"}}>{f||"Fornitore non indicato"} <span style={{color:C.textMuted,fontWeight:500}}>· {lista.length}</span></span>
              <button onClick={()=>copiaLista(f,lista)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:9,padding:"5px 10px",fontSize:12,fontWeight:600,color:C.text,cursor:"pointer"}}>{copiato===(f||"—")?"Copiata ✓":"Copia lista"}</button>
              <button onClick={()=>segnaTuttiOrdinati(lista)} style={{background:"none",border:"none",fontSize:12,fontWeight:600,color:C.textSub,cursor:"pointer"}}>Segna tutti ordinati</button>
            </div>
          ))}
        </div>
      )}

      {chiavi.length===0 && <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"40px 18px",textAlign:"center",color:C.textMuted,fontSize:13}}>Nessun ordine{filtro!=="tutti"?` in "${STATI_ORD.find(s=>s.id===filtro)?.label}"`:""}.</div>}

      {chiavi.map(k=>{
        const l = k==="mag" ? null : lavoroDi(k);
        const lista = gruppi[k];
        const tot = lista.reduce((t,o)=>t+(parseFloat(o.costo_totale)||0),0);
        const gg = l?.data_inizio ? Math.round((new Date(l.data_inizio+"T12:00:00")-new Date(today+"T12:00:00"))/86400000) : null;
        const urgente = gg!=null && gg>=0 && gg<=14 && lista.some(o=>o.stato==="da_ordinare");
        return(
          <div key={k} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"12px 18px",background:C.bg,flexWrap:"wrap"}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>{l ? l.titolo : k==="mag" ? "Scorta magazzino" : "Cantiere eliminato"}</div>
                <div style={{fontSize:11.5,color:urgente?C.brand:C.textMuted,marginTop:1,fontWeight:urgente?600:400}}>
                  {l ? `${l.cliente||""}${l.data_inizio?` · inizio ${fmt(l.data_inizio)}`:""}${gg!=null&&gg>=0?` · tra ${gg} gg`:""}` : "Arrivati, vanno in inventario"}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                {tot>0 && <span style={{fontSize:12.5,color:C.textSub}}>{euro(tot)}</span>}
                <button onClick={()=>nuovo(l?l.id:"")} style={{background:"none",border:"none",color:C.textSub,fontSize:12.5,fontWeight:600,cursor:"pointer"}}>+ Aggiungi</button>
              </div>
            </div>
            {lista.map(o=>{
              const st = STATI_ORD.find(s=>s.id===o.stato)||STATI_ORD[0];
              return(
                <div key={o.id} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 18px",borderTop:`1px solid ${C.border}`,flexWrap:"wrap"}}>
                  <div onClick={()=>setEdit({...o,lavoro_id:o.lavoro_id||"",quantita_necessaria:String(o.quantita_necessaria??""),quantita_ordinata:String(o.quantita_ordinata??""),quantita_consegnata:String(o.quantita_consegnata??""),prezzo_unitario:o.prezzo_unitario??""})} style={{flex:"1 1 200px",minWidth:0,cursor:"pointer"}}>
                    <div style={{fontSize:13.5,fontWeight:600,color:C.text}}>{o.nome}</div>
                    <div style={{fontSize:11.5,color:C.textMuted,marginTop:1}}>
                      {numIt(o.quantita_necessaria)} {o.unita}{o.fornitore?` · ${o.fornitore}`:""}{o.costo_totale>0?` · ${euro(o.costo_totale)}`:""}{o.note?` · ${o.note}`:""}
                    </div>
                  </div>
                  <span style={{fontSize:11.5,fontWeight:600,color:o.stato==="da_ordinare"?C.red:o.stato==="consumato"?C.textMuted:C.textSub}}>{st.label}</span>
                  {st.next && <button onClick={()=>avanza(o, st.next)} style={{background:o.stato==="da_ordinare"?C.text:C.card,color:o.stato==="da_ordinare"?"#fff":C.text,border:`1px solid ${o.stato==="da_ordinare"?C.text:C.border}`,borderRadius:9,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{st.azione}</button>}
                </div>
              );
            })}
          </div>
        );
      })}

      {edit && (
        <Modal title={edit.id?"Modifica ordine":"Nuovo ordine"} onClose={()=>setEdit(null)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <Input label="Materiale" value={edit.nome} onChange={v=>{ const art=stock.find(s=>s.nome.toLowerCase()===v.toLowerCase()); setEdit(f=>({...f,nome:v,...(art&&!f.id?{unita:art.unita,prezzo_unitario:art.prezzo_unitario??f.prezzo_unitario}:{})})); }} placeholder="Es. Colla MS, Rovere 10 mm, battiscopa…"/>
              {edit.nome.length>=2 && !stock.some(s=>s.nome.toLowerCase()===edit.nome.toLowerCase()) && (()=>{ const sug = stock.filter(s=>s.nome.toLowerCase().includes(edit.nome.toLowerCase())).slice(0,4); return sug.length>0 && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                  {sug.map(s=><button key={s.id} onClick={()=>setEdit(f=>({...f,nome:s.nome,unita:s.unita,prezzo_unitario:s.prezzo_unitario??f.prezzo_unitario}))} style={{background:C.bg,border:"none",borderRadius:20,padding:"4px 10px",fontSize:12,color:C.textSub,cursor:"pointer"}}>{s.nome}</button>)}
                </div>
              );})()}
            </div>
            <Select label="Per" value={edit.lavoro_id||""} onChange={v=>setEdit(f=>({...f,lavoro_id:v}))} options={[{value:"",label:"Scorta magazzino"},...(edit.lavoro_id && !lavoriAperti.some(l=>String(l.id)===String(edit.lavoro_id)) && lavoroDi(edit.lavoro_id) ? [{value:edit.lavoro_id,label:lavoroDi(edit.lavoro_id).titolo}] : []),...lavoriAperti.map(l=>({value:l.id,label:`${l.titolo}${l.data_inizio?` · ${fmt(l.data_inizio)}`:""}`}))]}/>
            <div>
              <Input label="Fornitore" value={edit.fornitore||""} onChange={v=>setEdit(f=>({...f,fornitore:v}))} placeholder="Es. rivenditore di zona"/>
              {fornitori.length>0 && !edit.fornitore && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                  {fornitori.slice(0,6).map(f=><button key={f} onClick={()=>setEdit(x=>({...x,fornitore:f}))} style={{background:C.bg,border:"none",borderRadius:20,padding:"4px 10px",fontSize:12,color:C.textSub,cursor:"pointer"}}>{f}</button>)}
                </div>
              )}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
              <Input label="Quantità" type="number" value={edit.quantita_necessaria} onChange={v=>setEdit(f=>({...f,quantita_necessaria:v}))}/>
              <Select label="Unità" value={edit.unita||"pz"} onChange={v=>setEdit(f=>({...f,unita:v}))} options={[...new Set([...UNITA_MAG, edit.unita||"pz"])].map(u=>({value:u,label:u}))}/>
              <Input label="€/unità" type="number" value={edit.prezzo_unitario} onChange={v=>setEdit(f=>({...f,prezzo_unitario:v}))}/>
            </div>
            {edit.id && (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <Input label="Ordinata" type="number" value={edit.quantita_ordinata} onChange={v=>setEdit(f=>({...f,quantita_ordinata:v}))}/>
                <Input label="Consegnata" type="number" value={edit.quantita_consegnata} onChange={v=>setEdit(f=>({...f,quantita_consegnata:v}))}/>
                <Select label="Stato" value={edit.stato} onChange={v=>setEdit(f=>({...f,stato:v}))} options={STATI_ORD.map(s=>({value:s.id,label:s.label}))}/>
              </div>
            )}
            <Input label="Note" value={edit.note||""} onChange={v=>setEdit(f=>({...f,note:v}))} placeholder="N° ordine, DDT, consegna…"/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
              {edit.id ? <button onClick={()=>elimina(edit)} style={{background:"none",border:"none",color:C.red,fontSize:13,cursor:"pointer",padding:0}}>Elimina</button> : <span style={{fontSize:12.5,color:C.textMuted}}>{(parseFloat(edit.prezzo_unitario)||0)*(parseFloat(edit.quantita_necessaria)||0)>0?`Totale ${euro((parseFloat(edit.prezzo_unitario)||0)*(parseFloat(edit.quantita_necessaria)||0))}`:""}</span>}
              <div style={{display:"flex",gap:10}}>
                <Btn variant="ghost" onClick={()=>setEdit(null)}>Annulla</Btn>
                <Btn disabled={!edit.nome?.trim()} onClick={salva}>Salva</Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── MOVIMENTI ──
function MagMovimenti({movimenti}){
  const [tipo, setTipo] = useState("tutti");
  const [q, setQ] = useState("");
  const lista = movimenti
    .filter(m=>tipo==="tutti"||m.tipo===tipo)
    .filter(m=>!q.trim() || [m.articolo_nome,m.lavoro_titolo,m.operaio_nome,m.note].some(x=>x?.toLowerCase().includes(q.toLowerCase())));
  const segno = (m)=>m.tipo==="rettifica" ? ((parseFloat(m.quantita)||0)>=0?"+":"") : (TIPI_MOV[m.tipo]?.segno>0?"+":"−");
  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca articolo, cantiere, operaio…" style={{...fieldBase,background:C.card,boxShadow:C.shadowSm,flex:"1 1 200px",fontSize:13.5}}/>
        <Segmenti value={tipo} onChange={setTipo} items={[{id:"tutti",label:"Tutti"},...Object.entries(TIPI_MOV).map(([id,t])=>({id,label:t.label}))]}/>
      </div>
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,overflow:"hidden"}}>
        {lista.length===0 && <div style={{padding:"40px 18px",textAlign:"center",color:C.textMuted,fontSize:13}}>Nessun movimento.</div>}
        {lista.map((m,i)=>(
          <div key={m.id} style={{display:"grid",gridTemplateColumns:"64px 84px minmax(0,1.6fr) minmax(0,1.4fr)",gap:12,alignItems:"center",padding:"10px 18px",borderTop:i?`1px solid ${C.border}`:"none",fontSize:13}}>
            <span style={{color:C.textMuted,fontSize:12}}>{fmt(m.data)}</span>
            <span style={{fontSize:12,fontWeight:600,color:C.textSub}}>{TIPI_MOV[m.tipo]?.label||m.tipo}</span>
            <span style={{minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:C.text}}>
              <b style={{fontWeight:600}}>{segno(m)}{numIt(Math.abs(parseFloat(m.quantita)||0))} {m.unita}</b> {m.articolo_nome}
            </span>
            <span style={{minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:C.textMuted,fontSize:12}}>
              {[m.lavoro_titolo,m.operaio_nome,m.note].filter(Boolean).join(" · ")||"—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
const LAVORI_PAGES = {
  "in-corso":    {stato:"Da organizzare",    titolo:"Da Organizzare"},
  "confermati":  {stato:"Confermato",  titolo:"Lavori Confermati"},
  "programmati": {stato:"Programmato", titolo:"In Corso"},
  "preventivi":  {stato:"Preventivo",  titolo:"Preventivi"},
  "sospesi":     {stato:"Sospeso",     titolo:"Lavori Sospesi"},
  "archivio":    {stato:"arch",        titolo:"Archivio Lavori"},
};

// ── CONTABILITÀ ──────────────────────────────────────────────────────────────
const CATEGORIE_SPESA = [
  {id:"materiali", label:"Materiali", color:C.blue},
  {id:"manodopera", label:"Manodopera", color:C.purple},
  {id:"trasporto", label:"Trasporto", color:C.orange},
  {id:"attrezzatura", label:"Attrezzatura", color:C.green},
  {id:"altro", label:"Altro", color:C.textMuted},
];

function AnelloMargine({percentuale, size=36}){
  const r = (size-5)/2, c = 2*Math.PI*r;
  const pct = Math.max(0, Math.min(100, percentuale));
  const colore = percentuale>=0 ? C.green : C.red;
  const cifre = Math.round(Math.abs(percentuale)).toString().length; // 1, 2 o 3 cifre (es. 100%)
  const fontSize = Math.round(size * (cifre>=3 ? 0.24 : 0.3));
  return(
    <div style={{position:"relative",width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.bg} strokeWidth="3"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={colore} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c-(pct/100)*c}/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize,fontWeight:800,color:colore,letterSpacing:-0.4}}>{Math.round(percentuale)}%</div>
    </div>
  );
}

function euro(n){ return (n||0).toLocaleString("it-IT",{style:"currency",currency:"EUR",maximumFractionDigits:0}); }

// Cursore a scorrimento per scegliere un orario, in step di 15 minuti tra le 05:00 e le 21:00.
function TimeSlider({value, onChange, min="05:00", max="21:00", step=15}){
  const toMin = t=>{ const [h,m]=(t||"07:00").split(":").map(Number); return h*60+(m||0); };
  const toStr = m=>`${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
  const minM=toMin(min), maxM=toMin(max);
  const curM = Math.min(maxM, Math.max(minM, toMin(value)));
  const pct = (curM-minM)/(maxM-minM)*100;
  return(
    <div style={{display:"flex",alignItems:"center",gap:14}}>
      <div style={{fontFamily:C.fontBrand,fontSize:22,fontWeight:800,color:C.text,minWidth:64,fontVariantNumeric:"tabular-nums",letterSpacing:-0.5}}>{toStr(curM)}</div>
      <input type="range" min={minM} max={maxM} step={step} value={curM}
        onChange={e=>onChange(toStr(parseInt(e.target.value)))}
        style={{flex:1,height:6,borderRadius:6,cursor:"pointer",accentColor:C.accent,background:`linear-gradient(to right, ${C.accent} ${pct}%, ${C.border} ${pct}%)`,WebkitAppearance:"none",appearance:"none"}}/>
    </div>
  );
}

function SpesaModal({lavoro, onClose, onSave, db, categorieCustom, onNuovaCategoria, operai=[], stockMagazzino=[]}){
  const [categoria, setCategoria] = useState("materiali");
  const [descrizione, setDescrizione] = useState("");
  const [importo, setImporto] = useState("");
  const [importoTouched, setImportoTouched] = useState(false);
  const [data, setData] = useState(today);
  const [nuovaCat, setNuovaCat] = useState(false);
  const [nomeNuovaCat, setNomeNuovaCat] = useState("");

  // ── Manodopera: dipendente / subappaltatore ──
  const [tipoMdo, setTipoMdo] = useState("dipendente"); // "dipendente" | "subappaltatore"
  const [operaioId, setOperaioId] = useState(operai[0]?.id||"");
  const [mattina, setMattina] = useState(false);   // 7:30-12:00 → 4.5h
  const [pomeriggio, setPomeriggio] = useState(false); // 13:00-16:30 → 3.5h
  const ORE_MATTINA = 4.5, ORE_POMERIGGIO = 3.5;
  const [nomeSubappaltatore, setNomeSubappaltatore] = useState("");

  // ── Materiali: collegato a magazzino ──
  const [articoloId, setArticoloId] = useState("");
  const [qtaMateriale, setQtaMateriale] = useState("");

  const tutteCategorie = [...CATEGORIE_SPESA.slice(0,-1), ...categorieCustom, CATEGORIE_SPESA[CATEGORIE_SPESA.length-1]];
  const operaioSel = operai.find(o=>String(o.id)===String(operaioId));
  const articoloSel = stockMagazzino.find(a=>String(a.id)===String(articoloId));

  // Ricalcolo automatico importo per manodopera dipendente (finché l'utente non lo tocca a mano)
  useEffect(()=>{
    if(categoria!=="manodopera" || tipoMdo!=="dipendente" || importoTouched) return;
    const ore = (mattina?ORE_MATTINA:0) + (pomeriggio?ORE_POMERIGGIO:0);
    if(operaioSel?.costo_ora && ore>0) setImporto((ore*operaioSel.costo_ora).toFixed(2));
  },[mattina, pomeriggio, operaioId, categoria, tipoMdo]);

  // Ricalcolo automatico importo per materiali da magazzino
  useEffect(()=>{
    if(categoria!=="materiali" || !articoloSel || importoTouched) return;
    const q = parseFloat(qtaMateriale)||0;
    if(articoloSel.prezzo_unitario>0 && q>0) setImporto((q*articoloSel.prezzo_unitario).toFixed(2));
  },[qtaMateriale, articoloId, categoria]);

  const canSave = (()=>{
    if(!(importo && parseFloat(importo)>0)) return false;
    if(categoria==="manodopera"){
      if(tipoMdo==="dipendente") return !!operaioId;
      return !!nomeSubappaltatore.trim();
    }
    return true;
  })();

  async function salvaCategoria(){
    if(!nomeNuovaCat.trim()) return;
    const id = "custom_"+Date.now();
    await onNuovaCategoria({id, nome:nomeNuovaCat.trim()});
    setCategoria(id);
    setNuovaCat(false);
    setNomeNuovaCat("");
  }

  function handleSave(){
    const imp = parseFloat(importo)||0;

    if(categoria==="manodopera"){
      let desc;
      if(tipoMdo==="dipendente"){
        const ore = (mattina?ORE_MATTINA:0) + (pomeriggio?ORE_POMERIGGIO:0);
        const fasce = [mattina?`mattina 7:30-12:00`:null, pomeriggio?`pomeriggio 13:00-16:30`:null].filter(Boolean).join(" + ");
        desc = descrizione.trim() || `${operaioSel?.nome||"Operaio"} — ${ore}h${fasce?` (${fasce})`:""}`;
      } else {
        desc = descrizione.trim() || `Subappalto — ${nomeSubappaltatore.trim()}`;
      }
      onSave({categoria, descrizione:desc, importo:imp, data});
      return;
    }

    if(categoria==="materiali" && articoloSel){
      const q = parseFloat(qtaMateriale)||0;
      const desc = descrizione.trim() || articoloSel.nome;
      onSave({categoria, descrizione:desc, importo:imp, data, articolo_id:articoloSel.id, quantita_scaricata:q});
      return;
    }

    onSave({categoria, descrizione, importo:imp, data});
  }

  return(
    <Modal title={`+ Spesa — ${lavoro.titolo}`} onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {nuovaCat ? (
          <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
            <Input label="Nuova categoria" value={nomeNuovaCat} onChange={setNomeNuovaCat} placeholder="Es. Noleggio attrezzi"/>
            <Btn small onClick={salvaCategoria}>Crea</Btn>
            <Btn small variant="ghost" onClick={()=>setNuovaCat(false)}>×</Btn>
          </div>
        ) : (
          <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
            <div style={{flex:1}}>
              <Select label="Categoria" value={categoria} onChange={setCategoria}
                options={tutteCategorie.map(c=>({value:c.id,label:c.label}))}/>
            </div>
            <Btn small variant="ghost" onClick={()=>setNuovaCat(true)}>+ Nuova</Btn>
          </div>
        )}

        {categoria==="manodopera" && (
          <div style={{display:"flex",flexDirection:"column",gap:12,background:C.bg,borderRadius:14,padding:14,border:`1px solid ${C.border}`}}>
            <Select label="Tipo" value={tipoMdo} onChange={setTipoMdo} options={[{value:"dipendente",label:"👷 Dipendente"},{value:"subappaltatore",label:"🤝 Subappaltatore"}]}/>
            {tipoMdo==="dipendente" ? (
              <>
                <Select label="Dipendente" value={operaioId} onChange={v=>{setOperaioId(v);setImportoTouched(false);}} options={operai.map(o=>({value:o.id,label:o.nome+(o.costo_ora?` (€${o.costo_ora}/h)`:"")}))}/>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div onClick={()=>setMattina(v=>!v)} style={{padding:"10px 12px",borderRadius:11,cursor:"pointer",border:`2px solid ${mattina?C.accent:C.border}`,background:mattina?C.accentGlow:C.card,transition:"all 0.15s"}}>
                    <div style={{fontWeight:700,fontSize:12,color:mattina?C.accent:C.text}}>☀️ Mattina</div>
                    <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>7:30 - 12:00 ({ORE_MATTINA}h)</div>
                  </div>
                  <div onClick={()=>setPomeriggio(v=>!v)} style={{padding:"10px 12px",borderRadius:11,cursor:"pointer",border:`2px solid ${pomeriggio?C.accent:C.border}`,background:pomeriggio?C.accentGlow:C.card,transition:"all 0.15s"}}>
                    <div style={{fontWeight:700,fontSize:12,color:pomeriggio?C.accent:C.text}}>🌤️ Pomeriggio</div>
                    <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>13:00 - 16:30 ({ORE_POMERIGGIO}h)</div>
                  </div>
                </div>
                {(mattina||pomeriggio) && (
                  <div style={{fontSize:12,color:C.textMuted}}>Totale: <b style={{color:C.text}}>{(mattina?ORE_MATTINA:0)+(pomeriggio?ORE_POMERIGGIO:0)}h</b></div>
                )}
              </>
            ) : (
              <Input label="Nome e cognome" value={nomeSubappaltatore} onChange={setNomeSubappaltatore} placeholder="Es. Mario Rossi"/>
            )}
          </div>
        )}

        {categoria==="materiali" && stockMagazzino.length>0 && (
          <div style={{display:"flex",flexDirection:"column",gap:12,background:C.bg,borderRadius:14,padding:14,border:`1px solid ${C.border}`}}>
            <Select label="Materiale da magazzino (opzionale)" value={articoloId} onChange={v=>{setArticoloId(v);setImportoTouched(false);}}
              options={[{value:"",label:"— nessuno / voce manuale —"},...stockMagazzino.map(a=>({value:a.id,label:`${a.nome} (${a.quantita} ${a.unita} in magazzino)`}))]}/>
            {articoloSel && (
              <Input label={`Quantità consumata (${articoloSel.unita})`} type="number" value={qtaMateriale} onChange={setQtaMateriale} placeholder="0"/>
            )}
            {articoloSel && !(articoloSel.prezzo_unitario>0) && (
              <div style={{fontSize:11.5,color:C.textMuted,fontStyle:"italic"}}>Nessun prezzo unitario impostato per questo articolo in Magazzino — inserisci l'importo manualmente.</div>
            )}
          </div>
        )}

        <Input label="Descrizione" value={descrizione} onChange={setDescrizione} placeholder={categoria==="materiali"&&articoloSel?articoloSel.nome:"Es. Colla, listelli rovere…"}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Input label="Importo (€)" type="number" value={importo} onChange={v=>{setImporto(v);setImportoTouched(true);}} placeholder="0"/>
          <Input label="Data" type="date" value={data} onChange={setData}/>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:6}}>
          <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
          <Btn disabled={!canSave} onClick={handleSave}>+ Aggiungi</Btn>
        </div>
      </div>
    </Modal>
  );
}

// Giornate e ore lavorate per operaio, aggregate sui cantieri passati (già filtrati per mese)
function giornateOperaiMese(righeLavori, operai){
  const map = {};
  righeLavori.forEach(r=>{
    const l = r.lavoro;
    const gd = l.giorni_dettaglio;
    const haDettaglio = gd && Object.values(gd).some(ivs=>(ivs||[]).some(iv=>(iv.operai_orari||[]).length>0));
    if(haDettaglio){
      Object.entries(gd).forEach(([data,interventi])=>{
        (interventi||[]).forEach(iv=>{
          (iv.operai_orari||[]).forEach(o=>{
            if(!map[o.operaio_id]) map[o.operaio_id]={giorni:new Set(),ore:0};
            map[o.operaio_id].giorni.add(data);
            map[o.operaio_id].ore += oreTraOrari(o.ora_inizio,o.ora_fine);
          });
        });
      });
    } else {
      const giorniList = (l.giorni_lavoro && l.giorni_lavoro.length>0)
        ? l.giorni_lavoro
        : giorniLavoroRange(l).filter(ds=>{ const dow=new Date(ds+"T12:00:00").getDay(); return dow!==0 && dow!==6; });
      giorniList.forEach(data=>{
        (l.operai_ids||[]).forEach(id=>{
          if(!map[id]) map[id]={giorni:new Set(),ore:0};
          map[id].giorni.add(data);
          map[id].ore += 8;
        });
      });
    }
  });
  operai.forEach(o=>{ if(map[o.id]) map[o.id].nome=o.nome; });
  return Object.entries(map)
    .map(([id,v])=>({id, nome:v.nome||"Operaio rimosso", giorni:v.giorni.size, ore:v.ore}))
    .sort((a,b)=>b.ore-a.ore);
}

function Contabilita({lavori, setLavori, operai, db}){
  const [spese, setSpese] = useState([]);
  const [stockMagazzino, setStockMagazzino] = useState([]);
  const [categorieCustom, setCategorieCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [espanso, setEspanso] = useState(null);
  const [spesaModal, setSpesaModal] = useState(null);
  const [pagamentoMenu, setPagamentoMenu] = useState(null);
  const [accontoDraft, setAccontoDraft] = useState(null);
  const [margineTipoOpen, setMargineTipoOpen] = useState(false);
  const [obiettivi, setObiettivi] = useState({});
  const [editObiettivo, setEditObiettivo] = useState(false);
  const [editObiettivoAnnuale, setEditObiettivoAnnuale] = useState(false);
  const periodoCorrente = today.slice(0,7);
  const [meseVista, setMeseVista] = useState(periodoCorrente); // 'tutti' oppure 'YYYY-MM'
  const TUTTI_STATI = Object.keys(STATI_COLOR);
  const [statiVisibili, setStatiVisibili] = useState(TUTTI_STATI.filter(s=>!["Annullato","Rifiutato"].includes(s)));
  const [statoMenuOpen, setStatoMenuOpen] = useState(false);

  useEffect(()=>{
    if(!statoMenuOpen) return;
    const chiudi = ()=>setStatoMenuOpen(false);
    window.addEventListener("click", chiudi);
    return ()=>window.removeEventListener("click", chiudi);
  },[statoMenuOpen]);

  useEffect(()=>{ load(); },[]);

  useEffect(()=>{
    if(!pagamentoMenu) return;
    const chiudi = ()=>{ setPagamentoMenu(null); setAccontoDraft(null); };
    window.addEventListener("click", chiudi);
    return ()=>window.removeEventListener("click", chiudi);
  },[pagamentoMenu]);

  async function salvaImportoIncassato(lavoro, ricavo, giaIncassato, nuovoValoreStr, modo, canale){
    const inputVal = parseFloat(nuovoValoreStr)||0;
    const aggiunta = modo==="percentuale" ? (ricavo*inputVal/100) : inputVal;
    const azzera = (parseFloat(giaIncassato)||0)===0 && inputVal===0;
    const val = Math.max(0, (parseFloat(giaIncassato)||0) + aggiunta);
    let payload;
    if(val<=0){
      payload = {saldato:false, acconto_importo:0};
    } else if(val>=(ricavo-0.01)){
      payload = {saldato:true, acconto_importo:0};
    } else {
      payload = {saldato:false, acconto_importo:val};
    }
    // Ripartizione fattura / diretto
    if(azzera){ payload.importo_fatturato = 0; payload.importo_diretto = 0; }
    else if(aggiunta>0 && canale==="diretto") payload.importo_diretto = numD(lavoro)+aggiunta;
    else if(aggiunta>0) payload.importo_fatturato = numF(lavoro)+aggiunta;
    const ok = await aggiornaLavoroIncassi(db, lavoro.id, payload, setLavori);
    if(!ok) return;
    setPagamentoMenu(null);
    setAccontoDraft(null);
  }

  async function load(){
    setLoading(true);
    const [{data:sp}, {data:ob}, {data:cc}, {data:mg}] = await Promise.all([
      db.from("spese_cantiere").select("*").order("data",{ascending:false}),
      db.from("obiettivi").select("*"),
      db.from("categorie_spesa_custom").select("*"),
      db.from("magazzino_stock").select("*").eq("categoria","materiale").order("nome"),
    ]);
    setSpese(sp||[]);
    setStockMagazzino(mg||[]);
    const om={}; (ob||[]).forEach(o=>{ om[o.periodo]=o.valore_target; });
    setObiettivi(om);
    setCategorieCustom((cc||[]).map(c=>({id:c.id, label:c.nome, color:C.textSub})));
    setLoading(false);
  }

  async function aggiungiCategoriaCustom(cat){
    const {data:n} = await db.from("categorie_spesa_custom").insert([{id:cat.id, nome:cat.nome}]).select().single();
    if(n) setCategorieCustom(cs=>[...cs, {id:n.id, label:n.nome, color:C.textSub}]);
  }

  async function aggiungiSpesa(lavoroId, s){
    const {articolo_id, quantita_scaricata, ...spesaData} = s;
    const {data:n} = await db.from("spese_cantiere").insert([{lavoro_id:lavoroId, ...spesaData}]).select().single();
    if(n) setSpese(arr=>[n,...arr]);
    // Se la spesa "materiali" è legata a un articolo di magazzino, scarica la quantità consumata
    if(articolo_id && quantita_scaricata>0){
      const art = stockMagazzino.find(a=>a.id===articolo_id);
      if(art){
        const nuovaQta = Math.max(0, art.quantita - quantita_scaricata);
        await db.from("magazzino_stock").update({quantita:nuovaQta}).eq("id",art.id);
        setStockMagazzino(arr=>arr.map(a=>a.id===art.id?{...a,quantita:nuovaQta}:a));
        const lavoro = lavori.find(l=>l.id===lavoroId);
        await db.from("movimenti_magazzino").insert([{
          articolo_id:art.id, articolo_nome:art.nome, categoria:art.categoria, unita:art.unita,
          tipo:"uscita", quantita:quantita_scaricata, lavoro_id:lavoroId, lavoro_titolo:lavoro?.titolo||null,
          note:"Da Contabilità", data:today,
        }]);
      }
    }
    setSpesaModal(null);
  }

  async function eliminaSpesa(id){
    if(!window.confirm("Eliminare questa spesa?")) return;
    await db.from("spese_cantiere").delete().eq("id",id);
    setSpese(arr=>arr.filter(s=>s.id!==id));
  }

  async function salvaObiettivo(valore){
    const v = parseFloat(valore)||0;
    await db.from("obiettivi").upsert({periodo:periodoCorrente, tipo:"fatturato", valore_target:v},{onConflict:"azienda_id,periodo,tipo"});
    setObiettivi(o=>({...o, [periodoCorrente]:v}));
    setEditObiettivo(false);
  }

  async function salvaObiettivoAnnuale(valoreAnnuale){
    const annuo = parseFloat(valoreAnnuale)||0;
    const mensile = Math.round(annuo/12);
    const anno = periodoCorrente.slice(0,4);
    const righeUp = Array.from({length:12},(_,i)=>({periodo:`${anno}-${String(i+1).padStart(2,"0")}`, tipo:"fatturato", valore_target:mensile}));
    await db.from("obiettivi").upsert(righeUp,{onConflict:"azienda_id,periodo,tipo"});
    setObiettivi(o=>{ const n={...o}; righeUp.forEach(r=>{ n[r.periodo]=r.valore_target; }); return n; });
    setEditObiettivoAnnuale(false);
  }

  async function togglePagato(lavoro){
    const nuovo = !lavoro.saldato;
    await db.from("lavori").update({saldato:nuovo}).eq("id",lavoro.id);
    setLavori(ls=>ls.map(l=>l.id===lavoro.id?{...l,saldato:nuovo}:l));
  }

  function esportaCSV(){
    const header = ["Cantiere","Cliente","Data inizio","Data fine","Importo","Spese","Squadre esterne","Manodopera interna","Utile","Margine %","Pagato"];
    const rows = righe.map(r=>[
      r.lavoro.titolo, r.lavoro.cliente||"", fmt(r.lavoro.data_inizio), fmt(r.lavoro.data_fine),
      r.ricavo.toFixed(2), r.totSpeseManuali.toFixed(2), r.costoSquadre.toFixed(2), r.costoManodopera.toFixed(2), r.utile.toFixed(2),
      r.margine!==null?r.margine+"%":"", r.lavoro.saldato?"Sì":"No"
    ]);
    const csv = [header, ...rows].map(row=>row.map(cell=>`"${String(cell).replace(/"/g,'""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF"+csv], {type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contabilita_${meseVista}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function generaPDFContabilita(){
    const periodoLabel = meseVista==="tutti" ? "Tutti i periodi" : new Date(meseVista+"-01").toLocaleDateString("it-IT",{month:"long",year:"numeric"});
    const dataOggi = new Date().toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"});
    const giornate = giornateOperaiMese(righe, operai);

    const rigaCantiereHTML = righe.map(r=>`
      <tr>
        <td>${r.lavoro.titolo||""}</td>
        <td>${r.lavoro.cliente||""}</td>
        <td style="text-align:right">€${r.ricavo.toFixed(2)}</td>
        <td style="text-align:right">€${r.totSpese.toFixed(2)}</td>
        <td style="text-align:right;color:${r.utile>=0?'#2E8B57':'#C2412F'}">€${r.utile.toFixed(2)}</td>
        <td style="text-align:center">${r.margine!==null?r.margine+"%":"—"}</td>
        <td style="text-align:center">${r.lavoro.saldato?"Sì":"No"}</td>
      </tr>`).join("");

    const rigaOperaioHTML = giornate.map(g=>`
      <tr>
        <td>${g.nome}</td>
        <td style="text-align:center">${g.giorni}</td>
        <td style="text-align:right">${g.ore.toFixed(1)}h</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8">
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; color: #222; margin: 0; padding: 40px; font-size: 13px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 3px solid #E3701F; padding-bottom: 20px; }
      .logo { font-size: 24px; font-weight: 900; color: #16171A; }
      .periodo { font-size: 18px; font-weight: 700; text-transform: capitalize; margin-top: 4px; }
      .info { text-align: right; font-size: 12px; color: #555; }
      .kpi-grid { display: flex; gap: 14px; margin-bottom: 30px; }
      .kpi { flex: 1; background: #f8f6f2; border-radius: 8px; padding: 14px 16px; }
      .kpi-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: .5px; color: #888; margin-bottom: 6px; }
      .kpi-val { font-size: 19px; font-weight: 900; color: #222; }
      h2 { font-size: 14px; margin: 26px 0 10px; color: #222; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
      th { background: #16171A; color: #FAFAF8; padding: 8px 10px; text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: .5px; }
      td { padding: 8px 10px; border-bottom: 1px solid #eee; }
      tr:nth-child(even) td { background: #fafafa; }
      .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #aaa; border-top: 1px solid #eee; padding-top: 16px; }
    </style></head><body>
    <div class="header">
      <div>
        <div class="logo">PARQUETTI GROUP</div>
        <div class="periodo">Report Contabilità — ${periodoLabel}</div>
      </div>
      <div class="info">Generato il ${dataOggi}</div>
    </div>
    <div class="kpi-grid">
      <div class="kpi"><div class="kpi-label">Importo lavori</div><div class="kpi-val">€${fatturatoTot.toFixed(0)}</div></div>
      <div class="kpi"><div class="kpi-label">Fatturato</div><div class="kpi-val">€${fatturatoIncTot.toFixed(0)}</div></div>
      <div class="kpi"><div class="kpi-label">Diretto</div><div class="kpi-val">€${direttoIncTot.toFixed(0)}</div></div>
      <div class="kpi"><div class="kpi-label">Spese totali</div><div class="kpi-val">€${speseTot.toFixed(0)}</div></div>
      <div class="kpi"><div class="kpi-label">Utile</div><div class="kpi-val" style="color:${utileTot>=0?'#2E8B57':'#C2412F'}">€${utileTot.toFixed(0)}</div></div>
      <div class="kpi"><div class="kpi-label">Da incassare</div><div class="kpi-val">€${daIncassareTot.toFixed(0)}</div></div>
    </div>
    <h2>Cantieri (${righe.length})</h2>
    <table>
      <thead><tr><th>Cantiere</th><th>Cliente</th><th style="text-align:right">Importo</th><th style="text-align:right">Spese</th><th style="text-align:right">Utile</th><th style="text-align:center">Margine</th><th style="text-align:center">Pagato</th></tr></thead>
      <tbody>${rigaCantiereHTML||'<tr><td colspan="7" style="text-align:center;color:#999">Nessun cantiere nel periodo</td></tr>'}</tbody>
    </table>
    <h2>Giornate operai (${giornate.length})</h2>
    <table>
      <thead><tr><th>Operaio</th><th style="text-align:center">Giornate</th><th style="text-align:right">Ore totali</th></tr></thead>
      <tbody>${rigaOperaioHTML||'<tr><td colspan="3" style="text-align:center;color:#999">Nessuna giornata registrata nel periodo</td></tr>'}</tbody>
    </table>
    <div class="footer">Parquetti Group · Report generato automaticamente da Squadra</div>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    setTimeout(() => { w?.print(); URL.revokeObjectURL(url); }, 800);
  }

  const cantieriRilevanti = lavori.filter(l=>statiVisibili.includes(l.stato));

  const righeTutte = cantieriRilevanti.map(l=>{
    const speseCantiere = spese.filter(s=>s.lavoro_id===l.id);
    const totSpeseManuali = speseCantiere.reduce((s,x)=>s+(x.importo||0),0);
    const squadreEsterne = Array.isArray(l.squadre_esterne) ? l.squadre_esterne : [];
    const costoSquadre = squadreEsterne.reduce((s,sq)=>s+((parseFloat(sq.mq)||0)*(parseFloat(sq.prezzo_mq)||0)),0);
    const costoManodopera = costoManodoperaInterna(l, operai);
    const costoSubCal = costoSubappaltiCalendario(l);
    const totSpese = totSpeseManuali + costoSquadre + costoManodopera + costoSubCal;
    const ricavo = l.preventivo||0;
    const utile = ricavo - totSpese;
    const margine = ricavo>0 ? Math.round(utile/ricavo*100) : null;
    const inPerdita = ricavo>0 && utile<0;
    const accontoIncassato = parseFloat(l.acconto_importo)||0;
    const residuo = l.saldato ? 0 : Math.max(0, ricavo - accontoIncassato);
    let giorniRitardo = null;
    if(l.stato==="Completato" && !l.saldato && l.data_fine){
      giorniRitardo = Math.floor((new Date(today) - new Date(l.data_fine)) / 86400000);
    }
    return {lavoro:l, spese:speseCantiere, totSpeseManuali, costoSquadre, squadreEsterne, costoManodopera, costoSubCal, totSpese, ricavo, utile, margine, inPerdita, accontoIncassato, residuo, giorniRitardo};
  }).sort((a,b)=>(b.lavoro.data_inizio||"").localeCompare(a.lavoro.data_inizio||""));

  // Mesi disponibili (con almeno un cantiere), sempre includendo il mese corrente
  const mesiDisponibili = Array.from(new Set([
    periodoCorrente,
    ...righeTutte.map(r=>(r.lavoro.data_inizio||"").slice(0,7)).filter(Boolean),
    ...righeTutte.map(r=>(r.lavoro.data_fine||"").slice(0,7)).filter(Boolean),
  ])).sort().reverse();

  const righe = meseVista==="tutti" ? righeTutte : righeTutte.filter(r=>{
    const l = r.lavoro;
    if(!l.data_inizio && !l.data_fine) return true; // nessuna data ancora: sempre visibile
    const [anno,mese] = meseVista.split("-").map(Number);
    const ultimoGiorno = new Date(anno, mese, 0).getDate();
    const meseInizio = `${meseVista}-01`;
    const meseFine = `${meseVista}-${String(ultimoGiorno).padStart(2,"0")}`;
    const inizio = l.data_inizio || l.data_fine;
    const fine = l.data_fine || l.data_inizio;
    return inizio<=meseFine && fine>=meseInizio; // il cantiere è "attivo" durante il mese selezionato
  });

  const fatturatoTot = righe.reduce((s,r)=>s+r.ricavo,0);
  const speseTot = righe.reduce((s,r)=>s+r.totSpese,0);
  const utileTot = fatturatoTot - speseTot;
  const daIncassareTot = righe.reduce((s,r)=>s+r.residuo,0);
  const fatturatoIncTot = righe.reduce((s,r)=>s+numF(r.lavoro),0);
  const direttoIncTot   = righe.reduce((s,r)=>s+numD(r.lavoro),0);
  const inRitardo = righe.filter(r=>r.giorniRitardo!==null && r.giorniRitardo>30);
  const inPerditaList = righe.filter(r=>r.inPerdita);

  // Andamento ultimi 6 mesi
  const mesiChart = [];
  for(let i=5;i>=0;i--){
    const d = new Date(); d.setMonth(d.getMonth()-i);
    const periodo = d.toISOString().slice(0,7);
    const lavoriMese = cantieriRilevanti.filter(l=>(l.data_inizio||"").startsWith(periodo));
    const ric = lavoriMese.reduce((s,l)=>s+(l.preventivo||0),0);
    const spManuali = spese.filter(s=>lavoriMese.some(l=>l.id===s.lavoro_id)).reduce((s,x)=>s+(x.importo||0),0);
    const spSquadre = lavoriMese.reduce((s,l)=>s+(Array.isArray(l.squadre_esterne)?l.squadre_esterne:[]).reduce((t,sq)=>t+((parseFloat(sq.mq)||0)*(parseFloat(sq.prezzo_mq)||0)),0),0);
    const spManodopera = lavoriMese.reduce((s,l)=>s+costoManodoperaInterna(l, operai)+costoSubappaltiCalendario(l),0);
    const sp = spManuali + spSquadre + spManodopera;
    mesiChart.push({periodo, label:d.toLocaleDateString("it-IT",{month:"short"}), ricavo:ric, spese:sp, utile:ric-sp});
  }
  const maxChart = Math.max(1, ...mesiChart.map(m=>Math.max(m.ricavo,m.spese)));

  // Margine medio per tipo di lavorazione/materiale (righeTutte, per avere dati su tutto lo storico)
  const margineXTipo = {};
  righeTutte.forEach(r=>{
    const tipo = (r.lavoro.materiale||"").trim() || "Non specificato";
    if(!margineXTipo[tipo]) margineXTipo[tipo] = {ricavo:0, utile:0, n:0};
    margineXTipo[tipo].ricavo += r.ricavo;
    margineXTipo[tipo].utile += r.utile;
    margineXTipo[tipo].n += 1;
  });
  const margineXTipoList = Object.entries(margineXTipo)
    .map(([tipo,v])=>({tipo, n:v.n, margine: v.ricavo>0?Math.round(v.utile/v.ricavo*100):null}))
    .filter(v=>v.margine!==null && v.n>=1)
    .sort((a,b)=>b.margine-a.margine);

  // Stagionalità: fatturato mese per mese, anno corrente vs anno precedente
  const annoCorr = parseInt(periodoCorrente.slice(0,4));
  const stagionalita = Array.from({length:12},(_,i)=>{
    const mese = String(i+1).padStart(2,"0");
    const pCorr = `${annoCorr}-${mese}`, pPrec = `${annoCorr-1}-${mese}`;
    const ricCorr = cantieriRilevanti.filter(l=>(l.data_inizio||"").startsWith(pCorr)).reduce((s,l)=>s+(l.preventivo||0),0);
    const ricPrec = cantieriRilevanti.filter(l=>(l.data_inizio||"").startsWith(pPrec)).reduce((s,l)=>s+(l.preventivo||0),0);
    return {label:new Date(2000,i,1).toLocaleDateString("it-IT",{month:"short"}), corrente:ricCorr, precedente:ricPrec};
  });
  const maxStagionalita = Math.max(1, ...stagionalita.map(s=>Math.max(s.corrente,s.precedente)));
  const haDatiAnnoPrec = stagionalita.some(s=>s.precedente>0);

  // Produttività media per operaio (mq effettivi / giorni lavorati, sui cantieri completati)
  const produttivitaOperai = (operai||[]).map(op=>{
    const lavoriOp = cantieriRilevanti.filter(l=>l.stato==="Completato" && l.operai_ids?.includes(op.id) && l.mq_effettivi);
    let mqTot=0, giorniTot=0;
    lavoriOp.forEach(l=>{
      mqTot += l.mq_effettivi||0;
      const nOperai = l.operai_ids?.length||1;
      const giorni = l.data_inizio&&l.data_fine ? Math.max(1,Math.round((new Date(l.data_fine)-new Date(l.data_inizio))/86400000)+1) : 1;
      giorniTot += giorni/nOperai; // giorni pro-quota se squadra
    });
    return {operaio:op, mqGiorno: giorniTot>0 ? mqTot/giorniTot : null, cantieri: lavoriOp.length};
  }).filter(p=>p.mqGiorno!==null).sort((a,b)=>b.mqGiorno-a.mqGiorno);

  const meseCorrente = cantieriRilevanti.filter(l=>(l.data_inizio||"").startsWith(periodoCorrente));
  const fatturatoMese = meseCorrente.reduce((s,l)=>s+(l.preventivo||0),0);
  const targetMese = obiettivi[periodoCorrente]||0;
  const percTarget = targetMese>0 ? Math.min(100, Math.round(fatturatoMese/targetMese*100)) : 0;

  if(loading) return <div style={{color:C.textMuted,padding:40,textAlign:"center"}}>Caricamento…</div>;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <style>{`
        @media (max-width: 720px) {
          .contab-head { display:none !important; }
          .contab-row { display:flex !important; flex-wrap:wrap !important; row-gap:8px !important; }
          .contab-row > div:nth-child(2), .contab-row > div:nth-child(3) { display:none !important; }
        }
      `}</style>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>💶 Contabilità</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>Spese, utile e obiettivi per cantiere</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <Btn small variant="ghost" onClick={e=>{e.stopPropagation();setStatoMenuOpen(v=>!v);}}>🔎 Stati ({statiVisibili.length}/{TUTTI_STATI.length})</Btn>
            {statoMenuOpen && (
              <div onClick={e=>e.stopPropagation()} style={{position:"absolute",top:"110%",right:0,background:C.card,boxShadow:C.shadowLg,borderRadius:14,padding:12,zIndex:20,minWidth:210,display:"flex",flexDirection:"column",gap:8}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11.5}}>
                  <span onClick={()=>setStatiVisibili(TUTTI_STATI)} style={{color:C.accent,cursor:"pointer",fontWeight:700}}>Tutti</span>
                  <span onClick={()=>setStatiVisibili([])} style={{color:C.textMuted,cursor:"pointer",fontWeight:700}}>Nessuno</span>
                </div>
                {TUTTI_STATI.map(s=>{
                  const checked = statiVisibili.includes(s);
                  return(
                    <label key={s} style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:C.text,cursor:"pointer"}}>
                      <input type="checkbox" checked={checked} onChange={()=>setStatiVisibili(v=>checked?v.filter(x=>x!==s):[...v,s])}/>
                      <span style={{width:8,height:8,borderRadius:"50%",background:STATI_COLOR[s],flexShrink:0}}/>
                      {s}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <select value={meseVista} onChange={e=>setMeseVista(e.target.value)} style={{background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"9px 14px",fontSize:13,fontWeight:600,color:C.text,fontFamily:"inherit",cursor:"pointer"}}>
            {mesiDisponibili.map(m=>(
              <option key={m} value={m}>{new Date(m+"-01").toLocaleDateString("it-IT",{month:"long",year:"numeric"})}</option>
            ))}
            <option value="tutti">Tutti i mesi</option>
          </select>
          <Btn small variant="ghost" onClick={esportaCSV}>⬇ Esporta CSV</Btn>
          <Btn small variant="ghost" onClick={generaPDFContabilita}>📄 Scarica PDF</Btn>
        </div>
      </div>

      {statiVisibili.length===0 && (
        <div style={{background:C.red+"10",borderRadius:11,padding:"10px 14px",fontSize:12.5,color:C.red}}>
          Nessuno stato selezionato: apri "🔎 Stati" per scegliere quali cantieri mostrare.
        </div>
      )}

      {/* KPI generali */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Importo lavori</div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:22,fontWeight:800,color:C.text,marginTop:6}}>{euro(fatturatoTot)}</div>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Fatturato</div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:22,fontWeight:800,color:C.text,marginTop:6}}>{euro(fatturatoIncTot)}</div>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Diretto</div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:22,fontWeight:800,color:C.text,marginTop:6}}>{euro(direttoIncTot)}</div>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Spese</div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:22,fontWeight:800,color:C.red,marginTop:6}}>{euro(speseTot)}</div>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Utile</div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:22,fontWeight:800,color:utileTot>=0?C.green:C.red,marginTop:6}}>{euro(utileTot)}</div>
        </div>
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:10.5,color:C.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Da incassare</div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:22,fontWeight:800,color:C.accent,marginTop:6}}>{euro(daIncassareTot)}</div>
        </div>
      </div>

      {/* Obiettivo mensile */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text}}>🎯 Obiettivo importo lavori — {new Date(periodoCorrente+"-01").toLocaleDateString("it-IT",{month:"long",year:"numeric"})}</div>
          {!editObiettivo && !editObiettivoAnnuale && (
            <div style={{display:"flex",gap:12}}>
              <button onClick={()=>setEditObiettivo(true)} style={{background:"none",border:"none",color:C.accent,fontSize:12,fontWeight:700,cursor:"pointer"}}>Modifica mese</button>
              <button onClick={()=>setEditObiettivoAnnuale(true)} style={{background:"none",border:"none",color:C.textMuted,fontSize:12,fontWeight:700,cursor:"pointer"}}>Imposta obiettivo annuale</button>
            </div>
          )}
        </div>
        {editObiettivoAnnuale ? (
          <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
            <Input label={`Target annuale ${periodoCorrente.slice(0,4)} (€)`} type="number" placeholder="Es. 120000" onChange={v=>setObiettivi(o=>({...o, __annuale:v}))}/>
            <Btn small onClick={()=>salvaObiettivoAnnuale(obiettivi.__annuale)}>Distribuisci sui 12 mesi</Btn>
            <Btn small variant="ghost" onClick={()=>setEditObiettivoAnnuale(false)}>Annulla</Btn>
          </div>
        ) : editObiettivo ? (
          <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
            <Input label="Target mensile (€)" type="number" value={targetMese||""} onChange={v=>setObiettivi(o=>({...o,[periodoCorrente]:v}))}/>
            <Btn small onClick={()=>salvaObiettivo(obiettivi[periodoCorrente])}>Salva</Btn>
            <Btn small variant="ghost" onClick={()=>setEditObiettivo(false)}>Annulla</Btn>
          </div>
        ) : targetMese>0 ? (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.textSub,marginBottom:6}}>
              <span>{euro(fatturatoMese)} di {euro(targetMese)}</span>
              <span style={{fontWeight:700,color:C.accent}}>{percTarget}%</span>
            </div>
            <div style={{height:8,background:C.bg,borderRadius:20,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${percTarget}%`,background:C.accent,borderRadius:20,transition:"width 0.3s"}}/>
            </div>
          </div>
        ) : (
          <div style={{fontSize:12.5,color:C.textMuted}}>Nessun obiettivo impostato per questo mese.</div>
        )}
      </div>

      {/* Alert */}
      {(inRitardo.length>0 || inPerditaList.length>0) && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {inRitardo.length>0 && (
            <div style={{background:C.red+"10",borderRadius:14,padding:"12px 16px",fontSize:12.5,color:C.red}}>
              ⚠️ <b>{inRitardo.length}</b> cantiere{inRitardo.length>1?"i":""} completat{inRitardo.length>1?"i":"o"} da oltre 30 giorni e ancora da incassare: {inRitardo.map(r=>r.lavoro.titolo).join(", ")}
            </div>
          )}
          {inPerditaList.length>0 && (
            <div style={{background:C.orange+"10",borderRadius:14,padding:"12px 16px",fontSize:12.5,color:C.orange}}>
              📉 <b>{inPerditaList.length}</b> cantiere{inPerditaList.length>1?"i":""} in perdita (spese superiori al preventivo): {inPerditaList.map(r=>r.lavoro.titolo).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Andamento mensile */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:16}}>📈 Andamento ultimi 6 mesi</div>
        <div style={{display:"flex",alignItems:"flex-end",gap:14,height:120}}>
          {mesiChart.map(m=>(
            <div key={m.periodo} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6,height:"100%",justifyContent:"flex-end"}}>
              <div style={{display:"flex",gap:3,alignItems:"flex-end",height:90,width:"100%",justifyContent:"center"}}>
                <div title={`Importo ${euro(m.ricavo)}`} style={{width:10,height:`${Math.max(2,m.ricavo/maxChart*90)}px`,background:C.accent,borderRadius:"3px 3px 0 0"}}/>
                <div title={`Spese ${euro(m.spese)}`} style={{width:10,height:`${Math.max(2,m.spese/maxChart*90)}px`,background:C.red+"70",borderRadius:"3px 3px 0 0"}}/>
              </div>
              <span style={{fontSize:10,color:C.textMuted,fontWeight:600,textTransform:"capitalize"}}>{m.label}</span>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:16,marginTop:12,fontSize:11,color:C.textMuted}}>
          <span><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:C.accent,marginRight:5}}/>Importo</span>
          <span><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:C.red+"70",marginRight:5}}/>Spese</span>
        </div>
      </div>

      {/* Margine per tipo di lavorazione */}
      {margineXTipoList.length>0 && (
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:margineTipoOpen?"18px 20px":"12px 20px"}}>
          <div onClick={()=>setMargineTipoOpen(o=>!o)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text}}>🪚 Margine medio per tipo di lavorazione/materiale</div>
            <span style={{color:C.textMuted,fontSize:11,transform:margineTipoOpen?"rotate(180deg)":"none",transition:"transform 0.15s"}}>▾</span>
          </div>
          {margineTipoOpen && (
            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:14}}>
              {margineXTipoList.map(t=>(
                <div key={t.tipo} style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:12.5,color:C.text,fontWeight:600,width:150,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.tipo}</span>
                  <div style={{flex:1,height:8,background:C.bg,borderRadius:20,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.max(2,Math.min(100,t.margine))}%`,background:t.margine>=0?C.green:C.red,borderRadius:20}}/>
                  </div>
                  <span style={{fontSize:12.5,fontWeight:700,color:t.margine>=0?C.green:C.red,width:44,textAlign:"right"}}>{t.margine}%</span>
                  <span style={{fontSize:10.5,color:C.textMuted,width:50,textAlign:"right"}}>{t.n} cant.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stagionalità anno su anno */}
      {haDatiAnnoPrec && (
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:16}}>📅 Stagionalità — {annoCorr} vs {annoCorr-1}</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:6,height:120}}>
            {stagionalita.map(m=>(
              <div key={m.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6,height:"100%",justifyContent:"flex-end"}}>
                <div style={{display:"flex",gap:2,alignItems:"flex-end",height:90,width:"100%",justifyContent:"center"}}>
                  <div title={`${annoCorr-1}: ${euro(m.precedente)}`} style={{width:9,height:`${Math.max(2,m.precedente/maxStagionalita*90)}px`,background:C.borderLight,borderRadius:"3px 3px 0 0"}}/>
                  <div title={`${annoCorr}: ${euro(m.corrente)}`} style={{width:9,height:`${Math.max(2,m.corrente/maxStagionalita*90)}px`,background:C.accent,borderRadius:"3px 3px 0 0"}}/>
                </div>
                <span style={{fontSize:9.5,color:C.textMuted,fontWeight:600,textTransform:"capitalize"}}>{m.label}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,marginTop:12,fontSize:11,color:C.textMuted}}>
            <span><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:C.borderLight,marginRight:5}}/>{annoCorr-1}</span>
            <span><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:C.accent,marginRight:5}}/>{annoCorr}</span>
          </div>
        </div>
      )}

      {/* Produttività operai */}
      {produttivitaOperai.length>0 && (
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>👷 Produttività media (mq/giorno)</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {produttivitaOperai.map(p=>(
              <div key={p.operaio.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:12.5}}>
                <span style={{color:C.text,fontWeight:600}}>{p.operaio.nome}</span>
                <span style={{color:C.textMuted}}>{p.mqGiorno.toFixed(1)} mq/g · {p.cantieri} cantier{p.cantieri>1?"i":"e"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista cantieri */}
      {righe.length===0 ? (
        <div style={{color:C.textMuted,textAlign:"center",padding:50,background:C.card,borderRadius:C.radiusLg}}>Nessun cantiere</div>
      ) : (
      <div style={{background:C.card,borderRadius:C.radiusLg,boxShadow:C.shadowSm,overflow:"hidden"}}>
        {/* intestazione colonne — solo desktop */}
        <div className="contab-head" style={{display:"grid",gridTemplateColumns:"1fr 96px 96px 48px 96px 176px 16px",alignItems:"center",gap:16,padding:"12px 22px",borderBottom:`1px solid ${C.border}`}}>
          <span style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6}}>Cantiere</span>
          <span style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6,textAlign:"right"}}>Importo</span>
          <span style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6,textAlign:"right"}}>Spese</span>
          <span/>
          <span style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6,textAlign:"right"}}>Utile</span>
          <span style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.6}}>Stato</span>
          <span/>
        </div>
        {righe.map(r=>{
          const isOpen = espanso===r.lavoro.id;
          return(
            <div key={r.lavoro.id} style={{borderTop:r.lavoro.id===righe[0].lavoro.id?"none":`1px solid ${C.border}`}}>
              <div onClick={()=>setEspanso(isOpen?null:r.lavoro.id)} className="contab-row" style={{display:"grid",gridTemplateColumns:"1fr 96px 96px 48px 96px 176px 16px",alignItems:"center",gap:16,padding:"14px 22px",cursor:"pointer"}}
                onMouseEnter={e=>e.currentTarget.style.background=C.cardHover}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:600,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.lavoro.titolo}</div>
                  <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>{r.lavoro.cliente||"—"}</div>
                </div>
                <div style={{textAlign:"right",fontSize:13,color:C.text,fontVariantNumeric:"tabular-nums"}}>{euro(r.ricavo)}</div>
                <div style={{textAlign:"right",fontSize:13,color:C.textMuted,fontVariantNumeric:"tabular-nums"}}>{euro(r.totSpese)}</div>
                <div style={{display:"flex",justifyContent:"center"}}>{r.margine!==null && <AnelloMargine percentuale={r.margine} size={38}/>}</div>
                <div style={{textAlign:"right",fontSize:14,fontWeight:700,color:r.utile>=0?C.green:C.red,fontVariantNumeric:"tabular-nums"}}>{euro(r.utile)}</div>
                <div style={{position:"relative"}} onClick={e=>e.stopPropagation()}>
                    <div onClick={()=>{
                      const apro = pagamentoMenu!==r.lavoro.id;
                      setPagamentoMenu(apro?r.lavoro.id:null);
                      setAccontoDraft(apro?{id:r.lavoro.id,importo:"",modo:"importo"}:null);
                    }} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",flexShrink:0}}>
                      <div style={{width:19,height:19,borderRadius:6,background:r.lavoro.saldato?C.green:(r.accontoIncassato>0?C.orange:C.bg),border:(r.lavoro.saldato||r.accontoIncassato>0)?"none":`1.5px solid ${C.borderLight}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {r.lavoro.saldato&&<span style={{color:"#fff",fontSize:12,fontWeight:800,lineHeight:1}}>✓</span>}
                        {!r.lavoro.saldato&&r.accontoIncassato>0&&<span style={{color:"#fff",fontSize:11,fontWeight:800,lineHeight:1}}>◐</span>}
                      </div>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:11.5,fontWeight:600,color:r.lavoro.saldato?C.green:(r.accontoIncassato>0?C.orange:C.textMuted),whiteSpace:"nowrap"}}>
                          {r.lavoro.saldato?"Pagato":(r.accontoIncassato>0?`Acconto ${Math.round(r.ricavo>0?(r.accontoIncassato/r.ricavo*100):0)}%`:"Da incassare")}
                        </div>
                        {!r.lavoro.saldato && r.accontoIncassato>0 && <div style={{fontSize:10,color:C.textMuted}}>{euro(r.accontoIncassato)} · saldo {euro(r.residuo)}</div>}
                      </div>
                      <span style={{color:C.textMuted,fontSize:10}}>▾</span>
                    </div>
                    {pagamentoMenu===r.lavoro.id && (
                      <div style={{position:"fixed",inset:0,zIndex:50,background:"rgba(20,20,20,0.4)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
                        onClick={()=>{setPagamentoMenu(null);setAccontoDraft(null);}}>
                        <div style={{background:C.card,borderRadius:16,padding:20,width:280,boxShadow:"0 12px 40px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column",gap:10}} onClick={e=>e.stopPropagation()}>
                          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
                            <div>
                              <div style={{fontSize:14,fontWeight:700,color:C.text}}>Acconto ricevuto</div>
                              <div style={{fontSize:11.5,color:C.textMuted,marginTop:2}}>{r.lavoro.titolo}</div>
                            </div>
                            <button onClick={()=>{setPagamentoMenu(null);setAccontoDraft(null);}} style={{background:"none",border:"none",color:C.textMuted,fontSize:18,cursor:"pointer",lineHeight:1,padding:2}}>×</button>
                          </div>
                          <div style={{fontSize:11.5,color:C.textMuted,background:C.bg,borderRadius:8,padding:"8px 10px"}}>
                            già incassato {euro(r.accontoIncassato)} ({Math.round(r.ricavo>0?(r.accontoIncassato/r.ricavo*100):0)}%) su {euro(r.ricavo)}
                          </div>
                          <div style={{display:"flex",gap:4,background:C.bg,borderRadius:8,padding:3}}>
                            <button onClick={()=>setAccontoDraft(a=>({...a,modo:"importo"}))}
                              style={{flex:1,background:accontoDraft?.modo!=="percentuale"?C.card:"none",boxShadow:accontoDraft?.modo!=="percentuale"?C.shadowSm:"none",border:"none",borderRadius:6,padding:"7px 0",fontSize:12.5,fontWeight:700,color:accontoDraft?.modo!=="percentuale"?C.text:C.textMuted,cursor:"pointer"}}>€ Importo</button>
                            <button onClick={()=>setAccontoDraft(a=>({...a,modo:"percentuale"}))}
                              style={{flex:1,background:accontoDraft?.modo==="percentuale"?C.card:"none",boxShadow:accontoDraft?.modo==="percentuale"?C.shadowSm:"none",border:"none",borderRadius:6,padding:"7px 0",fontSize:12.5,fontWeight:700,color:accontoDraft?.modo==="percentuale"?C.text:C.textMuted,cursor:"pointer"}}>% Percentuale</button>
                          </div>
                          <div style={{display:"flex",gap:4,background:C.bg,borderRadius:8,padding:3}}>
                            {[{id:"fattura",label:"In fattura"},{id:"diretto",label:"Diretto"}].map(o=>{
                              const on = (accontoDraft?.canale||"fattura")===o.id;
                              return <button key={o.id} onClick={()=>setAccontoDraft(a=>({...a,canale:o.id}))} style={{flex:1,background:on?C.card:"none",boxShadow:on?C.shadowSm:"none",border:"none",borderRadius:6,padding:"7px 0",fontSize:12.5,fontWeight:700,color:on?C.text:C.textMuted,cursor:"pointer"}}>{o.label}</button>;
                            })}
                          </div>
                          <input autoFocus type="number" placeholder={accontoDraft?.modo==="percentuale"?"Es. 20":"Nuovo importo €"} value={accontoDraft?.importo??""}
                            onChange={e=>setAccontoDraft(a=>({...a,importo:e.target.value}))}
                            onKeyDown={e=>{ if(e.key==="Enter") salvaImportoIncassato(r.lavoro,r.ricavo,r.accontoIncassato,accontoDraft?.importo,accontoDraft?.modo,accontoDraft?.canale); }}
                            style={{fontSize:15,padding:"9px 10px",borderRadius:8,border:`1px solid ${C.border}`,width:"100%",boxSizing:"border-box"}}/>
                          {accontoDraft?.modo==="percentuale" && accontoDraft?.importo && (
                            <div style={{fontSize:11.5,color:C.textMuted,marginTop:-4}}>= {euro(r.ricavo*(parseFloat(accontoDraft.importo)||0)/100)}</div>
                          )}
                          <button onClick={()=>salvaImportoIncassato(r.lavoro,r.ricavo,r.accontoIncassato,accontoDraft?.importo,accontoDraft?.modo,accontoDraft?.canale)} style={{background:C.orange,color:"#fff",border:"none",borderRadius:8,padding:"10px 0",fontSize:13,fontWeight:700,cursor:"pointer"}}>Somma</button>
                          {r.accontoIncassato>0 && (
                            <button onClick={()=>salvaImportoIncassato(r.lavoro,r.ricavo,0,"0","importo")} style={{background:"none",border:"none",color:C.textMuted,fontSize:11.5,cursor:"pointer",padding:"0"}}>Azzera acconti</button>
                          )}
                        </div>
                      </div>
                    )}
                </div>
                <span style={{color:C.textMuted,fontSize:11,transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.15s"}}>▾</span>
              </div>
              {isOpen && (
                <div style={{borderTop:`1px solid ${C.border}`,padding:"14px 18px",background:C.bg}}>
                  {r.costoManodopera>0 && (
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Manodopera interna (dal calendario)</div>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.card,borderRadius:9,fontSize:12.5}}>
                        <span style={{color:C.textMuted}}>Ore squadra interna assegnate su questo cantiere × €/h operaio</span>
                        <span style={{fontWeight:700,color:C.text,flexShrink:0,marginLeft:10}}>{euro(r.costoManodopera)}</span>
                      </div>
                    </div>
                  )}
                  {r.costoSubCal>0 && (
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Subappaltatori a giornata (dal calendario)</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {Object.entries(Object.values(r.lavoro.giorni_dettaglio||{}).flat().filter(iv=>iv?.subappalto?.costo_giorno>0).reduce((m,iv)=>{ const n=iv.subappalto.nome; m[n]=m[n]||{gg:0,tot:0}; m[n].gg++; m[n].tot+=parseFloat(iv.subappalto.costo_giorno)||0; return m; },{})).map(([n,v])=>(
                          <div key={n} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.card,borderRadius:9,fontSize:12.5}}>
                            <span style={{color:C.textMuted}}>🤝 {n} · {v.gg} giornat{v.gg>1?"e":"a"}</span>
                            <span style={{fontWeight:700,color:C.text,flexShrink:0,marginLeft:10}}>{euro(v.tot)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {r.squadreEsterne.length>0 && (
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Squadre esterne</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {r.squadreEsterne.map((sq,i)=>{
                          const costo = (parseFloat(sq.mq)||0)*(parseFloat(sq.prezzo_mq)||0);
                          return(
                            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.card,borderRadius:9,fontSize:12.5}}>
                              <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                                <Badge label={sq.lavorazione||"Squadra"} color={C.purple}/>
                                <span style={{color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sq.nome||"—"}</span>
                              </div>
                              <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
                                <span style={{color:C.textMuted,fontSize:11}}>{sq.mq||0}mq × €{sq.prezzo_mq||0}</span>
                                <span style={{fontWeight:700,color:C.text}}>{euro(costo)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div style={{fontSize:10.5,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Spese</div>
                  {r.spese.length===0 && <div style={{fontSize:12.5,color:C.textMuted,marginBottom:10}}>Nessuna spesa registrata.</div>}
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                    {r.spese.map(s=>{
                      const cat = [...CATEGORIE_SPESA, ...categorieCustom].find(c=>c.id===s.categoria)||CATEGORIE_SPESA[4];
                      return(
                        <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.card,borderRadius:9,fontSize:12.5}}>
                          <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                            <Badge label={cat.label} color={cat.color}/>
                            <span style={{color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.descrizione||"—"}</span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
                            <span style={{color:C.textMuted,fontSize:11}}>{fmt(s.data)}</span>
                            <span style={{fontWeight:700,color:C.text}}>{euro(s.importo)}</span>
                            <button onClick={()=>eliminaSpesa(s.id)} style={{background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:14}}>×</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Btn small variant="ghost" onClick={()=>setSpesaModal(r.lavoro)}>+ Aggiungi spesa</Btn>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {spesaModal && <SpesaModal lavoro={spesaModal} onClose={()=>setSpesaModal(null)} onSave={s=>aggiungiSpesa(spesaModal.id,s)} db={db} categorieCustom={categorieCustom} onNuovaCategoria={aggiungiCategoriaCustom} operai={operai} stockMagazzino={stockMagazzino}/>}
    </div>
  );
}

export default function App(){
  const [session,  setSession]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState(()=>localStorage.getItem("posa_tab")||"dashboard");
  const [lavori,   setLavori]   = useState([]);
  const [operai,   setOperai]   = useState([]);
  const [rivendite,setRivendite]= useState([]);
  const [contatti, setContatti] = useState([]);
  const [dbReady,  setDbReady]  = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pagineUtente, setPagineUtente] = useState(null); // null=admin (accesso completo), array=elenco pagine consentite
  const [nonLetti, setNonLetti] = useState(0);
  const [membro, setMembro] = useState(undefined); // undefined=verifica in corso · null=non fa parte di nessuna azienda · oggetto=membro

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{ setSession(session); setLoading(false); });
    const {data:{subscription}} = supabase.auth.onAuthStateChange((_,s)=>setSession(s));
    return ()=>subscription.unsubscribe();
  },[]);

  const verificaAccesso = useCallback(async()=>{
    const email = session?.user?.email?.toLowerCase();
    if(!email){ setMembro(undefined); return; }
    const {data, error} = await supabase.from("utenti_ruoli").select("ruolo,pagine,azienda_id").eq("email",email).maybeSingle();
    if(error){
      // database non ancora aggiornato (manca lo script di sicurezza): comportamento di prima
      const {data:vecchio} = await supabase.from("utenti_ruoli").select("ruolo,pagine").eq("email",email).maybeSingle();
      if(vecchio) setPagineUtente((vecchio.pagine&&vecchio.pagine.length>0) ? vecchio.pagine : (vecchio.ruolo==="contabile" ? ["contabilita"] : []));
      setMembro({ruolo:vecchio?"limitato":"admin"}); return;
    }
    if(!data){ setMembro(null); return; }
    if(data.ruolo==="admin") setPagineUtente(null);
    else setPagineUtente((data.pagine&&data.pagine.length>0) ? data.pagine : (data.ruolo==="contabile" ? ["contabilita"] : []));
    setMembro(data);
  },[session?.user?.email]);
  useEffect(()=>{ verificaAccesso(); },[verificaAccesso]);

  const loadData = useCallback(async()=>{
    const [{data:lav},{data:op},{data:riv}] = await Promise.all([
      supabase.from("lavori").select("*").order("data_inizio",{ascending:true}),
      supabase.from("operai").select("*").order("nome"),
      supabase.from("rivendite").select("*").order("nome"),
    ]);
    setLavori(lav||[]); setOperai(op||[]); setRivendite(riv||[]); setDbReady(true);
    const cont = await caricaTuttiContatti(supabase); // CRM: se la tabella non esiste ancora resta vuoto
    setContatti(cont||[]);
  },[]);

  useEffect(()=>{ if(session && membro) loadData(); },[session,membro,loadData]);
  // contatore dei messaggi da leggere nel menu (solo per chi vede i Messaggi)
  const vedeMessaggi = !!membro && (pagineUtente===null || pagineUtente.includes("messaggi"));
  useEffect(()=>{
    if(!vedeMessaggi) return;
    const conta = ()=>supabase.from("conversazioni").select("non_letti").gt("non_letti",0).then(({data,error})=>{ if(!error) setNonLetti((data||[]).reduce((s,c)=>s+(c.non_letti||0),0)); });
    conta(); const t=setInterval(conta,30000); return ()=>clearInterval(t);
  },[vedeMessaggi]);

  if(loading || (session && membro===undefined)) return <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",color:C.textMuted,fontSize:16,fontFamily:C.font}}>Caricamento…</div>;
  if(!session) return <Login/>;
  if(membro===null) return <SenzaAzienda user={session.user} onCreata={verificaAccesso}/>;

  const accessoLimitato = pagineUtente!==null;
  const paginePermesseEffettive = accessoLimitato ? [...pagineUtente,"account"] : null;
  const tabEffettivo = accessoLimitato && !paginePermesseEffettive.includes(tab) ? (pagineUtente[0]||"account") : tab;
  const page = LAVORI_PAGES[tabEffettivo];

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  return(
    <div style={{display:"flex",minHeight:"100vh",background:C.bg,fontFamily:C.font,color:C.text}}>
      <style>{`
        * { box-sizing: border-box; -webkit-font-smoothing:antialiased; }
        body { margin:0; background:${C.bg}; }
        ::-webkit-scrollbar { width:8px; height:8px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:${C.borderLight}; border-radius:8px; }
        ::-webkit-scrollbar-thumb:hover { background:${C.textMuted}; }
        select, input, textarea, button { font-family:inherit; }
      `}</style>
      <Sidebar tab={tabEffettivo} setTab={(t)=>{if(accessoLimitato&&!paginePermesseEffettive.includes(t))return;localStorage.setItem("posa_tab",t);setTab(t);setMobileOpen(false);}} lavori={lavori} contatti={contatti} nonLetti={nonLetti} user={session?.user} onLogout={()=>supabase.auth.signOut()} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} paginePermesse={accessoLimitato?pagineUtente:null}/>
      <main style={{flex:1,overflow:"auto",minWidth:0}}>
        {/* Mobile topbar */}
        {isMobile && <div style={{display:"grid",gridTemplateColumns:"44px 1fr 44px",alignItems:"center",padding:"6px 8px",background:C.card,borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:100}}>
          <button onClick={()=>setMobileOpen(v=>!v)} aria-label="Menu" style={{width:44,height:44,background:"transparent",border:"none",borderRadius:11,cursor:"pointer",color:C.text,fontSize:18,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>☰</button>
          <div style={{display:"flex",justifyContent:"center"}}><SquadraLogo size={20}/></div>
          <span/>
        </div>}
        {!dbReady
          ? <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"80vh",color:C.textMuted}}>Caricamento dati…</div>
          : <div style={{maxWidth:1040,margin:"0 auto",padding:isMobile?"18px 16px 90px":"44px 32px 100px"}}>
              {tabEffettivo==="dashboard" && <Dashboard lavori={lavori} setLavori={setLavori} operai={operai} rivendite={rivendite} contatti={contatti} setTab={setTab} db={supabase}/>}
              {tabEffettivo==="calendario" && <Calendario lavori={lavori} setLavori={setLavori} operai={operai} setOperai={setOperai} db={supabase}/>}
              {page && <ListaLavori key={tabEffettivo} lavori={lavori} setLavori={setLavori} operai={operai} rivendite={rivendite} statoFilter={page.stato} db={supabase} titoloPagina={page.titolo}/>}
              {tabEffettivo==="operai" && <Operai operai={operai} setOperai={setOperai} lavori={lavori} db={supabase}/>}
              {tabEffettivo==="messaggi" && <Messaggi db={supabase} isAdmin={!accessoLimitato} contatti={contatti} setContatti={setContatti} setTab={(t)=>{localStorage.setItem("posa_tab",t);setTab(t);}} onNonLetti={setNonLetti}/>}
              {tabEffettivo==="contatti" && <Contatti contatti={contatti} setContatti={setContatti} lavori={lavori} setLavori={setLavori} rivendite={rivendite} setRivendite={setRivendite} db={supabase} setTab={(t)=>{localStorage.setItem("posa_tab",t);setTab(t);}} user={session?.user}/>}
              {tabEffettivo==="clienti" && <Clienti lavori={lavori} db={supabase}/>}
              {tabEffettivo==="crm" && <CRM rivendite={rivendite} setRivendite={setRivendite} lavori={lavori} db={supabase}/>}
              {tabEffettivo==="preventivi-rifiutati" && <PreventiviRifiutati lavori={lavori} setLavori={setLavori} db={supabase}/>}
              {tabEffettivo==="preventivi-new" && <Preventivi lavori={lavori} setLavori={setLavori} operai={operai} rivendite={rivendite} db={supabase} setTab={setTab}/>}
              {tabEffettivo==="materiali" && <Materiali lavori={lavori} operai={operai} db={supabase}/>}
              {tabEffettivo==="cantiere" && <ConsuntivoCantiere lavori={lavori} setLavori={setLavori} operai={operai} db={supabase}/>}
              {tabEffettivo==="contabilita" && <Contabilita lavori={lavori} setLavori={setLavori} operai={operai} db={supabase}/>}
              {tabEffettivo==="assenze" && <AssenzeOperai operai={operai} db={supabase}/>}
              {tabEffettivo==="account" && <Account user={session?.user} db={supabase} isAdmin={!accessoLimitato}/>}
            </div>
        }
      </main>
    </div>
  );
}

// ── CONSUNTIVO CANTIERE ───────────────────────────────────────────────────────
function ConsuntivoCantiere({lavori, setLavori, operai, db}){
  const [lavoroId, setLavoroId] = useState(null);
  const [search, setSearch] = useState("");
  const [statoFiltro, setStatoFiltro] = useState("attivi");
  const [sessioni, setSessioni] = useState([]);
  const [materiali, setMateriali] = useState([]);
  const [punchList, setPunchList] = useState([]);
  const [loadingS, setLoadingS] = useState(false);
  const [modalSessione, setModalSessione] = useState(false);
  const [modalFoto, setModalFoto] = useState(null);
  const [editSessione, setEditSessione] = useState(null);
  const [nuovoPunch, setNuovoPunch] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const ef = {data: new Date().toISOString().split("T")[0], note:"", presenze:[]};
  const [form, setForm] = useState(ef);

  useEffect(()=>{ if(lavoroId){ loadDati(); } },[lavoroId]);

  async function loadDati(){
    setLoadingS(true);
    const [{data:s},{data:m},{data:p}] = await Promise.all([
      db.from("sessioni_lavoro").select("*, sessioni_operai(*)").eq("lavoro_id",lavoroId).order("data",{ascending:false}),
      db.from("materiali").select("*").eq("lavoro_id",lavoroId),
      db.from("punch_list").select("*").eq("lavoro_id",lavoroId).order("created_at",{ascending:false}),
    ]);
    setSessioni(s||[]);
    setMateriali(m||[]);
    setPunchList(p||[]);
    setLoadingS(false);
  }

  async function aggiungiPunch(){
    if(!nuovoPunch.trim()) return;
    const {data:n} = await db.from("punch_list").insert([{lavoro_id:lavoroId, descrizione:nuovoPunch.trim(), risolto:false}]).select().single();
    if(n) setPunchList(p=>[n,...p]);
    setNuovoPunch("");
  }
  async function togglePunch(item){
    await db.from("punch_list").update({risolto:!item.risolto}).eq("id",item.id);
    setPunchList(p=>p.map(x=>x.id===item.id?{...x,risolto:!x.risolto}:x));
  }
  async function eliminaPunch(id){
    await db.from("punch_list").delete().eq("id",id);
    setPunchList(p=>p.filter(x=>x.id!==id));
  }

  async function caricaDocumento(file){
    setUploadingDoc(true);
    const path = `${lavoroId}/${Date.now()}_${file.name}`;
    const {error} = await db.storage.from("documenti-cantiere").upload(path, file);
    if(!error){
      const {data:pub} = db.storage.from("documenti-cantiere").getPublicUrl(path);
      const nuovi = [...(lavoro?.documenti||[]), {nome:file.name, url:pub.publicUrl}];
      await db.from("lavori").update({documenti:nuovi}).eq("id",lavoroId);
      setLavori(ls=>ls.map(l=>l.id===lavoroId?{...l,documenti:nuovi}:l));
    }
    setUploadingDoc(false);
  }
  async function rimuoviDocumento(idx){
    const nuovi = (lavoro?.documenti||[]).filter((_,i)=>i!==idx);
    await db.from("lavori").update({documenti:nuovi}).eq("id",lavoroId);
    setLavori(ls=>ls.map(l=>l.id===lavoroId?{...l,documenti:nuovi}:l));
  }

  async function salvaSessione(){
    const sessData = {lavoro_id:lavoroId, data:form.data, note:form.note};
    let sessId;
    if(editSessione){
      const {error} = await db.from("sessioni_lavoro").update(sessData).eq("id",editSessione.id);
      if(error){ alert("Errore salvataggio sessione: "+error.message); console.error(error); return; }
      sessId = editSessione.id;
      await db.from("sessioni_operai").delete().eq("sessione_id",sessId);
    } else {
      const {data:ns, error} = await db.from("sessioni_lavoro").insert([sessData]).select().single();
      if(error){ alert("Errore salvataggio sessione: "+error.message); console.error(error); return; }
      sessId = ns.id;
    }
    const presenze = form.presenze.filter(p=>p.operaio_id&&(parseFloat(p.ore)>0||p.ora_inizio)).map(p=>{
      let ore = parseFloat(p.ore)||0;
      if(p.ora_inizio&&p.ora_fine){
        const [h1,m1]=p.ora_inizio.split(":").map(Number);
        const [h2,m2]=p.ora_fine.split(":").map(Number);
        ore = Math.max(0,((h2*60+m2)-(h1*60+m1))/60);
      }
      return {sessione_id:sessId, operaio_id:p.operaio_id,
        ora_inizio:p.ora_inizio||null, ora_fine:p.ora_fine||null,
        ore, costo_ora:parseFloat(p.costo_ora)||0};
    });
    if(presenze.length>0){
      const {error} = await db.from("sessioni_operai").insert(presenze);
      if(error){ alert("Sessione salvata, ma errore nel salvare gli operai: "+error.message); console.error(error); }
    }
    setModalSessione(false);
    loadDati();
  }

  async function eliminaSessione(id){
    if(!window.confirm("Eliminare questa sessione?")) return;
    await db.from("sessioni_lavoro").delete().eq("id",id);
    setSessioni(ss=>ss.filter(s=>s.id!==id));
  }

  async function uploadFoto(sessId, files){
    for(const file of files){
      const path = `cantieri/${lavoroId}/${sessId}/${Date.now()}_${file.name}`;
      await db.storage.from("foto-cantiere").upload(path, file);
      const {data:u} = db.storage.from("foto-cantiere").getPublicUrl(path);
      const sess = sessioni.find(s=>s.id===sessId);
      const newUrls = [...(sess?.foto_urls||[]), u.publicUrl];
      await db.from("sessioni_lavoro").update({foto_urls:newUrls}).eq("id",sessId);
    }
    loadDati();
  }

  function apriNuovaSessione(){
    setEditSessione(null);
    setForm({...ef, presenze: operai.map(o=>({operaio_id:o.id,nome:o.nome,ora_inizio:"",ora_fine:"",ore:0,costo_ora:o.costo_ora||35}))});
    setModalSessione(true);
  }

  function apriModificaSessione(s){
    setEditSessione(s);
    const presMapped = operai.map(o=>{
      const found = (s.sessioni_operai||[]).find(p=>p.operaio_id===o.id);
      return {operaio_id:o.id,nome:o.nome,ora_inizio:found?.ora_inizio||"",ora_fine:found?.ora_fine||"",ore:found?found.ore:0,costo_ora:found?found.costo_ora:(o.costo_ora||35)};
    });
    setForm({data:s.data, note:s.note||"", presenze:presMapped});
    setModalSessione(true);
  }

  const lavoro = lavori.find(l=>l.id===lavoroId);

  // ── Lista cantieri (vista principale) ──
  const listaFiltrata = lavori
    .filter(l=>{
      if(statoFiltro==="tutti") return true;
      if(statoFiltro==="attivi") return !["Completato","Annullato","Rifiutato"].includes(l.stato);
      return l.stato===statoFiltro;
    })
    .filter(l=>{
      const q = search.toLowerCase();
      return !q || l.titolo?.toLowerCase().includes(q) || l.cliente?.toLowerCase().includes(q) || l.indirizzo?.toLowerCase().includes(q);
    })
    .sort((a,b)=>(b.data_inizio||"").localeCompare(a.data_inizio||""));

  if(!lavoroId){
    return(
      <div style={{display:"flex",flexDirection:"column",gap:18}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>📊 Consuntivo Cantiere</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>Scegli un cantiere per vedere sessioni, ore e costi reali</div>
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Cerca cantiere, cliente, indirizzo…"
            style={{flex:1,minWidth:200,background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
          <select value={statoFiltro} onChange={e=>setStatoFiltro(e.target.value)}
            style={{background:C.card,boxShadow:C.shadowSm,border:"none",borderRadius:11,padding:"10px 14px",fontSize:13,fontWeight:600,color:C.text,fontFamily:"inherit",cursor:"pointer"}}>
            <option value="attivi">Attivi</option>
            <option value="tutti">Tutti</option>
            <option value="Da organizzare">Da organizzare</option>
            <option value="Confermato">Confermati</option>
            <option value="Programmato">Programmati</option>
            <option value="Completato">Completati</option>
          </select>
        </div>
        {listaFiltrata.length===0 ? (
          <div style={{textAlign:"center",color:C.textMuted,padding:60,background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>Nessun cantiere trovato</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {listaFiltrata.map(l=>{
              const col = STATI_COLOR[l.stato]||C.textMuted;
              return(
                <div key={l.id} onClick={()=>setLavoroId(l.id)} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:14,padding:"14px 18px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.cardHover} onMouseLeave={e=>e.currentTarget.style.background=C.card}>
                  <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:col,flexShrink:0}}/>
                    <div style={{minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:14,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.titolo}</div>
                      <div style={{fontSize:12,color:C.textMuted,marginTop:2}}>{l.cliente||"senza cliente"} · {fmt(l.data_inizio)}</div>
                    </div>
                  </div>
                  <Badge label={l.stato} color={col}/>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Calcoli consuntivo (vista dettaglio) ──
  let totOre=0, totCostoMdo=0;
  const costiPerOp = {};
  sessioni.forEach(s=>{
    (s.sessioni_operai||[]).forEach(p=>{
      totOre += p.ore;
      const costo = p.ore * p.costo_ora;
      totCostoMdo += costo;
      if(!costiPerOp[p.operaio_id]) costiPerOp[p.operaio_id]={nome:"",ore:0,costo:0,costo_ora:p.costo_ora};
      costiPerOp[p.operaio_id].ore += p.ore;
      costiPerOp[p.operaio_id].costo += costo;
      costiPerOp[p.operaio_id].costo_ora = p.costo_ora;
    });
  });
  operai.forEach(o=>{ if(costiPerOp[o.id]) costiPerOp[o.id].nome=o.nome; });

  const totMaterie = materiali.reduce((s,m)=>s+(parseFloat(m.costo_totale)||0),0);
  const totCostoReale = totCostoMdo + totMaterie;
  const importoPrev = parseFloat(lavoro?.preventivo)||0;
  const margine = importoPrev - totCostoReale;

  const kpi = [
    {label:"Sessioni lavoro", value:sessioni.length, dot:C.blue},
    {label:"Ore totali",      value:totOre.toFixed(1)+"h", dot:C.purple},
    {label:"Costo manodopera",value:"€"+totCostoMdo.toFixed(0), dot:C.red},
    {label:"Costo materiali", value:"€"+totMaterie.toFixed(0), dot:C.orange},
    {label:"Costo reale tot.",value:"€"+totCostoReale.toFixed(0), dot:C.textMuted},
    {label:"Margine stimato", value:(importoPrev>0?"€"+margine.toFixed(0):"N/D"), dot:margine>=0?C.green:C.red},
  ];

  // Timeline unificata: rapporti + sessioni + punch list
  const timelineEventi = [
    ...(lavoro?.rapporti||[]).map(r=>({tipo:"rapporto", data:r.data, testo:r.testo, foto:r.foto||[]})),
    ...sessioni.map(s=>({tipo:"sessione", data:s.data, testo:s.note||`Sessione di lavoro (${(s.sessioni_operai||[]).length} operai)`, foto:s.foto_urls||[]})),
    ...punchList.map(p=>({tipo:"punch", data:p.created_at?.slice(0,10)||today, testo:p.descrizione, risolto:p.risolto})),
  ].sort((a,b)=>(b.data||"").localeCompare(a.data||""));

  const galleriaFoto = [
    ...(lavoro?.rapporti||[]).flatMap(r=>r.foto||[]),
    ...sessioni.flatMap(s=>s.foto_urls||[]),
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <button onClick={()=>setLavoroId(null)} style={{background:"none",border:"none",color:C.accent,fontSize:12.5,fontWeight:700,cursor:"pointer",padding:0,marginBottom:6}}>← Tutti i cantieri</button>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>📊 {lavoro?.titolo}</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>{lavoro?.cliente||"senza cliente"} · Sessioni di lavoro, ore e costi reali</div>
        </div>
        <Btn onClick={apriNuovaSessione}>+ Nuova Sessione</Btn>
      </div>

      {/* KPI */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10}}>
        {kpi.map(k=>(
          <div key={k.label} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"14px"}}>
            <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:700,color:C.text}}>{k.value}</div>
            <div style={{display:"flex",alignItems:"center",gap:5,marginTop:5}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:k.dot,flexShrink:0}}/>
              <span style={{fontSize:10,color:C.textMuted,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{k.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Timeline cronologica */}
      {timelineEventi.length>0 && (
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>🕓 Timeline cantiere</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {timelineEventi.map((e,i)=>(
              <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{fontSize:15,flexShrink:0,marginTop:1}}>{e.tipo==="rapporto"?"📋":e.tipo==="sessione"?"🕐":e.risolto?"✅":"🔧"}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,color:C.textMuted,fontWeight:700}}>{fmt(e.data)}</div>
                  <div style={{fontSize:12.5,color:C.text,marginTop:1}}>{e.testo}</div>
                  {e.foto?.length>0 && (
                    <div style={{display:"flex",gap:5,marginTop:6,flexWrap:"wrap"}}>
                      {e.foto.map((url,fi)=><img key={fi} src={url} alt="" onClick={()=>setModalFoto(url)} style={{width:44,height:44,objectFit:"cover",borderRadius:7,cursor:"pointer"}}/>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Galleria foto */}
      {galleriaFoto.length>0 && (
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>🖼️ Galleria foto ({galleriaFoto.length})</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(80px,1fr))",gap:8}}>
            {galleriaFoto.map((url,i)=>(
              <img key={i} src={url} alt="" onClick={()=>setModalFoto(url)} style={{width:"100%",aspectRatio:"1",objectFit:"cover",borderRadius:9,cursor:"pointer"}}/>
            ))}
          </div>
        </div>
      )}

      {/* Punch list */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>✅ Punch list — da sistemare prima della consegna</div>
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          <input value={nuovoPunch} onChange={e=>setNuovoPunch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&aggiungiPunch()} placeholder="Es. Ritoccare zoccolino angolo cucina…"
            style={{flex:1,background:C.bg,border:"none",borderRadius:9,padding:"8px 12px",fontSize:12.5,fontFamily:"inherit",outline:"none"}}/>
          <Btn small onClick={aggiungiPunch}>+ Aggiungi</Btn>
        </div>
        {punchList.length===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>Nessun punto da sistemare.</div> : (
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {punchList.map(p=>(
              <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.bg,borderRadius:9}}>
                <div onClick={()=>togglePunch(p)} style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",flex:1,minWidth:0}}>
                  <div style={{width:17,height:17,borderRadius:5,background:p.risolto?C.green:"transparent",border:p.risolto?"none":`1.5px solid ${C.borderLight}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {p.risolto&&<span style={{color:"#fff",fontSize:11,fontWeight:800}}>✓</span>}
                  </div>
                  <span style={{fontSize:12.5,color:p.risolto?C.textMuted:C.text,textDecoration:p.risolto?"line-through":"none"}}>{p.descrizione}</span>
                </div>
                <button onClick={()=>eliminaPunch(p.id)} style={{background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:14}}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documenti */}
      <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"18px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text}}>📁 Documenti — contratti, garanzie, schede tecniche</div>
          <label style={{background:C.bg,borderRadius:9,padding:"6px 14px",fontSize:12,fontWeight:600,color:C.accent,cursor:uploadingDoc?"not-allowed":"pointer"}}>
            {uploadingDoc?"Caricamento…":"+ Carica file"}
            <input type="file" style={{display:"none"}} disabled={uploadingDoc} onChange={e=>{if(e.target.files[0])caricaDocumento(e.target.files[0]); e.target.value="";}}/>
          </label>
        </div>
        {(lavoro?.documenti||[]).length===0 ? <div style={{fontSize:12.5,color:C.textMuted}}>Nessun documento caricato.</div> : (
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {(lavoro.documenti||[]).map((d,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.bg,borderRadius:9}}>
                <a href={d.url} target="_blank" rel="noreferrer" style={{fontSize:12.5,color:C.text,textDecoration:"none"}}>📄 {d.nome}</a>
                <button onClick={()=>rimuoviDocumento(i)} style={{background:"none",border:"none",color:C.textMuted,cursor:"pointer",fontSize:14}}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Costi per operaio */}
      {Object.keys(costiPerOp).length>0&&(
        <div style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"16px 18px"}}>
          <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:12}}>Costo per operaio</div>
          <div style={{display:"flex",flexDirection:"column",gap:2}}>
            {Object.entries(costiPerOp).map(([id,op])=>(
              <div key={id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,padding:"10px 12px",borderRadius:10}}>
                <div style={{fontWeight:600,color:C.text,fontSize:13.5}}>{op.nome||"Operaio"}</div>
                <div style={{display:"flex",gap:16,fontSize:12.5,color:C.textMuted}}>
                  <span>{op.ore.toFixed(1)}h × €{op.costo_ora}/h</span>
                  <span style={{fontWeight:700,color:C.text}}>= €{op.costo.toFixed(0)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sessioni */}
      <div style={{fontWeight:700,fontSize:14,color:C.text,marginTop:4}}>Storico sessioni</div>
      {loadingS
        ? <div style={{textAlign:"center",color:C.textMuted,padding:40}}>Caricamento…</div>
        : sessioni.length===0
          ? <div style={{textAlign:"center",color:C.textMuted,padding:60,background:C.card,boxShadow:C.shadowSm,borderRadius:16}}>
              Nessuna sessione. Aggiungi la prima giornata di lavoro.
            </div>
          : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {sessioni.map(s=>{
                const oreS = (s.sessioni_operai||[]).reduce((t,p)=>t+p.ore,0);
                const costoS = (s.sessioni_operai||[]).reduce((t,p)=>t+(p.ore*p.costo_ora),0);
                return(
                  <div key={s.id} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"14px 18px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginBottom:8}}>
                          <span style={{fontWeight:700,fontSize:14,color:C.text}}>
                            {new Date(s.data+"T12:00:00").toLocaleDateString("it-IT",{weekday:"short",day:"2-digit",month:"long",year:"numeric"})}
                          </span>
                          {oreS>0&&<Badge label={oreS.toFixed(1)+"h lavorate"} color={C.blue}/>}
                          {costoS>0&&<Badge label={"€"+costoS.toFixed(0)} color={C.red}/>}
                        </div>
                        {/* Operai presenti */}
                        {(s.sessioni_operai||[]).length>0&&(
                          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                            {(s.sessioni_operai||[]).map(p=>{
                              const op = operai.find(o=>o.id===p.operaio_id);
                              const orario = p.ora_inizio&&p.ora_fine?` (${p.ora_inizio}→${p.ora_fine})`:"";
                              return <span key={p.id} style={{fontSize:11.5,background:C.bg,color:C.textSub,borderRadius:9,padding:"3px 9px",fontWeight:600}}>
                                {op?.nome||"Operaio"} – {p.ore}h{orario}
                              </span>;
                            })}
                          </div>
                        )}
                        {s.note&&<div style={{fontSize:12,color:C.textMuted,fontStyle:"italic"}}>{s.note}</div>}
                        {/* Foto */}
                        {(s.foto_urls||[]).length>0&&(
                          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
                            {(s.foto_urls||[]).map((url,i)=>(
                              <img key={i} src={url} alt="foto cantiere"
                                style={{width:56,height:56,objectFit:"cover",borderRadius:9,cursor:"pointer"}}
                                onClick={()=>setModalFoto(url)}/>
                            ))}
                          </div>
                        )}
                        {/* Upload foto */}
                        <label style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,fontSize:12,color:C.textMuted,cursor:"pointer"}}>
                          📷 Aggiungi foto
                          <input type="file" accept="image/*" multiple style={{display:"none"}}
                            onChange={e=>uploadFoto(s.id,[...e.target.files])}/>
                        </label>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        <Btn small variant="ghost" onClick={()=>apriModificaSessione(s)}>✏️</Btn>
                        <Btn small variant="danger" onClick={()=>eliminaSessione(s.id)}>🗑</Btn>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
      }

      {/* Modal foto ingrandita */}
      {modalFoto&&(
        <div onClick={()=>setModalFoto(null)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
          <img src={modalFoto} alt="foto" style={{maxWidth:"92vw",maxHeight:"88vh",borderRadius:14,boxShadow:"0 8px 40px rgba(0,0,0,0.5)"}}/>
        </div>
      )}

      {/* Modal nuova/modifica sessione */}
      {modalSessione&&(
        <Modal title={editSessione?"Modifica Sessione":"Nuova Sessione di Lavoro"} onClose={()=>setModalSessione(false)} wide>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <Input label="Data" type="date" value={form.data} onChange={v=>setForm(f=>({...f,data:v}))}/>
            <div style={{fontWeight:700,fontSize:13,color:C.text}}>Operai presenti e ore lavorate</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {form.presenze.map((p,i)=>(
                <div key={p.operaio_id} style={{background:C.bg,borderRadius:11,padding:"10px 12px",marginBottom:4}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:8}}>{p.nome}</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 80px 90px",gap:8,alignItems:"flex-end"}}>
                    <div>
                      <div style={{fontSize:11,color:C.textMuted,marginBottom:3,fontWeight:700,letterSpacing:.5}}>ENTRATA</div>
                      <div style={{display:"flex",gap:4}}>
                        <input type="time" value={p.ora_inizio||""} onChange={e=>{const pr=[...form.presenze];pr[i]={...pr[i],ora_inizio:e.target.value};setForm(f=>({...f,presenze:pr}));}} style={{flex:1,minWidth:0,boxSizing:"border-box",background:C.card,boxShadow:C.shadowSm,borderRadius:9,color:C.text,padding:"7px 8px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                        <button type="button" title="Adesso" onClick={()=>{const now=new Date().toTimeString().slice(0,5);const pr=[...form.presenze];pr[i]={...pr[i],ora_inizio:now};setForm(f=>({...f,presenze:pr}));}} style={{background:C.bg,border:"none",borderRadius:9,color:C.accent,fontSize:12,fontWeight:700,cursor:"pointer",padding:"0 8px"}}>▶</button>
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:C.textMuted,marginBottom:3,fontWeight:700,letterSpacing:.5}}>USCITA</div>
                      <div style={{display:"flex",gap:4}}>
                        <input type="time" value={p.ora_fine||""} onChange={e=>{const pr=[...form.presenze];pr[i]={...pr[i],ora_fine:e.target.value};setForm(f=>({...f,presenze:pr}));}} style={{flex:1,minWidth:0,boxSizing:"border-box",background:C.card,boxShadow:C.shadowSm,borderRadius:9,color:C.text,padding:"7px 8px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                        <button type="button" title="Adesso" onClick={()=>{const now=new Date().toTimeString().slice(0,5);const pr=[...form.presenze];pr[i]={...pr[i],ora_fine:now};setForm(f=>({...f,presenze:pr}));}} style={{background:C.bg,border:"none",borderRadius:9,color:C.accent,fontSize:12,fontWeight:700,cursor:"pointer",padding:"0 8px"}}>■</button>
                      </div>
                    </div>
                    <Input label="Ore" type="number" value={p.ora_inizio&&p.ora_fine ? (Math.max(0,(((parseInt(p.ora_fine.split(":")[0])*60+parseInt(p.ora_fine.split(":")[1]))-(parseInt(p.ora_inizio.split(":")[0])*60+parseInt(p.ora_inizio.split(":")[1])))/60)).toFixed(1)) : (p.ore||"")} onChange={v=>{const pr=[...form.presenze];pr[i]={...pr[i],ore:v,ora_inizio:"",ora_fine:""};setForm(f=>({...f,presenze:pr}));}} placeholder="0"/>
                    <Input label="€/ora" type="number" value={p.costo_ora} onChange={v=>{const pr=[...form.presenze];pr[i]={...pr[i],costo_ora:v};setForm(f=>({...f,presenze:pr}));}}/>
                  </div>
                </div>
              ))}
            </div>
            <Textarea label="Note giornata" value={form.note} onChange={v=>setForm(f=>({...f,note:v}))} rows={2} placeholder="Es. levigatura piano terra completata, attesa asciugatura…"/>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn variant="ghost" onClick={()=>setModalSessione(false)}>Annulla</Btn>
              <Btn onClick={salvaSessione} disabled={!form.data}>💾 Salva</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── FERIE / ASSENZE OPERAI ────────────────────────────────────────────────────
function AssenzeOperai({operai, db}){
  const [assenze, setAssenze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const ef = {operaio_id:"", data_inizio:"", data_fine:"", tipo:"ferie", note:""};
  const [form, setForm] = useState(ef);

  useEffect(()=>{ loadAssenze(); },[]);

  async function loadAssenze(){
    setLoading(true);
    const {data} = await db.from("assenze_operai").select("*").order("data_inizio",{ascending:false});
    setAssenze(data||[]);
    setLoading(false);
  }

  async function salva(){
    const {id, ...rest} = form;
    if(id){
      const {error} = await db.from("assenze_operai").update(rest).eq("id",id);
      if(error){ window.alert("Errore nel salvataggio: "+error.message); return; }
      setAssenze(as=>as.map(a=>a.id===id?{...a,...rest}:a));
    } else {
      const {data:n,error} = await db.from("assenze_operai").insert([rest]).select().single();
      if(error){ window.alert("Errore nel salvataggio: "+error.message); return; }
      if(n) setAssenze(as=>[n,...as]);
    }
    setModal(false);
  }

  async function elimina(id){ if(!window.confirm("Eliminare?")) return; await db.from("assenze_operai").delete().eq("id",id); setAssenze(as=>as.filter(a=>a.id!==id)); }

  const TIPO_COLOR = {ferie:C.green, malattia:C.red, permesso:C.accent, altro:C.textMuted};
  const TIPO_LABEL = {ferie:"🌴 Ferie", malattia:"🤒 Malattia", permesso:"⏰ Permesso", altro:"📋 Altro"};

  function durataGiorni(di,df){
    const d1=new Date(di), d2=new Date(df);
    return Math.round((d2-d1)/(1000*60*60*24))+1;
  }

  // Raggruppamento per operaio
  const perOperaio = {};
  assenze.forEach(a=>{
    if(!perOperaio[a.operaio_id]) perOperaio[a.operaio_id]=[];
    perOperaio[a.operaio_id].push(a);
  });

  // Operai assenti oggi
  const oggi = new Date().toISOString().split("T")[0];
  const assentiOggi = assenze.filter(a=>a.data_inizio<=oggi&&a.data_fine>=oggi);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontFamily:C.fontBrand,letterSpacing:-0.5,fontSize:20,fontWeight:800,color:C.text}}>🗓 Ferie e Assenze</div>
          <div style={{fontSize:13,color:C.textMuted,marginTop:2}}>Calendario assenze squadra</div>
        </div>
        <Btn onClick={()=>{setForm({...ef,operaio_id:operai[0]?.id||""});setModal(true);}}>+ Aggiungi Assenza</Btn>
      </div>

      {/* Alert oggi */}
      {assentiOggi.length>0&&(
        <div style={{background:C.red+"12",border:`1px solid ${C.red}44`,borderRadius:16,padding:"12px 18px"}}>
          <div style={{fontWeight:700,color:C.red,fontSize:13,marginBottom:6}}>⚠️ Assenti oggi ({assentiOggi.length})</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {assentiOggi.map(a=>{
              const op=operai.find(o=>o.id===a.operaio_id);
              return <Badge key={a.id} label={`${op?.nome||"?"} – ${TIPO_LABEL[a.tipo]||a.tipo}`} color={TIPO_COLOR[a.tipo]||C.textMuted}/>;
            })}
          </div>
        </div>
      )}

      {/* Lista per operaio */}
      {loading
        ? <div style={{textAlign:"center",color:C.textMuted,padding:40}}>Caricamento…</div>
        : operai.length===0
          ? <div style={{textAlign:"center",color:C.textMuted,padding:40}}>Nessun operaio registrato</div>
          : operai.map(op=>{
              const ass = perOperaio[op.id]||[];
              return(
                <div key={op.id} style={{background:C.card,boxShadow:C.shadowSm,borderRadius:16,padding:"14px 18px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:ass.length?10:0}}>
                    <div style={{fontWeight:800,fontSize:14,color:C.text}}>◎ {op.nome}</div>
                    <Btn small onClick={()=>{setForm({...ef,operaio_id:op.id});setModal(true);}}>+ Assenza</Btn>
                  </div>
                  {ass.length===0
                    ? <div style={{fontSize:12,color:C.textMuted,fontStyle:"italic"}}>Nessuna assenza registrata</div>
                    : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {ass.map(a=>(
                          <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",padding:"6px 10px",background:C.bg,borderRadius:11,border:`1px solid ${C.border}`}}>
                            <Badge label={TIPO_LABEL[a.tipo]||a.tipo} color={TIPO_COLOR[a.tipo]||C.textMuted}/>
                            <span style={{fontSize:13,color:C.text}}>
                              {new Date(a.data_inizio+"T12:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"short"})}
                              {" → "}
                              {new Date(a.data_fine+"T12:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric"})}
                            </span>
                            <span style={{fontSize:12,color:C.textMuted}}>{durataGiorni(a.data_inizio,a.data_fine)} gg</span>
                            {a.note&&<span style={{fontSize:12,color:C.textMuted,fontStyle:"italic",flex:1}}>{a.note}</span>}
                            <div style={{display:"flex",gap:4,marginLeft:"auto"}}>
                              <Btn small variant="ghost" onClick={()=>{setForm({...a});setModal(true);}}>✏️</Btn>
                              <Btn small variant="danger" onClick={()=>elimina(a.id)}>🗑</Btn>
                            </div>
                          </div>
                        ))}
                      </div>
                  }
                </div>
              );
            })
      }

      {/* Modal */}
      {modal&&(
        <Modal title={form.id?"Modifica Assenza":"Nuova Assenza"} onClose={()=>setModal(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Select label="Operaio" value={form.operaio_id} onChange={v=>setForm(f=>({...f,operaio_id:v}))}
              options={operai.map(o=>({value:o.id,label:o.nome}))}/>
            <Select label="Tipo" value={form.tipo} onChange={v=>setForm(f=>({...f,tipo:v}))}
              options={Object.entries(TIPO_LABEL).map(([v,l])=>({value:v,label:l}))}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Input label="Data inizio" type="date" value={form.data_inizio} onChange={v=>setForm(f=>({...f,data_inizio:v}))}/>
              <Input label="Data fine"   type="date" value={form.data_fine}   onChange={v=>setForm(f=>({...f,data_fine:v}))}/>
            </div>
            <Textarea label="Note" value={form.note} onChange={v=>setForm(f=>({...f,note:v}))} rows={2} placeholder="Motivo, dettagli…"/>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn variant="ghost" onClick={()=>setModal(false)}>Annulla</Btn>
              <Btn onClick={salva} disabled={!form.operaio_id||!form.data_inizio||!form.data_fine}>💾 Salva</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
